'use client'

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import ReactMarkdown from 'react-markdown'
import {
  X,
  ArrowUp,
  Sparkles,
  Wand2,
  Check,
  Link2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Coins,
  Download,
  Info,
  Bot,
  CheckCircle2,
  XCircle,
  Loader2,
  BrainCircuit,
  StopCircle,
  Trash2,
  Funnel,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  Megaphone,
  Lock,
  Target,
  MousePointerClick,
  MessageCircle,
  MessageSquare,
  Pencil,
  RotateCcw,
  ShoppingBag,
  GraduationCap,
  Users,
  Trophy,
  ImageIcon,
  PenLine,
  Rocket,
  BarChart3,
  Facebook,
  DollarSign,
  CalendarDays,
  Layers3,
  Zap,
  Upload,
  WandSparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useIsOwner } from '@/hooks/useIsOwner'
import { useSellerId } from '@/hooks/useSellerId'
import { useAIMasterChat, type AIMasterChatMessage } from '@/hooks/useAIMasterChat'
import { useBranding } from '@/hooks/useBranding'
import { useSamanthaNavigation } from '@/hooks/useSamanthaNavigation'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { AIMasterTrainingOverlay } from './AIMasterTrainingOverlay'
import { useFunnel } from '@/contexts/FunnelContext'
import { CreditsBuyCreditsModal } from '@/components/dashboard/credits/CreditsBuyCreditsModal'
import { useSynSpeech } from '@/components/ai/useSynSpeech'
import { openTaskFlow } from '@/lib/task-flow/openTaskFlow'
import { useTaskFlowStore, TaskFlowRequest } from '@/lib/task-flow/taskFlowStore'
import {
  EMPTY_CREATE_CONTENT_DIRECTION,
  EMPTY_CREATE_CONTENT_DISCOVERY,
  applyCreateContentDirectionAnswer,
  applyCreateContentDiscoveryAnswer,
  getCreateContentDirectionQuestion,
  getCreateContentDirectionStep,
  normalizeCreateContentDirection,
  normalizeCreateContentDiscovery,
  parseCreateContentDirectionButton,
  type CreateContentDirectionState,
} from '@/lib/task-flow/chat-flows/createContentDirectionChat'
import {
  createAdvancedContentProject,
} from '@/lib/task-flow/createContentProject.client'
import {
  buildExpressSelectedHook,
  buildExpressSelectedIdea,
  createExpressContentProject,
  getExpressScriptJob,
  saveExpressContentSnapshot,
  startExpressScriptJob,
  updateExpressProject,
} from '@/lib/task-flow/createContentExpress.client'
import {
  prepareContentProduction,
} from '@/lib/task-flow/createContentProduction.client'
import {
  resolveLightweightIntentDetailed,
  type LightweightIntent,
} from '@/lib/task-flow/intentGuards'
import {
  getLightweightIntentSuggestions,
  type LightweightIntentSuggestion,
} from '@/lib/task-flow/lightweightIntentSuggestions'
import { SkillsProvider, useSkills } from '@/contexts/SkillsContext'
import { SkillSelector, ActiveSkillBanner } from '@/components/samantha/SkillSelector'
import { SamanthaSteps } from '@/components/samantha/SamanthaSteps'
import { SamanthaActivityHeader } from '@/components/samantha/SamanthaActivityHeader'
import {
  getSamanthaExpressActions,
  type SamanthaExpressAction,
} from '@/lib/samantha/express-actions'
import {
  ChatEntityResults,
  type ChatEntityResultsPayload,
} from '@/components/samantha/chat/ChatEntityResults'
import { useFunnelTemplatePicker } from '@/components/funnel/templates/FunnelTemplatePickerProvider'
import PublicationRenderer from '@/components/publications/PublicationRenderer'
import PublicationTemplateSelector from '@/components/publications/PublicationTemplateSelector'
import {
  getPublicationTemplateById,
  getPublicationTemplateOptions,
} from '@/lib/publications/template-options'
import { PublicationTemplateOption } from '@/lib/publications/types'
import { createPortal } from 'react-dom'
import {
  publishLaunchpadContext,
  subscribeLaunchpadContext,
} from '@/lib/launchpad/browser-context'
import type {
  LaunchpadContextPayload,
  LaunchpadExpressAction,
} from '@/hooks/useLaunchpadContext'
import { useOperationalCapacityGate } from '@/contexts/OperationalCapacityGateContext'
import {
  useSamanthaPageContext,
} from '@/hooks/useSamanthaPageContext'

import {
  getContextualComposerConfig,
  resolveContextualComposerIntent,
} from '@/lib/samantha/contextual-composer'
import {
  useContextPublishComposer,
} from '@/components/samantha/context-composer/useContextPublishComposer'

import {
  ContextPublishComposerExtras,
} from '@/components/samantha/context-composer/ContextPublishComposerExtras'
import type {
  EncounterContributionType,
} from '@/components/community/feed/types'

type AIMasterUIState = 'closed' | 'chat' | 'training' | 'draft'

type CreditPackage = {
  id: string
  name: string
  credits: number
  price: number
  priceLabel?: string
  popular?: boolean
  description?: string
  features?: string[]
  packKey?: string
  product_id?: number
}

type PublicationPlatform = 'instagram' | 'facebook' | 'tiktok' | 'multi'
type PublicationFormat = 'text' | 'image' | 'carousel' | 'mixed'

type PublicationDiscoveryStep =
  | 'platform'
  | 'format'
  | 'intent'
  | 'sector'
  | 'template'
  | 'reference'
  | 'instruction'
  | 'strategic_context'

type PublicationIntent =
  | 'announcement'
  | 'educational'
  | 'authority'
  | 'offer'
  | 'story'
  | 'community'
  | 'testimonial'
  | 'carousel'

type PublicationSector =
  | 'creator_personal_brand'
  | 'ai_technology'
  | 'coaching_education'
  | 'fitness_wellness'
  | 'beauty_skincare'
  | 'business_corporate'
  | 'network_marketing'
  | 'events_webinars'
  | 'digital_products'
  | 'local_business'

type PublicationVisualStyle =
  | 'premium_dark'
  | 'clean_white'
  | 'gradient_modern'
  | 'editorial_magazine'
  | 'futuristic_holographic'
  | 'bold_viral'
  | 'minimal_luxury'
  | 'ugc_native'
  | 'corporate_clean'
  | 'playful_creator'

type PendingPublicationDiscovery = {
  step: PublicationDiscoveryStep
  originalRequest: string
  platform?: string
  format?: string
  intent?: PublicationIntent
  sector?: PublicationSector
  visualStyle?: PublicationVisualStyle
  templateId?: string
  templateOptionId?: string
  themeId?: string
  wantsReferenceImage?: boolean
  instruction: string
  sourceFlow?: 'ads_campaign'
  forceCreateInChat?: boolean
  silentUserMessage?: boolean
  contextGate?: any
  creativeMode?: 'template' | 'professional_image'
  referenceImageUrl?: string
  productImageUrl?: string
  brandImageUrl?: string
}

type AdsCampaignGoal = 'leads' | 'sales' | 'traffic' | 'whatsapp'
type AdsCampaignCreativeMode = 'existing' | 'new_visual' | 'new_copy' | 'new_both'
type AdsCampaignSourceType = 'funnel' | 'product' | 'challenge' | 'course' | 'community' | 'manual'

type AdsCampaignDiscoveryStep =
  | 'source'
  | 'goal'
  | 'creative'
  | 'budget'
  | 'review'

type PendingAdsCampaignDiscovery = {
  step: AdsCampaignDiscoveryStep
  originalRequest: string
  sourceType?: AdsCampaignSourceType
  sourceId?: string
  sourceName?: string
  goal?: AdsCampaignGoal
  creativeMode?: AdsCampaignCreativeMode
  budgetDaily?: number
  creativeCandidates?: ChatEntityResultsPayload | null

  waitingForCreativeGeneration?: boolean
  generatedCreativeId?: string
  generatedCreativeTitle?: string
  generatedCreativePreview?: any
}

type PendingCreateContentPlan = {
  request: TaskFlowRequest
  direction: CreateContentDirectionState['direction']
  discovery: CreateContentDirectionState['discovery']
  snapshotId: number | null
  plan: any
}

type PendingExpressContent = {
  pending: PendingCreateContentPlan

  messageId: string

  projectId: number
  project: any
  brief: any

  selectedIdea: any
  selectedHook: any

  productImageUrl?: string | null

  jobId?: number | null
}

type CreateContentScriptCardPayload = {
  phase:
    | 'awaiting_product'
    | 'generating_script'
    | 'script_ready'
    | 'preparing_production'
    | 'production_ready'
    | 'script_error'
    | 'production_error'

  projectId: number

  jobId?: number | null
  status?: string | null
  progress?: number
  step?: string | null

  productionStage?: string | null
  productionJobId?: number | null

  plan?: any
  selectedIdea?: any
  selectedHook?: any

  script?: any
  generation?: any

  productRequired?: boolean
  productImageUrl?: string | null

  generateStoryboardImages?: boolean
  allowHyperframesScenes?: boolean

  dryRun?: boolean

  error?: string | null
}

type LaunchpadDiscoveryState = {
  step: 'start' | 'goal' | 'assets' | 'guidance' | 'done'
  role: 'owner' | 'user'
  answers: Record<string, string>
}

type EmbeddedConsoleStarter = {
  id: string
  label: string
  description: string
  prompt: string
  featured?: boolean
}

const EMBEDDED_CONTENT_STARTERS: EmbeddedConsoleStarter[] = [
  {
    id: 'create-content',
    label: 'Crear contenido',
    description:
      'Desarrolla contenido de video paso a paso.',
    prompt: 'Crear contenido',
    featured: true,
  },
  {
    id: 'create-publication',
    label: 'Crear una publicación',
    description:
      'Crea una imagen o carrusel para redes sociales.',
    prompt: 'Crear una publicación',
  },
  {
    id: 'give-me-an-idea',
    label: 'Dame una idea',
    description:
      'Encuentra el enfoque correcto antes de comenzar.',
    prompt: 'Dame una idea',
  },
]

const PUBLICATION_CHAT_DRY_RUN = true
const PUBLICATION_CREATE_IN_CHAT_FOR_ADMINS = true

const CREATE_CONTENT_CHAT_DRY_RUN =
  PUBLICATION_CHAT_DRY_RUN

const CREATE_CONTENT_DRY_RUN_PROJECT_ID =
  6

function getWorkerLabel(worker?: string | null) {
  if (!worker) return null

  switch (worker) {
    case 'discovery_worker':
      return '🔍 Discovery'
    case 'funnel_worker':
      return '🏗️ Builder'
    case 'funnel_editor':
      return '✏️ Editor'
    case 'content_creator':
      return '🎨 Content Creator'
    case 'platform_support':
      return '🛟 Soporte'
    case 'crm_assistant':
      return '📊 CRM'
    case 'ads_worker':
      return '📣 Ads'      
    case 'platform_navigator':
      return '🧭 Navegador'
    default:
      return worker
  }
}

function getActionLabel(tool: string) {
  switch (tool) {
    case 'save_user_strategic_profile':
      return 'Perfil estratégico guardado'
    case 'mark_profile_ready_for_funnel':
      return 'Perfil listo para crear embudo'
    case 'generate_funnel_from_strategy':
      return 'Embudo generado'
    case 'create_funnel':
      return 'Embudo creado'
    case 'replace_funnel_draft':
      return 'Estructura del embudo actualizada'
    case 'add_funnel_module':
      return 'Módulo agregado al embudo'
    case 'update_funnel_module':
      return 'Módulo actualizado'
    default:
      return String(tool || '').replace(/_/g, ' ')
  }
}

function hasStrategyContent(strategy: any) {
  if (!strategy) return false

  const fields = [
    strategy.audience,
    strategy.offer,
    strategy.angle,
    strategy.funnel_type,
  ]

  return fields.some((v) => String(v ?? '').trim().length > 0)
}

function coerceContentToString(content: any) {
  if (content == null) return ''
  if (typeof content === 'string') return content
  if (typeof content === 'number' || typeof content === 'boolean') return String(content)

  if (typeof content === 'object') {
    if (typeof content.text === 'string') return content.text
    if (typeof content.message === 'string') return content.message
    if (typeof content.reply === 'string') return content.reply

    try {
      return JSON.stringify(content)
    } catch {
      return '[object]'
    }
  }

  return String(content)
}

function PublicationPreviewSkeleton({ previewType = 'image' }: { previewType?: string }) {
  const isCarousel = previewType === 'carousel'
  const isText = previewType === 'text'

  return (
    <div className="mt-2 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex items-center gap-2 border-b border-neutral-100 px-3 py-2 dark:border-neutral-800">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        </div>
        <div>
          <div className="text-xs font-semibold text-neutral-900 dark:text-neutral-100">
            Generando publicación
          </div>
          <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
            {isCarousel ? 'Preparando carrusel visual…' : isText ? 'Escribiendo copy…' : 'Diseñando preview visual…'}
          </div>
        </div>
      </div>

      <div className="p-3">
        {isText ? (
          <div className="space-y-2">
            <div className="h-3 w-10/12 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
            <div className="h-3 w-8/12 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
            <div className="h-3 w-9/12 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
          </div>
        ) : (
          <div className="aspect-square w-full overflow-hidden rounded-2xl bg-neutral-100 dark:bg-neutral-900">
            <div className="h-full w-full animate-pulse bg-neutral-200 dark:bg-neutral-800" />
          </div>
        )}

        {isCarousel ? (
          <div className="mt-3 flex gap-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 flex-1 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-800"
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function resolvePublicationTemplateFromDiscovery(state: PendingPublicationDiscovery) {
  const format = state.format || 'image'
  const intent = state.intent || 'announcement'
  const sector = state.sector || 'creator_personal_brand'
  const visualStyle = state.visualStyle || inferPublicationStyle(state)

  if (format === 'carousel') {
    if (sector === 'beauty_skincare') {
      return {
        visualStyle,
        templateId: 'carousel-premium-framework',
        themeId: 'warm-natural',
      }
    }

    if (sector === 'ai_technology') {
      return {
        visualStyle,
        templateId: 'carousel-premium-framework',
        themeId: 'premium-dark-ai',
      }
    }

    return {
      visualStyle,
      templateId: 'carousel-premium-framework',
      themeId: 'minimal-clean',
    }
  }

  if (sector === 'beauty_skincare') {
    return {
      visualStyle,
      templateId: 'single-hero-split',
      themeId: 'warm-natural',
    }
  }

  if (sector === 'business_corporate') {
    return {
      visualStyle,
      templateId: 'single-hero-split',
      themeId: 'corporate-clean',
    }
  }

  if (intent === 'offer') {
    return {
      visualStyle,
      templateId: 'single-hero-split',
      themeId: 'gradient-modern',
    }
  }

  return {
    visualStyle,
    templateId: 'single-hero-split',
    themeId: 'minimal-clean',
  }
}

function AIFunnelReviewCard({
  data,
  onButtonClick,
  message,
}: {
  data: any
  onButtonClick?: (
    button: any,
    message?: AIMasterChatMessage
  ) => void
  message: AIMasterChatMessage
}) {
  const items = [
    {
      label: 'Idea',
      value: data.description,
      icon: Sparkles,
    },
    {
      label: 'Objetivo',
      value: aiFunnelGoalLabel(data.goal),
      icon: Target,
    },
    {
      label: 'Audiencia',
      value: data.audience || 'Inferida automáticamente',
      icon: Users,
    },
    {
      label: 'Oferta',
      value:
        data.offer ===
        'Oferta inferida automáticamente a partir de la idea'
          ? 'La IA definirá la oferta y el ángulo de conversión'
          : data.offer || 'Inferida automáticamente',
      icon: ShoppingBag,
    },
    {
      label: 'Diseño',
      value: data.useBranding
        ? `Branding de ${data.branding?.brandName || 'tu marca'}`
        : 'Dirección visual creada por IA',
      icon: WandSparkles,
    },
    {
      label: 'Referencias',
      value: data.references?.length
        ? `${data.references.length} referencia${
            data.references.length === 1 ? '' : 's'
          }`
        : 'Sin referencias externas',
      icon: Link2,
    },
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      className="mt-3 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950"
    >
      <div className="border-b border-neutral-100 bg-gradient-to-br from-violet-50 via-white to-cyan-50 p-4 dark:border-neutral-800 dark:from-violet-500/10 dark:via-neutral-950 dark:to-cyan-500/10">
        <div className="flex items-start gap-3">
          <motion.div
            initial={{ scale: 0.8, rotate: -8 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.1 }}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-neutral-950 text-white dark:bg-white dark:text-neutral-950"
          >
            <BrainCircuit className="h-5 w-5" />
          </motion.div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-300">
              Brief inteligente
            </div>

            <div className="mt-1 text-base font-black tracking-tight text-neutral-950 dark:text-neutral-50">
              Ya tengo suficiente información
            </div>

            <div className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
              Esta es la dirección que usaré para construir la primera versión.
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 p-4">
        {items.map((item, index) => {
          const Icon = item.icon

          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.08 + index * 0.06,
                duration: 0.24,
              }}
              className="flex items-start gap-3 rounded-2xl border border-neutral-100 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-900/60"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-neutral-700 shadow-sm dark:bg-neutral-800 dark:text-neutral-200">
                <Icon className="h-4 w-4" />
              </div>

              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400">
                  {item.label}
                </div>

                <div className="mt-0.5 text-sm font-medium leading-5 text-neutral-900 dark:text-neutral-100">
                  {item.value}
                </div>
              </div>
            </motion.div>
          )
        })}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="rounded-2xl bg-neutral-950 p-3 text-xs leading-5 text-white dark:bg-white dark:text-neutral-950"
        >
          Crearé una landing HTML completa, responsiva, editable y orientada a conversión.
        </motion.div>

        {Array.isArray(data.buttons) && data.buttons.length > 0 ? (
          <ChatActionButtons
            buttons={data.buttons}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}
      </div>
    </motion.div>
  )
}

function CreateContentPlanCard({
  payload,
}: {
  payload: {
    plan: any
    direction?: any
    discovery?: any
  }
}) {
  const plan = payload?.plan || {}
  const direction =
    payload?.direction || {}
  const discovery =
    payload?.discovery || {}

  const details = [
    {
      label: 'Objetivo',
      value:
        plan?.objective ||
        discovery?.objective ||
        '—',
    },
    {
      label: 'Audiencia',
      value:
        plan?.audience ||
        discovery?.audience ||
        '—',
    },
    {
      label: 'Formato',
      value:
        direction?.publishTarget ||
        plan?.platform ||
        '—',
    },
    {
      label: 'Duración',
      value:
        direction?.durationTargetSec
          ? `${direction.durationTargetSec} segundos`
          : plan?.durationTargetSec
            ? `${plan.durationTargetSec} segundos`
            : '—',
    },
  ]

  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 12,
        scale: 0.985,
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      transition={{
        duration: 0.28,
      }}
      className="mt-3 w-full overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.08)] dark:border-neutral-800 dark:bg-neutral-950 dark:shadow-[0_18px_60px_rgba(0,0,0,0.32)]"
    >
      <div className="relative overflow-hidden border-b border-neutral-100 px-5 py-5 dark:border-neutral-800">
        <div className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-violet-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 left-6 h-40 w-40 rounded-full bg-cyan-500/10 blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-400">
            <Sparkles className="h-3.5 w-3.5" />
            Dirección creativa
          </div>

          <h3 className="mt-3 text-xl font-bold tracking-tight text-neutral-950 dark:text-white">
            {plan?.title ||
              'Plan de contenido'}
          </h3>

          {plan?.reasoning ? (
            <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-600 dark:text-neutral-300">
              {plan.reasoning}
            </p>
          ) : null}
        </div>
      </div>

      <div className="p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {details.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-3 dark:border-neutral-800 dark:bg-neutral-900"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-400">
                {item.label}
              </div>

              <div className="mt-1.5 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {plan?.hook ? (
          <div className="mt-4 rounded-2xl border border-violet-500/15 bg-violet-500/[0.06] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-400">
              Hook propuesto
            </div>

            <div className="mt-2 text-base font-semibold leading-6 text-neutral-950 dark:text-white">
              “{plan.hook}”
            </div>
          </div>
        ) : null}
      </div>
    </motion.div>
  )
}

function CreateContentScriptCard({
  payload,
  onAction,
  onSettingsChange,
  masterAgentName,
}: {
  payload: CreateContentScriptCardPayload
  onAction?: (action: any) => void
  onSettingsChange?: (
    patch: Partial<CreateContentScriptCardPayload>
  ) => void
  masterAgentName: string
}) {
  const progress = Math.max(
    0,
    Math.min(100, Number(payload?.progress || 0))
  )

  const script = payload?.script || null

  if (payload.phase === 'awaiting_product') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 overflow-hidden rounded-[28px] border border-amber-500/25 bg-white shadow-sm dark:bg-neutral-950"
      >
        <div className="border-b border-amber-500/15 bg-amber-500/[0.06] p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-600">
              <ShoppingBag className="h-5 w-5" />
            </div>

            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-600">
                Producto de referencia
              </div>

              <h3 className="mt-1 text-base font-bold text-neutral-950 dark:text-white">
                Necesito ver el producto antes de escribir el guion
              </h3>

              <p className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                Usaré la imagen para mantener el empaque, identidad y presentación correctos durante la producción.
              </p>
            </div>
          </div>
        </div>

        <div className="p-4">
          {payload.productImageUrl ? (
            <div className="mb-4 overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900">
              <img
                src={payload.productImageUrl}
                alt="Producto de referencia"
                className="aspect-square w-full object-contain"
              />

              <div className="flex items-center gap-2 border-t border-neutral-200 px-3 py-2 text-xs font-medium text-emerald-600 dark:border-neutral-800">
                <CheckCircle2 className="h-4 w-4" />
                Producto cargado correctamente
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() =>
              onAction?.({
                value:
                  'create_content_product_upload',
              })
            }
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-neutral-950 px-4 py-3 text-sm font-semibold text-white dark:bg-white dark:text-neutral-950"
          >
            <Upload className="h-4 w-4" />

            {payload.productImageUrl
              ? 'Cambiar imagen del producto'
              : 'Subir imagen del producto'}
          </button>

          {payload.error ? (
            <div className="mt-3 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-3 py-2 text-xs leading-5 text-red-600">
              {payload.error}
            </div>
          ) : null}
        </div>
      </motion.div>
    )
  }

  if (
    payload.phase ===
      'preparing_production' ||
    payload.phase ===
      'production_ready'
  ) {
    const isReady =
      payload.phase ===
      'production_ready'

    return (
      <motion.div
        initial={{
          opacity: 0,
          y: 12,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        className="mt-3 overflow-hidden rounded-[28px] border border-cyan-500/20 bg-white shadow-sm dark:bg-neutral-950"
      >
        <div className="border-b border-neutral-100 bg-gradient-to-br from-cyan-500/[0.08] via-white to-violet-500/[0.08] p-5 dark:border-neutral-800 dark:via-neutral-950">
          <div className="flex items-start gap-3">
            <div
              className={[
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
                isReady
                  ? 'bg-emerald-500/10 text-emerald-600'
                  : 'bg-cyan-500/10 text-cyan-600',
              ].join(' ')}
            >
              {isReady ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div
                className={[
                  'text-[11px] font-bold uppercase tracking-[0.16em]',
                  isReady
                    ? 'text-emerald-600'
                    : 'text-cyan-600',
                ].join(' ')}
              >
                {isReady
                  ? 'Producción preparada'
                  : 'Preparando producción'}
              </div>

              <h3 className="mt-1 font-bold text-neutral-950 dark:text-white">
                {isReady
                  ? 'Tu storyboard está listo'
                  : `${masterAgentName} está construyendo el storyboard`}
              </h3>

              <p className="mt-1 text-xs leading-5 text-neutral-500">
                {payload.step ||
                  'Preparando el proyecto y sus escenas.'}
              </p>
            </div>

            <div className="rounded-full bg-cyan-500/10 px-3 py-1 text-xs font-bold text-cyan-600">
              {progress}%
            </div>
          </div>
        </div>

        <div className="space-y-4 p-5">
          <div className="h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
            <motion.div
              initial={{
                width: 0,
              }}
              animate={{
                width: `${Math.max(
                  5,
                  progress
                )}%`,
              }}
              className={[
                'h-full rounded-full',
                isReady
                  ? 'bg-emerald-500'
                  : 'bg-cyan-600',
              ].join(' ')}
            />
          </div>

          {payload.productImageUrl ? (
            <div className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900">
              <img
                src={
                  payload.productImageUrl
                }
                alt="Producto utilizado"
                className="h-14 w-14 rounded-xl object-contain"
              />

              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400">
                  Referencia conservada
                </div>

                <div className="mt-1 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                  Se utilizará en las escenas correspondientes.
                </div>
              </div>
            </div>
          ) : null}

          {!isReady ? (
            <div className="text-[11px] text-neutral-400">
              Puedes mantener abierto el chat mientras se completa el storyboard premium.
            </div>
          ) : null}
        </div>
      </motion.div>
    )
  }

  if (
    payload.phase ===
    'production_error'
  ) {
    return (
      <div className="mt-3 overflow-hidden rounded-[28px] border border-red-500/25 bg-white p-5 shadow-sm dark:bg-neutral-950">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-500/10 text-red-500">
            <XCircle className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-neutral-950 dark:text-white">
              No pude preparar la producción
            </h3>

            <p className="mt-1 text-xs leading-5 text-neutral-500">
              {payload.error ||
                'Ocurrió un error preparando el storyboard.'}
            </p>

            <button
              type="button"
              onClick={() =>
                onAction?.({
                  value:
                    'create_content_continue_production',
                })
              }
              className="mt-3 rounded-xl border border-neutral-200 px-3 py-2 text-xs font-semibold dark:border-neutral-800"
            >
              Intentar nuevamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (payload.phase === 'script_error') {
    return (
      <div className="mt-3 overflow-hidden rounded-[28px] border border-red-500/25 bg-white p-5 shadow-sm dark:bg-neutral-950">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-500/10 text-red-500">
            <XCircle className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-neutral-950 dark:text-white">
              No pude terminar el guion
            </h3>

            <p className="mt-1 text-xs leading-5 text-neutral-500">
              {payload.error || 'Ocurrió un error durante la generación.'}
            </p>

            <button
              type="button"
              onClick={() =>
                onAction?.({
                  value: 'create_content_script_retry',
                })
              }
              className="mt-3 rounded-xl border border-neutral-200 px-3 py-2 text-xs font-semibold dark:border-neutral-800"
            >
              Intentar nuevamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (payload.phase === 'generating_script') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 overflow-hidden rounded-[28px] border border-violet-500/20 bg-white shadow-sm dark:bg-neutral-950"
      >
        <div className="border-b border-neutral-100 bg-gradient-to-br from-violet-500/[0.08] via-white to-cyan-500/[0.08] p-5 dark:border-neutral-800 dark:via-neutral-950">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-500/10 text-violet-600">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-violet-600">
                Creación en progreso
              </div>

              <h3 className="mt-1 font-bold text-neutral-950 dark:text-white">
                Estoy creando tu guion premium
              </h3>

              <p className="mt-1 text-xs leading-5 text-neutral-500">
                {payload.step ||
                  'Construyendo narrativa, estructura y dirección creativa.'}
              </p>
            </div>

            <div className="rounded-full bg-violet-500/10 px-3 py-1 text-xs font-bold text-violet-600">
              {progress}%
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: `${Math.max(4, progress)}%`,
              }}
              className="h-full rounded-full bg-violet-600"
            />
          </div>

          <div className="mt-3 text-[11px] text-neutral-400">
            Puedes cerrar el chat y volver después. El proyecto y el job ya quedaron guardados.
          </div>
        </div>

        {payload.productImageUrl ? (
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900">
            <img
              src={payload.productImageUrl}
              alt="Producto utilizado"
              className="h-14 w-14 rounded-xl object-contain"
            />

            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400">
                Referencia de producto
              </div>

              <div className="mt-1 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                Esta imagen se conservará para el storyboard y las escenas.
              </div>
            </div>
          </div>
        ) : null}
      </motion.div>
    )
  }

  if (!script) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-3 overflow-hidden rounded-[28px] border border-emerald-500/25 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.08)] dark:bg-neutral-950"
    >
      <div className="border-b border-emerald-500/15 bg-emerald-500/[0.06] p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
              Guion listo
            </div>

            <h3 className="mt-1 text-lg font-bold text-neutral-950 dark:text-white">
              {script.title || 'Guion del proyecto'}
            </h3>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {script.hook ? (
          <div className="rounded-2xl border border-violet-500/15 bg-violet-500/[0.06] p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-600">
              Hook
            </div>

            <div className="mt-2 text-sm font-semibold leading-6 text-neutral-950 dark:text-white">
              “{script.hook}”
            </div>
          </div>
        ) : null}

        <div className="max-h-[340px] overflow-y-auto rounded-2xl bg-neutral-50 p-4 dark:bg-neutral-900">
          <div className="whitespace-pre-wrap text-sm leading-6 text-neutral-700 dark:text-neutral-200">
            {script.script}
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-neutral-950 dark:text-white">
                Generar imágenes del storyboard
              </div>

              <div className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                {masterAgentName} generará con IA los frames visuales de cada escena. Si lo desactivas, solo preparará la estructura del storyboard.
              </div>
            </div>

            <button
              type="button"
              role="switch"
              aria-checked={
                payload.generateStoryboardImages !== false
              }
              onClick={() =>
                onSettingsChange?.({
                  generateStoryboardImages:
                    payload.generateStoryboardImages === false,
                })
              }
              className={[
                'relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition',
                payload.generateStoryboardImages !== false
                  ? 'border-neutral-950 bg-neutral-950 dark:border-white dark:bg-white'
                  : 'border-neutral-300 bg-neutral-200 dark:border-neutral-700 dark:bg-neutral-800',
              ].join(' ')}
            >
              <span
                className={[
                  'inline-block h-5 w-5 rounded-full bg-white shadow transition dark:bg-neutral-950',
                  payload.generateStoryboardImages !== false
                    ? 'translate-x-5'
                    : 'translate-x-0.5',
                ].join(' ')}
              />
            </button>
          </label>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() =>
              onAction?.({
                value: 'create_content_continue_production',
              })
            }
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-neutral-950 px-4 py-3 text-sm font-semibold text-white dark:bg-white dark:text-neutral-950"
          >
            <WandSparkles className="h-4 w-4" />
            Continuar producción
          </button>

          <button
            type="button"
            onClick={() =>
              onAction?.({
                value: 'create_content_open_project',
                projectId: payload.projectId,
              })
            }
            className="inline-flex items-center justify-center rounded-2xl border border-neutral-200 px-4 py-3 text-sm font-semibold dark:border-neutral-800"
          >
            Ver proyecto
          </button>
        </div>
      </div>
    </motion.div>
  )
}

function PublicationPreviewCard({
  preview,
  onAction,
}: {
  preview: any
  onAction?: (action: any) => void
}) {
  const hashtags = Array.isArray(preview?.hashtags) ? preview.hashtags : []
  const actions = Array.isArray(preview?.actions) ? preview.actions : []
  const isCarousel = preview?.previewType === 'carousel'
  const isExportedCarousel = preview?.previewType === 'exported_carousel'

  function assetPublicPath(slide: any) {
    const key = String(slide?.key || slide?.storage_key || '').trim()

    if (key) {
      return key.startsWith('/') ? key : `/${key}`
    }

    return String(slide?.url || '').trim()
  }

  function getDownloadEndpoint(slide: any, index: number) {
    const generationId = Number(
      preview?.generationId ||
        preview?.generation_id ||
        slide?.generationId ||
        slide?.generation_id ||
        0
    )

    const slideIndex = Number.isFinite(Number(slide?.slideIndex))
      ? Number(slide.slideIndex)
      : index

    if (!generationId) return ''

    return `/api/creator-studio/publications/generations/${generationId}/slides/${slideIndex}/download`
  }

  function downloadByEndpoint(slide: any, index: number) {
    const href = getDownloadEndpoint(slide, index)

    if (!href) return

    const a = document.createElement('a')
    a.href = href
    a.download = ''
    a.style.display = 'none'

    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  async function downloadAllRenderedSlides() {
    for (let index = 0; index < renderedSlides.length; index += 1) {
      downloadByEndpoint(renderedSlides[index], index)

      await new Promise((resolve) => window.setTimeout(resolve, 550))
    }
  }

  const renderedSlides = Array.isArray(preview?.renderedSlides)
    ? preview.renderedSlides.map((slide: any) => ({
        ...slide,
        publicUrl: assetPublicPath(slide),
      }))
    : []
  const isText = preview?.previewType === 'text'
  const slides = Array.isArray(preview?.slides) ? preview.slides : []
  const draft = preview?.draft || null

  const generationId =
    preview?.generationId ||
    preview?.generation_id ||
    preview?.generation?.generation_id ||
    preview?.generation?.id ||
    draft?.generationId ||
    draft?.generation_id ||
    null

  const [activeIndex, setActiveIndex] = useState(0)
  const assetsBySlide = preview?.assetsBySlide || {}

  const draftWithPersistedAssets = useMemo(() => {
    if (!draft) return draft

    const originalSlideVisuals = Array.isArray(draft?.slideVisuals)
      ? draft.slideVisuals
      : []

    const slideVisuals = [...originalSlideVisuals]

    Object.entries(assetsBySlide || {}).forEach(([key, value]: any) => {
      const slideIndex = Number(key)

      if (!Number.isFinite(slideIndex) || slideIndex < 0) return

      const imageUrl =
        value?.ai_image?.url ||
        value?.source_image?.url ||
        null

      if (!imageUrl) return

      slideVisuals[slideIndex] = {
        ...(slideVisuals[slideIndex] || {}),
        backgroundImage: imageUrl,
        blurEnabled: false,
      }
    })

    return {
      ...draft,
      slideVisuals,
    }
  }, [draft, assetsBySlide])

  const draftSlides = Array.isArray(draftWithPersistedAssets?.slides)
  ? draftWithPersistedAssets.slides
  : []

  const activeSlideIndex = Math.min(activeIndex, Math.max(0, draftSlides.length - 1))
  
  const activeSlideAsset = assetsBySlide?.[String(activeSlideIndex)] || null

  const hasTemplateDraft = !!draftWithPersistedAssets?.templateId

  const activeVisual =
    draftWithPersistedAssets?.slideVisuals?.[activeSlideIndex] || {}

  const [colorMenuOpen, setColorMenuOpen] = useState(false)

  const colorOptions = [
    { label: 'Verde', accent: '#B6FF3B' },
    { label: 'Dorado', accent: '#C89B4B' },
    { label: 'Azul', accent: '#2F6BFF' },
    { label: 'Rosa', accent: '#FF4F8B' },
    { label: 'Rojo', accent: '#FF3B30' },
    { label: 'Naranja', accent: '#FF6B35' },
    { label: 'Negro', accent: '#111111' },
  ]

  const currentAccent = draft?.design?.accent || '#B6FF3B'

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const activePlan = draft?.slidePlan?.[activeSlideIndex]
  const slideSupportsImage = activePlan?.imagePriority && activePlan.imagePriority !== 'none'

  const [uploadingSlideImage, setUploadingSlideImage] = useState(false)
  const [slideImageUploadError, setSlideImageUploadError] = useState<string | null>(null)

  async function uploadSlideImage(file: File) {
    if (!generationId) {
      setSlideImageUploadError('Primero genera el carrusel antes de subir imágenes.')
      return
    }

    if (!slideSupportsImage) {
      setSlideImageUploadError('Este slide no usa imagen.')
      return
    }

    setUploadingSlideImage(true)
    setSlideImageUploadError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'publication_slide_upload')

      const res = await fetch(
        `/api/creator-studio/publications/generations/${generationId}/slides/${activeSlideIndex}/upload`,
        {
          method: 'POST',
          body: formData,
        }
      )

      const data = await res.json().catch(() => null)

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || 'No se pudo subir la imagen')
      }

      const imageUrl = data?.asset?.url

      if (!imageUrl) {
        throw new Error('La imagen se subió, pero no regresó URL')
      }

      onAction?.({
        id: 'upload_slide_image',
        action: 'update_publication_slide_visual',
        slideIndex: activeSlideIndex,
        patch: {
          backgroundImage: imageUrl,
          blurEnabled: false,
          overlayTone: activeVisual?.overlayTone || 'black',
          overlayOpacity: activeVisual?.overlayOpacity ?? 0.35,
          uploadedAssetId: data.asset.id,
          uploadedAssetRole: 'source_image',
        },
      } as any)
    } catch (error: any) {
      console.error('[uploadSlideImage] error:', error)
      setSlideImageUploadError(String(error?.message || 'No se pudo subir la imagen'))
    } finally {
      setUploadingSlideImage(false)
    }
  }

  useEffect(() => {
    setActiveIndex(0)
  }, [preview?.generationId, preview?.title, preview?.previewType])

  const activeSlide =
    isCarousel && slides.length > 0
      ? slides[Math.min(activeIndex, slides.length - 1)]
      : null

  const visibleSlideCount = isExportedCarousel
    ? renderedSlides.length
    : slides.length

  const goPrev = () => {
    if (!visibleSlideCount) return
    setActiveIndex((prev) => (prev - 1 + visibleSlideCount) % visibleSlideCount)
  }

  const goNext = () => {
    if (!visibleSlideCount) return
    setActiveIndex((prev) => (prev + 1) % visibleSlideCount)
  }

  const activeRenderedSlide =
    isExportedCarousel && renderedSlides.length > 0
      ? renderedSlides[Math.min(activeIndex, renderedSlides.length - 1)]
      : null

  const visualHint = String(activeSlide?.visualHint || '').toLowerCase()

  const placeholderTone =
    visualHint === 'hero'
      ? 'from-fuchsia-500/20 via-violet-500/10 to-cyan-500/20'
      : visualHint === 'problem'
        ? 'from-rose-500/15 via-orange-500/10 to-neutral-500/10'
        : visualHint === 'solution'
          ? 'from-emerald-500/15 via-cyan-500/10 to-blue-500/15'
          : visualHint === 'cta'
            ? 'from-amber-500/15 via-fuchsia-500/10 to-red-500/15'
            : 'from-cyan-500/15 via-violet-500/10 to-fuchsia-500/15'

  return (
    <div className="w-full mt-2 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-3 py-2 dark:border-neutral-800">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
            {String(preview?.platform || 'social').toUpperCase()} · {String(preview?.format || preview?.previewType || 'post').toUpperCase()}
          </div>
          <div className="truncate text-sm font-semibold text-neutral-950 dark:text-neutral-50">
            {preview?.title || 'Publicación generada'}
          </div>
        </div>

        {preview?.creditsSpent ? (
          <div className="shrink-0 rounded-full bg-neutral-100 px-2 py-1 text-[11px] text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
            {preview.creditsSpent} créditos
          </div>
        ) : null}
      </div>

      <div className="p-3">
        {hasTemplateDraft && draftSlides.length ? (
          <div className="mb-2 flex items-center justify-between gap-2 px-1">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() =>
                  onAction?.({
                    id: 'toggle_blur',
                    action: 'update_publication_slide_visual',
                    slideIndex: activeSlideIndex,
                    patch: {
                      blurEnabled: !activeVisual?.blurEnabled,
                      blurAmount: activeVisual?.blurAmount || 12,
                    },
                  } as any)
                }
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition ${
                  activeVisual?.blurEnabled
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-black'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700'
                }`}
              >
                Blur
              </button>

              <button
                type="button"
                onClick={() =>
                  onAction?.({
                    id: 'overlay_white',
                    action: 'update_publication_slide_visual',
                    slideIndex: activeSlideIndex,
                    patch: {
                      overlayTone: 'white',
                      overlayOpacity: 0.42,
                    },
                  } as any)
                }
                className={`flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-bold transition ${
                  activeVisual?.overlayTone === 'white'
                    ? 'border-neutral-900 bg-white text-black ring-2 ring-neutral-900/20'
                    : 'border-neutral-300 bg-white text-black hover:ring-2 hover:ring-neutral-900/10'
                }`}
              >
                W
              </button>

              <button
                type="button"
                onClick={() =>
                  onAction?.({
                    id: 'overlay_black',
                    action: 'update_publication_slide_visual',
                    slideIndex: activeSlideIndex,
                    patch: {
                      overlayTone: 'black',
                      overlayOpacity: 0.38,
                    },
                  } as any)
                }
                className={`flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-bold transition ${
                  activeVisual?.overlayTone === 'black'
                    ? 'border-black bg-black text-white ring-2 ring-black/20'
                    : 'border-neutral-700 bg-black text-white hover:ring-2 hover:ring-black/20'
                }`}
              >
                B
              </button>

              <button
                type="button"
                onClick={() =>
                  onAction?.({
                    id: 'overlay_none',
                    action: 'update_publication_slide_visual',
                    slideIndex: activeSlideIndex,
                    patch: {
                      overlayTone: 'none',
                      overlayOpacity: 0,
                    },
                  } as any)
                }
                className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-100 text-[10px] font-bold text-neutral-500 transition hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
              >
                —
              </button>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setColorMenuOpen((v) => !v)}
                  className="flex h-7 w-7 items-center justify-center rounded-full border border-white/60 shadow-sm ring-1 ring-black/10 transition hover:scale-105"
                  style={{ background: currentAccent }}
                  title="Cambiar color"
                />

                {colorMenuOpen ? (
                  <div className="absolute left-0 top-9 z-50 w-36 rounded-2xl border border-neutral-200 bg-white p-2 shadow-xl dark:border-neutral-800 dark:bg-neutral-950">
                    {colorOptions.map((color) => (
                      <button
                        key={color.accent}
                        type="button"
                        onClick={() => {
                          setColorMenuOpen(false)
                          onAction?.({
                            id: 'update_publication_color',
                            action: 'update_publication_color',
                            patch: {
                              accent: color.accent,
                              accentSoft: color.accent,
                              accentDeep: color.accent,
                            },
                          } as any)
                        }}
                        className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-900"
                      >
                        <span
                          className="h-4 w-4 rounded-full border border-black/10"
                          style={{ background: color.accent }}
                        />
                        <span>{color.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''

                  if (!file) return

                  void uploadSlideImage(file)
                }}
              />

              <button
                type="button"
                disabled={!slideSupportsImage}
                onClick={() =>
                  onAction?.({
                    id: 'generate_slide_image',
                    action: 'generate_slide_image',
                    slideIndex: activeSlideIndex,
                  } as any)
                }
                className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-900 text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30 dark:bg-white dark:text-black"
                title={slideSupportsImage ? 'Generar imagen con IA' : 'Este slide no usa imagen'}
              >
                <WandSparkles className="h-3.5 w-3.5" />
              </button>

              <button
                type="button"
                disabled={!slideSupportsImage || uploadingSlideImage}
                onClick={() => fileInputRef.current?.click()}
                className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 transition hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-30 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                title={
                  !slideSupportsImage
                    ? 'Este slide no usa imagen'
                    : uploadingSlideImage
                      ? 'Subiendo imagen...'
                      : 'Subir imagen propia'
                }
              >
                {uploadingSlideImage ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
              </button>

              {slideImageUploadError ? (
                <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] leading-4 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
                  {slideImageUploadError}
                </div>
              ) : null}
            </div>

            <div className="text-[11px] text-neutral-400">
              Slide {activeSlideIndex + 1}
            </div>
          </div>
        ) : null}
        {isExportedCarousel ? (
          <div className="w-full space-y-4">
            <div className="overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
              <div className="relative mx-auto aspect-[4/5] w-full max-w-[520px] overflow-hidden bg-neutral-100 dark:bg-neutral-900">
                {activeRenderedSlide?.publicUrl ? (
                  <>
                  <img
                    src={activeRenderedSlide.publicUrl}
                    alt={`Slide exportado ${activeIndex + 1}`}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!activeRenderedSlide) return
                      downloadByEndpoint(activeRenderedSlide, activeIndex)
                    }}
                    className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-black/35 text-white shadow-lg backdrop-blur-md transition hover:scale-105 hover:bg-black/45"
                    aria-label="Descargar carrusel"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-neutral-400">
                    No encontré este slide exportado.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 transition hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Slide {Math.min(activeIndex + 1, renderedSlides.length)} / {renderedSlides.length}
              </div>

              <button
                type="button"
                onClick={goNext}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 transition hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center justify-center gap-1.5">
              {renderedSlides.map((slide: any, index: number) => (
                <button
                  key={slide.key || slide.url || index}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === activeIndex
                      ? 'w-6 bg-neutral-900 dark:bg-white'
                      : 'w-2 bg-neutral-300 hover:bg-neutral-400 dark:bg-neutral-700 dark:hover:bg-neutral-600'
                  }`}
                  aria-label={`Ver slide exportado ${index + 1}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const editablePreview = preview?.editablePreview

                  if (!editablePreview) {
                    onAction?.({
                      id: 'open_creator_studio',
                      action: 'open_creator_studio',
                    } as any)
                    return
                  }

                  onAction?.({
                    id: 'edit_exported_publication_carousel',
                    action: 'edit_exported_publication_carousel',
                    editablePreview,
                  } as any)
                }}
                className="flex-1 rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm font-semibold text-neutral-800 transition hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800"
              >
                Editar
              </button>

              <button
                type="button"
                onClick={() => {
                  void downloadAllRenderedSlides()
                }}
                className="flex-1 rounded-xl bg-neutral-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
              >
                Descargar todo
              </button>
            </div>
          </div>
        ) : hasTemplateDraft ? (
          <PublicationRenderer
            draft={draftWithPersistedAssets}
            activeIndex={activeIndex}
            onActiveIndexChange={(index) => {
              setActiveIndex(index)

              onAction?.({
                id: 'set_active_publication_slide',
                action: 'set_active_publication_slide',
                slideIndex: index,
              } as any)
            }}
          />
        ) : isText ? null : isCarousel ? (
          <div className="space-y-3">
            <div className="relative overflow-hidden rounded-[28px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
              <div className={`relative aspect-square w-full bg-gradient-to-br ${placeholderTone}`}>
                {activeSlide?.imageUrl ? (
                  <img
                    src={activeSlide.imageUrl}
                    alt={activeSlide?.title || `Slide ${activeIndex + 1}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.25),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.18),transparent_24%)] dark:bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.06),transparent_24%)]" />
                  </div>
                )}

                <div className="absolute inset-0 flex flex-col justify-between p-5">
                  <div className="flex items-center justify-between">
                    <div className="rounded-full bg-black/10 px-2.5 py-1 text-[11px] font-semibold text-neutral-900 backdrop-blur dark:bg-white/10 dark:text-white">
                      Slide {activeIndex + 1} / {slides.length}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={goPrev}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-neutral-900 shadow-sm backdrop-blur hover:bg-white dark:bg-black/50 dark:text-white dark:hover:bg-black/70"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-neutral-900 shadow-sm backdrop-blur hover:bg-white dark:bg-black/50 dark:text-white dark:hover:bg-black/70"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="max-w-[88%] rounded-3xl bg-white/75 p-4 shadow-lg backdrop-blur dark:bg-black/35">
                    {activeSlide?.title ? (
                      <div className="text-xl font-bold leading-tight text-neutral-950 dark:text-white">
                        {activeSlide.title}
                      </div>
                    ) : null}

                    {activeSlide?.subtitle ? (
                      <div className="mt-2 text-sm font-medium leading-6 text-neutral-700 dark:text-neutral-200">
                        {activeSlide.subtitle}
                      </div>
                    ) : null}

                    {activeSlide?.body ? (
                      <div className="mt-2 text-sm leading-6 text-neutral-600 dark:text-neutral-300">
                        {activeSlide.body}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-1.5">
              {slides.map((_: any, idx: number) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === activeIndex
                      ? 'w-6 bg-neutral-900 dark:bg-white'
                      : 'w-2 bg-neutral-300 hover:bg-neutral-400 dark:bg-neutral-700 dark:hover:bg-neutral-600'
                  }`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {preview?.imageUrl ? (
              <img
                src={preview.imageUrl}
                alt={preview?.title || 'Publicación generada'}
                className="aspect-square w-full rounded-2xl object-cover"
              />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 text-center dark:border-neutral-700 dark:bg-neutral-900">
                <div className="px-6">
                  <Sparkles className="mx-auto h-6 w-6 text-neutral-400" />
                  <div className="mt-2 text-xs font-medium text-neutral-600 dark:text-neutral-300">
                    Preview visual en modo prueba
                  </div>
                  <div className="mt-1 text-[11px] leading-4 text-neutral-400">
                    Aquí aparecerá la imagen generada cuando activemos producción.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2 px-3 pb-3">
        {preview?.caption ? (
          <div className="rounded-2xl bg-neutral-50 px-3 py-3 text-sm leading-6 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-100">
            {preview.caption}
          </div>
        ) : null}

        {hashtags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {hashtags.map((tag: string) => (
              <span
                key={tag}
                className="rounded-full bg-neutral-100 px-2 py-1 text-[11px] font-medium text-neutral-500 dark:bg-neutral-800 dark:text-neutral-300"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {preview?.cta ? (
          <div className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
            CTA: {preview.cta}
          </div>
        ) : null}

        {actions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {actions.map((action: any) => (
              <button
                key={action.id || action.label}
                type="button"
                onClick={() => onAction?.(action)}
                className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                {action.label || action.id}
              </button>
            ))}
          </div>
        ) : null}

        {Array.isArray(preview?.templateOptions) && preview.templateOptions.length > 1 ? (
          <div className="hidden border-t border-neutral-200 px-3 py-3 dark:border-neutral-800">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                Cambiar diseño
              </div>
              <div className="text-[11px] text-neutral-400">
                Vista rápida
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1">
              {preview.templateOptions.map((template: PublicationTemplateOption) => {
                const isActive =
                  preview?.draft?.themeId === template.themeId &&
                  preview?.draft?.templateId === template.templateId

                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() =>
                      onAction?.({
                        id: 'swap_publication_template',
                        label: template.name,
                        templateOptionId: template.id,
                        action: 'swap_publication_template',
                      } as any)
                    }
                    className={`w-[92px] shrink-0 rounded-2xl border p-1.5 text-left transition hover:-translate-y-0.5 ${
                      isActive
                        ? 'border-neutral-900 bg-neutral-100 dark:border-white dark:bg-neutral-800'
                        : 'border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700'
                    }`}
                  >
                    <div
                      className="relative aspect-square overflow-hidden rounded-xl"
                      style={{ background: template.preview.background }}
                    >
                      <div
                        className="absolute -right-5 top-4 h-14 w-14 rounded-full blur-xl"
                        style={{ background: template.preview.accent, opacity: 0.35 }}
                      />
                      <div
                        className="absolute -left-5 bottom-4 h-14 w-14 rounded-full blur-xl"
                        style={{ background: '#fff', opacity: 0.65 }}
                      />

                      <div className="relative z-10 flex h-full flex-col justify-between p-2">
                        <div
                          className="w-fit rounded-full bg-white/65 px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-[0.12em]"
                          style={{ color: template.preview.muted }}
                        >
                          {template.preview.badge || 'Design'}
                        </div>

                        <div>
                          <div
                            className="whitespace-pre-line text-[15px] font-serif leading-[0.9]"
                            style={{ color: template.preview.text }}
                          >
                            {template.preview.headline}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-1.5 truncate px-0.5 text-[10px] font-semibold text-neutral-800 dark:text-neutral-100">
                      {template.name}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function getAdsButtonIcon(value: string, emoji?: string) {
  if (emoji) return <span className="text-lg leading-none">{emoji}</span>

  if (value.includes('ads_source:funnel')) return <Layers3 className="h-4 w-4" />
  if (value.includes('ads_source:product')) return <ShoppingBag className="h-4 w-4" />
  if (value.includes('ads_source:challenge')) return <Trophy className="h-4 w-4" />
  if (value.includes('ads_source:course')) return <GraduationCap className="h-4 w-4" />
  if (value.includes('ads_source:community')) return <Users className="h-4 w-4" />

  if (value.includes('ads_goal:leads')) return <Target className="h-4 w-4" />
  if (value.includes('ads_goal:sales')) return <DollarSign className="h-4 w-4" />
  if (value.includes('ads_goal:traffic')) return <MousePointerClick className="h-4 w-4" />
  if (value.includes('ads_goal:whatsapp')) return <MessageCircle className="h-4 w-4" />

  if (value.includes('ads_creative:existing')) return <ImageIcon className="h-4 w-4" />
  if (value.includes('ads_creative:new_visual')) return <Sparkles className="h-4 w-4" />
  if (value.includes('ads_creative:new_copy')) return <PenLine className="h-4 w-4" />
  if (value.includes('ads_creative:new_both')) return <Zap className="h-4 w-4" />

  if (value.includes('ads_budget')) return <DollarSign className="h-4 w-4" />
  if (value.includes('ads_launch')) return <Rocket className="h-4 w-4" />
  if (value.includes('ads_connect_meta')) return <Facebook className="h-4 w-4" />
  if (value.includes('ads_results')) return <BarChart3 className="h-4 w-4" />

  return <Sparkles className="h-4 w-4" />
}

function getAdsButtonDescription(value: string, label: string) {
  if (value.startsWith('ads_source:funnel:')) return 'Embudo encontrado en tu cuenta'
  if (value === 'ads_source:selected_funnel') return 'Usar el embudo abierto actualmente'
  if (value === 'ads_source:product') return 'Ideal para ventas directas o checkout'
  if (value === 'ads_source:challenge') return 'Perfecto para captar registros a un reto'
  if (value === 'ads_source:course') return 'Promociona una clase, curso o lección'
  if (value === 'ads_source:community') return 'Atrae miembros a tu comunidad'
  if (value === 'ads_source:manual') return 'Crear campaña desde una idea libre'

  if (value === 'ads_goal:leads') return 'Captura prospectos para CRM o WhatsApp'
  if (value === 'ads_goal:sales') return 'Optimiza hacia compras o suscripciones'
  if (value === 'ads_goal:traffic') return 'Lleva visitas a tu página o embudo'
  if (value === 'ads_goal:whatsapp') return 'Inicia conversaciones con prospectos'

  if (value === 'ads_creative:existing') return 'Elegir una pieza ya creada en Hubee'
  if (value === 'ads_creative:new_visual') return 'Crear una imagen publicitaria nueva'
  if (value === 'ads_creative:new_copy') return 'Crear texto, headline y CTA'
  if (value === 'ads_creative:new_both') return 'Preparar visual, texto y ángulo completo'

  if (value === 'ads_budget:100') return 'Prueba ligera para validar respuesta'
  if (value === 'ads_budget:150') return 'Recomendado para iniciar con control'
  if (value === 'ads_budget:300') return 'Más velocidad para conseguir datos'
  if (value === 'ads_budget:500') return 'Mayor alcance desde el primer día'

  if (value === 'ads_launch:simulate') return 'Ver cómo se sentiría el lanzamiento'
  if (value === 'ads_connect_meta') return 'Preparar conexión con Meta Ads Manager'
  if (value === 'ads_edit:restart') return 'Volver a configurar la campaña'
  if (value === 'ads_results:simulated') return 'Ver métricas simuladas de prueba'
  if (value === 'ads_create:another') return 'Iniciar otra campaña desde cero'
  if (value === 'ads_optimize:simulated') return 'Recibir sugerencias de mejora'

  return label
}

function isAdsButton(btn: any) {
  const value = String(btn?.value || '')
  return (
    value.startsWith('ads_') ||
    value.includes('ads_source:') ||
    value.includes('ads_goal:') ||
    value.includes('ads_creative:') ||
    value.includes('ads_budget:')
  )
}

function ChatActionButtons({
  buttons,
  onButtonClick,
  message,
}: {
  buttons: any[]
  onButtonClick?: (button: any, message?: AIMasterChatMessage) => void
  message: AIMasterChatMessage
}) {
  const allAds = buttons.length > 0 && buttons.every(isAdsButton)

  if (allAds) {
    return (
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {buttons.map((btn: any, i: number) => {
          const value = String(btn?.value || '')
          const description = btn.description || getAdsButtonDescription(value, btn.label)

          return (
            <button
              key={`${value || btn.label || i}`}
              type="button"
              onClick={() => onButtonClick?.(btn, message)}
              className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white p-3 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700"
            >
              <div className="absolute right-0 top-0 h-16 w-16 rounded-bl-[2rem] bg-gradient-to-br from-yellow-100 via-orange-100 to-transparent opacity-80 transition group-hover:scale-110 dark:from-yellow-500/10 dark:via-orange-500/10" />

              <div className="relative flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-neutral-200 bg-neutral-50 text-neutral-900 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100">
                  {getAdsButtonIcon(value, btn.emoji)}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold leading-snug text-neutral-950 dark:text-neutral-50">
                    {btn.label}
                  </div>

                  <div className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                    {description}
                  </div>
                </div>

                <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-400 transition group-hover:border-neutral-900 group-hover:bg-neutral-900 group-hover:text-white dark:border-neutral-800 dark:group-hover:border-neutral-100 dark:group-hover:bg-neutral-100 dark:group-hover:text-neutral-950">
                  <ChevronRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {buttons.map((btn: any, i: number) => (
        <button
          key={`${btn.value || btn.label || i}`}
          type="button"
          onClick={() => onButtonClick?.(btn, message)}
          className="inline-flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-3.5 py-2 text-xs font-semibold text-neutral-900 shadow-sm transition-all hover:-translate-y-0.5 hover:border-neutral-300 hover:bg-neutral-50 hover:shadow-md dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:border-neutral-700 dark:hover:bg-neutral-900"
        >
          {btn.emoji ? <span>{btn.emoji}</span> : null}
          <span>{btn.label}</span>
        </button>
      ))}
    </div>
  )
}

function AdsCampaignReviewCard({
  data,
  onButtonClick,
  message,
}: {
  data: any
  onButtonClick?: (button: any, message?: AIMasterChatMessage) => void
  message: AIMasterChatMessage
}) {
  if (!data) return null

  return (
    <div className="mt-3 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="border-b border-neutral-100 bg-gradient-to-br from-yellow-50 via-white to-orange-50 p-4 dark:border-neutral-800 dark:from-yellow-500/10 dark:via-neutral-950 dark:to-orange-500/10">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-yellow-200 bg-yellow-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-yellow-900 dark:border-yellow-500/20 dark:bg-yellow-500/10 dark:text-yellow-200">
              <Megaphone className="h-3 w-3" />
              Ad Preview
            </div>

            <div className="mt-3 text-base font-black tracking-tight text-neutral-950 dark:text-neutral-50">
              {data.name || 'Campaña publicitaria'}
            </div>

            <div className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
              Lista para revisar antes de conectarla a Meta Ads.
            </div>
          </div>

          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-neutral-950 text-white shadow-sm dark:bg-white dark:text-neutral-950">
            <Rocket className="h-5 w-5" />
          </div>
        </div>
      </div>

      <div className="grid gap-3 p-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
              Objetivo
            </div>
            <div className="mt-1 text-sm font-bold text-neutral-950 dark:text-neutral-50">
              {data.goalLabel || 'Leads'}
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
              Presupuesto
            </div>
            <div className="mt-1 text-sm font-bold text-neutral-950 dark:text-neutral-50">
              ${Number(data.budgetDaily || 0).toLocaleString('es-MX')} MXN/día
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-200 p-3 dark:border-neutral-800">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold text-neutral-900 dark:text-neutral-100">
            <Target className="h-4 w-4" />
            Audiencia sugerida
          </div>
          <div className="text-xs leading-5 text-neutral-500 dark:text-neutral-400">
            {data.audience ||
              'Personas interesadas en emprendimiento digital, creación de contenido, educación online y soluciones relacionadas con tu oferta.'}
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-200 p-3 dark:border-neutral-800">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold text-neutral-900 dark:text-neutral-100">
            <Sparkles className="h-4 w-4" />
            Ángulo creativo
          </div>
          <div className="text-xs leading-5 text-neutral-500 dark:text-neutral-400">
            {data.angle ||
              'Presentar el problema principal, mostrar la transformación prometida y llevar al usuario al CTA del embudo o recurso seleccionado.'}
          </div>
        </div>

        {Array.isArray(data.buttons) && data.buttons.length > 0 ? (
          <ChatActionButtons
            buttons={data.buttons}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}
      </div>
    </div>
  )
}

function AdsCampaignStatusCard({
  data,
  onButtonClick,
  message,
}: {
  data: any
  onButtonClick?: (button: any, message?: AIMasterChatMessage) => void
  message: AIMasterChatMessage
}) {
  if (!data) return null

  return (
    <div className="mt-3 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300">
            <CheckCircle2 className="h-6 w-6" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-green-600 dark:text-green-300">
              {data.statusLabel || 'Activa en simulación'}
            </div>

            <div className="mt-1 text-base font-black tracking-tight text-neutral-950 dark:text-neutral-50">
              {data.name || 'Campaña simulada'}
            </div>

            <div className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
              Esta campaña está corriendo en modo prueba. Después conectaremos Meta para crearla en estado pausado y publicarla con confirmación.
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="rounded-2xl bg-neutral-50 p-3 text-center dark:bg-neutral-900">
            <div className="text-[11px] text-neutral-400">Presupuesto</div>
            <div className="mt-1 text-sm font-bold text-neutral-950 dark:text-neutral-50">
              ${Number(data.budgetDaily || 150).toLocaleString('es-MX')}/día
            </div>
          </div>

          <div className="rounded-2xl bg-neutral-50 p-3 text-center dark:bg-neutral-900">
            <div className="text-[11px] text-neutral-400">Objetivo</div>
            <div className="mt-1 text-sm font-bold text-neutral-950 dark:text-neutral-50">
              {data.goalLabel || 'Leads'}
            </div>
          </div>

          <div className="rounded-2xl bg-neutral-50 p-3 text-center dark:bg-neutral-900">
            <div className="text-[11px] text-neutral-400">Duración</div>
            <div className="mt-1 text-sm font-bold text-neutral-950 dark:text-neutral-50">
              5 días
            </div>
          </div>
        </div>

        {Array.isArray(data.buttons) && data.buttons.length > 0 ? (
          <ChatActionButtons
            buttons={data.buttons}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}
      </div>
    </div>
  )
}

function AdsMetricsCard({
  data,
  onButtonClick,
  message,
}: {
  data: any
  onButtonClick?: (button: any, message?: AIMasterChatMessage) => void
  message: AIMasterChatMessage
}) {
  if (!data) return null

  const metrics = [
    { label: 'Gasto', value: data.spend || '$450 MXN' },
    { label: 'Impresiones', value: data.impressions || '8,240' },
    { label: 'Clicks', value: data.clicks || '214' },
    { label: 'CTR', value: data.ctr || '2.59%' },
    { label: 'CPC', value: data.cpc || '$2.10' },
    { label: 'Leads', value: data.leads || '32' },
  ]

  return (
    <div className="mt-3 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex items-center justify-between border-b border-neutral-100 p-4 dark:border-neutral-800">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
            <BarChart3 className="h-3 w-3" />
            Métricas
          </div>

          <div className="mt-2 text-base font-black tracking-tight text-neutral-950 dark:text-neutral-50">
            {data.name || 'Resultados de campaña'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3">
        {metrics.map((item) => (
          <div
            key={item.label}
            className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900/60"
          >
            <div className="text-[11px] font-medium text-neutral-400">
              {item.label}
            </div>
            <div className="mt-1 text-lg font-black tracking-tight text-neutral-950 dark:text-neutral-50">
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {Array.isArray(data.buttons) && data.buttons.length > 0 ? (
        <div className="px-4 pb-4">
          <ChatActionButtons
            buttons={data.buttons}
            onButtonClick={onButtonClick}
            message={message}
          />
        </div>
      ) : null}
    </div>
  )
}

function ChatBubble({
  message,
  masterAgentName,
  onButtonClick,
  isAdmin,
}: {
  message: AIMasterChatMessage
  masterAgentName: string
  onButtonClick?: (button: any, message?: AIMasterChatMessage) => void
  isAdmin?: boolean
}) {
  const isAssistant = message.role === 'assistant'
  const text = coerceContentToString(message.content)
  const workerLabel = getWorkerLabel(message.meta?.worker)
  const hasContentPreview = Boolean((message.meta as any)?.content_preview)

  const imageUrl =
    (message.meta as any)?.imageUrl ||
    (message.meta as any)?.referenceImageUrl ||
    ''

  return (
    <div className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}>
      <div
        className={
          isAssistant
            ? hasContentPreview
              ? 'w-full max-w-[72%] space-y-2 pb-1'
              : 'max-w-[72%] space-y-2 pb-1'
            : 'max-w-[82%] space-y-2'
        }
      >
        {isAssistant &&
        Array.isArray(message.meta?.executed_actions) &&
        message.meta.executed_actions.length > 0 ? (
          <div className="space-y-1">
            {message.meta.executed_actions.map((a: any, i: number) => {
              const isPending = a?.status === 'pending' || a?.loading === true
              const isOk = a?.ok === true
              const isError = a?.ok === false || a?.error

              return (
                <div
                  key={i}
                  className="flex items-center gap-2 text-[11px] text-neutral-500 dark:text-neutral-400"
                >
                  {isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : isOk ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 animate-in zoom-in-50 duration-200" />
                  ) : isError ? (
                    <XCircle className="w-3.5 h-3.5 text-red-500" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5 text-neutral-400" />
                  )}

                  <span>{getActionLabel(a?.tool)}</span>
                </div>
              )
            })}
          </div>
        ) : null}

        <div
          className={`
            px-4 py-2 rounded-2xl text-sm leading-relaxed shadow-sm break-words
            ${
              isAssistant
                ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100'
                : 'bg-black dark:bg-white text-white dark:text-black whitespace-pre-wrap'
            }
          `}
        >
          {isAssistant ? (
            <div className="prose prose-sm dark:prose-invert max-w-none leading-relaxed prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
              <ReactMarkdown>{text}</ReactMarkdown>
            </div>
          ) : (
            text ? <div>{text}</div> : null
          )}

          {imageUrl ? (
            <div className="mt-2 max-w-[260px] overflow-hidden rounded-2xl border border-white/20 bg-background shadow-sm">
              <img
                src={imageUrl}
                alt="Imagen de referencia"
                className="h-auto w-full object-cover"
              />
            </div>
          ) : null}
        </div>

        {isAssistant && (message.meta as any)?.entity_results ? (
          <ChatEntityResults
            payload={(message.meta as any).entity_results}
            onSelect={(item) =>
              onButtonClick?.(
                {
                  label: item.title,
                  value: item.value,
                  entity: item,
                },
                message
              )
            }
          />
        ) : null}

        {isAssistant && isAdmin && message.meta?.worker ? (
          <div className="pointer-events-none mt-1 opacity-0 transition-opacity duration-150 group-hover:opacity-60">
            <div className="text-[10px] text-neutral-400 dark:text-neutral-500">
              worker: {String(message.meta.worker)}
            </div>
          </div>
        ) : null}

        {isAssistant && (message.meta as any)?.ai_funnel_review ? (
          <AIFunnelReviewCard
            data={(message.meta as any).ai_funnel_review}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}

        {isAssistant && (message.meta as any)?.ads_campaign_review ? (
          <AdsCampaignReviewCard
            data={(message.meta as any).ads_campaign_review}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}

        {isAssistant && (message.meta as any)?.ads_campaign_status ? (
          <AdsCampaignStatusCard
            data={(message.meta as any).ads_campaign_status}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}

        {isAssistant && (message.meta as any)?.ads_metrics ? (
          <AdsMetricsCard
            data={(message.meta as any).ads_metrics}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}

        {/* AQUÍ VA: publication loading / preview */}
        {isAssistant && (message.meta as any)?.content_preview_loading ? (
          <PublicationPreviewSkeleton
            previewType={(message.meta as any).content_preview_loading?.previewType || 'image'}
          />
        ) : null}

        {isAssistant && (message.meta as any)?.content_preview ? (
          <div className="w-full">
            <PublicationPreviewCard
              preview={(message.meta as any)?.content_preview}
              onAction={(action: any) =>
                onButtonClick?.(
                  action?.action === 'swap_publication_template'
                    ? {
                        label: action.label || action.id,
                        value: action.id,
                        action: 'swap_publication_template',
                        templateOptionId: action.templateOptionId,
                      }
                    : action?.action === 'update_publication_slide_visual'
                      ? {
                          label: action.label || action.id,
                          value: action.id,
                          action: 'update_publication_slide_visual',
                          slideIndex: action.slideIndex,
                          patch: action.patch,
                        }
                      : action?.action === 'set_active_publication_slide'
                        ? {
                            label: action.label || action.id,
                            value: action.id,
                            action: 'set_active_publication_slide',
                            slideIndex: action.slideIndex,
                          }
                        : action?.action === 'update_publication_color'
                          ? {
                              label: action.label || action.id,
                              value: action.id,
                              action: 'update_publication_color',
                              patch: action.patch,
                            }
                          : action?.action === 'generate_slide_image'
                            ? {
                                label: action.label || action.id,
                                value: action.id,
                                action: 'generate_slide_image',
                                slideIndex: action.slideIndex,
                              }
                            : action?.action === 'export_publication_carousel' ||
                              action?.id === 'export_publication_carousel'
                              ? {
                                  label: action.label || action.id || 'Exportar carrusel',
                                  value: 'export_publication_carousel',
                                  action: 'publication_preview_action',
                                  publicationAction: 'export_publication_carousel',
                                }
                              : action?.action === 'edit_exported_publication_carousel'
                                ? {
                                    label: action.label || action.id,
                                    value: action.id,
                                    action: 'edit_exported_publication_carousel',
                                    editablePreview: action.editablePreview,
                                  }
                                : action?.action === 'download_exported_publication_carousel'
                                  ? {
                                      label: action.label || action.id,
                                      value: action.id,
                                      action: 'download_exported_publication_carousel',
                                      renderedSlides: action.renderedSlides,
                                    }
                                  : {
                                      label: action.label || action.id,
                                      value: action.id,
                                      action: 'publication_preview_action',
                                      publicationAction: action.id,
                                    },
                  message
                )
              }
            />
          </div>
        ) : null}

        {isAssistant && (message.meta as any)?.template_selector ? (
          <PublicationTemplateSelector
            templates={(message.meta as any).template_selector.templates || []}
            onSelect={(template: PublicationTemplateOption) =>
              onButtonClick?.(
                {
                  label: template.name,
                  value: `publication_template:${template.id}`,
                  action: 'select_publication_template',
                  templateOptionId: template.id,
                  templateId: template.templateId,
                  themeId: template.themeId,
                  visualStyle: template.visualStyle,
                },
                message
              )
            }
          />
        ) : null}

        {isAssistant && hasStrategyContent(message.meta?.strategy) ? (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/60 backdrop-blur px-3 py-3 text-xs space-y-2">
            <div className="flex items-center gap-2 font-semibold text-neutral-700 dark:text-neutral-200">
              <BrainCircuit className="w-4 h-4" />
              Estrategia propuesta
            </div>

            <div className="grid gap-2">
              {message.meta?.strategy?.audience ? (
                <div>
                  <span className="font-medium">Audiencia:</span>{' '}
                  {message.meta.strategy.audience}
                </div>
              ) : null}
              {message.meta?.strategy?.offer ? (
                <div>
                  <span className="font-medium">Oferta:</span>{' '}
                  {message.meta.strategy.offer}
                </div>
              ) : null}
              {message.meta?.strategy?.angle ? (
                <div>
                  <span className="font-medium">Ángulo:</span>{' '}
                  {message.meta.strategy.angle}
                </div>
              ) : null}
              {message.meta?.strategy?.funnel_type ? (
                <div>
                  <span className="font-medium">Tipo:</span>{' '}
                  {message.meta.strategy.funnel_type}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {isAssistant && message.meta?.discovery?.needed ? (
          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/60 backdrop-blur px-3 py-3 text-xs space-y-2">
            <div className="font-semibold text-neutral-700 dark:text-neutral-200">
              {masterAgentName} necesita afinar algunos puntos:
            </div>

            {Array.isArray(message.meta?.discovery?.questions) &&
            message.meta.discovery.questions.length > 0 ? (
              <ol className="space-y-1 list-decimal pl-4">
                {message.meta.discovery.questions.map((q: string, i: number) => (
                  <li key={i}>{q}</li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}

        {isAssistant &&
        Array.isArray(message.meta?.steps) &&
        message.meta.steps.length > 0 ? (
          <div className="mt-2">
            <SamanthaSteps
              steps={message.meta.steps}
              onStepChange={(step, index, total) => {
                window.dispatchEvent(
                  new CustomEvent('samantha:activity', {
                    detail: {
                      active: true,
                      label: step.label,
                      index,
                      total,
                    },
                  })
                )
              }}
              onComplete={() => {
                window.dispatchEvent(
                  new CustomEvent('samantha:activity', {
                    detail: { active: false },
                  })
                )
              }}
            />
          </div>
        ) : null}

        {isAssistant &&
        (message.meta as any)?.thinking ? (
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <span className="flex gap-1">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current opacity-30" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current opacity-60 [animation-delay:160ms]" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current opacity-90 [animation-delay:320ms]" />
            </span>

            <span className="animate-pulse">
              {(message.meta as any).thinking.label || 'Ejecutando…'}
            </span>
          </div>
        ) : null}

        {isAssistant &&
        (message.meta as any)
          ?.create_content_plan?.plan ? (
          <CreateContentPlanCard
            payload={
              (message.meta as any)
                .create_content_plan
            }
          />
        ) : null}

        {isAssistant &&
        (message.meta as any)?.create_content_script ? (
          <CreateContentScriptCard
            payload={
              (message.meta as any)
                .create_content_script
            }
            onAction={(action) =>
              onButtonClick?.(
                action,
                message
              )
            }
            onSettingsChange={(patch) =>
              onButtonClick?.(
                {
                  value:
                    'create_content_production_settings',
                  patch,
                },
                message
              )
            }
            masterAgentName={masterAgentName}
          />
        ) : null}

        {isAssistant &&
        Array.isArray((message.meta as any)?.content_actions) &&
        (message.meta as any).content_actions.some(
          (action: any) => action?.type === 'resume_option'
        ) ? (
          <div className="mt-2 space-y-2">
            {(message.meta as any).content_actions
              .filter((action: any) => action?.type === 'resume_option')
              .map((action: any, i: number) => (
                <button
                  key={`${action.value || action.route || i}`}
                  type="button"
                  onClick={() =>
                    onButtonClick?.(
                      {
                        label: action.label,
                        value: action.value,
                        emoji: action.emoji,
                        action: action.route ? 'navigate' : undefined,
                        route: action.route || undefined,
                      },
                      message
                    )
                  }
                  className="w-full text-left rounded-2xl border border-neutral-200 bg-white px-3 py-3 shadow-sm transition hover:border-primary/40 hover:bg-primary/5 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-primary/40"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-base dark:bg-neutral-800">
                      {action.emoji || '▶️'}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                        {action.title || action.category || 'Continuar'}
                      </div>

                      <div className="mt-0.5 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                        {action.label}
                      </div>

                      {action.description ? (
                        <div className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                          {action.description}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        ) : null}

        {isAssistant &&
        Array.isArray(message.meta?.buttons) &&
        message.meta.buttons.length > 0 &&
        !(message.meta as any)?.ai_funnel_review &&
        !(message.meta as any)?.entity_results &&
        !(message.meta as any)?.ads_campaign_review &&
        !(message.meta as any)?.ads_campaign_status &&
        !(message.meta as any)?.ads_metrics &&
        !(
          Array.isArray((message.meta as any)?.content_actions) &&
          (message.meta as any).content_actions.some(
            (action: any) => action?.type === 'resume_option'
          )
        ) ? (
          <ChatActionButtons
            buttons={message.meta.buttons}
            onButtonClick={onButtonClick}
            message={message}
          />
        ) : null}
      </div>
    </div>
  )
}

function normalizeSmallReply(input: string) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[¿?¡!.,;:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function isAffirmativeReply(input: string) {
  const value = normalizeSmallReply(input)

  if (!value) return false

  return [
    'si',
    'si por favor',
    'si porfavor',
    'si gracias',
    'claro',
    'ok',
    'okay',
    'dale',
    'va',
    'perfecto',
    'de acuerdo',
    'yes',
    'yeah',
    'yep',
  ].some((phrase) => value === phrase || value.includes(phrase))
}

function wantsTemplateReply(input: string) {
  const value = normalizeSmallReply(input)

  return (
    value.includes('plantilla') ||
    value.includes('template') ||
    value.includes('clonar') ||
    value.includes('usar una') ||
    value.includes('usar plantilla')
  )
}

function wantsMyFunnelsReply(input: string) {
  const value = normalizeSmallReply(input)

  return (
    value.includes('mis embudos') ||
    value.includes('ver embudos') ||
    value.includes('ir a embudos') ||
    value.includes('abrir embudos')
  )
}

function wantsScratchFunnelReply(input: string) {
  const value = normalizeSmallReply(input)

  return (
    value.includes('desde cero') ||
    value.includes('cero') ||
    value.includes('sin plantilla') ||
    value.includes('nuevo en blanco') ||
    value.includes('blanco')
  )
}

function TypingIndicator({ agentName = 'Samantha' }: { agentName?: string }) {
  return (
    <div className="flex gap-2 items-start">
      <div className="w-8 h-8 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center">
        <Sparkles className="w-4 h-4" />
      </div>

      <div className="space-y-2">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>{agentName} está pensando…</span>
        </div>

        <div className="px-4 py-2 rounded-2xl bg-neutral-100 dark:bg-neutral-800">
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 bg-neutral-400 dark:bg-neutral-500 rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function NoCreditsCard({
  onBuy,
  compact = false,
  packages = [],
  loadingPackages = false,
}: {
  onBuy?: (packKey?: string) => void
  compact?: boolean
  packages?: Array<{
    id: string
    name: string
    credits: number
    price: number
    priceLabel?: string
    popular?: boolean
    description?: string
    packKey?: string
  }>
  loadingPackages?: boolean
}) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/60 backdrop-blur px-4 py-3 shadow-sm space-y-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-8 h-8 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
            <Coins className="w-4 h-4" />
          </div>

          <div className="flex-1">
            <div className="text-sm font-semibold">😅 Te quedaste sin créditos</div>

            {!compact && (
              <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
                Recarga aquí mismo y seguimos en la conversación sin salir del chat.
              </div>
            )}
          </div>
        </div>

        {loadingPackages ? (
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            Cargando paquetes…
          </div>
        ) : packages.length > 0 ? (
          <div className="grid gap-2">
            {packages.map((p) => {
              const packKey = String(p.packKey || p.id || '')
              return (
                <button
                  key={packKey}
                  type="button"
                  onClick={() => onBuy?.(packKey)}
                  className="text-left rounded-2xl border border-neutral-200 dark:border-neutral-800 px-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/70 transition"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">
                        {p.name} {p.popular ? '🔥' : ''}
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        {p.credits.toLocaleString('es-MX')} créditos
                      </div>
                    </div>

                    <div className="text-sm font-semibold">
                      {p.priceLabel || `$${p.price}`}
                    </div>
                  </div>

                  {p.description ? (
                    <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      {p.description}
                    </div>
                  ) : null}
                </button>
              )
            })}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onBuy?.()}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-black text-white dark:bg-white dark:text-black hover:opacity-90 transition"
            >
              <Sparkles className="w-4 h-4" />
              Comprar créditos
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function aiFunnelGoalLabel(goal: string) {
  const labels: Record<string, string> = {
    checkout: 'Vender directamente',
    whatsapp: 'Llevar personas a WhatsApp',
    community: 'Llevar personas a una comunidad',
    lead: 'Captar prospectos',
    booking: 'Agendar una llamada o cita',
    inform: 'Presentar una marca, producto o servicio',
    auto: 'Definir automáticamente',
  }

  return labels[goal] || 'Objetivo definido por IA'
}

export function AIMasterWidget() {
  return (
    <SkillsProvider scope="selector">
      <AIMasterWidgetInner />
    </SkillsProvider>
  )
}

function AIMasterWidgetInner() {
  const { loggedIn } =
    useCurrentUser()
  const pathname = usePathname()
  const samanthaPageContext = useSamanthaPageContext()
  const hasContextBarToggle =
    samanthaPageContext.kind ===
      'space' ||
    samanthaPageContext.kind ===
      'encounter'
  const isAIHostRoute =
    pathname === '/launchpad' ||
    pathname === '/dashboard' ||
    pathname?.startsWith(
      '/dashboard/'
    )
  
  const router = useRouter()
  const { openFunnelTemplatePicker } = useFunnelTemplatePicker()

  const hiddenPrefixes = [
    '/acceptable-use',
    '/aia',
    '/compensation-plan',
    '/community-rules',
    '/f',
    '/funnel-view',
    '/funnel-preview',
    '/inbox',
    '/income-disclaimer',
    '/launch',
    '/login',
    '/product',
    '/onboarding',
    '/start',
    '/activate',
    '/awaken',
    '/settings',
    '/setup',
    '/privacy',
    '/terms',
    '/cookie-policy',
    '/pricing-and-fees',
    '/refund-policy',
  ]

  const safePathname = pathname || ''

  const [
    hiddenByExperience,
    setHiddenByExperience,
  ] = useState(false)

  const isPublicEventDetail =
    /^\/events\/[^/]+\/?$/.test(
      safePathname
    )

  const isHidden =
    hiddenByExperience ||
    !safePathname ||
    safePathname === '/' ||
    (
      isPublicEventDetail &&
      !loggedIn
    ) ||
    hiddenPrefixes.some(
      p =>
        safePathname === p ||
        safePathname.startsWith(
          p + '/'
        )
    )

  const { sellerId } = useSellerId()
  const user = useCurrentUser()
  const firstName =
  String(user?.firstname || user?.name || '')
    .trim()
    .split(/\s+/)[0] || ''

  const { isOwner } = useIsOwner()
  const { isAdmin, loading: adminLoading } = useIsAdmin()
  const { brand } = useBranding(sellerId)
  const { executeNavigation } = useSamanthaNavigation()

  const { check } = useOperationalCapacityGate()

  const [
    embeddedContentCreationAllowed,
    setEmbeddedContentCreationAllowed,
  ] = useState<boolean | null>(null)

  const [launchpadContext, setLaunchpadContext] =
    useState<LaunchpadContextPayload | null>(null)

  const expressActions = useMemo(() => {
    if (isAIHostRoute && launchpadContext?.expressActions?.length) {
      return launchpadContext.expressActions
    }

    return getSamanthaExpressActions({ pathname })
  }, [isAIHostRoute, launchpadContext?.expressActions, pathname])

  const canUseAdsCampaignSimulator = Boolean(isAdmin)

  const currentUserId = Number(
    (user as any)?.userId ||
      (user as any)?.customer_id ||
      (user as any)?.customerId ||
      (user as any)?.id ||
      0
  )

  const canCreatePublicationInChat =
    (Boolean(isAdmin) || currentUserId === 19613) &&
    PUBLICATION_CREATE_IN_CHAT_FOR_ADMINS

  const canUseCreateContentDryRun =
    CREATE_CONTENT_CHAT_DRY_RUN &&
    PUBLICATION_CREATE_IN_CHAT_FOR_ADMINS &&
    Boolean(isAdmin)

  const { selectionMode, forcedSkillId, activeSkill } = useSkills()

  const effectiveSelectionMode = isAIHostRoute ? 'auto' : selectionMode
  const effectiveForcedSkillId = isAIHostRoute ? null : forcedSkillId
  const effectiveActiveSkill = isAIHostRoute ? null : activeSkill

  const {
    funnels,
    selectedFunnelId,
    selectedPageId,
    selectedSectionId,
    selectedBlockId,

    loadFunnels,
    setSelectedFunnelId,
    setSelectedPageId,
    loadPageTree,
  } = useFunnel()

  const selectedFunnel = funnels?.find((f: any) => String(f.id) === String(selectedFunnelId))
  const selectedPage = selectedFunnel?.pages?.find(
    (p: any) => String(p.id) === String(selectedPageId)
  )
  const selectedSection = selectedPage?.sections?.find(
    (s: any) => String(s.id) === String(selectedSectionId)
  )
  const selectedBlock = selectedSection?.blocks?.find(
    (b: any) => String(b.id) === String(selectedBlockId)
  )

  const expressActionIcons = {
    sparkles: Sparkles,
    pen: Pencil,
    users: Users,
    funnel: Funnel,
    crm: MessageSquare,
    course: GraduationCap,
    rocket: Rocket,
    product: ShoppingBag,
    calendar: CalendarDays,
    profile: Users,
    community: Users,
  }

  function buildLaunchpadDiscoveryButtons(
    buttons: Array<{
      label: string
      value: string
      emoji?: string | null
      href?: string | null
      kind?: string | null
      intent?: string | null
      actionId?: string | null
    }>
  ) {
    return buttons.map((button) => ({
      ...button,
      action: 'launchpad_discovery_reply',
      launchpadDiscovery: true,
      label: String(button.label || '').trim(),
      value: String(button.value || '').trim(),
    }))
  }

  function buildLaunchpadFinalActionButtons(
    buttons: Array<{
      label: string
      value?: string
      emoji?: string | null
      href?: string | null
      kind?: string | null
      intent?: string | null
      actionId?: string | null
    }>
  ) {
    return buttons.map((button) => {
      const intent = String(button.intent || '').trim()
      const href = String(button.href || getLaunchpadIntentRoute(intent) || '').trim()

      return {
        ...button,
        action: 'launchpad_system_action',
        launchpadDiscovery: false,
        label: String(button.label || '').trim(),
        value: href ? `navigation:${href}` : String(button.value || '').trim(),
        href: href || null,
        intent,
        kind: button.kind || 'system_intent',
      }
    })
  }

  function pushLaunchpadSystemReply(input: {
    id: string
    content: string
    buttons?: Array<{
      label: string
      value: string
      emoji?: string
    }>
  }) {
    setUIState('chat')
    setChatViewMode('dock')
    setInput('')
    setCommandFocused(false)
    setActiveSuggestionIndex(-1)
    setCompactTurnStartedAt(Date.now())

    setMessages((prev) => [
      ...prev,
      {
        id: `${input.id}-${Date.now()}`,
        role: 'assistant',
        content: input.content,
        timestamp: new Date(),
        meta: {
          worker: 'platform_navigator',
          source: 'launchpad',
          compact: true,
          buttons: input.buttons || [],
        },
      } as AIMasterChatMessage,
    ])
  }

  function getLaunchpadIntentRoute(intent: string) {
    const routes: Record<string, string> = {
      publish_funnel: '/funnel-builder',
      configure_user_onboarding: '/onboarding/setup',
      complete_profile: '/settings/profile',
      create_first_offer: '/offers',
      create_course: '/courses',
      start_course: '/courses',
      continue_course: '/courses',
      start_onboarding: '/onboarding',
      continue_onboarding: '/onboarding',
    }

    return routes[intent] || null
  }

  async function handleLaunchpadFinalButton(button: any) {
    const intent = String(button?.intent || '').trim()

    if (intent) {
      const handled = await handleLaunchpadSystemAction({
        id: button?.actionId || button?.id || intent,
        label: button?.label || intent,
        prompt: button?.value || button?.label || intent,
        icon: 'sparkles',
        intent,
        launchpadIntent: intent,
        href: button?.href || null,
      } as any)

      if (handled) return true
    }

    const href = String(
      button?.href ||
        getLaunchpadIntentRoute(intent) ||
        ''
    ).trim()

    if (href) {
      pushLaunchpadSystemReply({
        id: 'launchpad-navigation',
        content: 'Listo. Te llevo al lugar correcto para continuar.',
        buttons: [
          {
            label: button?.label || 'Continuar',
            value: `navigation:${href}`,
            emoji: button?.emoji || '➡️',
          },
        ],
      })

      setTimeout(() => {
        hideDockForExternalSurface()
        router.push(href)
      }, 650)

      return true
    }

    const value = String(button?.value || '').trim()

    if (value.startsWith('intent:')) {
      const valueIntent = value.replace('intent:', '').trim()

      if (valueIntent) {
        return handleLaunchpadSystemAction({
          id: valueIntent,
          label: button?.label || valueIntent,
          prompt: valueIntent,
          icon: 'sparkles',
          intent: valueIntent,
          launchpadIntent: valueIntent,
        } as any)
      }
    }

    if (value.startsWith('navigation:')) {
      const path = value.replace('navigation:', '').trim()

      if (path) {
        pushLaunchpadSystemReply({
          id: 'launchpad-navigation',
          content: 'Listo. Te llevo al lugar correcto para continuar.',
          buttons: [
            {
              label: button?.label || 'Continuar',
              value,
              emoji: button?.emoji || '➡️',
            },
          ],
        })

        setTimeout(() => {
          hideDockForExternalSurface()
          router.push(path)
        }, 650)

        return true
      }
    }

    return false
  }

  async function handleLaunchpadSystemAction(action: SamanthaExpressAction) {
    const actionId = String((action as any).id || '').trim()

    const intentFromId: Record<string, string> = {
      'launchpad-diagnosis': 'launchpad_discovery',
      'launchpad-automate-funnel': 'automate_user_funnel',
      'automate-funnel': 'automate_user_funnel',
      'automate-user-funnel': 'automate_user_funnel',
      'continue-onboarding': 'continue_onboarding',
      'create-funnel': 'create_funnel',
      'create-content': 'create_promo_content',
      'launchpad-create-content': 'create_promo_content',
      'launchpad-whatsapp-sequence': 'create_whatsapp_sequence',
    }

    const intent = String(
      (action as any).intent ||
        (action as any).launchpadIntent ||
        intentFromId[actionId] ||
        ''
    ).trim()

    if (!intent) return false

    if (intent === 'launchpad_discovery') {
      startLaunchpadDiscovery()
      return true
    }

    if (intent === 'publish_funnel') {
      pushLaunchpadSystemReply({
        id: 'launchpad-publish-funnel',
        content:
          'Perfecto. Te llevo a tus embudos para que revises cuál quieres publicar. Abre el embudo correspondiente y usa el botón **Publicar** cuando esté listo.',
        buttons: [
          {
            label: 'Ir a mis embudos',
            value: 'navigation:/funnels',
            emoji: '🧲',
          },
        ],
      })

      setTimeout(() => {
        hideDockForExternalSurface()
        router.push('/funnel-builder')
      }, 650)

      return true
    }

    if (intent === 'install_funnel_template') {
      hideDockForExternalSurface()

      openFunnelTemplatePicker({
        initialTab: 'official',
      })

      return true
    }

    if (intent === 'create_funnel') {
      startAIFunnelDiscovery({
        source: 'express_action',
      })

      return true
    }

    if (
      intent === 'create_promo_content' ||
      intent === 'create_content_for_prospects'
    ) {
      setUIState('chat')
      setChatViewMode('dock')
      setInput('')
      setCommandFocused(false)
      setActiveSuggestionIndex(-1)
      setCompactTurnStartedAt(Date.now())

      startPublicationDiscovery(
        intent === 'create_content_for_prospects'
          ? 'Quiero crear una publicación para conseguir prospectos'
          : 'Quiero crear contenido para promocionar mi embudo'
      )

      return true
    }

    if (intent === 'get_instagram_prospects') {
      setUIState('chat')
      setChatViewMode('dock')
      setInput('')
      setCommandFocused(false)
      setActiveSuggestionIndex(-1)
      setCompactTurnStartedAt(Date.now())

      startPublicationDiscovery(
        'Quiero crear contenido para conseguir prospectos desde Instagram'
      )

      return true
    }

    if (intent === 'view_funnels') {
      hideDockForExternalSurface()
      router.push('/funnel-builder')
      return true
    }

    if (intent === 'view_community') {
      hideDockForExternalSurface()
      router.push('/community')
      return true
    }

    if (intent === 'create_whatsapp_sequence' || intent === 'automate_user_funnel') {
      pushLaunchpadSystemReply({
        id: 'launchpad-whatsapp-sequence',
        content:
          'Perfecto. Te llevo al CRM para crear o revisar tus secuencias de seguimiento. Después podemos conectar esto a un flujo interno más guiado.',
        buttons: [
          {
            label: 'Ir al CRM',
            value: 'navigation:/crm',
            emoji: '💬',
          },
          {
            label: 'Crear embudo primero',
            value: 'intent:create_funnel',
            emoji: '🧲',
          },
        ],
      })

      setTimeout(() => {
        hideDockForExternalSurface()
        router.push('/crm')
      }, 650)

      return true
    }

    if (intent === 'promote_funnel' || intent === 'promote_user_funnel') {
      pushLaunchpadSystemReply({
        id: 'launchpad-promote-funnel',
        content:
          'Buen momento para promocionar. Primero revisa tu embudo publicado; después podemos crear contenido o una campaña para mover tráfico.',
        buttons: [
          {
            label: 'Ver embudos',
            value: 'navigation:/funnels',
            emoji: '🧲',
          },
          {
            label: 'Crear contenido',
            value: 'navigation:/creator-studio?tab=publications',
            emoji: '✍️',
          },
        ],
      })

      return true
    }

    if (intent === 'complete_profile') {
      pushLaunchpadSystemReply({
        id: 'launchpad-complete-profile',
        content:
          'Vamos a dejar tu perfil listo. Te llevo a tu perfil para subir foto, completar tu bio y mejorar tu presencia dentro de la plataforma.',
        buttons: [
          {
            label: 'Abrir mi perfil',
            value: 'navigation:/profile',
            emoji: '👤',
          },
        ],
      })

      setTimeout(() => {
        hideDockForExternalSurface()
        router.push('/settings/profile')
      }, 650)

      return true
    }

    if (intent === 'configure_user_onboarding') {
      pushLaunchpadSystemReply({
        id: 'launchpad-configure-onboarding',
        content:
          'Perfecto. Te llevo a la configuración de onboarding para que definas el recorrido que verán tus usuarios al entrar.',
        buttons: [
          {
            label: 'Abrir onboarding',
            value: 'navigation:/onboarding/setup',
            emoji: '🪜',
          },
        ],
      })

      setTimeout(() => {
        hideDockForExternalSurface()
        router.push('/onboarding/setup')
      }, 650)

      return true
    }

    if (
      intent === 'continue_course' ||
      intent === 'view_courses' ||
      intent === 'start_course'
    ) {
      hideDockForExternalSurface()
      router.push('/courses')
      return true
    }

    if (intent === 'continue_onboarding' || intent === 'start_onboarding') {
      hideDockForExternalSurface()
      router.push('/onboarding')
      return true
    }

    if (intent === 'view_funnels') {
      hideDockForExternalSurface()
      router.push('/funnels')
      return true
    }

    if (intent === 'view_community') {
      hideDockForExternalSurface()
      router.push('/community')
      return true
    }

    return false
  }

  async function runExpressAction(action: SamanthaExpressAction) {
    const text = action.prompt.trim()
    if (!text || loading) return

    if (await handleLaunchpadSystemAction(action)) {
      return
    }

    if ((action as any).href) {
      setUIState('closed')
      setChatViewMode('dock')
      setInput('')
      setCommandFocused(false)
      router.push((action as any).href)
      return
    }

    setUIState('chat')
    setChatViewMode('dock')
    setInput('')
    setCommandFocused(false)
    setActiveSuggestionIndex(-1)

    handleSend(text)

    requestAnimationFrame(() => {
      if (commandInputRef.current) {
        commandInputRef.current.style.height = 'auto'
      }
    })
  }

  function closeDockAfterNavigation(options?: { focusCommandBar?: boolean }) {
    setUIState('closed')
    setChatViewMode('dock')
    setActiveSuggestionIndex(-1)
    setInput('')

    if (inputRef.current) {
      inputRef.current.blur()
      inputRef.current.style.height = 'auto'
    }

    if (commandInputRef.current) {
      commandInputRef.current.style.height = 'auto'

      if (options?.focusCommandBar) {
        requestAnimationFrame(() => {
          commandInputRef.current?.focus()
          setCommandFocused(true)
        })
      } else {
        commandInputRef.current.blur()
        setCommandFocused(false)
      }
    } else {
      setCommandFocused(false)
    }
  }

  function hideDockForExternalSurface() {
    stopSpeech()
    setUIState('closed')
    setChatViewMode('dock')
    setInput('')
    setCommandFocused(false)
    setActiveSuggestionIndex(-1)

    if (inputRef.current) {
      inputRef.current.blur()
      inputRef.current.style.height = 'auto'
    }

    if (commandInputRef.current) {
      commandInputRef.current.blur()
      commandInputRef.current.style.height = 'auto'
    }
  }

  function getFunnelPagesCount(funnel: any) {
    return Array.isArray(funnel?.pages) ? funnel.pages.length : 0
  }

  function getFunnelThumbnailUrl(funnel: any) {
    return String(
      funnel?.thumbnailUrl ||
        funnel?.thumbnail_url ||
        funnel?.thumbnail ||
        funnel?.image ||
        funnel?.cover ||
        funnel?.settings?.thumbnailUrl ||
        funnel?.settings?.thumbnail_url ||
        funnel?.settings?.thumbnail ||
        ''
    )
  }

  function getFunnelUpdatedLabel(funnel: any) {
    const rawDate =
      funnel?.updatedAt ||
      funnel?.updated_at ||
      funnel?.dateModified ||
      funnel?.date_modified ||
      funnel?.modifiedAt ||
      funnel?.modified_at

    if (!rawDate) return 'Listo para promocionar'

    const date = new Date(rawDate)

    if (Number.isNaN(date.getTime())) {
      return 'Listo para promocionar'
    }

    const diffMs = Date.now() - date.getTime()
    const diffMinutes = Math.floor(diffMs / 1000 / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 1) return 'Editado ahora'
    if (diffMinutes < 60) return `Editado hace ${diffMinutes} min`
    if (diffHours < 24) return `Editado hace ${diffHours} h`
    if (diffDays < 7) return `Editado hace ${diffDays} d`

    return 'Editado recientemente'
  }

  function normalizeFunnelsForChatEntityResults(
    list: any[]
  ): ChatEntityResultsPayload {
    const items = Array.isArray(list)
      ? list
          .filter((funnel: any) => funnel?.id)
          .slice(0, 6)
          .map((funnel: any) => {
            const funnelId = String(funnel.id)
            const pagesCount = getFunnelPagesCount(funnel)

            return {
              id: funnelId,
              title: String(funnel?.name || 'Embudo sin nombre'),
              subtitle:
                pagesCount === 1 ? '1 página' : `${pagesCount} páginas`,
              description: getFunnelUpdatedLabel(funnel),
              thumbnailUrl: getFunnelThumbnailUrl(funnel),
              badge: 'Embudo',
              value: `ads_source:funnel:${funnelId}`,
              actionLabel: 'Promocionar',
              meta: {
                sourceType: 'funnel',
                funnelId,
                pagesCount,
              },
            }
          })
      : []

    return {
      kind: 'funnel',
      title: 'Elige el embudo que quieres promocionar',
      subtitle:
        'Encontré estos embudos en tu cuenta. Selecciona uno para preparar la campaña.',
      emptyTitle: 'No encontré embudos disponibles',
      emptyDescription:
        `Primero crea o publica un embudo para poder promocionarlo con ${masterAgentName}.`,
      items,
    }
  }

  const credits = useMemo(() => Number(user?.ai_credits ?? 0), [user?.ai_credits])
  const hasCredits = credits > 0

  const AUDIO_PREF_KEY = 'hubee:ai-widget-audio-enabled:v1'
  const AUDIO_DISCLOSURE_SEEN_KEY = 'hubee:ai-widget-audio-disclosure-seen:v1'

  const [
    pendingCreateContentDirection,
    setPendingCreateContentDirection,
  ] = useState<CreateContentDirectionState | null>(
    null
  )

  const [
    pendingCreateContentPlan,
    setPendingCreateContentPlan,
  ] = useState<PendingCreateContentPlan | null>(null)

  const [
    pendingExpressContent,
    setPendingExpressContent,
  ] = useState<PendingExpressContent | null>(null)

  const [
    createContentDryRunProject,
    setCreateContentDryRunProject,
  ] = useState<any>(null)


  const [
    embeddedComposerActivated,
    setEmbeddedComposerActivated,
  ] = useState(true)

  const expressProductInputRef =
    useRef<HTMLInputElement | null>(null)

  const expressScriptPollingRef =
    useRef<Set<number>>(new Set())

  const [pendingSupportTicket, setPendingSupportTicket] = useState<{
    originalMessage?: string
    matchedArticle?: any
    awaitingDescription?: boolean
  } | null>(null)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const commandInputRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const pendingDisplayTextRef = useRef<string | null>(null)
  const suppressNextAutoScrollRef = useRef(false)

  const commandBarWrapRef = useRef<HTMLDivElement | null>(null)
  const compactPanelRef = useRef<HTMLDivElement | null>(null)

  const INITIAL_VISIBLE_MESSAGES = 24
  const LOAD_MORE_STEP = 20

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MESSAGES)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)

  const [audioEnabled, setAudioEnabled] = useState(false)
  const [audioVoice] = useState<'samantha' | 'syn_default'>('samantha')

  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([])
  const [uploadingReference, setUploadingReference] = useState(false)
  const [
    uploadingExpressProduct,
    setUploadingExpressProduct,
  ] = useState(false)
  const [lastUploadedReferenceUrl, setLastUploadedReferenceUrl] = useState('')

  const [showAudioDisclosure, setShowAudioDisclosure] = useState(false)
  const [dontShowAudioDisclosureAgain, setDontShowAudioDisclosureAgain] = useState(false)

  const [launchpadSlot, setLaunchpadSlot] = useState<HTMLElement | null>(null)
  type AIHostRenderMode =
    | 'inline-bar'
    | 'embedded-console'

  const [aiHostRenderMode, setAIHostRenderMode] =
    useState<AIHostRenderMode>('inline-bar')

  const isEmbeddedConsole =
    aiHostRenderMode === 'embedded-console'
  const [compactTurnStartedAt, setCompactTurnStartedAt] = useState<number | null>(null)
  const launchpadCommandFormRef = useRef<HTMLFormElement | null>(null)
  const [launchpadDockTop, setLaunchpadDockTop] = useState<number | null>(null)

  const [pendingLaunchpadDiscovery, setPendingLaunchpadDiscovery] =
    useState<LaunchpadDiscoveryState | null>(null)

  const [pendingCreateContentResume, setPendingCreateContentResume] = useState<{
    snapshotId: number
    snapshot: any
    request: any
  } | null>(null)

  const [pendingLightweightFlow, setPendingLightweightFlow] = useState<{
    intent: LightweightIntent
    text: string
  } | null>(null)

  const [pendingPublicationDiscovery, setPendingPublicationDiscovery] =
    useState<PendingPublicationDiscovery | null>(null)

  const [pendingAdsCampaignDiscovery, setPendingAdsCampaignDiscovery] =
    useState<PendingAdsCampaignDiscovery | null>(null)    

  type AIFunnelGoal =
    | 'lead'
    | 'whatsapp'
    | 'checkout'
    | 'community'
    | 'booking'
    | 'inform'
    | 'auto'

  type AIFunnelReference = {
    type: 'image' | 'url' | 'document'
    url: string
    name?: string
  }

  type AIFunnelDiscoveryStep =
    | 'idea'
    | 'goal'
    | 'audience'
    | 'offer'
    | 'branding'
    | 'references'
    | 'review'
    | 'generating'
    | 'done'

  type PendingFunnelDiscovery =
    | {
        mode: 'legacy'
        step: 'start'
        originalRequest: string
      }
    | {
        mode: 'ai_html'
        step: AIFunnelDiscoveryStep
        source: string

        originalRequest: string
        description: string

        goal?: AIFunnelGoal
        audience?: string
        offer?: string
        primaryAction?: string

        useBranding: boolean
        branding?: {
          brandName?: string
          logoUrl?: string
          primaryColor?: string
          secondaryColor?: string
          accentColor?: string
        }

        references: AIFunnelReference[]

        inferred: {
          pageType?: 'landing' | 'lead_capture' | 'sales' | 'registration'
          pageCount?: number
          visualStyle?: string
          tone?: string
          sections?: string[]
        }
      }

  const [pendingFunnelDiscovery, setPendingFunnelDiscovery] =
    useState<PendingFunnelDiscovery | null>(null)

  const canUploadPublicationReference =
    Boolean(pendingPublicationDiscovery) &&
    pendingPublicationDiscovery?.format === 'image' &&
    pendingPublicationDiscovery?.creativeMode === 'professional_image'

  const [aiConfig, setAIConfig] = useState({
    chatEnabled: true,
    trainingEnabled: true,
    sellerTrainingExcluded: false,
  })

  const [uiState, setUIState] = useState<AIMasterUIState>('closed')
  const [currentAgentId, setCurrentAgentId] = useState<number | null>(null)
  const [showNoCreditsCard, setShowNoCreditsCard] = useState(false)
  const [input, setInput] = useState('')
  const [commandFocused, setCommandFocused] = useState(false)

  type ContextBarMode =
    | 'publish'
    | 'agent'

  const [
    contextBarMode,
    setContextBarMode,
  ] = useState<ContextBarMode>(
    'agent'
  )

  const [
    contextBarExpanded,
    setContextBarExpanded,
  ] = useState(false)

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)
    
  const [platformSnapshot, setPlatformSnapshot] = useState<any>({
    user_progress: [],
    upcoming_events: [],
    unread_posts: [],
    user_activity: null,
    new_courses: [],
    available_transcripts: [],
    seller_metrics: null,
    crm: {
      pipeline_summary: {},
      total_leads: 0,
      leads: [],
      recent_chats: [],
      stats: {},
      labels: [],
      members_summary: {},
      agents: [],
    },
  })

  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false)
  const [selectedCreditPackKey, setSelectedCreditPackKey] = useState<string | null>(null)
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([])
  const [loadingCreditPackages, setLoadingCreditPackages] = useState(false)
  const [creditPackagesLoaded, setCreditPackagesLoaded] = useState(false)

  const [assistantNotifications, setAssistantNotifications] = useState<any[]>([])
  const [notificationsLoaded, setNotificationsLoaded] = useState(false)
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false)
  const [showNotificationHint, setShowNotificationHint] = useState(false)

  type ChatViewMode = 'dock' | 'focus'

  const [chatViewMode, setChatViewMode] = useState<ChatViewMode>('dock')

  const {
    speak,
    stop: stopSpeech,
    isLoading: speechLoading,
    isPlaying: speechPlaying,
    lastError: speechError,
    lastErrorCode: speechErrorCode,
    lastMeta: speechMeta,
  } = useSynSpeech()

  /*
  type Skill = {
    id: number
    name: string
    slug: string
    status: 'active' | 'draft' | 'paused'
    is_trained: number
  }

  const [skills, setSkills] = useState<Skill[]>([])
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null)
  const [skillsOpen, setSkillsOpen] = useState(false)
  */

  const {
    messages,
    setMessages,
    sendMessage,
    loading,
    cancelStream,
    clearMessages,
    masterAgent,
    conversationId,
    injectAssistantMessage,
  } = useAIMasterChat(
    sellerId,
    {
      onNavigation: (navigation) => {
        executeNavigation(navigation)
      },
      onPublicationFlow: (flow) => {
        if (!flow?.action) return

        if (flow.action === 'preview') {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('samantha:auto-publish', {
                detail: {
                  channelId: flow.channelId,
                  title: flow.title,
                  content: flow.content,
                  productId: flow.productId || null,
                  imagePrompt: flow.imageStyle !== 'none' ? flow.imagePrompt : null,
                  imageStyle: flow.imageStyle !== 'none' ? flow.imageStyle : null,
                  imageUrl: flow.imageUrl || null,
                },
              })
            )
          }, 1000)
        } else if (flow.action === 'confirm_publish') {
          window.dispatchEvent(new CustomEvent('samantha:confirm-publish'))
        } else if (flow.action === 'update_text') {
          window.dispatchEvent(
            new CustomEvent('samantha:update-text', {
              detail: {
                title: flow.title,
                content: flow.content,
              },
            })
          )
        } else if (flow.action === 'regenerate_image') {
          window.dispatchEvent(
            new CustomEvent('samantha:regenerate-image', {
              detail: {
                imagePrompt: flow.imagePrompt,
                imageStyle: flow.imageStyle,
              },
            })
          )
        }
      },
      onButtons: () => {},
      onActionsExecuted: (actions) => {
        //console.log('[AIMasterWidget] Actions executed:', actions)
      },
    },
    {
      userId: Number(user?.userId || 0),
      skillMode: effectiveSelectionMode,
      skillId: effectiveForcedSkillId,
      activeSkill: effectiveActiveSkill,
    }
  )

  async function continueCreateContentAdvancedInChat(
    pending: PendingCreateContentPlan
  ) {
    const loadingMessageId =
      `create-content-advanced-${Date.now()}`

    setMessages((prev) => [
      ...prev,
      {
        id: loadingMessageId,
        role: 'assistant',
        content:
          'Estoy preparando tu proyecto para generar ideas, hooks y guiones…',
        timestamp: new Date(),
        meta: {
          worker: 'content_creator',
          intent:
            'CREATE_CONTENT_ADVANCED_PREPARING',
          thinking: {
            label: 'Preparando proyecto…',
          },
        },
      } as AIMasterChatMessage,
    ])

    try {
      const created =
        await createAdvancedContentProject({
          request: pending.request,
          plan: pending.plan,
          direction: pending.direction,
          discovery: pending.discovery,
        })

      const params =
        new URLSearchParams()

      params.set(
        'projectId',
        String(created.projectId)
      )

      params.set('tab', 'ideas')
      params.set('autoGenerate', 'true')
      params.set(
        'fromFlow',
        'create_content'
      )

      setMessages((prev) =>
        prev.map((message) =>
          message.id === loadingMessageId
            ? {
                ...message,
                content:
                  'Tu proyecto quedó listo. Abriré Creator Studio para generar varias ideas.',
                meta: {
                  ...(message.meta || {}),
                  thinking: undefined,
                  intent:
                    'CREATE_CONTENT_ADVANCED_READY',
                },
              }
            : message
        )
      )

      window.location.href =
        `/creator-studio?${params.toString()}`
    } catch (error: any) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === loadingMessageId
            ? {
                ...message,
                content:
                  error?.message ||
                  'No se pudo preparar el proyecto.',
                meta: {
                  ...(message.meta || {}),
                  thinking: undefined,
                  intent:
                    'CREATE_CONTENT_ADVANCED_ERROR',
                  buttons: [
                    {
                      label:
                        'Intentar nuevamente',
                      value:
                        'create_content_plan_advanced',
                      emoji: '↻',
                    },
                  ],
                },
              }
            : message
        )
      )
    }
  }

  async function startLaunchpadDiscovery() {
    if (!sellerId || loading) return

    const role = launchpadContext?.role || (isOwner ? 'owner' : 'user')

    setUIState('chat')
    setChatViewMode('dock')
    setInput('')
    setCommandFocused(false)
    setActiveSuggestionIndex(-1)
    setCompactTurnStartedAt(Date.now())

    try {
      const res = await fetch('/api/launchpad/discovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sellerId,
          role,
          step: 'start',
          answers: {},
          capabilities: {
            onboardingAvailable:
              Boolean(launchpadContext?.state?.user?.onboardingAvailable) ||
              Boolean(launchpadContext?.state?.seller?.hasOnboardingConfigured),
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo iniciar el diagnóstico.')
      }

      setPendingLaunchpadDiscovery({
        step: json.step || 'goal',
        role,
        answers: json.answers || {},
      })

      setMessages((prev) => [
        ...prev,
        {
          id: `launchpad-discovery-${Date.now()}`,
          role: 'assistant',
          content:
            json.message ||
            'Te voy a hacer unas preguntas rápidas para recomendarte el siguiente paso correcto.',
          timestamp: new Date(),
          meta: {
            intent: 'launchpad_discovery',
            source: 'launchpad',
            compact: true,
            buttons: buildLaunchpadDiscoveryButtons(json.buttons || []),
          },
        } as AIMasterChatMessage,
      ])
    } catch (error: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: `launchpad-discovery-error-${Date.now()}`,
          role: 'assistant',
          content:
            error?.message ||
            'No pude iniciar el diagnóstico. Inténtalo de nuevo en un momento.',
          timestamp: new Date(),
          meta: {
            intent: 'launchpad_discovery_error',
            source: 'launchpad',
          },
        } as AIMasterChatMessage,
      ])
    }
  }

  async function handleLaunchpadDiscoveryReply(answer: string) {
    if (!pendingLaunchpadDiscovery || !sellerId) return false

    const cleanAnswer = String(answer || '').trim()
    if (!cleanAnswer) return true

    const current = pendingLaunchpadDiscovery

    setCompactTurnStartedAt(Date.now())

    try {
      const res = await fetch('/api/launchpad/discovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sellerId,
          role: current.role,
          step: current.step,
          answer: cleanAnswer,
          answers: current.answers || {},
          capabilities: {
            onboardingAvailable:
              Boolean(launchpadContext?.state?.user?.onboardingAvailable) ||
              Boolean(launchpadContext?.state?.seller?.hasOnboardingConfigured),
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No pude continuar el diagnóstico.')
      }

      if (json.done) {
        setPendingLaunchpadDiscovery(null)

        if (json.immediateAction?.intent) {
          setMessages((prev) => [
            ...prev,
            {
              id: `launchpad-discovery-terminal-${Date.now()}`,
              role: 'assistant',
              content:
                json.message ||
                'Perfecto. Te llevo al siguiente paso correcto.',
              timestamp: new Date(),
              meta: {
                intent: 'launchpad_discovery_terminal',
                source: 'launchpad',
                compact: true,
              },
            } as AIMasterChatMessage,
          ])

          try {
            const contextRes = await fetch('/api/launchpad/context', {
              method: 'GET',
              cache: 'no-store',
            })

            const contextJson = await contextRes.json().catch(() => null)

            if (contextRes.ok && contextJson?.ok) {
              setLaunchpadContext(contextJson)
              publishLaunchpadContext(contextJson)
            }
          } catch (refreshError) {
            console.warn('[launchpad discovery] context refresh skipped:', refreshError)
          }

          await handleLaunchpadSystemAction({
            id: json.immediateAction.id || json.immediateAction.intent,
            label: json.immediateAction.label || json.immediateAction.intent,
            prompt: json.immediateAction.label || json.immediateAction.intent,
            icon: 'sparkles',
            intent: json.immediateAction.intent,
            launchpadIntent: json.immediateAction.intent,
            href: json.immediateAction.href || null,
          } as any)

          return true
        }

        setMessages((prev) => [
          ...prev,
          {
            id: `launchpad-discovery-done-${Date.now()}`,
            role: 'assistant',
            content:
              json.message ||
              'Listo. Ya tengo suficiente contexto para recomendarte el siguiente paso.',
            timestamp: new Date(),
            meta: {
              intent: 'launchpad_discovery_done',
              source: 'launchpad',
              compact: true,
              buttons: buildLaunchpadFinalActionButtons(json.buttons || []),
            },
          } as AIMasterChatMessage,
        ])

        try {
          const contextRes = await fetch('/api/launchpad/context', {
            method: 'GET',
            cache: 'no-store',
          })

          const contextJson = await contextRes.json().catch(() => null)

          if (contextRes.ok && contextJson?.ok) {
            setLaunchpadContext(contextJson)
            publishLaunchpadContext(contextJson)
          }
        } catch (refreshError) {
          console.warn('[launchpad discovery] context refresh skipped:', refreshError)
        }

        return true
      }

      setPendingLaunchpadDiscovery({
        step: json.step || current.step,
        role: current.role,
        answers: json.answers || current.answers || {},
      })

      setMessages((prev) => [
        ...prev,
        {
          id: `launchpad-discovery-step-${Date.now()}`,
          role: 'assistant',
          content: json.message || 'Perfecto. Sigamos con el siguiente paso.',
          timestamp: new Date(),
          meta: {
            intent: 'launchpad_discovery',
            source: 'launchpad',
            compact: true,
            buttons: buildLaunchpadDiscoveryButtons(json.buttons || []),
          },
        } as AIMasterChatMessage,
      ])

      return true
    } catch (error: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: `launchpad-discovery-error-${Date.now()}`,
          role: 'assistant',
          content:
            error?.message ||
            'No pude continuar el diagnóstico. Inténtalo otra vez.',
          timestamp: new Date(),
          meta: {
            intent: 'launchpad_discovery_error',
            source: 'launchpad',
          },
        } as AIMasterChatMessage,
      ])

      return true
    }
  }

  

  function createChatUserMessage(
    content: string,
    meta?: Record<string, any>
  ): AIMasterChatMessage {
    return {
      id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      role: 'user',
      content,
      timestamp: new Date(),
      meta,
    }
  }

  function createFunnelAssistantMessage(input: {
    content: string
    intent: string
    buttons?: any[]
    meta?: Record<string, any>
  }): AIMasterChatMessage {
    return {
      id: `ai-funnel-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      role: 'assistant',
      content: input.content,
      timestamp: new Date(),
      meta: {
        worker: 'funnel_worker',
        source: 'ai_html_builder',
        compact: true,
        intent: input.intent,
        buttons: input.buttons || [],
        ...(input.meta || {}),
      },
    }
  }

  function inferAIFunnelGoal(text: string): AIFunnelGoal | undefined {
    const value = normalizeSmallReply(text)

    if (
      value.includes('whatsapp') ||
      value.includes('mensaje') ||
      value.includes('contacten')
    ) {
      return 'whatsapp'
    }

    if (
      value.includes('vender') ||
      value.includes('venta') ||
      value.includes('comprar') ||
      value.includes('checkout') ||
      value.includes('pagar')
    ) {
      return 'checkout'
    }

    if (
      value.includes('prospecto') ||
      value.includes('lead') ||
      value.includes('registro') ||
      value.includes('captar')
    ) {
      return 'lead'
    }

    if (value.includes('comunidad')) return 'community'

    if (
      value.includes('cita') ||
      value.includes('llamada') ||
      value.includes('agenda') ||
      value.includes('reservar')
    ) {
      return 'booking'
    }

    return undefined
  }

  function inferAIFunnelType(goal?: AIFunnelGoal) {
    switch (goal) {
      case 'lead':
        return 'lead_capture'
      case 'checkout':
        return 'sales'
      case 'booking':
        return 'registration'
      case 'whatsapp':
      case 'community':
      case 'inform':
      case 'auto':
      default:
        return 'landing'
    }
  }

  function inferAIFunnelSections(input: {
    goal?: AIFunnelGoal
    description: string
  }) {
    const sections = [
      'navbar',
      'hero',
      'problem',
      'solution',
      'benefits',
      'how_it_works',
      'social_proof',
    ]

    if (input.goal === 'checkout') {
      sections.push('offer', 'pricing', 'guarantee')
    }

    if (input.goal === 'lead') {
      sections.push('lead_form')
    }

    if (input.goal === 'booking') {
      sections.push('booking')
    }

    sections.push('faq', 'final_cta', 'footer')

    return sections
  }

  function getDefaultAIFunnelState(input?: {
    source?: string
  }): Extract<PendingFunnelDiscovery, { mode: 'ai_html' }> {
    return {
      mode: 'ai_html',
      step: 'idea',
      source: input?.source || 'unknown',

      originalRequest: '',
      description: '',

      useBranding: false,
      references: [],

      inferred: {
        pageCount: 1,
        visualStyle: 'premium_modern',
        tone: 'clear_conversion_focused',
        sections: [],
      },
    }
  }

  function startAIFunnelDiscovery(input?: {
    source?: string
  }) {
    const nextState = getDefaultAIFunnelState(input)

    setPendingFunnelDiscovery(nextState)

    setUIState('chat')
    setChatViewMode('dock')
    setInput('')
    setCommandFocused(false)
    setActiveSuggestionIndex(-1)
    setCompactTurnStartedAt(Date.now())

    setMessages((prev) => [
      ...prev,
      createFunnelAssistantMessage({
        intent: 'AI_FUNNEL_DISCOVERY_IDEA',
        content:
          'Describe tu producto, servicio o idea. Puedes escribirlo como se lo explicarías a una persona.\n\nNo necesitas saber de diseño ni decirme qué secciones usar. Yo me encargo de convertirlo en una landing completa.',
        buttons: [
          {
            label: 'Agregar referencia',
            value: 'ai_funnel:add_reference',
            action: 'ai_funnel_action',
            emoji: '📎',
          },
          {
            label: 'Usar mi branding',
            value: 'ai_funnel:use_branding',
            action: 'ai_funnel_action',
            emoji: '🎨',
          },
        ],
      }),
    ])

    focusInput()
  }

  function getAIFunnelQuestion(
    state: Extract<PendingFunnelDiscovery, { mode: 'ai_html' }>
  ) {
    switch (state.step) {
      case 'goal':
        return createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_DISCOVERY_GOAL',
          content:
            'Perfecto. Ya entendí la idea general.\n\n¿Cuál debe ser el resultado principal de esta página?',
          buttons: [
            {
              label: 'Conseguir prospectos',
              value: 'ai_funnel:goal:lead',
              action: 'ai_funnel_action',
              emoji: '🎯',
            },
            {
              label: 'Llevar a WhatsApp',
              value: 'ai_funnel:goal:whatsapp',
              action: 'ai_funnel_action',
              emoji: '💬',
            },
            {
              label: 'Vender directamente',
              value: 'ai_funnel:goal:checkout',
              action: 'ai_funnel_action',
              emoji: '💳',
            },
            {
              label: 'Agendar una cita',
              value: 'ai_funnel:goal:booking',
              action: 'ai_funnel_action',
              emoji: '📅',
            },
            {
              label: 'Promover comunidad',
              value: 'ai_funnel:goal:community',
              action: 'ai_funnel_action',
              emoji: '👥',
            },
            {
              label: 'Decide por mí',
              value: 'ai_funnel:goal:auto',
              action: 'ai_funnel_action',
              emoji: '✨',
            },
          ],
        })

      case 'audience':
        return createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_DISCOVERY_AUDIENCE',
          content:
            '¿Para quién es esta oferta?\n\nDescríbeme brevemente a la persona que quieres atraer. También puedes escribir **“decide por mí”** y la inferiré con lo que ya me contaste.',
          buttons: [
            {
              label: 'Decide por mí',
              value: 'ai_funnel:audience:auto',
              action: 'ai_funnel_action',
              emoji: '✨',
            },
          ],
        })

      case 'offer':
        return createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_DISCOVERY_OFFER',
          content:
            '¿Qué recibirá exactamente la persona cuando tome acción?\n\nPuede ser un producto, servicio, llamada, registro, membresía, curso o simplemente una conversación por WhatsApp.',
          buttons: [
            {
              label: 'Usa lo que ya te expliqué',
              value: 'ai_funnel:offer:auto',
              action: 'ai_funnel_action',
              emoji: '🧠',
            },
          ],
        })

      case 'branding':
        return createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_DISCOVERY_BRANDING',
          content:
            'Ahora definamos la dirección visual. ¿Quieres que use el branding que ya tienes configurado o prefieres que diseñe una identidad visual nueva para esta página?',
          buttons: [
            {
              label: 'Usar mi branding',
              value: 'ai_funnel:branding:existing',
              action: 'ai_funnel_action',
              emoji: '🎨',
            },
            {
              label: 'Diseña libremente',
              value: 'ai_funnel:branding:auto',
              action: 'ai_funnel_action',
              emoji: '✨',
            },
            {
              label: 'Agregar referencia',
              value: 'ai_funnel:add_reference',
              action: 'ai_funnel_action',
              emoji: '📎',
            },
          ],
        })

      case 'references':
        return createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_DISCOVERY_REFERENCES',
          content:
            'Puedes agregar una página, imagen o documento como referencia. No la copiaré exactamente; la usaré para entender estilo, estructura o contenido.\n\nTambién puedes continuar sin referencia.',
          buttons: [
            {
              label: 'Agregar enlace',
              value: 'ai_funnel:reference:url',
              action: 'ai_funnel_action',
              emoji: '🔗',
            },
            {
              label: 'Subir imagen',
              value: 'ai_funnel:reference:image',
              action: 'ai_funnel_action',
              emoji: '🖼️',
            },
            {
              label: 'Continuar sin referencia',
              value: 'ai_funnel:reference:none',
              action: 'ai_funnel_action',
              emoji: '➡️',
            },
          ],
        })

      default:
        return null
    }
  }

  function buildAIFunnelReviewMessage(
    state: Extract<PendingFunnelDiscovery, { mode: 'ai_html' }>
  ) {
    const referencesLabel = state.references.length
      ? state.references
          .map((reference) => reference.name || reference.url)
          .join(', ')
      : 'Sin referencias externas'

    const brandingLabel = state.useBranding
      ? `Usar branding de ${state.branding?.brandName || brand?.name || 'la marca'}`
      : 'Crear dirección visual automáticamente'

    const offerLabel =
      state.offer === 'Oferta inferida automáticamente a partir de la idea'
        ? 'La IA definirá la oferta y el ángulo de conversión'
        : state.offer || 'Inferida automáticamente'

    return createFunnelAssistantMessage({
      intent: 'AI_FUNNEL_DISCOVERY_REVIEW',
      content:
        'Revisé tus respuestas y preparé la dirección inicial de la landing.',
      buttons: [
        {
          label: 'Crear landing',
          value: 'ai_funnel:generate',
          action: 'ai_funnel_action',
          emoji: '✨',
        },
        {
          label: 'Agregar referencia',
          value: 'ai_funnel:add_reference',
          action: 'ai_funnel_action',
          emoji: '📎',
        },
        {
          label: 'Cambiar información',
          value: 'ai_funnel:edit_brief',
          action: 'ai_funnel_action',
          emoji: '✏️',
        },
      ],
      meta: {
        ai_funnel_review: {
          goal: state.goal,
          description: state.description,
          audience: state.audience,
          offer: state.offer,
          useBranding: state.useBranding,
          branding: state.branding,
          references: state.references,
          inferred: state.inferred,
          buttons: [
            {
              label: 'Crear landing',
              value: 'ai_funnel:generate',
              action: 'ai_funnel_action',
              emoji: '✨',
            },
            {
              label: 'Agregar referencia',
              value: 'ai_funnel:add_reference',
              action: 'ai_funnel_action',
              emoji: '📎',
            },
            {
              label: 'Cambiar información',
              value: 'ai_funnel:edit_brief',
              action: 'ai_funnel_action',
              emoji: '✏️',
            },
          ],
        },
      },
    })
  }

  const masterAgentName = useMemo(() => {
    return String(masterAgent?.name || '').trim() || 'Samantha'
  }, [masterAgent?.name])

  type EncounterSuggestion = {
    encounterId: number
    title: string
    description?: string | null
    score: number
    matchedContributionId?: number | null
  }

  const [
    encounterSuggestion,
    setEncounterSuggestion,
  ] =
    useState<EncounterSuggestion | null>(
      null
    )

  const [
    encounterSuggestionLoading,
    setEncounterSuggestionLoading,
  ] =
    useState(false)

  const [
    encounterSuggestionDismissed,
    setEncounterSuggestionDismissed,
  ] =
    useState(false)

  const contextPublishComposer =
    useContextPublishComposer({
      sellerId,

      context:
        samanthaPageContext,

      canUsePremiumContent:
        Boolean(
          isOwner ||
          isAdmin
        ),

      onPublished: result => {
        setInput('')
        setContextBarExpanded(false)
        setCommandFocused(false)

        requestAnimationFrame(() => {
          if (commandInputRef.current) {
            commandInputRef.current.style.height =
              'auto'

            commandInputRef.current.blur()
          }
        })

        if (
          samanthaPageContext.kind === 'space' &&
          result.encounterId
        ) {
          const postId =
            Number(
              result.post?.id ||
              result.post?.customer_feed_id ||
              0
            )

          const qs =
            new URLSearchParams()

          qs.set(
            'sort',
            'recent'
          )

          if (postId > 0) {
            qs.set(
              'highlight',
              String(postId)
            )
          }

          if (result.contributionType) {
            qs.set(
              'contributionType',
              result.contributionType
            )
          }

          router.push(
            `/spaces/${result.spaceId}/encounters/${result.encounterId}?${qs.toString()}`
          )
        }
      },
    })

  const contextualComposer =
    useMemo(
      () =>
        getContextualComposerConfig(
          samanthaPageContext,
          masterAgentName
        ),
      [
        samanthaPageContext,
        masterAgentName,
      ]
    )

  const inferredContributionType =
  useMemo(() => {
    if (
      !hasContextBarToggle ||
      contextBarMode !==
        'publish' ||
      !input.trim()
    ) {
      return null
    }

    const result =
      resolveContextualComposerIntent({
        text:
          input,

        context:
          samanthaPageContext,

        masterAgentName,

        /*
         * Forzamos publish porque
         * solo queremos inferir TIPO.
         */
        override:
          'publish',
      })

    return (
      result.contributionType ||
      null
    )
  }, [
    input,
    hasContextBarToggle,
    contextBarMode,
    samanthaPageContext,
    masterAgentName,
  ])

  function toggleContextBarMode() {
    if (
      !hasContextBarToggle
    ) {
      /*
      * Fuera de Space/Encounter
      * el círculo conserva su
      * comportamiento actual.
      */
      setUIState('chat')
      setChatViewMode('dock')

      requestAnimationFrame(() => {
        commandInputRef.current?.focus()
      })

      return
    }

    const nextMode =
      contextBarMode ===
        'publish'
        ? 'agent'
        : 'publish'

    if (
      nextMode === 'publish'
    ) {
      contextPublishComposer.openComposer({
        initialContent: input,
      })

      setContextBarExpanded(true)
    } else {
      contextPublishComposer.closeComposer({
        reset: false,
      })

      setContextBarExpanded(false)
    }

    setContextBarMode(
      nextMode
    )

    /*
    * Si cambiamos a publicar,
    * abrimos inmediatamente.
    */
    setContextBarExpanded(
      nextMode === 'publish'
    )

    setCommandFocused(
      true
    )

    requestAnimationFrame(() => {
      commandInputRef.current?.focus()
      resizeTextarea(
        commandInputRef.current
      )
    })
  }

  useEffect(() => {
    if (
      !hasContextBarToggle ||
      contextBarMode !== 'publish' ||
      samanthaPageContext.kind !== 'space'
    ) {
      setEncounterSuggestion(null)

      contextPublishComposer
        .setSuggestedEncounterId(
          null
        )

      contextPublishComposer
        .setSuggestedContributionType(
          null
        )

      return
    }

    const spaceId =
      Number(
        samanthaPageContext.spaceId || 0
      )

    const title =
      String(
        contextPublishComposer.title || ''
      ).trim()

    const content =
      String(
        contextPublishComposer.content || ''
      ).trim()

    const combined =
      `${title} ${content}`.trim()

    if (
      !spaceId ||
      combined.length < 12
    ) {
      setEncounterSuggestion(null)

      contextPublishComposer
        .setSuggestedEncounterId(
          null
        )

      contextPublishComposer
        .setSuggestedContributionType(
          null
        )

      return
    }

    const timer =
      window.setTimeout(
        async () => {
          try {
            setEncounterSuggestionLoading(
              true
            )

            const response =
              await fetch(
                `/api/spaces/${spaceId}/encounters/match`,
                {
                  method: 'POST',

                  credentials:
                    'include',

                  headers: {
                    'Content-Type':
                      'application/json',
                  },

                  body:
                    JSON.stringify({
                      title,
                      content,
                    }),
                }
              )

            const json =
              await response.json()

            if (!response.ok) {
              throw new Error(
                json?.error ||
                'No se pudo analizar el aporte'
              )
            }

            const match =
              json?.match
                ?.existingEncounter ||
              null

            const isQuestion =
              Boolean(
                json?.match
                  ?.isQuestion
              )

            /*
            * Guardamos la clasificación
            * aunque todavía NO seleccionamos
            * automáticamente el Encounter.
            */
            contextPublishComposer
              .setSuggestedContributionType(
                isQuestion
                  ? 'question'
                  : null
              )

            if (
              match &&
              Number(
                match.score || 0
              ) >= 0.25
            ) {
              setEncounterSuggestion({
                encounterId:
                  Number(
                    match.encounterId
                  ),

                title:
                  String(
                    match.title || ''
                  ),

                description:
                  match.description ||
                  null,

                score:
                  Number(
                    match.score || 0
                  ),

                matchedContributionId:
                  match.matchedContributionId
                    ? Number(
                        match
                          .matchedContributionId
                      )
                    : null,
              })

              /*
              * IMPORTANTE:
              * Nueva sugerencia ≠ selección.
              *
              * El usuario todavía debe decidir.
              */
              contextPublishComposer
                .setSuggestedEncounterId(
                  null
                )

              setEncounterSuggestionDismissed(
                false
              )
            } else {
              setEncounterSuggestion(
                null
              )

              contextPublishComposer
                .setSuggestedEncounterId(
                  null
                )
            }
          } catch (
            error
          ) {
            console.error(
              '[encounter composer match]',
              error
            )

            setEncounterSuggestion(
              null
            )

            contextPublishComposer
              .setSuggestedEncounterId(
                null
              )
          } finally {
            setEncounterSuggestionLoading(
              false
            )
          }
        },
        700
      )

    return () => {
      window.clearTimeout(
        timer
      )
    }
  }, [
    hasContextBarToggle,
    contextBarMode,
    samanthaPageContext.kind,
    samanthaPageContext.spaceId,
    contextPublishComposer.title,
    contextPublishComposer.content,
    contextPublishComposer
      .setSuggestedEncounterId,
    contextPublishComposer
      .setSuggestedContributionType,
  ])

  useEffect(() => {
    const handleVisibility =
      (
        event: Event
      ) => {
        const customEvent =
          event as CustomEvent<{
            hidden?: boolean
          }>

        setHiddenByExperience(
          Boolean(
            customEvent.detail
              ?.hidden
          )
        )
      }

    window.addEventListener(
      'samantha:visibility',
      handleVisibility
    )

    return () => {
      window.removeEventListener(
        'samantha:visibility',
        handleVisibility
      )
    }
  }, [])

  useEffect(() => {
    if (
      !inferredContributionType
    ) {
      return
    }

    if (
      contextPublishComposer
        .contributionType
    ) {
      return
    }

    contextPublishComposer
      .setContributionType(
        inferredContributionType
      )
  }, [
    inferredContributionType,
    contextPublishComposer
      .contributionType,
    contextPublishComposer
      .setContributionType,
  ])

  useEffect(() => {
    if (
      samanthaPageContext.kind ===
        'encounter' ||
      samanthaPageContext.kind ===
        'space'
    ) {
      setContextBarMode(
        'publish'
      )
    } else {
      setContextBarMode(
        'agent'
      )
    }

    setContextBarExpanded(
      false
    )
  }, [
    pathname,
    samanthaPageContext.kind,
  ])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const name = String(masterAgentName || 'Samantha').trim()

    ;(window as any).__hubeeMasterAgentName = name
    window.localStorage.setItem('hubee:master-agent-name', name)

    window.dispatchEvent(
      new CustomEvent('samantha:master-agent-name', {
        detail: { name },
      })
    )
  }, [masterAgentName])

  const sellerWelcomeMessage = useMemo<AIMasterChatMessage>(() => {
    return {
      id: 'seller-welcome',
      role: 'assistant',
      content:
        `Hola.\n\nSoy ${masterAgentName}.\n\nYa estoy lista para ayudarte a crear, optimizar y hacer avanzar tu negocio dentro de la plataforma.\n\n¿Qué quieres construir hoy?`,
      timestamp: new Date(),
    }
  }, [masterAgentName])

  const firstContactMessage = useMemo<AIMasterChatMessage>(() => {
    return {
      id: 'first-contact',
      role: 'assistant',
      content:
        `Hola.\n\nSoy ${masterAgentName}.\n\nEstoy aquí para ayudarte a convertir ideas en algo real.\n\nPodemos empezar desde cero, mejorar lo que ya tienes o simplemente pensar juntos.\n\n¿Qué te gustaría hacer primero?`,
      timestamp: new Date(),
    }
  }, [masterAgentName])

  const embeddedConsoleWelcomeMessage =
    useMemo<AIMasterChatMessage>(() => {
      return {
        id: 'embedded-console-content-welcome',
        role: 'assistant',
        content:
          'Puedo ayudarte a convertir una idea, una oferta o un objetivo en contenido listo para desarrollar.\n\n¿Qué quieres poner en marcha?',
        timestamp: new Date(),
        meta: {
          source: 'embedded-console',
          intent: 'proactive_content_creation',
          compact: false,
        },
      } as AIMasterChatMessage
    }, [])

  function getMessageTime(message: AIMasterChatMessage) {
    const value = message.timestamp

    if (value instanceof Date) {
      return value.getTime()
    }

    const parsed = new Date(value as any).getTime()

    return Number.isFinite(parsed) ? parsed : 0
  }

  const visibleMessages = useMemo(() => {
    if (chatViewMode === 'focus') {
      const start = Math.max(0, messages.length - visibleCount)
      return messages.slice(start)
    }

    if (compactTurnStartedAt) {
      return messages.filter((message) => {
        const time = getMessageTime(message)

        return (
          time >= compactTurnStartedAt &&
          message.role !== 'user'
        )
      })
    }

    return messages.slice(-1).filter((message) => message.role !== 'user')
  }, [messages, visibleCount, chatViewMode, compactTurnStartedAt])

  const commandBarActive =
    commandFocused ||
    uiState === 'chat'

  const canShowLightweightAutocomplete =
    commandBarActive &&
    !loading &&
    (!isEmbeddedConsole || input.trim().length > 0) &&
    effectiveSelectionMode === 'auto' &&
    !effectiveForcedSkillId &&
    !effectiveActiveSkill &&
    !hasActiveGuidedFlow()

  const lightweightSuggestions = useMemo(() => {
    if (!canShowLightweightAutocomplete) return []

    return getLightweightIntentSuggestions(input, {
      limit: 4,
    })
  }, [input, canShowLightweightAutocomplete])

  const isContextPublishing =
    hasContextBarToggle &&
    contextBarMode === 'publish'
    
  const showLightweightSuggestions =
    !isContextPublishing && canShowLightweightAutocomplete && lightweightSuggestions.length > 0

  const shouldShowExpressActions =
    !isContextPublishing &&
    expressActions.length > 0 &&
    uiState !== 'chat' &&
    (isAIHostRoute || commandFocused) &&
    !showLightweightSuggestions

  const [
    publishPermission,
    setPublishPermission,
  ] = useState<{
    scope:
      | 'space'
      | 'encounter'

    spaceId?: number
    spaceName?: string
    canPost: boolean
  } | null>(
    null
  )

  const manuallyClosedRef = useRef(false)
  const lastSpokenMessageIdRef = useRef<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const openedFromExperienceRef = useRef(false)

  const isMasterReady =
    !!masterAgent &&
    masterAgent?.status === 'active' &&
    Number(masterAgent?.is_trained || 0) === 1

  const agentStatus = String(masterAgent?.status || '')
  const isTrained = Number(masterAgent?.is_trained || 0) === 1

  const isDraft = agentStatus === 'draft'
  const isActive = agentStatus === 'active'
  const isPaused = agentStatus === 'paused'

  const shouldShowTrainingOverlay =
    isOwner &&
    isDraft &&
    !isTrained &&
    aiConfig.chatEnabled &&
    aiConfig.trainingEnabled &&
    !aiConfig.sellerTrainingExcluded

  const canSeeChatWidget =
    aiConfig.chatEnabled &&
    !isPaused &&
    (
      (isOwner && isDraft) ||
      (isActive && isTrained)
    )

  const funnelContext = useMemo(() => {
    if (!selectedFunnel) return null

    return {
      funnelId: String(selectedFunnel.id),
      funnelName: selectedFunnel.name,
      funnelStatus: selectedFunnel.status,
      totalPages: Array.isArray(selectedFunnel.pages) ? selectedFunnel.pages.length : 0,
      pages: Array.isArray(selectedFunnel.pages)
        ? selectedFunnel.pages.map((p: any) => ({
            id: String(p.id),
            name: p.name,
            type: p.type,
            sectionsCount: Array.isArray(p.sections) ? p.sections.length : 0,
            blocksCount: Array.isArray(p.sections)
              ? p.sections.reduce(
                  (sum: number, s: any) => sum + (Array.isArray(s.blocks) ? s.blocks.length : 0),
                  0
                )
              : 0,
          }))
        : [],
      currentPage: selectedPage
        ? {
            id: String(selectedPage.id),
            name: selectedPage.name,
            type: selectedPage.type,
            settings: selectedPage.settings,
            sections: Array.isArray(selectedPage.sections)
              ? selectedPage.sections.map((s: any) => ({
                  id: String(s.id),
                  name: s.name,
                  backgroundColor: s.backgroundColor,
                  blocks: Array.isArray(s.blocks)
                    ? s.blocks.map((b: any) => ({
                        id: String(b.id),
                        type: b.type,
                        data: b.data,
                      }))
                    : [],
                }))
              : [],
          }
        : null,
      currentBlock: selectedBlock
        ? {
            id: String(selectedBlock.id),
            type: selectedBlock.type,
            data: selectedBlock.data,
          }
        : null,
      allFunnelNames: Array.isArray(funnels)
        ? funnels.map((f: any) => ({
            id: String(f.id),
            name: f.name,
            status: f.status,
          }))
        : [],
    }
  }, [funnels, selectedFunnel, selectedPage, selectedBlock])

  const pageContext = useMemo(() => {
    return {
      pathname: safePathname,
      route: safePathname,
      pageName:
        safePathname === '/'
          ? 'Dashboard'

          : safePathname.match(
              /^\/spaces\/\d+\/encounters\/\d+/
            )
          ? 'Encuentro'

          : safePathname.match(
              /^\/spaces\/\d+/
            )
          ? 'Space'
          : safePathname.startsWith('/courses')
          ? 'Cursos'
          : safePathname.startsWith('/community')
          ? 'Comunidad'
          : safePathname.startsWith('/events')
          ? 'Eventos'
          : safePathname.startsWith('/offers')
          ? 'Productos'
          : safePathname.startsWith('/funnel-builder')
          ? 'Funnel Builder'
          : safePathname.startsWith('/challenges')
          ? 'Retos'
          : safePathname.startsWith('/settings')
          ? 'Configuración'
          : safePathname.startsWith('/crm')
          ? 'CRM'
          : 'Otra',
      role: isOwner ? 'owner' : 'member',
      composerContext:
        samanthaPageContext,
    }
  }, [safePathname, isOwner, samanthaPageContext])

  useEffect(() => {
    function handlePublishContext(
      event: Event
    ) {
      const customEvent =
        event as CustomEvent<{
          scope:
            | 'space'
            | 'encounter'

          spaceId?: number
          spaceName?: string
          canPost: boolean
        } | null>

      setPublishPermission(
        customEvent.detail ||
        null
      )
    }

    window.addEventListener(
      'samantha:publish-context',
      handlePublishContext
    )

    return () => {
      window.removeEventListener(
        'samantha:publish-context',
        handlePublishContext
      )
    }
  }, [])

  const canPublishInContext =
    publishPermission?.canPost !==
    false

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(AUDIO_PREF_KEY)
      setAudioEnabled(saved === '1')
    } catch {
      setAudioEnabled(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadNotifications() {
      if (!sellerId) return

      try {
        const res = await fetch(
          `/api/ai/user/notifications?sellerId=${sellerId}`,
          {
            cache: 'no-store',
            credentials: 'include',
          }
        )

        const json = await res.json()

        if (cancelled) return

        setAssistantNotifications(
          Array.isArray(json?.notifications) ? json.notifications : []
        )

        const nextNotifications = Array.isArray(json?.notifications)
          ? json.notifications
          : []

        setAssistantNotifications(nextNotifications)

        if (nextNotifications.length > 0) {
          setShowNotificationHint(true)

          window.setTimeout(() => {
            setShowNotificationHint(false)
          }, 5000)
        }
        setNotificationsLoaded(true)
      } catch {
        if (!cancelled) {
          setAssistantNotifications([])
          setNotificationsLoaded(true)
        }
      }
    }

    loadNotifications()

    return () => {
      cancelled = true
    }
  }, [sellerId])

  const capacityCheckRef = useRef(check)

  useEffect(() => {
    capacityCheckRef.current = check
  }, [check])

  useEffect(() => {
    let cancelled = false

    if (!isEmbeddedConsole) {
      setEmbeddedContentCreationAllowed(null)
      return
    }

    async function resolveContentCreationAccess() {
      try {
        const allowed = await capacityCheckRef.current({
          capabilityId: 'content-creation',
        })

        if (!cancelled) {
          setEmbeddedContentCreationAllowed(allowed)
        }
      } catch (error) {
        console.error(
          '[AIMasterWidget] Could not resolve content-creation capability',
          error
        )

        if (!cancelled) {
          setEmbeddedContentCreationAllowed(false)
        }
      }
    }

    void resolveContentCreationAccess()

    return () => {
      cancelled = true
    }
  }, [isEmbeddedConsole])

  useEffect(() => {
    if (uiState !== 'chat') return
    if (chatViewMode === 'focus') return
    if (isEmbeddedConsole) return

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null
      if (!target) return

      const clickedCommandBar =
        commandBarWrapRef.current?.contains(target)

      const clickedCompactPanel =
        compactPanelRef.current?.contains(target)

      if (clickedCommandBar || clickedCompactPanel) return

      setUIState('closed')
      setCommandFocused(false)
      setActiveSuggestionIndex(-1)
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [uiState, chatViewMode, isEmbeddedConsole])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const open = uiState === 'chat' && chatViewMode !== 'focus'

    window.dispatchEvent(
      new CustomEvent('samantha:dock-visibility-change', {
        detail: { open },
      })
    )

    return () => {
      window.dispatchEvent(
        new CustomEvent('samantha:dock-visibility-change', {
          detail: { open: false },
        })
      )
    }
  }, [uiState, chatViewMode])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    if (!isAIHostRoute) {
      setLaunchpadSlot(null)
      setAIHostRenderMode('inline-bar')
      return
    }

    let disposed = false

    const resolveSlot = () => {
      if (disposed) return false

      const slot =
        document.getElementById('ai-master-slot')

      if (!slot) {
        return false
      }

      setLaunchpadSlot((current) =>
        current === slot
          ? current
          : slot
      )

      setAIHostRenderMode(
        slot.dataset.aiMasterMode ===
          'embedded-console'
          ? 'embedded-console'
          : 'inline-bar'
      )

      return true
    }

    const foundImmediately =
      resolveSlot()

    if (foundImmediately) {
      return () => {
        disposed = true
        setLaunchpadSlot(null)
        setAIHostRenderMode('inline-bar')
      }
    }

    const observer =
      new MutationObserver(() => {
        const found = resolveSlot()

        if (found) {
          observer.disconnect()
        }
      })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      disposed = true
      observer.disconnect()
      setLaunchpadSlot(null)
      setAIHostRenderMode('inline-bar')
    }
  }, [isAIHostRoute, pathname])
  
  useEffect(() => {
    if (!isEmbeddedConsole || !launchpadSlot) {
      return
    }

    manuallyClosedRef.current = false
    setUIState('chat')
    setChatViewMode('dock')
  }, [
    isEmbeddedConsole,
    launchpadSlot,
  ])

  useEffect(() => {
    if (!isAIHostRoute) {
      setLaunchpadDockTop(null)
      return
    }

    function updateDockTop() {
      const rect = launchpadCommandFormRef.current?.getBoundingClientRect()
      if (!rect) return

      setLaunchpadDockTop(Math.round(rect.bottom + 14))
    }

    updateDockTop()

    window.addEventListener('resize', updateDockTop)
    window.addEventListener('scroll', updateDockTop, true)

    return () => {
      window.removeEventListener('resize', updateDockTop)
      window.removeEventListener('scroll', updateDockTop, true)
    }
  }, [isAIHostRoute, uiState, chatViewMode, input])

  useEffect(() => {
    return subscribeLaunchpadContext((context) => {
      setLaunchpadContext(context)
    })
  }, [])

  useEffect(() => {
    function handleGlobalFlowVisibility(event: Event) {
      const customEvent = event as CustomEvent<{ open?: boolean }>
      const open = Boolean(customEvent.detail?.open)

      if (!open) return

      hideDockForExternalSurface()
    }

    window.addEventListener(
      'hubee:global-flow-visibility-change',
      handleGlobalFlowVisibility
    )

    return () => {
      window.removeEventListener(
        'hubee:global-flow-visibility-change',
        handleGlobalFlowVisibility
      )
    }
  }, [])

  /*
  useEffect(() => {
    async function loadSkills() {
      try {
        const res = await fetch('/api/ai/skills', { cache: 'no-store' })
        const json = await res.json()
        if (res.ok && json?.ok) {
          setSkills(json.items || [])
        }
      } catch (e) {
        console.error('[skills] load error', e)
      }
    }
    loadSkills()
  }, [])
  */

  useEffect(() => {
    try {
      window.localStorage.setItem(AUDIO_PREF_KEY, audioEnabled ? '1' : '0')
    } catch {}
  }, [audioEnabled])

  useEffect(() => {
    let cancelled = false

    async function loadAIConfig() {
      try {
        const qs = new URLSearchParams({
          sellerId: String(sellerId || 0),
        })

        const res = await fetch(`/api/settings/ai-widget?${qs.toString()}`, {
          cache: 'no-store',
          credentials: 'include',
        })

        const json = await res.json().catch(() => null)

        if (cancelled) return

        if (res.ok && json?.ok) {
          setAIConfig({
            chatEnabled: !!json.chatEnabled,
            trainingEnabled: !!json.trainingEnabled,
            sellerTrainingExcluded: !!json.sellerTrainingExcluded,
          })
        } else {
          // fallback seguro
          setAIConfig({
            chatEnabled: true,
            trainingEnabled: true,
            sellerTrainingExcluded: false,
          })
        }
      } catch (err) {
        console.error('[AIMasterWidget] ai-widget config error:', err)

        if (!cancelled) {
          setAIConfig({
            chatEnabled: true,
            trainingEnabled: true,
            sellerTrainingExcluded: false,
          })
        }
      }
    }

    if (sellerId) loadAIConfig()

    return () => {
      cancelled = true
    }
  }, [sellerId])

  useEffect(() => {
    function handleOpen(event: Event) {
      const custom = event as CustomEvent<{
        starter?: {
          message?: string
          suggestions?: string[]
        }
      }>

      // 1) abre el drawer / modal / bubble
      setUIState('chat')
      injectAssistantNotifications()

      // 2) si quieres inyectar un starter especial
      const starterMessage = custom.detail?.starter?.message
      const starterSuggestions = custom.detail?.starter?.suggestions || []

      if (starterMessage) {
        setMessages((prev) => [
          ...prev,
          {
            id: `starter-${Date.now()}`,
            role: 'assistant',
            content: starterMessage,
            timestamp: new Date(),
            meta: {
              source: 'external_starter',
              buttons: starterSuggestions.map((suggestion) => ({
                label: suggestion,
                value: suggestion,
              })),
            },
          },
        ])
      }
    }

    window.addEventListener('samantha:open-chat', handleOpen as EventListener)
    return () => {
      window.removeEventListener('samantha:open-chat', handleOpen as EventListener)
    }
  }, [])

  useEffect(() => {
    function handleStartAction(event: Event) {
      const customEvent = event as CustomEvent<{
        action?: string
        source?: string
        payload?: Record<string, unknown>
      }>

      const action = String(customEvent.detail?.action || '').trim()

      if (action !== 'create_ai_funnel') return

      setUIState('chat')
      setChatViewMode('dock')
      setInput('')
      setCommandFocused(false)
      setActiveSuggestionIndex(-1)
      setCompactTurnStartedAt(Date.now())

      startAIFunnelDiscovery({
        source: customEvent.detail?.source || 'unknown',
      })
    }

    window.addEventListener(
      'samantha:start-action',
      handleStartAction as EventListener
    )

    return () => {
      window.removeEventListener(
        'samantha:start-action',
        handleStartAction as EventListener
      )
    }
  }, [])

  useEffect(() => {
    async function handleAudioRequest() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach((track) => track.stop())
      } catch (err) {
        console.warn('audio permission denied', err)
      }
    }

    window.addEventListener('samantha:audio-requested', handleAudioRequest)
    return () => {
      window.removeEventListener('samantha:audio-requested', handleAudioRequest)
    }
  }, [])

  useEffect(() => {
    if (!sellerId) return

    let cancelled = false

    async function loadPlatformSnapshot() {
      try {
        const qs = new URLSearchParams({
          sellerId: String(sellerId),
          userId: String(user?.userId || 0),
          pathname: pathname || '/',
        })

        const res = await fetch(`/api/ai/platform-context?${qs.toString()}`, {
          cache: 'no-store',
        })

        if (!res.ok) return

        const data = await res.json()
        if (cancelled) return

        setPlatformSnapshot({
          current_lesson: data?.current_lesson || null,
          user_progress: Array.isArray(data?.user_progress) ? data.user_progress : [],
          upcoming_events: Array.isArray(data?.upcoming_events) ? data.upcoming_events : [],
          unread_posts: Array.isArray(data?.unread_posts) ? data.unread_posts : [],
          user_activity: data?.user_activity || null,
          new_courses: Array.isArray(data?.new_courses) ? data.new_courses : [],
          available_transcripts: Array.isArray(data?.available_transcripts)
            ? data.available_transcripts
            : [],
          seller_metrics: data?.seller_metrics || null,
          crm: {
            pipeline_summary: data?.crm?.pipeline_summary || {},
            total_leads: Number(data?.crm?.total_leads || 0),
            leads: Array.isArray(data?.crm?.leads) ? data.crm.leads : [],
            recent_chats: Array.isArray(data?.crm?.recent_chats) ? data.crm.recent_chats : [],
            stats: data?.crm?.stats || {},
            labels: Array.isArray(data?.crm?.labels) ? data.crm.labels : [],
            members_summary: data?.crm?.members_summary || {},
            agents: Array.isArray(data?.crm?.agents) ? data.crm.agents : [],
          },
        })
      } catch (err) {
        console.error('[AIMasterWidget] platform-context error:', err)
      }
    }

    loadPlatformSnapshot()

    return () => {
      cancelled = true
    }
  }, [sellerId, pathname, user?.userId])

  const resizeTextarea = useCallback((el?: HTMLTextAreaElement | null) => {
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [])

  const acceptLightweightSuggestion = useCallback(
    (suggestion: LightweightIntentSuggestion) => {
      setInput(suggestion.text)
      setActiveSuggestionIndex(-1)

      requestAnimationFrame(() => {
        resizeTextarea(commandInputRef.current)
        commandInputRef.current?.focus()
      })
    },
    [resizeTextarea]
  )

  function consumeDisplayText(rawText: string) {
    const displayText = pendingDisplayTextRef.current

    pendingDisplayTextRef.current = null

    return String(displayText || rawText || '').trim()
  }

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      commandInputRef.current?.focus()
      resizeTextarea(commandInputRef.current)
    })
  }, [resizeTextarea])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' })
    })
  }, [])



  useEffect(() => {
    async function handleSamanthaOSSubmit(event: Event) {
      const custom = event as CustomEvent<{
        text?: string
        source?: string
      }>

      const text = String(custom.detail?.text || '').trim()
      if (!text) return

      setUIState('chat')
      setChatViewMode('dock')
      injectAssistantNotifications()
      setInput('')
      setActiveSuggestionIndex(-1)

      await handleSend(text)

      requestAnimationFrame(() => {
        if (commandInputRef.current) commandInputRef.current.style.height = 'auto'
      })

      focusInput()
    }

    window.addEventListener(
      'samantha:submit-command',
      handleSamanthaOSSubmit as EventListener
    )

    return () => {
      window.removeEventListener(
        'samantha:submit-command',
        handleSamanthaOSSubmit as EventListener
      )
    }
  }, [focusInput])

  useEffect(() => {
    if (uiState !== 'chat') return

    // Si llegaron mensajes nuevos, mostramos los últimos
    setVisibleCount((prev) => Math.max(prev, INITIAL_VISIBLE_MESSAGES))

    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false
      return
    }

    if (!isLoadingOlder) {
      scrollToBottom(messages.length > 1 ? 'smooth' : 'auto')
    }

    focusInput()
  }, [messages, loading, showNoCreditsCard, uiState, isLoadingOlder, focusInput, scrollToBottom])

  useEffect(() => {
    setActiveSuggestionIndex(-1)
  }, [input])

  useEffect(() => {
    if (!sellerId) return

    if (manuallyClosedRef.current) {
      setUIState('closed')
      return
    }

    setCurrentAgentId(Number(masterAgent?.id || 0) || null)

    if (shouldShowTrainingOverlay) {
      setUIState('draft')
      return
    }

    if (openedFromExperienceRef.current) {
      setUIState('chat')
      return
    }

    setUIState('closed')
  }, [sellerId, masterAgent, aiConfig, shouldShowTrainingOverlay])

  useEffect(() => {
    if (uiState !== 'chat') return

    focusInput()
    scrollToBottom('auto')
  }, [uiState, focusInput, scrollToBottom])

  const injectAssistantNotifications = useCallback(() => {
    if (!assistantNotifications.length) return

    setMessages((prev) => {
      if (prev.some((m) => m.id === 'samantha-notifications')) {
        return prev
      }

      return [
        ...prev,
        {
          id: 'samantha-notifications',
          role: 'assistant',
          content:
            'Vi que tienes algunas cosas pendientes. Te dejo tus mejores opciones para continuar:',
          timestamp: new Date(),
          meta: {
            source: 'notification',
            mode: 'notification',
            content_actions: assistantNotifications,
            buttons: [],
            context_snapshot: {
              mode: 'notification',
              phase: 'proactive_resume',
              source: 'user_summary',
              sellerId,
              notificationCount: assistantNotifications.length,
            },
          },
        } as any,
      ]
    })
  }, [assistantNotifications, sellerId])

  useEffect(() => {
    if (!sellerId || !isOwner) return
    if (openedFromExperienceRef.current) return

    const needsTraining =
      (!masterAgent || !isTrained) &&
      aiConfig.trainingEnabled &&
      !aiConfig.sellerTrainingExcluded

    if (needsTraining) {
      setMessages((prev) => {
        if (
          prev.length === 1 &&
          prev[0]?.id === 'welcome-training'
        ) {
          return prev
        }

        return [
          {
            id: 'welcome-training',
            role: 'assistant',
            content:
              'Aún no tienes configurado tu Agente Master. ¿Quieres configurarlo ahora?',
            timestamp: new Date(),
          },
        ]
      })

      return
    }

    /*
    * Comportamiento exclusivo de AIA Console.
    */
    if (isEmbeddedConsole) {
      if (embeddedContentCreationAllowed === null) {
        return
      }

      const conversationAlreadyStarted =
        messages.length > 1 ||
        messages.some(
          (message) => message.role === 'user'
        )

      if (conversationAlreadyStarted) {
        return
      }

      setMessages((prev) => {
        const desiredMessage =
          embeddedContentCreationAllowed
            ? embeddedConsoleWelcomeMessage
            : ({
                id: 'embedded-console-welcome',
                role: 'assistant',
                content:
                  'Estoy operativa. ¿Qué necesitas resolver?',
                timestamp: new Date(),
                meta: {
                  source: 'embedded-console',
                  compact: false,
                },
              } as AIMasterChatMessage)

        if (
          prev.length === 1 &&
          prev[0]?.id === desiredMessage.id
        ) {
          return prev
        }

        return [desiredMessage]
      })

      return
    }

    /*
    * Comportamiento normal de Hubee.
    */
    if (messages.length > 1) return

    const firstContactKey =
      `ai_first_contact:${sellerId}`

    const isFirstContact =
      typeof window !== 'undefined' &&
      !window.localStorage.getItem(firstContactKey)

    if (isFirstContact) {
      setMessages((prev) => {
        if (
          prev.length === 1 &&
          prev[0]?.id === firstContactMessage.id
        ) {
          return prev
        }

        return [firstContactMessage]
      })

      window.localStorage.setItem(
        firstContactKey,
        '1'
      )

      return
    }

    setMessages((prev) => {
      if (
        prev.length === 1 &&
        prev[0]?.id === sellerWelcomeMessage.id
      ) {
        return prev
      }

      return [sellerWelcomeMessage]
    })
  }, [
    sellerId,
    masterAgent,
    isOwner,
    setMessages,
    messages,
    isTrained,
    aiConfig,
    isEmbeddedConsole,
    embeddedContentCreationAllowed,
    embeddedConsoleWelcomeMessage,
    firstContactMessage,
    sellerWelcomeMessage,
  ])

  useEffect(() => {
    function handleCreditsRefresh() {
      router.refresh()
    }

    window.addEventListener('hubee:credits_refresh', handleCreditsRefresh)
    return () => {
      window.removeEventListener('hubee:credits_refresh', handleCreditsRefresh)
    }
  }, [router])

  useEffect(() => {
    if (!sellerId) return

    let cancelled = false

    async function run() {
      try {
        const res = await fetch('/api/experience/state', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sellerId,
            brandName: brand?.name || 'Hubee',
          }),
        })

        const json = await res.json().catch(() => ({} as any))
        if (cancelled) return

        const snap = json?.snapshot || {}
        const handoff = snap?.handoff || {}

        if (handoff?.widget_nudge_pending) {
          openedFromExperienceRef.current = true
          setUIState('chat')

          const text = String(handoff?.widget_nudge_text || '').trim()
          if (text) {
            setMessages([
              {
                id: `handoff-${Date.now()}`,
                role: 'assistant',
                content: text,
                timestamp: new Date(),
              },
            ])
          }

          await fetch('/api/experience/handoff/consume', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sellerId,
              brandName: brand?.name || 'Hubee',
            }),
          }).catch(() => null)
        }
      } catch {
        // silence
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [sellerId, brand?.name, setMessages])

  useEffect(() => {
    try {
      if (audioEnabled && dontShowAudioDisclosureAgain) {
        window.localStorage.setItem(AUDIO_DISCLOSURE_SEEN_KEY, '1')
      }
    } catch {}
  }, [audioEnabled, dontShowAudioDisclosureAgain])

  const loadCreditPackages = useCallback(async () => {
    if (loadingCreditPackages || creditPackagesLoaded) return

    setLoadingCreditPackages(true)

    try {
      const r = await fetch('/api/credits/packages', {
        method: 'GET',
        cache: 'no-store',
      })

      const j = await r.json().catch(() => null)

      if (!r.ok || !j?.ok) {
        throw new Error(j?.error || `HTTP ${r.status}`)
      }

      setCreditPackages(Array.isArray(j.packages) ? j.packages : [])
      setCreditPackagesLoaded(true)
    } catch (err) {
      console.error('[AIMasterWidget] loadCreditPackages error:', err)
    } finally {
      setLoadingCreditPackages(false)
    }
  }, [loadingCreditPackages, creditPackagesLoaded])

  useEffect(() => {
    if (!audioEnabled) return
    if (uiState !== 'chat') return
    if (loading) return
    if (speechLoading || speechPlaying) return

    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')
    if (!lastAssistant) return
    if (!lastAssistant.id) return
    if (lastSpokenMessageIdRef.current === lastAssistant.id) return

    const text = coerceContentToString(lastAssistant.content).trim()
    if (!text) return

    // Evita leer mensajes largos, técnicos o de recarga
    const blocked =
      text.includes('te quedaste sin créditos') ||
      text.includes('Comprar créditos') ||
      text.length > 900

    if (blocked) {
      lastSpokenMessageIdRef.current = lastAssistant.id
      return
    }

    lastSpokenMessageIdRef.current = lastAssistant.id

    ;(async () => {
      const result = await speak({
        voice: audioVoice,
        mode: 'realtime_conversation',
        text,
        instructions:
          'Habla en español latino, tono natural, claro, cálido y futurista. No exageres la actuación. Mantén ritmo conversacional.',
      })

      if (!result?.ok && result?.errorCode === 'NO_CREDITS') {
        setShowNoCreditsCard(true)
        loadCreditPackages()
        setAudioEnabled(false)

        setMessages((prev) => {
          const alreadyExists = prev.some(
            (m) =>
              m.role === 'assistant' &&
              typeof m.content === 'string' &&
              m.content.includes('no tienes créditos disponibles para escuchar a Samantha')
          )

          if (alreadyExists) return prev

          return [
            ...prev,
            {
              id: `nocredits-audio-${Date.now()}`,
              role: 'assistant',
              content:
                `No tienes créditos disponibles para escuchar a ${masterAgentName}. Puedes recargar aquí mismo y, cuando quieras, volver a activar el audio.`,
              timestamp: new Date(),
            },
          ]
        })
      }
    })()
  }, [
    audioEnabled,
    uiState,
    loading,
    speechLoading,
    speechPlaying,
    messages,
    audioVoice,
    speak,
    loadCreditPackages,
    setMessages,
  ])

  function isPositiveIntent(text: string) {
    return ['si', 'sí', 'yes', 'ok', 'claro', 'va'].some((w) =>
      text.toLowerCase().includes(w)
    )
  }

  function handleBuyCredits(packKey?: string) {
    if (packKey) {
      setSelectedCreditPackKey(packKey)
    }
    setBuyCreditsOpen(true)
  }
  
  const requestEnableAudio = useCallback(() => {
    if (!hasCredits) {
      setShowNoCreditsCard(true)
      loadCreditPackages()

      setMessages((prev) => {
        const alreadyExists = prev.some(
          (m) =>
            m.role === 'assistant' &&
            typeof m.content === 'string' &&
            m.content.includes('no tienes créditos disponibles para activar el audio')
        )

        if (alreadyExists) return prev

        return [
          ...prev,
          {
            id: `nocredits-audio-toggle-${Date.now()}`,
            role: 'assistant',
            content:
              'No tienes créditos disponibles para activar el audio. Puedes recargar aquí mismo y volver a intentarlo.',
            timestamp: new Date(),
          },
        ]
      })

      return
    }

    try {
      const seen = window.localStorage.getItem(AUDIO_DISCLOSURE_SEEN_KEY) === '1'

      if (!seen) {
        setDontShowAudioDisclosureAgain(false)
        setShowAudioDisclosure(true)
        return
      }

      setAudioEnabled(true)
    } catch {
      setShowAudioDisclosure(true)
    }
  }, [hasCredits, loadCreditPackages, setMessages])

  const confirmEnableAudio = useCallback(() => {
    try {
      if (dontShowAudioDisclosureAgain) {
        window.localStorage.setItem(AUDIO_DISCLOSURE_SEEN_KEY, '1')
      }
    } catch {}

    setAudioEnabled(true)
    setShowAudioDisclosure(false)
  }, [dontShowAudioDisclosureAgain])

  const cancelEnableAudio = useCallback(() => {
    setShowAudioDisclosure(false)
  }, [])

  const [pendingFunnelTemplateOffer, setPendingFunnelTemplateOffer] = useState<{
    templateType?: string
    communityCount?: number
  } | null>(null)

  async function handleReferenceImageSelected(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const file = event.target.files?.[0]

    event.target.value = ''

    if (!file) return

    try {
      setUploadingReference(true)

      const uploadedUrl = await uploadSamanthaReferenceImage(file)

      setLastUploadedReferenceUrl(uploadedUrl)
      setPendingUploadFiles([])

      setPendingPublicationDiscovery((prev) =>
        prev
          ? {
              ...prev,
              referenceImageUrl: uploadedUrl,
              wantsReferenceImage: true,
            }
          : prev
      )

      setMessages((prev) => {
        const next = [
          ...prev,
          {
            id: `user-reference-image-${Date.now()}`,
            role: 'user',
            content: 'Imagen de referencia',
            timestamp: new Date(),
            meta: {
              intent: 'PUBLICATION_REFERENCE_IMAGE',
              attachmentType: 'image',
              imageUrl: uploadedUrl,
              referenceImageUrl: uploadedUrl,
            },
          } as AIMasterChatMessage,
          {
            id: `publication-reference-uploaded-${Date.now()}`,
            role: 'assistant',
            content:
              'Listo. Ya tengo la imagen de referencia. Ahora dime qué quieres comunicar, vender o promocionar.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_REFERENCE_UPLOADED',
              referenceImageUrl: uploadedUrl,
            },
          } as AIMasterChatMessage,
        ]

        /*
        console.log('REFERENCE_UPLOAD_MESSAGES', {
          uploadedUrl,
          next: next.slice(-2),
        })
        */

        return next
      })

      focusInput()
    } catch (error: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: `publication-reference-upload-error-${Date.now()}`,
          role: 'assistant',
          content:
            error?.message || 'No pude subir la imagen. Intenta con otra imagen.',
          timestamp: new Date(),
          meta: {
            worker: 'creator_studio_worker',
            intent: 'PUBLICATION_REFERENCE_UPLOAD_ERROR',
          },
        } as AIMasterChatMessage,
      ])
    } finally {
      setUploadingReference(false)
    }
  }

  async function handleAIFunnelActionButton(input: {
    value: string
    label: string
  }) {
    const value = String(input.value || '').trim()

    if (
      !pendingFunnelDiscovery ||
      pendingFunnelDiscovery.mode !== 'ai_html'
    ) {
      return
    }

    const current = pendingFunnelDiscovery

    if (value === 'ai_funnel:add_reference') {
      const next = {
        ...current,
        step: 'references' as const,
      }

      setPendingFunnelDiscovery(next)

      const question = getAIFunnelQuestion(next)

      if (question) {
        setMessages((prev) => [
          ...prev,
          createChatUserMessage('Agregar referencia'),
          question,
        ])
      }

      focusInput()
      return
    }

    if (value === 'ai_funnel:use_branding') {
      const next = {
        ...current,
        useBranding: true,
        branding: {
          brandName: brand?.name || '',
          logoUrl:
            (brand as any)?.logoUrl ||
            (brand as any)?.logo_url ||
            (brand as any)?.logo ||
            '',
          primaryColor:
            (brand as any)?.primaryColor ||
            (brand as any)?.primary_color ||
            '',
          secondaryColor:
            (brand as any)?.secondaryColor ||
            (brand as any)?.secondary_color ||
            '',
          accentColor:
            (brand as any)?.accentColor ||
            (brand as any)?.accent_color ||
            '',
        },
      }

      setPendingFunnelDiscovery(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage('Usar mi branding'),
        createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_BRANDING_SELECTED',
          content:
            `Perfecto. Usaré el branding de **${brand?.name || 'tu marca'}** como punto de partida.\n\nAhora descríbeme el producto, servicio o idea que quieres convertir en una landing.`,
        }),
      ])

      focusInput()
      return
    }

    if (value.startsWith('ai_funnel:goal:')) {
      const goal = value.replace('ai_funnel:goal:', '') as AIFunnelGoal

      const next = {
        ...current,
        step: 'audience' as const,
        goal,
        inferred: {
          ...current.inferred,
          pageType: inferAIFunnelType(goal),
          sections: inferAIFunnelSections({
            goal,
            description: current.description,
          }),
        },
      }

      setPendingFunnelDiscovery(next)

      const question = getAIFunnelQuestion(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage(aiFunnelGoalLabel(goal)),
        ...(question ? [question] : []),
      ])

      focusInput()
      return
    }

    if (value === 'ai_funnel:audience:auto') {
      await handleAIFunnelDiscoveryReply(value)
      return
    }

    if (value === 'ai_funnel:offer:auto') {
      await handleAIFunnelDiscoveryReply(value)
      return
    }

    if (
      value === 'ai_funnel:branding:existing' ||
      value === 'ai_funnel:branding:auto'
    ) {
      await handleAIFunnelDiscoveryReply(value)
      return
    }

    if (value === 'ai_funnel:reference:url') {
      setMessages((prev) => [
        ...prev,
        createChatUserMessage('Agregar enlace'),
        createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_REFERENCE_URL',
          content:
            'Pega aquí la URL completa de la página que quieres usar como referencia.',
        }),
      ])

      focusInput()
      return
    }

    if (value === 'ai_funnel:reference:image') {
      setPendingUploadFiles([])

      const fileInput = document.getElementById(
        'samantha-ai-funnel-reference-input'
      ) as HTMLInputElement | null

      fileInput?.click()
      return
    }

    if (value === 'ai_funnel:reference:none') {
      await handleAIFunnelDiscoveryReply(value)
      return
    }

    if (value === 'ai_funnel:edit_brief') {
      const next = {
        ...current,
        step: 'idea' as const,
      }

      setPendingFunnelDiscovery(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage('Cambiar información'),
        createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_EDIT_BRIEF',
          content:
            'Perfecto. Vuelve a describirme la página como quieres que quede y reconstruiré el brief.',
        }),
      ])

      focusInput()
      return
    }

    if (value === 'ai_funnel:generate') {
      await generateAIFunnelFromDiscovery(current)
      return
    }
  }

  function updateExpressScriptMessage(
    messageId: string,
    patch: Partial<CreateContentScriptCardPayload>
  ) {
    setMessages((prev) =>
      prev.map((message) => {
        if (message.id !== messageId) {
          return message
        }

        const previous =
          (message.meta as any)
            ?.create_content_script ||
          {}

        return {
          ...message,
          meta: {
            ...(message.meta || {}),

            create_content_script: {
              ...previous,
              ...patch,
            },
          },
        } as AIMasterChatMessage
      })
    )
  }

  function buildExpressScriptInputs(
    context: PendingExpressContent
  ) {
    const {
      pending,
      projectId,
      project,
      brief,
      selectedIdea,
      selectedHook,
      productImageUrl,
    } = context

    const direction =
      pending.direction || {}

    const discovery =
      pending.discovery || {}

    const plan =
      pending.plan || {}

    const durationTargetSec =
      Number(
        direction?.durationTargetSec ||
          plan?.durationTargetSec ||
          brief?.durationTargetSec ||
          60
      )

    const resolvedBrief = {
      ...brief,

      ...(productImageUrl
        ? {
            productImageUrl,
            referenceImageUrl:
              productImageUrl,
            initialImage:
              productImageUrl,
            hasProduct:
              'yes',
            hasProductImage:
              'yes',
          }
        : {}),

      projectId,

      project: {
        ...(project || {}),

        project_id:
          projectId,

        brief_json: {
          ...brief,

          ...(productImageUrl
            ? {
                productImageUrl,
                referenceImageUrl:
                  productImageUrl,
                initialImage:
                  productImageUrl,
                hasProduct:
                  'yes',
                hasProductImage:
                  'yes',
              }
            : {}),
        },

        creative_direction_json:
          direction,
      },
    }

    return {
      niche: String(
        discovery?.niche ||
          discovery?.topic ||
          plan?.goal ||
          plan?.objective ||
          plan?.title ||
          ''
      ).trim(),

      topic: String(
        selectedIdea?.title ||
          plan?.title ||
          discovery?.topic ||
          ''
      ).trim(),

      platform: String(
        plan?.platform ||
          direction?.publishTarget ||
          discovery?.platform ||
          'TikTok'
      ).trim(),

      tone: String(
        plan?.voiceStyle ||
          plan?.style ||
          direction?.style ||
          'conversacional'
      ).trim(),

      durationMode:
        direction?.durationMode ||
        plan?.durationMode ||
        brief?.durationMode ||
        'auto',

      durationTargetSec,

      productionMode:
        direction?.productionMode ||
        plan?.productionMode ||
        brief?.productionMode ||
        (
          durationTargetSec >= 300
            ? 'cinematic_longform'
            : 'express'
        ),

      visualOutputMode:
        direction?.visualOutputMode ||
        plan?.visualOutputMode ||
        brief?.visualOutputMode ||
        (
          durationTargetSec >= 300
            ? 'static_images'
            : 'mixed'
        ),

      language:
        plan?.language ||
        brief?.language ||
        'es',

      goal:
        plan?.goal ||
        direction?.contentGoal ||
        'viral',

      style:
        plan?.style ||
        direction?.style ||
        '',

      projectId,
      projectStep: 'scripts',
      sourceGenerationId: null,

      selectedIdea,
      selectedHook,

      handoffBrief:
        resolvedBrief,

      creativeDirection:
        direction,

      discovery,

      productImageUrl:
        productImageUrl ||
        null,

      referenceImageUrl:
        productImageUrl ||
        null,

      hasProduct:
        productImageUrl
          ? 'yes'
          : (
              direction?.hasProduct ||
              brief?.hasProduct ||
              'no'
            ),

      hasProductImage:
        productImageUrl
          ? 'yes'
          : (
              direction?.hasProductImage ||
              brief?.hasProductImage ||
              'no'
            ),

      useCharacter:
        direction?.useCharacter ||
        brief?.useCharacter ||
        'no',

      skillAgentId:
        pending.request.payload?.skillAgentId ||
        pending.request.payload?.skill_agent_id ||
        null,

      skillAgentSlug:
        pending.request.payload?.skillAgentSlug ||
        pending.request.payload?.skill_agent_slug ||
        null,

      skillAgentName:
        pending.request.payload?.skillName ||
        pending.request.payload?.skillAgentName ||
        pending.request.payload?.skill_agent_name ||
        null,
    }
  }

  async function persistExpressSnapshot(params: {
    context: PendingExpressContent
    phase: string
    job?: any
    generation?: any
    script?: any
    error?: string | null
  }) {
    const {
      context,
      phase,
      job,
      generation,
      script,
      error,
    } = params

    const pending =
      context.pending

    const result = {
      plan:
        pending.plan,

      projectId:
        context.projectId,

      creatorStudioProject:
        context.project,

      creativeDirection:
        pending.direction,

      discovery:
        pending.discovery,

      selectedIdea:
        context.selectedIdea,

      selectedHook:
        context.selectedHook,

      productImageUrl:
        context.productImageUrl ||
        null,

      product: context.productImageUrl
        ? {
            imageUrl:
              context.productImageUrl,

            productImageUrl:
              context.productImageUrl,

            referenceImageUrl:
              context.productImageUrl,
          }
        : null,

      scriptJob:
        job
          ? {
              jobId:
                Number(
                  job.jobId ||
                    context.jobId ||
                    0
                ) || null,

              status:
                job.status ||
                null,

              progress:
                Number(job.progress || 0),

              step:
                job.step ||
                null,

              resultGenerationId:
                job.resultGenerationId ||
                null,

              errorMessage:
                job.errorMessage ||
                error ||
                null,
            }
          : null,

      scriptGeneration:
        generation ||
        null,

      script:
        script ||
        null,

      __snapshotMeta: {
        direction:
          pending.direction,

        discovery:
          pending.discovery,

        chatDirectionStep:
          'completed',

        expressPhase:
          phase,

        projectId:
          context.projectId,

        scriptJobId:
          Number(
            job?.jobId ||
              context.jobId ||
              0
          ) || null,

        productImageUrl:
          context.productImageUrl ||
          null,

        sceneProgress: [],
      },
    }

    const snapshotId =
      await saveExpressContentSnapshot({
        snapshotId:
          pending.snapshotId,

        sellerId:
          pending.request.payload?.sellerId,

        generationId:
          generation?.generation_id ||
          null,

        phase,

        request: {
          ...pending.request,

          phase,

          payload: {
            ...(pending.request.payload || {}),

            projectId:
              context.projectId,

            plan:
              pending.plan,

            creativeDirection:
              pending.direction,

            discovery:
              pending.discovery,

            selectedIdea:
              context.selectedIdea,

            selectedHook:
              context.selectedHook,

            productImageUrl:
              context.productImageUrl ||
              null,

            referenceImageUrl:
              context.productImageUrl ||
              null,
          },
        },

        result,
      })

    if (snapshotId) {
      useTaskFlowStore
        .getState()
        .setSnapshotId(snapshotId)

      setPendingExpressContent((previous) =>
        previous
          ? {
              ...previous,

              pending: {
                ...previous.pending,
                snapshotId,
              },
            }
          : previous
      )
    }

    useTaskFlowStore
      .getState()
      .setResult(result)

    useTaskFlowStore
      .getState()
      .setPhase(phase as any)

    return snapshotId
  }

  async function pollExpressScriptJob(
    context: PendingExpressContent,
    jobId: number
  ) {
    if (
      expressScriptPollingRef.current.has(jobId)
    ) {
      return
    }

    expressScriptPollingRef.current.add(jobId)

    try {
      let attempts = 0
      let previousSnapshotStatus = ''

      while (true) {
        attempts += 1

        const data =
          await getExpressScriptJob(jobId)

        const job =
          data.job || {}

        const generation =
          data.generation ||
          null

        updateExpressScriptMessage(
          context.messageId,
          {
            phase:
              job.status === 'completed'
                ? 'script_ready'
                : job.status === 'failed'
                  ? 'script_error'
                  : 'generating_script',

            projectId:
              context.projectId,

            productImageUrl:
              context.productImageUrl ||
              null,

            jobId,

            status:
              job.status,

            progress:
              Number(job.progress || 0),

            step:
              job.step ||
              null,

            generation,

            script:
              generation?.result_json ||
              null,

            error:
              job.errorMessage ||
              null,
          }
        )

        const snapshotStatusKey =
          `${job.status}:${job.step}:${Math.floor(
            Number(job.progress || 0) / 10
          )}`

        if (
          snapshotStatusKey !==
          previousSnapshotStatus
        ) {
          previousSnapshotStatus =
            snapshotStatusKey

          await persistExpressSnapshot({
            context: {
              ...context,
              jobId,
            },

            phase:
              job.status === 'completed'
                ? 'script_ready'
                : job.status === 'failed'
                  ? 'script_error'
                  : 'generating_script',

            job,
            generation,

            script:
              generation?.result_json ||
              null,

            error:
              job.errorMessage ||
              null,
          }).catch((error) => {
            console.error(
              '[AIMasterWidget] persist express polling snapshot error',
              error
            )
          })
        }

        if (job.status === 'completed') {
          if (
            !generation?.generation_id ||
            !generation?.result_json
          ) {
            throw new Error(
              'El job terminó, pero no devolvió un guion válido.'
            )
          }

          setPendingExpressContent({
            ...context,
            jobId,
          })

          toast.success(
            'Tu guion premium está listo'
          )

          return
        }

        if (job.status === 'failed') {
          throw new Error(
            job.errorMessage ||
              'No se pudo generar el guion.'
          )
        }

        if (job.status === 'cancelled') {
          throw new Error(
            'La generación fue cancelada.'
          )
        }

        const delay =
          attempts < 10
            ? 2000
            : attempts < 30
              ? 5000
              : 10000

        await new Promise((resolve) =>
          window.setTimeout(
            resolve,
            delay
          )
        )
      }
    } catch (error: any) {
      updateExpressScriptMessage(
        context.messageId,
        {
          phase: 'script_error',
          projectId:
            context.projectId,
          jobId,
          error:
            error?.message ||
            'No se pudo generar el guion.',
        }
      )

      await persistExpressSnapshot({
        context: {
          ...context,
          jobId,
        },
        phase:
          'script_error',
        job: {
          jobId,
          status: 'failed',
          progress: 0,
          step: 'Error',
          errorMessage:
            error?.message ||
            null,
        },
        error:
          error?.message ||
          null,
      }).catch(() => null)
    } finally {
      expressScriptPollingRef.current.delete(
        jobId
      )
    }
  }

  async function startExpressScript(
    context: PendingExpressContent
  ) {
    const inputs =
      buildExpressScriptInputs(context)

    updateExpressScriptMessage(
      context.messageId,
      {
        phase:
          'generating_script',

        projectId:
          context.projectId,

        status:
          'queued',

        progress:
          0,

        step:
          'Preparando solicitud',

        plan:
          context.pending.plan,

        selectedIdea:
          context.selectedIdea,

        selectedHook:
          context.selectedHook,

        productImageUrl:
          context.productImageUrl ||
          null,
      }
    )

    const started =
      await startExpressScriptJob(
        inputs
      )

    const nextContext = {
      ...context,
      jobId:
        started.jobId,
    }

    setPendingExpressContent(
      nextContext
    )

    updateExpressScriptMessage(
      context.messageId,
      {
        phase:
          'generating_script',

        projectId:
          context.projectId,

        jobId:
          started.jobId,

        status:
          started.status,

        progress:
          started.progress,

        step:
          started.step,
      }
    )

    await persistExpressSnapshot({
      context:
        nextContext,

      phase:
        'generating_script',

      job: {
        jobId:
          started.jobId,

        status:
          started.status,

        progress:
          started.progress,

        step:
          started.step,

        resultGenerationId:
          started.resultGenerationId,
      },
    })

    void pollExpressScriptJob(
      nextContext,
      started.jobId
    )
  }

  async function startCreateContentExpressDryRun(
    pending: PendingCreateContentPlan
  ) {
    const messageId =
      `create-content-script-${Date.now()}`

    const project =
      await loadCreateContentDryRunProject()

    const projectId =
      Number(
        project?.project_id ||
          project?.projectId ||
          project?.id ||
          CREATE_CONTENT_DRY_RUN_PROJECT_ID
      )

    const plan =
      resolveDryRunProjectPlan(
        project,
        pending.direction,
        pending.discovery
      )

    const selectedIdea =
      buildExpressSelectedIdea(
        plan
      )

    const selectedHook =
      buildExpressSelectedHook(
        plan
      )

    setMessages((prev) => [
      ...prev,
      {
        id:
          messageId,

        role:
          'assistant',

        content:
          'Voy a convertir el plan aprobado en un proyecto real y preparar el guion.',

        timestamp:
          new Date(),

        meta: {
          worker:
            'content_creator',

          intent:
            'CREATE_CONTENT_EXPRESS_STARTING',

          create_content_script: {
            phase:
              'generating_script',

            projectId,

            progress:
              4,

            step:
              'Preparando proyecto',

            plan,

            selectedIdea,

            selectedHook,

            generateStoryboardImages:
              true,

            allowHyperframesScenes:
              false,

            dryRun:
              true,
          },
        },
      } as AIMasterChatMessage,
    ])

    const scriptGeneration =
      resolveDryRunScriptGeneration(
        project
      )

    const script =
      scriptGeneration
        ?.result_json ||
      project?.script ||
      project?.brief_json?.script ||
      project?.brief?.script ||
      null

    if (!scriptGeneration) {
      throw new Error(
        `El proyecto ${projectId} no tiene una generación de guion.`
      )
    }

    if (!script) {
      throw new Error(
        `El proyecto ${projectId} no tiene un guion disponible.`
      )
    }

    const brief =
      project?.brief_json ||
      project?.brief ||
      project?.project_json?.brief ||
      project?.projectJson?.brief ||
      {}

    const productImageUrl =
      String(
        brief?.productImageUrl ||
          brief?.referenceImageUrl ||
          plan?.productImageUrl ||
          ''
      ).trim() || null

    const context: PendingExpressContent = {
      pending: {
        ...pending,
        plan,
      },

      messageId,

      projectId,

      project,

      brief,

      selectedIdea,

      selectedHook,

      productImageUrl,

      jobId:
        null,
    }

    setPendingExpressContent(
      context
    )

    updateExpressScriptMessage(
      messageId,
      {
        phase:
          'generating_script',

        projectId,

        progress:
          12,

        step:
          'Analizando la idea seleccionada',

        plan,

        selectedIdea,

        selectedHook,

        productImageUrl,

        generateStoryboardImages:
          true,

        allowHyperframesScenes:
          false,

        dryRun:
          true,
      }
    )

    await waitForDryRun(
      1000
    )

    updateExpressScriptMessage(
      messageId,
      {
        phase:
          'generating_script',

        projectId,

        progress:
          28,

        step:
          'Construyendo el hook',

        dryRun:
          true,
      }
    )

    await waitForDryRun(
      1200
    )

    updateExpressScriptMessage(
      messageId,
      {
        phase:
          'generating_script',

        projectId,

        progress:
          48,

        step:
          'Organizando la narrativa',

        dryRun:
          true,
      }
    )

    await waitForDryRun(
      1400
    )

    updateExpressScriptMessage(
      messageId,
      {
        phase:
          'generating_script',

        projectId,

        progress:
          68,

        step:
          'Preparando escenas y ritmo visual',

        dryRun:
          true,
      }
    )

    await waitForDryRun(
      1300
    )

    updateExpressScriptMessage(
      messageId,
      {
        phase:
          'generating_script',

        projectId,

        progress:
          87,

        step:
          'Revisando el guion final',

        dryRun:
          true,
      }
    )

    await waitForDryRun(
      1100
    )

    updateExpressScriptMessage(
      messageId,
      {
        phase:
          'script_ready',

        projectId,

        progress:
          100,

        step:
          'Guion listo',

        status:
          'completed',

        plan,

        selectedIdea,

        selectedHook,

        script,

        generation:
          scriptGeneration,

        productImageUrl,

        generateStoryboardImages:
          true,

        allowHyperframesScenes:
          false,

        dryRun:
          true,
      }
    )

    useTaskFlowStore
      .getState()
      .setResult({
        ...(useTaskFlowStore
          .getState()
          .result || {}),

        plan,

        projectId,

        creatorStudioProject:
          project,

        creativeDirection:
          pending.direction,

        discovery:
          pending.discovery,

        selectedIdea,

        selectedHook,

        script,

        scriptGeneration,

        scriptGenerationId:
          Number(
            scriptGeneration?.generation_id ||
              scriptGeneration?.generationId ||
              0
          ),

        sourceScriptGenerationId:
          Number(
            scriptGeneration?.generation_id ||
              scriptGeneration?.generationId ||
              0
          ),

        productImageUrl,

        dryRun:
          true,
      })

    toast.success(
      'Tu guion premium está listo'
    )
  }

  async function startCreateContentExpress(
    pending: PendingCreateContentPlan
  ) {
    if (
      canUseCreateContentDryRun
    ) {
      await startCreateContentExpressDryRun(
        pending
      )

      return
    }
    const messageId =
      `create-content-script-${Date.now()}`

    const selectedIdea =
      buildExpressSelectedIdea(
        pending.plan
      )

    const selectedHook =
      buildExpressSelectedHook(
        pending.plan
      )

    setMessages((prev) => [
      ...prev,
      {
        id: messageId,
        role: 'assistant',
        content:
          'Voy a convertir el plan aprobado en un proyecto real y preparar el guion.',
        timestamp:
          new Date(),
        meta: {
          worker:
            'content_creator',

          intent:
            'CREATE_CONTENT_EXPRESS_STARTING',

          create_content_script: {
            phase:
              'generating_script',

            projectId:
              0,

            progress:
              0,

            step:
              'Creando proyecto',

            plan:
              pending.plan,

            selectedIdea,
            selectedHook,

            generateStoryboardImages:
              true,

            allowHyperframesScenes:
              false,
          },
        },
      } as AIMasterChatMessage,
    ])

    try {
      const created =
        await createExpressContentProject({
          request:
            pending.request,

          plan:
            pending.plan,

          direction:
            pending.direction,

          discovery:
            pending.discovery,
        })

      const existingProductImageUrl =
        String(
          pending.plan?.productImageUrl ||
            pending.direction?.productImageUrl ||
            pending.discovery?.productImageUrl ||
            ''
        ).trim()

      const productRequired =
        (
          pending.direction?.hasProductImage ===
            'yes' ||
          pending.plan?.hasProductImage ===
            'yes'
        )

      const context: PendingExpressContent = {
        pending,
        messageId,

        projectId:
          created.projectId,

        project:
          created.project,

        brief:
          created.brief,

        selectedIdea,
        selectedHook,

        productImageUrl:
          existingProductImageUrl ||
          null,

        jobId:
          null,
      }

      setPendingExpressContent(
        context
      )

      if (
        productRequired &&
        !existingProductImageUrl
      ) {
        updateExpressScriptMessage(
          messageId,
          {
            phase:
              'awaiting_product',

            projectId:
              created.projectId,

            progress:
              0,

            step:
              'Esperando producto',

            plan:
              pending.plan,

            generateStoryboardImages:
              true,

            allowHyperframesScenes:
              false,

            selectedIdea,
            selectedHook,

            productRequired:
              true,

            productImageUrl:
              null,
          }
        )

        await persistExpressSnapshot({
          context,
          phase:
            'awaiting_product',
        })

        return
      }

      await startExpressScript(
        context
      )
    } catch (error: any) {
      updateExpressScriptMessage(
        messageId,
        {
          phase:
            'script_error',

          projectId:
            0,

          error:
            error?.message ||
            'No se pudo iniciar el proyecto express.',
        }
      )
    }
  }

  async function handleExpressProductSelected(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const file =
      event.target.files?.[0]

    event.target.value = ''

    if (
      !file ||
      !pendingExpressContent ||
      uploadingExpressProduct
    ) {
      return
    }

    const context =
      pendingExpressContent

    try {
      setUploadingExpressProduct(true)

      updateExpressScriptMessage(
        context.messageId,
        {
          phase:
            'awaiting_product',

          projectId:
            context.projectId,

          progress:
            0,

          step:
            'Subiendo producto',

          productRequired:
            true,

          productImageUrl:
            context.productImageUrl ||
            null,
        }
      )

      /*
      * Reutiliza el uploader que ya usa Samantha
      * para imágenes de referencia.
      */
      const uploadedUrl =
        await uploadSamanthaReferenceImage(
          file
        )

      if (!uploadedUrl) {
        throw new Error(
          'La imagen se subió, pero no regresó una URL.'
        )
      }

      /*
      * IMPORTANTE:
      * storyboard y escenas deben recibir siempre URL absoluta.
      */
      const productImageUrl =
        typeof window !== 'undefined'
          ? new URL(
              uploadedUrl,
              window.location.origin
            ).toString()
          : uploadedUrl

      const nextBrief = {
        ...context.brief,

        productImageUrl,

        initialImage:
          productImageUrl,

        hasProduct:
          'yes',

        hasProductImage:
          'yes',
      }

      /*
      * Conserva la referencia también en Creator Studio.
      */
      await updateExpressProject({
        projectId:
          context.projectId,

        currentStep:
          'scripts',

        brief:
          nextBrief,
      })

      const nextContext: PendingExpressContent = {
        ...context,

        brief:
          nextBrief,

        productImageUrl,
      }

      setPendingExpressContent(
        nextContext
      )

      /*
      * Muestra la imagen subida inmediatamente en el chat.
      */
      updateExpressScriptMessage(
        context.messageId,
        {
          phase:
            'awaiting_product',

          projectId:
            context.projectId,

          progress:
            100,

          step:
            'Producto cargado',

          productRequired:
            false,

          productImageUrl,
        }
      )

      /*
      * Guarda URL absoluta en el snapshot antes
      * de iniciar cualquier generación.
      */
      await persistExpressSnapshot({
        context:
          nextContext,

        phase:
          'generating_script',
      })

      /*
      * La imagen ya está lista.
      * Ahora sí inicia el guion.
      */
      await startExpressScript(
        nextContext
      )
    } catch (error: any) {
      updateExpressScriptMessage(
        context.messageId,
        {
          phase:
            'awaiting_product',

          projectId:
            context.projectId,

          progress:
            0,

          step:
            'Error al subir producto',

          productRequired:
            true,

          error:
            error?.message ||
            'No se pudo subir la imagen del producto.',
        }
      )

      toast.error(
        error?.message ||
          'No se pudo subir la imagen del producto.'
      )
    } finally {
      setUploadingExpressProduct(false)
    }
  }

  function waitForDryRun(
    milliseconds: number
  ) {
    return new Promise<void>(
      (resolve) => {
        window.setTimeout(
          resolve,
          milliseconds
        )
      }
    )
  }

  function resolveExistingDryRunProductionData(input: {
    context: PendingExpressContent
    sourceMessage: AIMasterChatMessage
  }) {
    const {
      context,
      sourceMessage,
    } = input

    const cardPayload =
      (sourceMessage.meta as any)
        ?.create_content_script ||
      {}

    const taskResult =
      useTaskFlowStore
        .getState()
        .result ||
      {}

    const project =
      createContentDryRunProject ||
      context.project ||
      taskResult
        ?.creatorStudioProject ||
      null

    const projectId =
      CREATE_CONTENT_DRY_RUN_PROJECT_ID

    const generations =
      Array.isArray(
        project?.generations
      )
        ? project.generations
        : []

    const scriptGeneration =
      cardPayload?.generation ||
      taskResult
        ?.scriptGeneration ||
      [...generations]
        .reverse()
        .find(
          (generation: any) =>
            generation
              ?.project_step ===
              'scripts' ||
            generation?.tab ===
              'scripts'
        ) ||
      null

    const script =
      cardPayload?.script ||
      scriptGeneration
        ?.result_json ||
      taskResult?.script ||
      project
        ?.project_json
        ?.script_package ||
      project
        ?.projectJson
        ?.script_package ||
      null

    const scriptGenerationId =
      Number(
        scriptGeneration
          ?.generation_id ||
          scriptGeneration
            ?.generationId ||
          taskResult
            ?.scriptGenerationId ||
          taskResult
            ?.sourceScriptGenerationId ||
          taskResult
            ?.scriptJob
            ?.resultGenerationId ||
          project
            ?.project_json
            ?.source_script_generation_id ||
          project
            ?.projectJson
            ?.source_script_generation_id ||
          0
      )

    /*
    * Para producción no debes buscar solamente
    * tab === storyboard, porque en tu respuesta
    * la generación preparada tiene:
    *
    * tab: video
    * project_step: storyboard
    * generation_id: 703
    */
    const productionGeneration =
      taskResult
        ?.premiumStoryboardGeneration ||
      [...generations]
        .reverse()
        .find(
          (generation: any) =>
            generation
              ?.project_step ===
              'storyboard' &&
            Boolean(
              generation
                ?.project_json
            )
        ) ||
      [...generations]
        .reverse()
        .find(
          (generation: any) =>
            generation?.tab ===
              'video' &&
            Boolean(
              generation
                ?.project_json
            )
        ) ||
      null

    /*
    * En tu proyecto 6 el storyboard preparado
    * está en productionGeneration.project_json,
    * no en result_json.
    */
    const productionProjectJson =
      productionGeneration
        ?.project_json ||
      taskResult?.projectJson ||
      project?.project_json ||
      project?.projectJson ||
      null

    /*
    * Las escenas están dentro de:
    * project_json.planner.scenes
    */
    const preparedStoryboardPlan =
      taskResult
        ?.preparedStoryboardPlan ||
      productionGeneration
        ?.result_json ||
      productionGeneration
        ?.project_json ||
      project?.project_json ||
      project?.projectJson ||
      null

    /*
    * La corrección principal:
    * tomar generation_id.
    */
    const generationId =
      Number(
        taskResult?.generationId ||
          productionGeneration
            ?.generation_id ||
          productionGeneration
            ?.generationId ||
          productionGeneration
            ?.production_generation_id ||
          productionGeneration
            ?.source_generation_id ||
          project
            ?.latestVideoGenerationId ||
          project
            ?.video_generation_id ||
          0
      )

    const brief =
      productionProjectJson
        ?.brief ||
      project?.brief_json ||
      project?.brief ||
      {}

    const productImageUrl =
      String(
        context.productImageUrl ||
          taskResult
            ?.productImageUrl ||
          brief
            ?.productImageUrl ||
          brief
            ?.initialImage ||
          brief
            ?.referenceImageUrl ||
          ''
      ).trim() || null

    return {
      project,
      projectId,

      script,
      scriptGeneration,
      scriptGenerationId,

      productionGeneration,

      preparedStoryboardPlan,

      projectJson:
        productionProjectJson,

      generationId,

      productImageUrl,
    }
  }

  async function runCreateContentProductionDryRun(input: {
    context: PendingExpressContent
    sourceMessage: AIMasterChatMessage
  }) {
    const {
      context,
      sourceMessage,
    } = input

    const cardPayload =
      (sourceMessage.meta as any)
        ?.create_content_script ||
      {}

    const existing =
      resolveExistingDryRunProductionData({
        context,
        sourceMessage,
      })

    if (!existing.projectId) {
      throw new Error(
        'No se encontró el proyecto de demostración.'
      )
    }

    if (!existing.script) {
      throw new Error(
        'El proyecto de demostración no tiene un guion disponible.'
      )
    }

    if (
      !existing
        .scriptGenerationId
    ) {
      throw new Error(
        'El proyecto de demostración no tiene una generación de guion válida.'
      )
    }

    if (
      !existing.generationId ||
      !existing.projectJson
    ) {
      throw new Error(
        'El proyecto de demostración todavía no tiene una producción preparada.'
      )
    }

    const generateStoryboardImages =
      cardPayload
        ?.generateStoryboardImages !==
      false

    const allowHyperframesScenes =
      false

    const absoluteProductImageUrl =
      existing.productImageUrl
        ? new URL(
            existing.productImageUrl,
            window.location.origin
          ).toString()
        : null

    const updateDryRunProgress = (
      progress: number,
      step: string,
      productionStage: string
    ) => {
      updateExpressScriptMessage(
        context.messageId,
        {
          phase:
            'preparing_production',

          dryRun: true,

          projectId:
            existing.projectId,

          progress,
          step,
          productionStage,

          status:
            'running',

          script:
            existing.script,

          generation:
            existing
              .scriptGeneration,

          productImageUrl:
            absoluteProductImageUrl,

          generateStoryboardImages,

          allowHyperframesScenes,
        }
      )
    }

    /*
    * Estos pasos solo actualizan la UI.
    * No llaman ningún endpoint de producción.
    */

    updateDryRunProgress(
      4,
      'Validando idea, hook y guion',
      'validating_material'
    )

    await waitForDryRun(900)

    updateDryRunProgress(
      12,
      'Actualizando el proyecto creativo',
      'updating_project'
    )

    await waitForDryRun(1100)

    updateDryRunProgress(
      24,
      'Preparando el contenedor de producción',
      'creating_shell'
    )

    await waitForDryRun(1300)

    updateDryRunProgress(
      36,
      'Analizando el guion escena por escena',
      'analyzing_script'
    )

    await waitForDryRun(1500)

    updateDryRunProgress(
      50,
      'Diseñando el storyboard premium',
      'starting_storyboard'
    )

    await waitForDryRun(1700)

    updateDryRunProgress(
      64,
      'Definiendo encuadres, talento y producto',
      'planning_scenes'
    )

    await waitForDryRun(1500)

    updateDryRunProgress(
      76,
      'Aplicando continuidad visual',
      'visual_continuity'
    )

    await waitForDryRun(1400)

    updateDryRunProgress(
      87,
      generateStoryboardImages
        ? 'Preparando imágenes del storyboard'
        : 'Preparando la estructura del storyboard',
      'preparing_scenes'
    )

    await waitForDryRun(1700)

    updateDryRunProgress(
      96,
      'Organizando las escenas para revisión',
      'organizing_scenes'
    )

    await waitForDryRun(1100)

    const goal =
      String(
        existing.projectJson
          ?.brief?.goal ||
          context.pending.plan
            ?.goal ||
          context.pending.direction
            ?.contentGoal ||
          ''
      )
        .trim()
        .toLowerCase()

    const needsAvatar =
      goal === 'ugc'

    const expectsProduct =
      context.pending.direction
        ?.hasProductImage ===
      'yes'

    const needsProductUpload =
      expectsProduct &&
      !absoluteProductImageUrl

    const nextPhase =
      needsProductUpload
        ? 'product_input'
        : needsAvatar
          ? 'creating_character'
          : 'generating_scenes'

    const existingScenes =
      Array.isArray(
        existing.projectJson
          ?.planner?.scenes
      )
        ? existing.projectJson
            .planner.scenes
        : Array.isArray(
            existing
              .preparedStoryboardPlan
              ?.planner?.scenes
          )
          ? existing
              .preparedStoryboardPlan
              .planner.scenes
          : Array.isArray(
              existing
                .preparedStoryboardPlan
                ?.scenes
            )
            ? existing
                .preparedStoryboardPlan
                .scenes
            : []

    const sceneProgress =
      existingScenes.map(
        (
          scene: any,
          index: number
        ) => ({
          ...scene,

          sceneId:
            scene?.sceneId ??
            scene?.scene_id ??
            scene?.id ??
            null,

          sceneOrder:
            Number(
              scene?.sceneOrder ??
                scene?.scene_order ??
                index + 1
            ),

          title:
            String(
              scene?.title ||
                `Escena ${index + 1}`
            ),

          prompt:
            String(
              scene?.prompt || ''
            ),

          imageUrl:
            scene?.imageUrl ||
            scene?.image_url ||
            null,

          status:
            scene?.imageUrl ||
            scene?.image_url
              ? 'done'
              : 'pending',

          metadata:
            scene?.metadata ||
            scene?.metadata_json ||
            {},
        })
      )

    const currentResult =
      useTaskFlowStore
        .getState()
        .result ||
      {}

    const nextResult = {
      ...currentResult,

      plan:
        context.pending.plan,

      projectId:
        existing.projectId,

      creatorStudioProject:
        existing.project,

      creativeDirection:
        context.pending.direction,

      discovery:
        context.pending.discovery,

      selectedIdea:
        context.selectedIdea,

      selectedHook:
        context.selectedHook,

      script:
        existing.script,

      scriptGeneration:
        existing.scriptGeneration,

      scriptGenerationId:
        existing.scriptGenerationId,

      sourceScriptGenerationId:
        existing.scriptGenerationId,

      productImageUrl:
        absoluteProductImageUrl,

      generationId:
        existing.generationId,

      projectJson:
        existing.projectJson,

      premiumStoryboardGeneration:
        existing.productionGeneration,

      preparedStoryboardPlan:
        existing
          .preparedStoryboardPlan,

      dryRun:
        true,

      __snapshotMeta: {
        ...(currentResult
          ?.__snapshotMeta || {}),

        direction:
          context.pending.direction,

        discovery:
          context.pending.discovery,

        expressPhase:
          nextPhase,

        projectId:
          existing.projectId,

        generationId:
          existing.generationId,

        productImageUrl:
          absoluteProductImageUrl,

        sceneProgress,

        dryRun:
          true,
      },
    }

    updateExpressScriptMessage(
      context.messageId,
      {
        phase:
          'production_ready',

        projectId:
          existing.projectId,

        productionStage:
          'completed',

        status:
          'completed',

        progress:
          100,

        step:
          nextPhase ===
          'creating_character'
            ? 'Storyboard listo. Vamos a elegir el avatar.'
            : nextPhase ===
              'product_input'
              ? 'Storyboard listo. Falta agregar el producto.'
              : 'Storyboard listo para revisar las escenas.',

        script:
          existing.script,

        generation:
          existing.scriptGeneration,

        productImageUrl:
          absoluteProductImageUrl,

        generateStoryboardImages,

        allowHyperframesScenes,
      }
    )

    await waitForDryRun(800)

    const flowRequest: TaskFlowRequest = {
      ...context.pending.request,

      id:
        `create-content-dry-run-${existing.projectId}`,

      type:
        'create_content',

      title:
        context.pending.plan
          ?.title ||
        existing.projectJson
          ?.brief?.title ||
        'Producción de contenido',

      subtitle:
        nextPhase ===
        'creating_character'
          ? 'Selecciona el avatar para continuar'
          : 'Revisa y produce las escenas',

      phase:
        nextPhase,

      routeFallback:
        `/creator-studio?projectId=${existing.projectId}`,

      payload: {
        ...(context.pending.request
          .payload || {}),

        source:
          'creator_studio_project_video',

        dryRun:
          true,

        projectId:
          existing.projectId,

        creatorStudioProject: {
          ...(existing.project || {}),

          project_id:
            existing.projectId,

          project_json:
            existing.projectJson,

          brief_json:
            existing.projectJson
              ?.brief ||
            context.brief,

          creative_direction_json:
            context.pending.direction,
        },

        plan:
          context.pending.plan,

        selectedIdea:
          context.selectedIdea,

        selectedHook:
          context.selectedHook,

        script:
          existing.script,

        scriptGeneration:
          existing.scriptGeneration,

        scriptGenerationId:
          existing.scriptGenerationId,

        sourceScriptGenerationId:
          existing.scriptGenerationId,

        generationId:
          existing.generationId,

        projectJson:
          existing.projectJson,

        productImageUrl:
          absoluteProductImageUrl,

        referenceImageUrl:
          absoluteProductImageUrl,

        creativeDirection:
          context.pending.direction,

        discovery:
          context.pending.discovery,

        generateStoryboardImages,

        allowHyperframesScenes,
      },
    }

    useTaskFlowStore
      .getState()
      .setResult(
        nextResult
      )

    useTaskFlowStore
      .getState()
      .setPhase(
        nextPhase as any
      )

    openTaskFlow(
      flowRequest
    )

    /*
    * Lo volvemos a fijar porque openTaskFlow
    * puede inicializar el store.
    */
    useTaskFlowStore
      .getState()
      .setResult(
        nextResult
      )

    useTaskFlowStore
      .getState()
      .setPhase(
        nextPhase as any
      )

    hideDockForExternalSurface()
  }

  async function continueExpressContentProduction(input: {
    context: PendingExpressContent
    sourceMessage: AIMasterChatMessage
  }) {
    const {
      context,
      sourceMessage,
    } = input

    if (
      canUseCreateContentDryRun
    ) {
      await runCreateContentProductionDryRun({
        context,
        sourceMessage,
      })

      return
    }

    const cardPayload =
      (sourceMessage.meta as any)
        ?.create_content_script ||
      {}

    const taskResult =
      useTaskFlowStore
        .getState()
        .result ||
      {}

    const scriptGeneration =
      cardPayload?.generation ||
      taskResult
        ?.scriptGeneration ||
      null

    const script =
      cardPayload?.script ||
      scriptGeneration
        ?.result_json ||
      taskResult?.script ||
      null

    const scriptGenerationId =
      Number(
        scriptGeneration
          ?.generation_id ||
          scriptGeneration
            ?.generationId ||
          taskResult
            ?.scriptJob
            ?.resultGenerationId ||
          0
      )

    if (!script) {
      throw new Error(
        'No se encontró el guion terminado.'
      )
    }

    if (!scriptGenerationId) {
      throw new Error(
        'No se encontró el ID de generación del guion premium.'
      )
    }

    const generateStoryboardImages =
      cardPayload
        ?.generateStoryboardImages !==
      false

    const allowHyperframesScenes =
      false

    const absoluteProductImageUrl =
      context.productImageUrl
        ? new URL(
            context.productImageUrl,
            window.location.origin
          ).toString()
        : null

    /*
    * Transformamos la tarjeta del guion
    * en tarjeta de progreso.
    */
    updateExpressScriptMessage(
      context.messageId,
      {
        phase:
          'preparing_production',

        dryRun: true,

        projectId:
          context.projectId,

        productionStage:
          'updating_project',

        progress:
          3,

        step:
          'Actualizando proyecto',

        script,

        generation:
          scriptGeneration,

        productImageUrl:
          absoluteProductImageUrl,

        generateStoryboardImages,

        allowHyperframesScenes,
      }
    )

    const prepared =
      await prepareContentProduction({
        sellerId:
          context.pending.request
            .payload?.sellerId,

        customerId:
          context.pending.request
            .payload?.userId,

        projectId:
          context.projectId,

        plan:
          context.pending.plan,

        creativeDirection:
          context.pending.direction,

        discovery:
          context.pending.discovery,

        script,

        scriptGenerationId,

        productImageUrl:
          absoluteProductImageUrl,

        generateStoryboardImages,

        allowHyperframesScenes,

        requestPayload:
          context.pending.request
            .payload || {},

        onProgress:
          (update) => {
            updateExpressScriptMessage(
              context.messageId,
              {
                phase:
                  'preparing_production',

                dryRun: true,

                projectId:
                  context.projectId,

                productionStage:
                  update.stage,

                productionJobId:
                  update.jobId ||
                  null,

                status:
                  update.status ||
                  null,

                progress:
                  update.progress,

                step:
                  update.label,

                script,

                generation:
                  scriptGeneration,

                productImageUrl:
                  absoluteProductImageUrl,

                generateStoryboardImages,

                allowHyperframesScenes,
              }
            )
          },
      })

    const plannerScenes =
      Array.isArray(
        prepared
          ?.projectJson
          ?.planner
          ?.scenes
      )
        ? prepared
            .projectJson
            .planner
            .scenes
        : []

    const sceneProgress =
      prepared.generatedScenes
        .length > 0
        ? prepared
            .generatedScenes
            .map(
              (
                scene: any,
                index: number
              ) => ({
                ...scene,

                sceneId:
                  scene?.sceneId ??
                  scene?.scene_id ??
                  scene?.id ??
                  null,

                sceneOrder:
                  Number(
                    scene?.sceneOrder ??
                      scene?.scene_order ??
                      index + 1
                  ),

                title:
                  String(
                    scene?.title ||
                      `Escena ${index + 1}`
                  ),

                prompt:
                  String(
                    scene?.prompt || ''
                  ),

                imageUrl:
                  scene?.imageUrl ||
                  scene?.image_url ||
                  null,

                status:
                  scene?.imageUrl ||
                  scene?.image_url
                    ? 'done'
                    : 'pending',

                metadata:
                  scene?.metadata ||
                  scene?.metadata_json ||
                  {},
              })
            )
        : plannerScenes.map(
            (
              scene: any,
              index: number
            ) => ({
              ...scene,

              sceneId:
                scene?.sceneId ??
                scene?.scene_id ??
                scene?.id ??
                null,

              sceneOrder:
                Number(
                  scene?.sceneOrder ??
                    scene?.scene_order ??
                    index + 1
                ),

              title:
                String(
                  scene?.title ||
                    `Escena ${index + 1}`
                ),

              prompt:
                String(
                  scene?.prompt || ''
                ),

              imageUrl:
                scene?.imageUrl ||
                scene?.image_url ||
                null,

              status:
                scene?.imageUrl ||
                scene?.image_url
                  ? 'done'
                  : 'pending',

              metadata:
                scene?.metadata ||
                scene?.metadata_json ||
                {},
            })
          )

    const nextResult = {
      ...taskResult,

      plan:
        prepared.approvedPlan,

      projectId:
        context.projectId,

      creatorStudioProject:
        context.project,

      creativeDirection:
        context.pending.direction,

      discovery:
        context.pending.discovery,

      selectedIdea:
        context.selectedIdea,

      selectedHook:
        context.selectedHook,

      script,

      scriptGeneration,

      scriptGenerationId,

      sourceScriptGenerationId:
        scriptGenerationId,

      productImageUrl:
        prepared.productImageUrl,

      generationId:
        prepared.generationId,

      projectJson:
        prepared.projectJson,

      premiumStoryboardGeneration:
        prepared
          .premiumStoryboardGeneration,

      preparedStoryboardPlan:
        prepared
          .preparedStoryboardPlan,

      __snapshotMeta: {
        ...(taskResult
          ?.__snapshotMeta || {}),

        direction:
          context.pending.direction,

        discovery:
          context.pending.discovery,

        expressPhase:
          prepared.nextPhase,

        projectId:
          context.projectId,

        generationId:
          prepared.generationId,

        productImageUrl:
          prepared.productImageUrl,

        sceneProgress,
      },
    }

    /*
    * Guardamos el estado ya preparado
    * antes de abrir el flow visual.
    */
    const nextSnapshotId =
      await saveExpressContentSnapshot({
        snapshotId:
          context.pending.snapshotId,

        sellerId:
          context.pending.request
            .payload?.sellerId,

        generationId:
          prepared.generationId,

        phase:
          prepared.nextPhase,

        request: {
          ...context.pending.request,

          phase:
            prepared.nextPhase,

          payload: {
            ...(context.pending.request
              .payload || {}),

            source:
              'creator_studio_project_video',

            projectId:
              context.projectId,

            creatorStudioProject: {
              ...(context.project || {}),

              project_id:
                context.projectId,

              brief_json:
                prepared
                  .projectJson
                  ?.brief ||
                context.brief,

              project_json:
                prepared.projectJson,

              creative_direction_json:
                context.pending.direction,
            },

            plan:
              prepared.approvedPlan,

            creativeDirection:
              context.pending.direction,

            discovery:
              context.pending.discovery,

            selectedIdea:
              context.selectedIdea,

            selectedHook:
              context.selectedHook,

            script,

            scriptGeneration,

            scriptGenerationId,

            sourceScriptGenerationId:
              scriptGenerationId,

            productImageUrl:
              prepared.productImageUrl,

            referenceImageUrl:
              prepared.productImageUrl,

            generationId:
              prepared.generationId,

            projectJson:
              prepared.projectJson,

            generateStoryboardImages,

            allowHyperframesScenes,
          },
        },

        result:
          nextResult,
      })

    if (nextSnapshotId) {
      useTaskFlowStore
        .getState()
        .setSnapshotId(
          nextSnapshotId
        )
    }

    updateExpressScriptMessage(
      context.messageId,
      {
        phase:
          'production_ready',

        projectId:
          context.projectId,

        productionStage:
          'completed',

        progress:
          100,

        step:
          prepared.nextPhase ===
          'creating_character'
            ? 'Storyboard listo. Vamos a elegir el avatar.'
            : prepared.nextPhase ===
              'product_input'
              ? 'Storyboard listo. Falta agregar el producto.'
              : 'Storyboard listo para revisar las escenas.',

        productImageUrl:
          prepared.productImageUrl,

        script,

        generation:
          scriptGeneration,

        generateStoryboardImages,

        allowHyperframesScenes,
      }
    )

    /*
    * Ahora sí abrimos SynCreateContentFlow,
    * pero directamente en la fase visual.
    */
    const flowRequest: TaskFlowRequest = {
      ...context.pending.request,

      id:
        `create-content-production-${context.projectId}`,

      type:
        'create_content',

      title:
        prepared.approvedPlan
          ?.title ||
        context.pending.plan
          ?.title ||
        'Producción de contenido',

      subtitle:
        prepared.nextPhase ===
        'creating_character'
          ? 'Selecciona el avatar para continuar'
          : 'Revisa y produce las escenas',

      phase:
        prepared.nextPhase,

      routeFallback:
        `/creator-studio?projectId=${context.projectId}`,

      payload: {
        ...(context.pending.request
          .payload || {}),

        source:
          'creator_studio_project_video',

        sellerId:
          context.pending.request
            .payload?.sellerId,

        userId:
          context.pending.request
            .payload?.userId,

        projectId:
          context.projectId,

        creatorStudioProject: {
          ...(context.project || {}),

          project_id:
            context.projectId,

          brief_json:
            prepared
              .projectJson
              ?.brief ||
            context.brief,

          project_json:
            prepared.projectJson,

          creative_direction_json:
            context.pending.direction,
        },

        plan:
          prepared.approvedPlan,

        selectedIdea:
          context.selectedIdea,

        selectedHook:
          context.selectedHook,

        script,

        scriptGeneration,

        scriptGenerationId,

        sourceScriptGenerationId:
          scriptGenerationId,

        productImageUrl:
          prepared.productImageUrl,

        referenceImageUrl:
          prepared.productImageUrl,

        generationId:
          prepared.generationId,

        projectJson:
          prepared.projectJson,

        generateStoryboardImages,

        allowHyperframesScenes,
      },
    }

    useTaskFlowStore
      .getState()
      .setResult(
        nextResult
      )

    useTaskFlowStore
      .getState()
      .setPhase(
        prepared.nextPhase as any
      )

    openTaskFlow(
      flowRequest
    )

    /*
    * openTaskFlow puede inicializar la fase.
    * La volvemos a fijar después de abrirlo.
    */
    useTaskFlowStore
      .getState()
      .setResult(
        nextResult
      )

    useTaskFlowStore
      .getState()
      .setPhase(
        prepared.nextPhase as any
      )

    hideDockForExternalSurface()
  }

  async function resumeCreateContentDryRun() {
    const project =
      await loadCreateContentDryRunProject()

    const projectId =
      Number(
        project?.project_id ||
          project?.projectId ||
          project?.id ||
          CREATE_CONTENT_DRY_RUN_PROJECT_ID
      )

    const scriptGeneration =
      resolveDryRunScriptGeneration(
        project
      )

    if (!scriptGeneration) {
      throw new Error(
        `El proyecto ${projectId} no tiene una generación de guion.`
      )
    }

    const script =
      scriptGeneration?.result_json ||
      project?.project_json?.script_package ||
      project?.projectJson?.script_package ||
      null

    if (!script) {
      throw new Error(
        `El proyecto ${projectId} no tiene un guion disponible.`
      )
    }

    const brief =
      project?.brief_json ||
      project?.brief ||
      project?.project_json?.brief ||
      project?.projectJson?.brief ||
      {}

    const direction =
      project?.creative_direction_json ||
      project?.project_json?.creative_direction ||
      project?.projectJson?.creative_direction ||
      EMPTY_CREATE_CONTENT_DIRECTION

    const discovery =
      script?.discovery ||
      project?.project_json
        ?.script_package
        ?.discovery ||
      EMPTY_CREATE_CONTENT_DISCOVERY

    const plan =
      resolveDryRunProjectPlan(
        project,
        direction,
        discovery
      )

    const selectedIdea =
      script?.selected_idea ||
      project?.project_json
        ?.script_package
        ?.selected_idea ||
      buildExpressSelectedIdea(plan)

    const selectedHook =
      script?.selected_hook ||
      project?.project_json
        ?.script_package
        ?.selected_hook ||
      buildExpressSelectedHook(plan)

    const productImageUrl =
      String(
        brief?.productImageUrl ||
          brief?.referenceImageUrl ||
          brief?.initialImage ||
          ''
      ).trim() || null

    /*
    * En dry run no existe snapshot real.
    */
    const request: TaskFlowRequest = {
      taskType:
        'create_content',

      worker:
        'content_worker',

      title:
        plan?.title ||
        'Crear contenido',

      subtitle:
        'Continuar producción',

      routeFallback:
        `/creator-studio?projectId=${projectId}`,

      requiresLLM:
        false,

      phase:
        'script_ready',

      payload: {
        sellerId,

        userId:
          Number(
            user?.userId ||
              currentUserId ||
              0
          ),

        source:
          'samantha_dry_run_resume',

        dryRun:
          true,

        projectId,

        creatorStudioProject:
          project,

        initialBrief:
          brief,

        plan,

        creativeDirection:
          direction,

        discovery,

        selectedIdea,

        selectedHook,

        script,

        scriptGeneration,

        scriptGenerationId:
          Number(
            scriptGeneration
              ?.generation_id ||
              scriptGeneration
                ?.generationId ||
              0
          ),

        sourceScriptGenerationId:
          Number(
            scriptGeneration
              ?.generation_id ||
              scriptGeneration
                ?.generationId ||
              0
          ),

        productImageUrl,

        referenceImageUrl:
          productImageUrl,

        generateStoryboardImages:
          true,

        allowHyperframesScenes:
          false,
      },
    }

    const resumedPending: PendingCreateContentPlan = {
      request,

      plan,

      direction,

      discovery,

      /*
      * No asociamos ningún snapshot.
      */
      snapshotId:
        null,
    }

    const messageId =
      `create-content-dry-run-resume-${Date.now()}`

    const context: PendingExpressContent = {
      pending:
        resumedPending,

      messageId,

      projectId,

      project,

      brief,

      selectedIdea,

      selectedHook,

      productImageUrl,

      jobId:
        null,
    }

    const result = {
      plan,

      projectId,

      creatorStudioProject:
        project,

      creativeDirection:
        direction,

      discovery,

      selectedIdea,

      selectedHook,

      script,

      scriptGeneration,

      scriptGenerationId:
        Number(
          scriptGeneration
            ?.generation_id ||
            0
        ),

      sourceScriptGenerationId:
        Number(
          scriptGeneration
            ?.generation_id ||
            0
        ),

      productImageUrl,

      projectJson:
        project?.project_json ||
        project?.projectJson ||
        null,

      dryRun:
        true,

      __snapshotMeta: {
        direction,

        discovery,

        expressPhase:
          'script_ready',

        projectId,

        productImageUrl,

        sceneProgress:
          [],

        dryRun:
          true,
      },
    }

    /*
    * Limpiamos cualquier snapshot anterior.
    */
    useTaskFlowStore
      .getState()
      .setSnapshotId(null)

    useTaskFlowStore
      .getState()
      .setResult(result)

    useTaskFlowStore
      .getState()
      .setPhase(
        'script_ready' as any
      )

    setPendingCreateContentPlan(
      resumedPending
    )

    setPendingExpressContent(
      context
    )

    setPendingCreateContentResume(
      null
    )

    setMessages((prev) => [
      ...prev,

      {
        id:
          messageId,

        role:
          'assistant',

        content:
          'Retomé tu proyecto y recuperé el guion que ya estaba preparado.',

        timestamp:
          new Date(),

        meta: {
          worker:
            'content_creator',

          intent:
            'CREATE_CONTENT_DRY_RUN_RESUMED',

          create_content_script: {
            phase:
              'script_ready',

            projectId,

            status:
              'completed',

            progress:
              100,

            step:
              'Guion recuperado',

            plan,

            selectedIdea,

            selectedHook,

            script,

            generation:
              scriptGeneration,

            productImageUrl,

            generateStoryboardImages:
              true,

            allowHyperframesScenes:
              false,

            dryRun:
              true,
          },
        },
      } as AIMasterChatMessage,
    ])

    setUIState(
      'chat'
    )

    setChatViewMode(
      'focus'
    )

    focusInput()
  }

  const handleButtonClick = useCallback(
    async (buttonOrValue: any, sourceMessage?: AIMasterChatMessage) => {
      const button =
        typeof buttonOrValue === 'string'
          ? { value: buttonOrValue }
          : buttonOrValue
      const value = String(button?.value || '').trim()
      const buttonAction = String(button?.action || button?.value || '').trim()

      if (
        value ===
        'create_content_product_upload'
      ) {
        if (
          uploadingExpressProduct
        ) {
          return
        }

        const fileInput =
          document.getElementById(
            'samantha-create-content-product-input'
          ) as HTMLInputElement | null

        if (!fileInput) {
          console.error(
            '[AIMasterWidget] product input not mounted'
          )

          toast.error(
            'No se encontró el selector de imagen.'
          )

          return
        }

        fileInput.click()

        return
      }

      if (
        value ===
        'create_content_script_retry'
      ) {
        if (
          pendingExpressContent
        ) {
          await startExpressScript(
            pendingExpressContent
          )
        }

        return
      }

      if (value === 'create_content_open_project') {
        const projectId = Number(
          button?.projectId ||
            pendingExpressContent?.projectId ||
            0
        )

        if (!projectId) {
          toast.error('No se encontró el proyecto.')
          return
        }

        hideDockForExternalSurface()

        router.push(
          `/creator-studio?projectId=${projectId}`
        )

        return
      }

      if (
        value ===
        'create_content_production_settings'
      ) {
        if (!sourceMessage?.id) {
          return
        }

        const patch =
          button?.patch || {}

        setMessages((prev) =>
          prev.map((message) => {
            if (
              message.id !==
              sourceMessage.id
            ) {
              return message
            }

            const previous =
              (message.meta as any)
                ?.create_content_script ||
              {}

            return {
              ...message,
              meta: {
                ...(message.meta || {}),

                create_content_script: {
                  ...previous,
                  ...patch,
                },
              },
            } as AIMasterChatMessage
          })
        )

        return
      }

      if (
        value ===
        'create_content_continue_production'
      ) {
        if (!pendingExpressContent) {
          toast.error(
            'No se encontró el proyecto actual.'
          )
          return
        }

        if (!sourceMessage) {
          toast.error(
            'No se encontró el guion actual.'
          )
          return
        }

        try {
          setChatViewMode('focus')

          await continueExpressContentProduction({
            context:
              pendingExpressContent,

            sourceMessage,
          })
        } catch (error: any) {
          console.error(
            '[AIMasterWidget] continue production error',
            error
          )

          const rawErrorMessage =
            String(error?.message || '').trim()

          const isProviderCreditsError =
            rawErrorMessage.includes(
              'Your credit balance is too low'
            ) ||
            rawErrorMessage.includes(
              'Anthropic API'
            ) ||
            rawErrorMessage.includes(
              'invalid_request_error'
            )

          const friendlyErrorMessage =
            isProviderCreditsError
              ? 'Estamos teniendo problemas para completar esta generación en este momento. Inténtalo nuevamente más tarde.'
              : rawErrorMessage ||
                'No se pudo preparar la producción. Inténtalo nuevamente más tarde.'

          updateExpressScriptMessage(
            pendingExpressContent.messageId,
            {
              phase:
                'production_error',

              projectId:
                pendingExpressContent.projectId,

              error:
                friendlyErrorMessage,

              progress:
                0,
            }
          )

          toast.error(
            friendlyErrorMessage
          )
        }

        return
      }

      if (!sellerId || loading) return
      
      if (
        value ===
        'create_content_plan_continue'
      ) {
        const pending =
          pendingCreateContentPlan

        if (!pending) {
          return
        }

        setChatViewMode('focus')

        try {
          await startCreateContentExpress(
            pending
          )
        } catch (error: any) {
          console.error(
            '[AIMasterWidget] start express content error',
            error
          )

          const message =
            error?.message ||
            'No se pudo preparar el proyecto de demostración.'

          setMessages((prev) =>
            prev.map((chatMessage) => {
              const scriptPayload =
                (chatMessage.meta as any)
                  ?.create_content_script

              if (
                !scriptPayload ||
                scriptPayload.phase !==
                  'generating_script'
              ) {
                return chatMessage
              }

              return {
                ...chatMessage,

                meta: {
                  ...(chatMessage.meta || {}),

                  create_content_script: {
                    ...scriptPayload,

                    phase:
                      'script_error',

                    progress:
                      0,

                    step:
                      'No se pudo cargar el guion',

                    error:
                      message,
                  },
                },
              } as AIMasterChatMessage
            })
          )

          toast.error(
            'El proyecto de demostración no tiene todavía un guion disponible.'
          )
        }

        return
      }

      if (value === 'create_content_plan_advanced') {
        const pending = pendingCreateContentPlan

        if (!pending) {
          return
        }

        await continueCreateContentAdvancedInChat(pending)
        return
      }

      if (
        value ===
        'create_content_plan_restart'
      ) {
        const pending =
          pendingCreateContentPlan

        if (!pending) {
          return
        }

        setPendingCreateContentPlan(
          null
        )

        useTaskFlowStore
          .getState()
          .setResult(null)

        useTaskFlowStore
          .getState()
          .setPhase(
            'creative_direction'
          )

        await beginCreateContentDirectionChat({
          request: pending.request,
          direction:
            EMPTY_CREATE_CONTENT_DIRECTION,
          discovery:
            EMPTY_CREATE_CONTENT_DISCOVERY,
          snapshotId:
            pending.snapshotId,
        })

        setChatViewMode('focus')
        return
      }

      if (
        value ===
        'create_content_plan_retry'
      ) {
        if (
          pendingCreateContentDirection
        ) {
          await runCreateContentAnalysisInChat(
            pendingCreateContentDirection
          )
        }

        return
      }

      if (
        value.startsWith(
          'create_content_direction:'
        )
      ) {
        await handleCreateContentDirectionButton(
          value
        )
        return
      }

      if (
        buttonAction === 'ai_funnel_action' ||
        value.startsWith('ai_funnel:')
      ) {
        await handleAIFunnelActionButton({
          value,
          label: String(button?.label || value),
        })

        return
      }

      if (
        buttonAction === 'launchpad_system_action' ||
        String(button?.kind || '').trim() === 'system_intent'
      ) {
        if (await handleLaunchpadFinalButton(button)) {
          return
        }
      }

      if (button?.launchpadDiscovery || buttonAction === 'launchpad_discovery_reply') {
        if ((button as any).href) {
          setUIState('closed')
          setChatViewMode('dock')
          setInput('')
          setCommandFocused(false)
          router.push((button as any).href)
          return
        }

        if (pendingLaunchpadDiscovery) {
          await handleLaunchpadDiscoveryReply(value)
          return
        }

        if (value) {
          pendingDisplayTextRef.current = String(button?.label || value).trim()
          await handleSend(value)
          return
        }

        return
      }

      if (button?.action === 'select_publication_template' || value.startsWith('publication_template:')) {
        const templateOptionId = String(
          button?.templateOptionId || value.replace('publication_template:', '')
        ).trim()

        const template = getPublicationTemplateById(templateOptionId)

        if (!template || !pendingPublicationDiscovery) {
          focusInput()
          return
        }

        const next: PendingPublicationDiscovery = {
          ...pendingPublicationDiscovery,
          templateOptionId: template.id,
          templateId: template.templateId,
          themeId: template.themeId,
          visualStyle: template.visualStyle as PublicationVisualStyle,
        }

        const nextStep = getNextPublicationStep(next)

        if (nextStep) {
          next.step = nextStep
          setPendingPublicationDiscovery(next)

          const question = getPublicationQuestionMessage(nextStep)

          setMessages((prev) => [
            ...prev,
            {
              id: `publication-template-selected-${Date.now()}`,
              role: 'assistant',
              content: `Perfecto. Usaremos el diseño **${template.name}**.`,
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
                intent: 'PUBLICATION_TEMPLATE_SELECTED',
              },
            } as AIMasterChatMessage,
            {
              id: `publication-question-${Date.now()}`,
              role: 'assistant',
              content: question.content,
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
                intent: `PUBLICATION_DISCOVERY_${nextStep.toUpperCase()}`,
                buttons: question.buttons,
              },
            } as AIMasterChatMessage,
          ])

          focusInput()
          return
        }

        setPendingPublicationDiscovery(next)
        focusInput()
        return
      }

      if (buttonAction === 'set_active_publication_slide') {
        const slideIndex = Number(button?.slideIndex ?? 0)

        if (!Number.isFinite(slideIndex) || slideIndex < 0) {
          return
        }

        if (sourceMessage?.id) {
          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.id !== sourceMessage.id) return msg

              const preview = (msg.meta as any)?.content_preview

              if (!preview) return msg

              return {
                ...msg,
                meta: {
                  ...(msg.meta || {}),
                  content_preview: {
                    ...preview,
                    activeSlideIndex: slideIndex,
                  },
                },
              } as AIMasterChatMessage
            })
          )
        }

        focusInput()
        return
      }

      if (buttonAction === 'open_latest_publication_carousel') {
        try {
          await openLatestPublicationCarousel()
        } catch (error: any) {
          console.error('[open_latest_publication_carousel] error:', error)

          setMessages((prev) => [
            ...prev,
            {
              id: `latest-publication-error-${Date.now()}`,
              role: 'assistant',
              content: String(error?.message || 'No pude abrir tu último carrusel.'),
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
                intent: 'OPEN_LATEST_PUBLICATION_CAROUSEL_ERROR',
              },
            } as AIMasterChatMessage,
          ])
        }

        setChatViewMode('focus')

        suppressNextAutoScrollRef.current = false
        focusInput()
        return
      }

      if (buttonAction === 'edit_exported_publication_carousel') {
        const editablePreview = button?.editablePreview

        if (!editablePreview) {
          await openLatestPublicationCarousel()
          return
        }

        setMessages((prev) => [
          ...prev,
          {
            id: `editable-publication-carousel-${Date.now()}`,
            role: 'assistant',
            content: 'Claro. Aquí está la versión editable del carrusel.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'OPEN_EDITABLE_PUBLICATION_CAROUSEL',
              content_preview: editablePreview,
            },
          } as AIMasterChatMessage,
        ])

        setUIState('chat')
        setChatViewMode('focus')
        suppressNextAutoScrollRef.current = false
        focusInput()
        return
      }

      if (buttonAction === 'download_exported_publication_carousel') {
        const renderedSlides = Array.isArray(button?.renderedSlides)
          ? button.renderedSlides
          : []

        if (!renderedSlides.length) {
          setMessages((prev) => [
            ...prev,
            {
              id: `download-export-error-${Date.now()}`,
              role: 'assistant',
              content: 'No encontré imágenes exportadas para descargar.',
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
                intent: 'DOWNLOAD_EXPORTED_PUBLICATION_ERROR',
              },
            } as AIMasterChatMessage,
          ])

          return
        }

        renderedSlides.forEach((slide: any, index: number) => {
          const href = slide.publicUrl || (slide.key ? `/${String(slide.key).replace(/^\/+/, '')}` : slide.url)

          if (!href) return

          setTimeout(() => {
            const a = document.createElement('a')
            a.href = href
            a.download = `slide-${String(index + 1).padStart(2, '0')}.png`
            a.target = '_blank'
            document.body.appendChild(a)
            a.click()
            a.remove()
          }, index * 250)
        })

        return
      }

      if (buttonAction === 'generate_slide_image') {
        setMessages((prev) => [
          ...prev,
          {
            id: `slide-image-placeholder-${Date.now()}`,
            role: 'assistant',
            content:
              'Perfecto. Aquí después generaremos una imagen con IA solo para este slide, sin regenerar todo el carrusel.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_SLIDE_IMAGE_GENERATION_PENDING',
            },
          } as AIMasterChatMessage,
        ])

        suppressNextAutoScrollRef.current = true
        return
      }

      if (button?.action === 'upload_slide_image') {
        setMessages((prev) => [
          ...prev,
          {
            id: `slide-upload-placeholder-${Date.now()}`,
            role: 'assistant',
            content:
              'Perfecto. La subida de imagen propia para este slide la conectamos al panel de Creator Studio o a un uploader inline.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_SLIDE_IMAGE_UPLOAD_PENDING',
            },
          } as AIMasterChatMessage,
        ])

        suppressNextAutoScrollRef.current = true
        return
      }

      if (button?.action === 'update_publication_color') {
        const currentPreview = (sourceMessage?.meta as any)?.content_preview

        if (!currentPreview || !sourceMessage) {
          focusInput()
          return
        }

        const patch = button?.patch || {}

        const updatedPreview = {
          ...currentPreview,
          draft: {
            ...(currentPreview.draft || {}),
            design: {
              ...((currentPreview.draft || {}).design || {}),
              ...patch,
            },
          },
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === sourceMessage.id
              ? ({
                  ...msg,
                  meta: {
                    ...(msg.meta || {}),
                    content_preview: updatedPreview,
                    intent: 'PUBLICATION_COLOR_UPDATED',
                  },
                } as AIMasterChatMessage)
              : msg
          )
        )

        suppressNextAutoScrollRef.current = true
        return
      }

      if (button?.action === 'generate_slide_image') {
        const currentPreview = (sourceMessage?.meta as any)?.content_preview

        if (!currentPreview || !sourceMessage) {
          focusInput()
          return
        }

        const slideIndex = Number(button?.slideIndex || 0)

        const currentDraft = currentPreview.draft || {}
        const currentVisuals = Array.isArray(currentDraft.slideVisuals)
          ? currentDraft.slideVisuals
          : []

        const nextVisuals = [...currentVisuals]

        const mockGeneratedImages = [
          'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1200&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1200&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1549476464-37392f717541?q=80&w=1200&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1200&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1596462502278-27bfdc403348?q=80&w=1200&auto=format&fit=crop',
        ]

        nextVisuals[slideIndex] = {
          ...(nextVisuals[slideIndex] || {}),
          backgroundImage:
            mockGeneratedImages[(Date.now() + slideIndex) % mockGeneratedImages.length],
          blurEnabled: false,
          overlayTone: nextVisuals[slideIndex]?.overlayTone || 'black',
          overlayOpacity: nextVisuals[slideIndex]?.overlayOpacity ?? 0.35,
        }

        const updatedPreview = {
          ...currentPreview,
          draft: {
            ...currentDraft,
            slideVisuals: nextVisuals,
          },
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === sourceMessage.id
              ? ({
                  ...msg,
                  meta: {
                    ...(msg.meta || {}),
                    content_preview: updatedPreview,
                    intent: 'PUBLICATION_SLIDE_IMAGE_UPDATED',
                  },
                } as AIMasterChatMessage)
              : msg
          )
        )

        suppressNextAutoScrollRef.current = true
        return
      }

      if (button?.action === 'publication_preview_action') {
        const publicationAction = String(button?.publicationAction || value || '').trim()

        await runPublicationPreviewAction({
          actionId: publicationAction,
          sourceMessage: sourceMessage as AIMasterChatMessage,
        })

        return
      }

      if (button?.action === 'swap_publication_template') {
        //console.log('[swap_publication_template]', button)
        const templateOptionId = String(button?.templateOptionId || '').trim()
        const template = getPublicationTemplateById(templateOptionId)

        if (!template || !sourceMessage) {
          focusInput()
          return
        }

        const currentPreview = (sourceMessage.meta as any)?.content_preview

        if (!currentPreview) {
          focusInput()
          return
        }

        const updatedPreview = applyTemplateToPublicationPreview(currentPreview, template, {
          keepCurrentIntentAndSector: true,
        })

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === sourceMessage.id
              ? ({
                  ...msg,
                  content: `Listo. Cambié el diseño a **${template.name}**.`,
                  meta: {
                    ...(msg.meta || {}),
                    content_preview: updatedPreview,
                    intent: 'PUBLICATION_TEMPLATE_SWAPPED',
                  },
                } as AIMasterChatMessage)
              : msg
          )
        )

        suppressNextAutoScrollRef.current = true
        return
      }

      if (button?.action === 'set_active_publication_slide') {
        const currentPreview = (sourceMessage?.meta as any)?.content_preview

        if (!currentPreview || !sourceMessage) {
          focusInput()
          return
        }

        const slideIndex = Number(button?.slideIndex || 0)

        const updatedPreview = {
          ...currentPreview,
          activeSlideIndex: slideIndex,
          draft: {
            ...(currentPreview.draft || {}),
            activeSlideIndex: slideIndex,
          },
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === sourceMessage.id
              ? ({
                  ...msg,
                  meta: {
                    ...(msg.meta || {}),
                    content_preview: updatedPreview,
                  },
                } as AIMasterChatMessage)
              : msg
          )
        )

        suppressNextAutoScrollRef.current = true

        return
      }

      if (button?.action === 'update_publication_slide_visual') {
        const currentPreview = (sourceMessage?.meta as any)?.content_preview

        if (!currentPreview || !sourceMessage) {
          focusInput()
          return
        }

        const slideIndex = Number(button?.slideIndex || 0)
        const patch = button?.patch || {}

        const updatedPreview = updatePublicationSlideVisual(currentPreview, {
          slideIndex,
          patch,
        })

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === sourceMessage.id
              ? ({
                  ...msg,
                  meta: {
                    ...(msg.meta || {}),
                    content_preview: updatedPreview,
                    intent: 'PUBLICATION_SLIDE_VISUAL_UPDATED',
                  },
                } as AIMasterChatMessage)
              : msg
          )
        )

        suppressNextAutoScrollRef.current = true
        return
      }

      if (button?.action === 'navigate' && button?.route) {
        executeNavigation({
          route: String(button.route),
          params: button.params || {},
        })

        closeDockAfterNavigation()
        return
      }

      if (value.startsWith('navigate:')) {
        const route = value.replace('navigate:', '').trim()

        if (route.startsWith('/')) {
          executeNavigation({
            route,
            params: {},
          })

          closeDockAfterNavigation()
          return
        }
      }

      if (value === 'open_courses') {
        closeDockAfterNavigation()

        executeNavigation({
          route: '/courses',
          params: {},
        })
        return
      }

      if (value === 'open_onboarding') {
        closeDockAfterNavigation()

        executeNavigation({
          route: '/onboarding',
          params: {},
        })
        return
      }

      if (button?.action === 'open_support_ticket' || value === 'open_support_ticket') {
        setPendingSupportTicket({
          originalMessage: sourceMessage?.content || '',
          matchedArticle: (sourceMessage?.meta as any)?.supportMatch || null,
          awaitingDescription: true,
        })

        setMessages((prev) => [
          ...prev,
          {
            id: `support-ticket-prompt-${Date.now()}`,
            role: 'assistant',
            content:
              'Entendido. Voy a ayudarte a abrir un ticket de soporte.\n\nCuéntame con el mayor detalle posible qué pasó, qué intentaste hacer y qué necesitas que el equipo revise.',
            timestamp: new Date(),
            meta: {
              intent: 'PLATFORM',
              worker: 'platform_support',
            },
          },
        ])

        focusInput()
        return
      }

      if (value === 'funnel_template:start') {
        setPendingFunnelDiscovery(null)

        setMessages((prev) => [
          ...prev,
          {
            id: `funnel-template-open-${Date.now()}`,
            role: 'assistant',
            content:
              'Listo. Te abro las plantillas disponibles para que elijas una y la clones como embudo editable.',
            timestamp: new Date(),
            meta: {
              worker: 'funnel_worker',
              intent: 'FUNNEL_TEMPLATE_PICKER',
            },
          },
        ])

        hideDockForExternalSurface()

        openFunnelTemplatePicker({
          initialTab: 'official',
        })

        return
      }

      if (value === 'funnel_scratch:start') {
        setPendingFunnelDiscovery(null)

        setMessages((prev) => [
          ...prev,
          {
            id: `funnel-scratch-${Date.now()}`,
            role: 'assistant',
            content:
              'Perfecto. Te llevo al constructor para crear un embudo desde cero.',
            timestamp: new Date(),
            meta: {
              worker: 'funnel_worker',
              intent: 'FUNNEL_SCRATCH',
            },
          },
        ])

        setChatViewMode('dock')
        hideDockForExternalSurface()
        router.push('/funnel-builder')
        focusInput()
        return
      }

      if (value === 'funnel_navigate:my_funnels') {
        setPendingFunnelDiscovery(null)

        setMessages((prev) => [
          ...prev,
          {
            id: `funnel-navigate-${Date.now()}`,
            role: 'assistant',
            content: 'Listo. Te llevo a tus embudos.',
            timestamp: new Date(),
            meta: {
              worker: 'funnel_worker',
              intent: 'FUNNEL_NAVIGATION',
            },
          },
        ])
        
        setChatViewMode('dock')
        hideDockForExternalSurface()
        router.push('/funnel-builder')
        focusInput()
        return
      }

      if (value === 'confirm_lightweight_flow') {
        const pending = pendingLightweightFlow
        if (!pending) return

        setPendingLightweightFlow(null)
        await openConfirmedLightweightFlow(pending.intent, pending.text)
        return
      }

      if (value === 'continue_with_orchestrator') {
        const pending = pendingLightweightFlow
        if (!pending?.text) return

        setPendingLightweightFlow(null)
        await handleSend(pending.text)
        return
      }

      if (
        value ===
        'resume_create_content'
      ) {
        if (
          canUseCreateContentDryRun
        ) {
          try {
            await resumeCreateContentDryRun()
          } catch (error: any) {
            console.error(
              '[AIMasterWidget] dry run resume error',
              error
            )

            toast.error(
              error?.message ||
                'No se pudo recuperar el proyecto de demostración.'
            )
          }

          return
        }

        const pending =
          pendingCreateContentResume

        if (!pending?.snapshot) return

        const snapshot = pending.snapshot

        const snapshotRequest =
          snapshot.request ||
          pending.request

        const snapshotResult =
          snapshot.result ||
          null

        const snapshotPhase =
          snapshot.phase ||
          snapshotResult?.phase ||
          snapshotRequest?.phase ||
          'creative_direction'

        const snapshotId =
          Number(pending.snapshotId || 0)

        useTaskFlowStore
          .getState()
          .setSnapshotId(snapshotId)

        const expressPhase =
          snapshotResult
            ?.__snapshotMeta
            ?.expressPhase ||
          snapshotPhase

        const resumedProjectId =
          Number(
            snapshotResult
              ?.projectId ||
              snapshotResult
                ?.__snapshotMeta
                ?.projectId ||
              snapshotRequest
                ?.payload
                ?.projectId ||
              0
          )

        const resumedJobId =
          Number(
            snapshotResult
              ?.scriptJob
              ?.jobId ||
              snapshotResult
                ?.__snapshotMeta
                ?.scriptJobId ||
              0
          )

        if (
          snapshotRequest &&
          snapshotResult?.plan &&
          resumedProjectId &&
          [
            'awaiting_product',
            'generating_script',
            'script_ready',
            'script_error',
          ].includes(
            String(expressPhase)
          )
        ) {
          const direction =
            snapshotResult
              ?.creativeDirection ||
            snapshotResult
              ?.__snapshotMeta
              ?.direction ||
            snapshotRequest
              ?.payload
              ?.creativeDirection ||
            EMPTY_CREATE_CONTENT_DIRECTION

          const discovery =
            snapshotResult
              ?.discovery ||
            snapshotResult
              ?.__snapshotMeta
              ?.discovery ||
            snapshotRequest
              ?.payload
              ?.discovery ||
            EMPTY_CREATE_CONTENT_DISCOVERY

          const resumedPending: PendingCreateContentPlan = {
            request:
              snapshotRequest,

            plan:
              snapshotResult.plan,

            direction,

            discovery,

            snapshotId,
          }

          const messageId =
            `create-content-express-resume-${Date.now()}`

          const context: PendingExpressContent = {
            pending:
              resumedPending,

            messageId,

            projectId:
              resumedProjectId,

            project:
              snapshotResult
                ?.creatorStudioProject ||
              null,

            brief:
              snapshotResult
                ?.creatorStudioProject
                ?.brief_json ||
              snapshotRequest
                ?.payload
                ?.plan ||
              snapshotResult.plan ||
              {},

            selectedIdea:
              snapshotResult
                ?.selectedIdea ||
              buildExpressSelectedIdea(
                snapshotResult.plan
              ),

            selectedHook:
              snapshotResult
                ?.selectedHook ||
              buildExpressSelectedHook(
                snapshotResult.plan
              ),

            productImageUrl:
              snapshotResult
                ?.productImageUrl ||
              snapshotResult
                ?.__snapshotMeta
                ?.productImageUrl ||
              null,

            jobId:
              resumedJobId ||
              null,
          }

          setPendingCreateContentPlan(
            resumedPending
          )

          setPendingExpressContent(
            context
          )

          setPendingCreateContentResume(
            null
          )

          useTaskFlowStore
            .getState()
            .setResult(
              snapshotResult
            )

          useTaskFlowStore
            .getState()
            .setPhase(
              expressPhase as any
            )

          setMessages((prev) => [
            ...prev,
            {
              id:
                messageId,

              role:
                'assistant',

              content:
                expressPhase ===
                'script_ready'
                  ? 'Retomé tu proyecto. El guion ya está listo.'
                  : 'Retomé la creación de tu proyecto.',

              timestamp:
                new Date(),

              meta: {
                worker:
                  'content_creator',

                intent:
                  'CREATE_CONTENT_EXPRESS_RESUMED',

                create_content_script: {
                  phase:
                    expressPhase,

                  projectId:
                    resumedProjectId,

                  jobId:
                    resumedJobId ||
                    null,

                  status:
                    snapshotResult
                      ?.scriptJob
                      ?.status ||
                    null,

                  progress:
                    Number(
                      snapshotResult
                        ?.scriptJob
                        ?.progress ||
                      0
                    ),

                  step:
                    snapshotResult
                      ?.scriptJob
                      ?.step ||
                    null,

                  plan:
                    snapshotResult.plan,

                  selectedIdea:
                    context.selectedIdea,

                  selectedHook:
                    context.selectedHook,

                  script:
                    snapshotResult
                      ?.script ||
                    null,

                  generation:
                    snapshotResult
                      ?.scriptGeneration ||
                    null,

                  productImageUrl:
                    context.productImageUrl,

                  productRequired:
                    expressPhase ===
                    'awaiting_product',

                  error:
                    snapshotResult
                      ?.scriptJob
                      ?.errorMessage ||
                    null,
                },
              },
            } as AIMasterChatMessage,
          ])

          setUIState('chat')
          setChatViewMode('focus')

          if (
            expressPhase ===
              'generating_script' &&
            resumedJobId
          ) {
            void pollExpressScriptJob(
              context,
              resumedJobId
            )
          }

          focusInput()
          return
        }

        /*
        * PLAN YA GENERADO:
        * restaurar la tarjeta dentro del chat.
        * Nunca abrir el flow.
        */
        if (
          snapshotResult?.plan &&
          snapshotRequest
        ) {
          const plan =
            snapshotResult.plan

          const direction =
            snapshotResult?.creativeDirection ||
            snapshotResult?.__snapshotMeta?.direction ||
            snapshotRequest?.payload?.creativeDirection ||
            EMPTY_CREATE_CONTENT_DIRECTION

          const discovery =
            snapshotResult?.discovery ||
            snapshotResult?.__snapshotMeta?.discovery ||
            snapshotRequest?.payload?.discovery ||
            EMPTY_CREATE_CONTENT_DISCOVERY

          const resumedPlan: PendingCreateContentPlan = {
            request: snapshotRequest,
            plan,
            direction,
            discovery,
            snapshotId,
          }

          setPendingCreateContentPlan(
            resumedPlan
          )

          setPendingCreateContentResume(
            null
          )

          useTaskFlowStore
            .getState()
            .setResult(snapshotResult)

          useTaskFlowStore
            .getState()
            .setPhase('proposed_plan')

          setMessages((prev) => [
            ...prev,
            {
              id: `create-content-plan-resume-${Date.now()}`,
              role: 'assistant',
              content:
                'Retomé el plan de contenido que estabas construyendo.',
              timestamp: new Date(),
              meta: {
                worker: 'content_creator',
                intent:
                  'CREATE_CONTENT_PLAN_READY',

                create_content_plan: {
                  plan,
                  direction,
                  discovery,
                },

                buttons: [
                  {
                    label:
                      'Crear proyecto y avanzar',
                    value:
                      'create_content_plan_continue',
                    emoji: '✨',
                  },
                  {
                    label:
                      'Generar varias ideas',
                    value:
                      'create_content_plan_advanced',
                    emoji: '🧠',
                  },
                  {
                    label:
                      'Cambiar dirección',
                    value:
                      'create_content_plan_restart',
                    emoji: '↩️',
                  },
                ],
              },
            } as AIMasterChatMessage,
          ])

          setUIState('chat')
          setChatViewMode('focus')

          focusInput()
          return
        }

        /*
        * CREATIVE DIRECTION O DISCOVERY:
        * continuar las preguntas dentro del chat normal.
        */
        if (
          snapshotRequest &&
          [
            'creative_direction',
            'discovery',
            'analyzing',
          ].includes(snapshotPhase)
        ) {
          const savedDirection =
            snapshotResult?.__snapshotMeta?.direction ||
            snapshotResult?.creativeDirection ||
            snapshotRequest?.payload?.creativeDirection ||
            EMPTY_CREATE_CONTENT_DIRECTION

          const savedDiscovery =
            snapshotResult?.__snapshotMeta?.discovery ||
            snapshotResult?.discovery ||
            snapshotRequest?.payload?.discovery ||
            EMPTY_CREATE_CONTENT_DISCOVERY

          setPendingCreateContentResume(
            null
          )

          await beginCreateContentDirectionChat({
            request: snapshotRequest,
            direction: savedDirection,
            discovery: savedDiscovery,
            snapshotId,
          })

          setUIState('chat')

          // IMPORTANTE:
          // No cambiar a focus aquí.
          // El focus ocurre hasta mostrar el plan.
          focusInput()
          return
        }

        /*
        * Ya no queremos abrir el flow
        * desde el resume.
        */
        setPendingCreateContentResume(
          null
        )

        setMessages((prev) => [
          ...prev,
          {
            id: `create-content-resume-unavailable-${Date.now()}`,
            role: 'assistant',
            content:
              'No pude reconstruir correctamente ese proyecto. Puedes iniciar uno nuevo sin perder el resto de tu conversación.',
            timestamp: new Date(),
            meta: {
              worker: 'content_creator',
            },
          },
        ])

        focusInput()
        return
      }

      if (value === 'restart_create_content') {

        if (
          canUseCreateContentDryRun
        ) {
          setPendingCreateContentResume(
            null
          )

          setPendingCreateContentPlan(
            null
          )

          setPendingExpressContent(
            null
          )

          useTaskFlowStore
            .getState()
            .setSnapshotId(null)

          useTaskFlowStore
            .getState()
            .setResult(null)

          useTaskFlowStore
            .getState()
            .setPhase(
              'creative_direction'
            )

          await beginCreateContentDirectionChat({
            request:
              pendingCreateContentResume
                ?.request ||
              buildCreateContentTaskRequest(
                'Quiero crear un nuevo proyecto de contenido'
              ),

            direction:
              EMPTY_CREATE_CONTENT_DIRECTION,

            discovery:
              EMPTY_CREATE_CONTENT_DISCOVERY,

            snapshotId:
              null,
          })

          return
        }

        const pending =
          pendingCreateContentResume

        if (!pending) return

        await fetch(
          '/api/task-flows/create-content/abandon',
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              snapshotId: Number(
                pending.snapshotId
              ),
            }),
          }
        ).catch((error) => {
          console.error(
            'abandon snapshot error',
            error
          )
        })

        useTaskFlowStore
          .getState()
          .setSnapshotId(null)

        useTaskFlowStore
          .getState()
          .setResult(null)

        useTaskFlowStore
          .getState()
          .setPhase(
            'creative_direction'
          )

        setPendingCreateContentResume(
          null
        )

        setPendingCreateContentPlan(
          null
        )

        await beginCreateContentDirectionChat({
          request: pending.request,

          direction:
            EMPTY_CREATE_CONTENT_DIRECTION,

          discovery:
            EMPTY_CREATE_CONTENT_DISCOVERY,

          snapshotId: null,
        })

        setUIState('chat')

        // NO poner focus aquí.
        // Debe seguir en el modo normal.
        focusInput()
        return
      }

      if (value.startsWith('open_lesson_segment:')) {
        const [, courseId, lessonId, rawTimecode] = value.split(':')
        const parsedTimecode =
          rawTimecode != null ? parseFloat(String(rawTimecode).trim()) : NaN

        const timecode = Number.isFinite(parsedTimecode)
          ? parsedTimecode
          : undefined

        if (/^\d+$/.test(courseId || '') && /^\d+$/.test(lessonId || '')) {
          closeDockAfterNavigation()

          executeNavigation({
            route: `/courses/${courseId}/lesson/${lessonId}`,
            params: {
              courseId,
              lessonId,
              ...(timecode != null ? { timecode } : {}),
            },
          })
        }
        return
      }

      if (value.startsWith('open_lesson:')) {
        const [, courseId, lessonId] = value.split(':')

        if (/^\d+$/.test(courseId || '') && /^\d+$/.test(lessonId || '')) {
          closeDockAfterNavigation()

          executeNavigation({
            route: `/courses/${courseId}/lesson/${lessonId}`,
            params: {
              courseId,
              lessonId,
            },
          })
        }
        return
      }

      pendingDisplayTextRef.current = String(button?.label || value || '').trim()
      await handleSend(value)
    },
    [
      sellerId,
      loading,
      router,
      executeNavigation,
      uploadingExpressProduct,
      pendingCreateContentResume,
      pendingLightweightFlow,
      pendingPublicationDiscovery,
      pendingAdsCampaignDiscovery,
      pendingLaunchpadDiscovery,
      focusInput,
      handleSend,
    ]
  )

  useEffect(() => {
    function onCreatorStudioSelection(event: Event) {
      const customEvent = event as CustomEvent
      const detail = customEvent.detail || {}

      if (detail?.type !== 'idea_selected') return

      const idea = detail.idea || {}
      const title = String(idea.title || '').trim()

      if (!title) return

      injectAssistantMessage({
        content:
          'Listo. Ya tomé esa idea como base. Ahora voy a generar hooks para convertirla en una entrada irresistible.',
        meta: {
          intent: 'PLATFORM',
          worker: 'creator-studio',
        },
      })
    }

    window.addEventListener(
      'samantha:creator-studio-selection',
      onCreatorStudioSelection
    )

    return () => {
      window.removeEventListener(
        'samantha:creator-studio-selection',
        onCreatorStudioSelection
      )
    }
  }, [injectAssistantMessage])

  const handleScrollMessages = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    if (el.scrollTop <= 40) {
      if (visibleCount < messages.length) {
        const previousHeight = el.scrollHeight
        setIsLoadingOlder(true)

        setVisibleCount((prev) => Math.min(prev + LOAD_MORE_STEP, messages.length))

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const nextHeight = el.scrollHeight
            el.scrollTop = nextHeight - previousHeight
            setIsLoadingOlder(false)
          })
        })
      }
    }
  }, [visibleCount, messages.length])

  async function openLatestPublicationCarousel() {
    const res = await fetch('/api/creator-studio/publications/generations/latest', {
      method: 'GET',
    })

    const data = await res.json().catch(() => null)

    //console.log('[latest carousel assetsBySlide]', data.assetsBySlide)

    if (!res.ok || !data?.ok) {
      throw new Error(
        data?.message ||
          data?.error ||
          'No se encontró ningún carrusel generado todavía.'
      )
    }

    const draft = data.publicationDraft

    if (!draft) {
      throw new Error('La generación existe, pero no trae publicationDraft.')
    }

    function toPublicAssetUrl(asset: any) {
      const key = String(asset?.storage_key || asset?.key || '').trim()

      if (key) {
        return key.startsWith('/') ? key : `/${key}`
      }

      return String(asset?.url || '').trim()
    }

    function getRenderedSlidesFromAssets(assets: any[]) {
      return assets
        .filter(
          (asset: any) =>
            asset?.asset_role === 'rendered_slide' &&
            Number(asset?.is_current) === 1
        )
        .sort((a: any, b: any) => Number(a.slide_index) - Number(b.slide_index))
        .map((asset: any) => ({
          slideIndex: Number(asset.slide_index),
          key: asset.storage_key,
          publicUrl: toPublicAssetUrl(asset),
          width: asset.width,
          height: asset.height,
          mimeType: asset.mime_type,
          sizeBytes: asset.file_size,
        }))
    }

    const assets = Array.isArray(data.assets) ? data.assets : []
    const renderedSlides = getRenderedSlidesFromAssets(assets)

    const generationId = data.generation?.generation_id || data.generation?.id

    const editablePreview = {
      previewType: 'carousel',
      generationId,
      generation_id: generationId,
      title: data.generation?.title || 'Último carrusel',
      draft,
      slides: Array.isArray(draft.slides) ? draft.slides : data.slides || [],
      assetsBySlide: data.assetsBySlide || {},
      activeSlideIndex: 0,
      actions: [{ id: 'export_publication_carousel', label: 'Exportar carrusel' }],
    }

    const preview =
      renderedSlides.length > 0
        ? {
            previewType: 'exported_carousel',
            generationId,
            generation_id: generationId,
            title: data.generation?.title || 'Carrusel exportado',
            renderedSlides,
            assets,
            editablePreview,
          }
        : editablePreview

    setMessages((prev) => [
      ...prev,
      {
        id: `latest-publication-carousel-${Date.now()}`,
        role: 'assistant',
        content: 'Aquí está tu último carrusel creado.',
        timestamp: new Date(),
        meta: {
          worker: 'creator_studio_worker',
          intent: 'OPEN_LATEST_PUBLICATION_CAROUSEL',
          content_preview: preview,
        },
      } as AIMasterChatMessage,
    ])

    suppressNextAutoScrollRef.current = true
  }

  async function generateTemplateCarouselFromCreatorStudio(
    discovery: PendingPublicationDiscovery,
    enrichedInstruction: string
  ) {
    const platform = discovery.platform || 'instagram'
    const format = discovery.format || 'carousel'

    const intent =
      (discovery as any).intent ||
      (discovery as any).publicationIntent ||
      'educational'

    const sector =
      (discovery as any).sector ||
      (discovery as any).publicationSector ||
      'creator_personal_brand'

    const templateId =
      (discovery as any).templateId ||
      'sector-intent-pack'

    const themeId =
      (discovery as any).themeId ||
      (discovery as any).selectedTemplate?.themeId ||
      `${String(sector).replaceAll('_', '-')}-${intent}`

    const res = await fetch('/api/creator-studio/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        tab: 'publications',
        projectStep: 'publications',
        inputs: {
          outputMode: 'template_carousel',

          platform,
          format: 'carousel',
          intent,
          sector,
          templateId,
          themeId,

          instruction: enrichedInstruction,
          topic: enrichedInstruction,

          brief: {
            platform,
            format: 'carousel',
            intent,
            sector,
            templateId,
            themeId,
            instruction: enrichedInstruction,
            topic: enrichedInstruction,
            source: 'chat',
          },

          generateImage: false,
          source: 'chat',
        },
      }),
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(String(json?.error || 'No se pudo generar la publicación.'))
    }

    const generation = json.generation
    const resultJson = generation?.result_json
    const draft = resultJson?.publicationDraft

    if (!draft) {
      throw new Error('La generación no devolvió publicationDraft.')
    }

    return {
      generation,
      resultJson,
      draft,
    }
  }

  async function evaluatePublicationContextGate(state: PendingPublicationDiscovery) {
    const payload = {
      source: 'samantha_chat',
      rawPrompt: state.originalRequest,
      instruction: state.instruction,
      platform: state.platform,
      format: state.format,
      intent: state.intent,
      sector: state.sector,
      style: state.visualStyle,
      creativeMode: state.creativeMode || 'template',
      topic: state.instruction || state.originalRequest,
      objective: state.intent,
      pathname,
      brandName: brand?.name || '',
    }

    const res = await fetch('/api/task-flows/create-content/context-gate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'evaluate_and_extract',
        sellerId,
        customerId: Number(user?.userId || 0),
        payload,
      }),
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      return {
        canGenerate: true,
        missingFields: [],
      }
    }

    return json.gate
  }

  async function savePublicationContextAnswer(
    state: PendingPublicationDiscovery,
    answer: string
  ) {
    const payload = {
      source: 'samantha_chat',
      rawPrompt: state.originalRequest,
      instruction: state.instruction,
      platform: state.platform,
      format: state.format,
      intent: state.intent,
      sector: state.sector,
      style: state.visualStyle,
      creativeMode: state.creativeMode || 'template',
      topic: state.instruction || state.originalRequest,
      objective: state.intent,
      pathname,
      brandName: brand?.name || '',
    }

    const res = await fetch('/api/task-flows/create-content/context-gate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        action: 'save_answer',
        sellerId,
        customerId: Number(user?.userId || 0),
        payload,
        answer,
        missingFields: state.contextGate?.missingFields || [],
      }),
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || 'No pude guardar el contexto creativo')
    }

    return json.gate
  }

  async function enrichPublicationInstruction(
    state: PendingPublicationDiscovery
  ) {
    const fallback = buildPublicationInstruction(state)

    try {
      const res = await fetch(
        '/api/creator-studio/publications/enrich-instruction',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            platform: state.platform,
            format: state.format,

            intent: state.intent,
            sector: state.sector,
            visualStyle: state.visualStyle,
            templateId: state.templateId,
            themeId: state.themeId,

            style: state.visualStyle || 'auto',

            wantsReferenceImage: state.wantsReferenceImage,
            originalRequest: state.originalRequest,
            instruction: state.instruction,
          }),
        }
      )

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok || !json?.instruction) {
        return fallback
      }

      return String(json.instruction || '').trim() || fallback
    } catch {
      return fallback
    }
  }

  async function fetchCreateContentResume() {
    const res = await fetch('/api/task-flows/create-content/resume', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        sellerId,
        customerId: Number(user?.userId || 0),
      }),
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      return { found: false }
    }

    return json
  }

  function normalizeCreatorItemForAds(item: any) {
    const id = String(
      item?.id ||
        item?.publication_id ||
        item?.content_id ||
        item?.uuid ||
        ''
    )

    if (!id) return null

    const title = String(
      item?.title ||
        item?.name ||
        item?.topic ||
        item?.brief ||
        item?.instruction ||
        'Pieza publicitaria'
    )

    const imageUrl = String(
      item?.imageUrl ||
        item?.image_url ||
        item?.thumbnailUrl ||
        item?.thumbnail_url ||
        item?.url ||
        item?.preview_url ||
        item?.media_url ||
        ''
    )

    return {
      id,
      title,
      subtitle: String(item?.platform || item?.format || 'Creator Studio'),
      description: String(
        item?.caption ||
          item?.description ||
          item?.brief ||
          item?.instruction ||
          'Pieza creada en Hubee'
      ),
      thumbnailUrl: imageUrl,
      badge: 'Pieza',
      value: `ads_creative_item:${id}`,
      actionLabel: 'Usar pieza',
      meta: {
        creativeId: id,
        sourceType: item?.sourceType || item?.source_type || null,
        sourceId: item?.sourceId || item?.source_id || item?.funnel_id || null,
        raw: item,
      },
    }
  }

  function findCreativeCandidatesForAds(
    state: Partial<PendingAdsCampaignDiscovery>
  ): ChatEntityResultsPayload | null {
    const sourceId = String(state.sourceId || '')
    const sourceName = normalizeSmallReply(state.sourceName || '')

    /**
     * Aquí usa la colección real que ya tengas cargada en el widget.
     * Puede llamarse publications, creatorPublications, recentPublications, etc.
     *
     * Si todavía no existe en el widget, por ahora déjalo como [].
     * Luego conectamos el endpoint real.
     */
    const rawItems: any[] = Array.isArray((window as any).__debugCreatorItems)
      ? (window as any).__debugCreatorItems
      : []

    const normalized = rawItems
      .map(normalizeCreatorItemForAds)
      .filter(Boolean) as any[]

    if (!normalized.length) return null

    const scored = normalized
      .map((item) => {
        const raw = item.meta?.raw || {}
        const directSourceId = String(
          item.meta?.sourceId ||
            raw?.funnel_id ||
            raw?.source_id ||
            ''
        )

        const text = normalizeSmallReply(
          [
            item.title,
            item.subtitle,
            item.description,
            raw?.caption,
            raw?.brief,
            raw?.instruction,
            raw?.topic,
          ]
            .filter(Boolean)
            .join(' ')
        )

        let score = 0

        if (sourceId && directSourceId && directSourceId === sourceId) {
          score += 100
        }

        if (sourceName && text.includes(sourceName)) {
          score += 30
        }

        if (state.goal === 'leads' && /lead|registro|prospecto|contacto/.test(text)) {
          score += 10
        }

        if (state.goal === 'sales' && /venta|comprar|checkout|precio|oferta/.test(text)) {
          score += 10
        }

        if (state.goal === 'whatsapp' && /whatsapp|mensaje|dm|inbox/.test(text)) {
          score += 10
        }

        return {
          ...item,
          meta: {
            ...item.meta,
            relevanceScore: score,
          },
        }
      })
      .filter((item) => Number(item.meta?.relevanceScore || 0) > 0)
      .sort(
        (a, b) =>
          Number(b.meta?.relevanceScore || 0) -
          Number(a.meta?.relevanceScore || 0)
      )
      .slice(0, 6)

    if (!scored.length) return null

    return {
      kind: 'creative',
      title: 'Piezas publicitarias encontradas',
      subtitle:
        'Encontré estas creaciones que podrían funcionar para promocionar este recurso.',
      emptyTitle: 'No encontré piezas relacionadas',
      emptyDescription:
        'Podemos crear una nueva pieza visual o preparar copy desde cero.',
      items: scored,
    }
  }

  type CreateContentAnalysisStepStatus =
    | 'pending'
    | 'running'
    | 'done'
    | 'error'

  function buildCreateContentAnalysisSteps(
    activeStep:
      | 'context'
      | 'strategy'
      | 'structure'
      | 'plan'
      | 'completed'
      | 'error'
  ) {
    const order = [
      'context',
      'strategy',
      'structure',
      'plan',
    ] as const

    const activeIndex =
      activeStep === 'completed'
        ? order.length
        : order.indexOf(
            activeStep as (typeof order)[number]
          )

    return [
      {
        id: 'context',
        label: 'Analizando tema, audiencia y objetivo',
        status: resolveAnalysisStepStatus(
          0,
          activeIndex,
          activeStep
        ),
      },
      {
        id: 'strategy',
        label: 'Definiendo el enfoque creativo',
        status: resolveAnalysisStepStatus(
          1,
          activeIndex,
          activeStep
        ),
      },
      {
        id: 'structure',
        label: 'Organizando narrativa, hook y formato',
        status: resolveAnalysisStepStatus(
          2,
          activeIndex,
          activeStep
        ),
      },
      {
        id: 'plan',
        label: 'Construyendo el plan de contenido',
        status: resolveAnalysisStepStatus(
          3,
          activeIndex,
          activeStep
        ),
      },
    ]
  }

  function resolveAnalysisStepStatus(
    index: number,
    activeIndex: number,
    activeStep: string
  ): CreateContentAnalysisStepStatus {
    if (activeStep === 'error') {
      return index === Math.max(0, activeIndex)
        ? 'error'
        : index < activeIndex
          ? 'done'
          : 'pending'
    }

    if (activeStep === 'completed') {
      return 'done'
    }

    if (index < activeIndex) {
      return 'done'
    }

    if (index === activeIndex) {
      return 'running'
    }

    return 'pending'
  }

  function updateCreateContentAnalysisMessage(
    messageId: string,
    patch: {
      content?: string
      activeStep?:
        | 'context'
        | 'strategy'
        | 'structure'
        | 'plan'
        | 'processing_result'
        | 'completed'
        | 'error'
      plan?: any
      direction?: any
      discovery?: any
      error?: string | null
      buttons?: any[]
    }
  ) {
    setMessages((prev) =>
      prev.map((message) => {
        if (message.id !== messageId) {
          return message
        }

        const activeStep =
          patch.activeStep || 'context'

        return {
          ...message,

          content:
            patch.content ??
            message.content,

          meta: {
            ...(message.meta || {}),

            worker: 'content_creator',
            intent:
              activeStep === 'completed'
                ? 'CREATE_CONTENT_PLAN_READY'
                : activeStep === 'error'
                  ? 'CREATE_CONTENT_PLAN_ERROR'
                  : 'CREATE_CONTENT_ANALYZING',

            steps:
              activeStep === 'completed' ||
              activeStep === 'processing_result'
                ? undefined
                : buildCreateContentAnalysisSteps(
                    activeStep
                  ),

            thinking:
              activeStep === 'processing_result'
                ? {
                    label: 'Ejecutando…',
                  }
                : undefined,

            create_content_plan:
              patch.plan
                ? {
                    plan: patch.plan,
                    direction:
                      patch.direction || null,
                    discovery:
                      patch.discovery || null,
                  }
                : (
                    message.meta as any
                  )?.create_content_plan,

            buttons:
              patch.buttons ??
              message.meta?.buttons,

            error:
              patch.error ??
              (
                message.meta as any
              )?.error,
          },
        } as AIMasterChatMessage
      })
    )
  }

  async function sleepCreateContentAnalysis(
    ms: number
  ) {
    await new Promise((resolve) =>
      window.setTimeout(resolve, ms)
    )
  }

  async function loadCreateContentDryRunProject() {
    if (
      createContentDryRunProject &&
      Number(
        createContentDryRunProject?.project_id ||
          createContentDryRunProject?.projectId ||
          createContentDryRunProject?.id ||
          0
      ) === CREATE_CONTENT_DRY_RUN_PROJECT_ID
    ) {
      return createContentDryRunProject
    }

    const res = await fetch(
      `/api/creator-studio/projects/${CREATE_CONTENT_DRY_RUN_PROJECT_ID}`,
      {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      }
    )

    const json =
      await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(
        json?.error ||
          `No se pudo cargar el proyecto ${CREATE_CONTENT_DRY_RUN_PROJECT_ID}.`
      )
    }

    const rawProject =
      json?.project ||
      json?.data ||
      json?.item ||
      null

    if (!rawProject) {
      throw new Error(
        `El proyecto ${CREATE_CONTENT_DRY_RUN_PROJECT_ID} no devolvió información.`
      )
    }

    /*
    * El endpoint devuelve generations fuera de project.
    * Las anexamos para que los resolvers puedan encontrarlas.
    */
    const project = {
      ...rawProject,

      generations:
        Array.isArray(json?.generations)
          ? json.generations
          : Array.isArray(rawProject?.generations)
            ? rawProject.generations
            : [],
    }

    setCreateContentDryRunProject(
      project
    )

    return project
  }

  function resolveDryRunProjectPlan(
    project: any,
    direction: any,
    discovery: any
  ) {
    const brief =
      project?.brief_json ||
      project?.brief ||
      project?.project_json?.brief ||
      project?.projectJson?.brief ||
      {}

    const creativeDirection =
      project?.creative_direction_json ||
      project?.creativeDirection ||
      direction ||
      {}

    return {
      title:
        brief?.title ||
        project?.title ||
        project?.name ||
        'Contenido de demostración',

      objective:
        brief?.objective ||
        brief?.goal ||
        creativeDirection?.contentGoal ||
        discovery?.objective ||
        'Crear contenido de alto impacto',

      audience:
        brief?.audience ||
        discovery?.audience ||
        'Audiencia objetivo',

      platform:
        brief?.platform ||
        creativeDirection?.publishTarget ||
        'TikTok',

      durationTargetSec:
        Number(
          brief?.durationTargetSec ||
            creativeDirection?.durationTargetSec ||
            60
        ),

      language:
        brief?.language ||
        'es',

      goal:
        brief?.goal ||
        creativeDirection?.contentGoal ||
        'viral',

      style:
        brief?.style ||
        creativeDirection?.style ||
        'ugc',

      hook:
        brief?.hook ||
        project?.selected_hook?.hook ||
        project?.selectedHook?.hook ||
        '',

      cta:
        brief?.cta ||
        '',

      reasoning:
        brief?.reasoning ||
        'La dirección creativa fue preparada para captar atención rápidamente, presentar el producto con claridad y cerrar con una acción concreta.',

      productImageUrl:
        brief?.productImageUrl ||
        brief?.referenceImageUrl ||
        null,

      hasProductImage:
        brief?.productImageUrl ||
        brief?.referenceImageUrl
          ? 'yes'
          : creativeDirection?.hasProductImage ||
            'no',
    }
  }

  function resolveDryRunScriptGeneration(
    project: any
  ) {
    const generations =
      Array.isArray(project?.generations)
        ? project.generations
        : []

    return (
      project?.latestByStep?.scripts ||
      project?.generationsByStep?.scripts?.[0] ||
      [...generations]
        .reverse()
        .find(
          (generation: any) =>
            generation?.project_step ===
              'scripts' ||
            generation?.tab ===
              'scripts'
        ) ||
      null
    )
  }

  function resolveDryRunStoryboardGeneration(
    project: any
  ) {
    const generations =
      Array.isArray(project?.generations)
        ? project.generations
        : []

    return (
      project?.latestByStep?.storyboard ||
      project?.generationsByStep?.storyboard?.[0] ||
      generations.find(
        (generation: any) =>
          generation?.project_step === 'storyboard' ||
          generation?.step === 'storyboard' ||
          generation?.tab === 'storyboard'
      ) ||
      null
    )
  }

  async function runCreateContentAnalysisDryRun(
    state: CreateContentDirectionState
  ) {
    const {
      request,
      direction,
      discovery,
      snapshotId,
    } = state

    const loadingMessageId =
      `create-content-analysis-${Date.now()}`

    setMessages((prev) => [
      ...prev,
      {
        id: loadingMessageId,
        role: 'assistant',
        content:
          'Ya tengo la información necesaria. Estoy construyendo la dirección estratégica de tu contenido…',
        timestamp:
          new Date(),
        meta: {
          worker:
            'content_creator',

          intent:
            'CREATE_CONTENT_ANALYZING',

          steps:
            buildCreateContentAnalysisSteps(
              'context'
            ),
        },
      } as AIMasterChatMessage,
    ])

    try {
      await sleepCreateContentAnalysis(
        700
      )

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Estoy analizando el tema, la audiencia y el resultado que quieres conseguir…',

          activeStep:
            'strategy',
        }
      )

      const project =
        await loadCreateContentDryRunProject()

      await sleepCreateContentAnalysis(
        900
      )

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Estoy definiendo el mejor ángulo creativo para que la pieza tenga una intención clara…',

          activeStep:
            'structure',
        }
      )

      await sleepCreateContentAnalysis(
        900
      )

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Ahora estoy organizando el hook, la estructura narrativa y el formato de producción…',

          activeStep:
            'plan',
        }
      )

      await sleepCreateContentAnalysis(
        900
      )

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            `${masterAgentName} está terminando de preparar tu plan…`,

          activeStep:
            'processing_result',
        }
      )

      await sleepCreateContentAnalysis(
        800
      )

      const plan =
        resolveDryRunProjectPlan(
          project,
          direction,
          discovery
        )

      const nextResult = {
        plan,

        projectId:
          CREATE_CONTENT_DRY_RUN_PROJECT_ID,

        creatorStudioProject:
          project,

        generationId:
          null,

        projectJson:
          project?.project_json ||
          project?.projectJson ||
          null,

        creativeDirection:
          direction,

        discovery,

        dryRun:
          true,

        __snapshotMeta: {
          direction,
          discovery,

          chatDirectionStep:
            'completed',

          sceneProgress:
            [],

          dryRun:
            true,
        },
      }

      /*
      * En dry run no guardamos snapshot.
      */
      setPendingCreateContentPlan({
        request,

        direction,

        discovery,

        snapshotId:
          snapshotId ||
          null,

        plan,
      })

      useTaskFlowStore
        .getState()
        .setResult(
          nextResult
        )

      useTaskFlowStore
        .getState()
        .setPhase(
          'proposed_plan'
        )

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Listo. Construí una dirección creativa completa para esta pieza.',

          activeStep:
            'completed',

          plan,

          direction,

          discovery,

          buttons: [
            {
              label:
                'Crear proyecto y avanzar',

              value:
                'create_content_plan_continue',

              emoji:
                '✨',
            },

            {
              label:
                'Cambiar dirección',

              value:
                'create_content_plan_restart',

              emoji:
                '↩️',
            },
          ],
        }
      )

      setPendingCreateContentDirection(
        null
      )

      setUIState(
        'chat'
      )

      setChatViewMode(
        'focus'
      )

      focusInput()
    } catch (error: any) {
      console.error(
        '[AIMasterWidget] create content dry run analysis error',
        error
      )

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'No pude cargar el proyecto de demostración.',

          activeStep:
            'error',

          error:
            error?.message ||
            'No se pudo cargar la demostración.',

          buttons: [
            {
              label:
                'Intentar de nuevo',

              value:
                'create_content_plan_retry',

              emoji:
                '↻',
            },
          ],
        }
      )
    }
  }

  async function runCreateContentAnalysisInChat(
    state: CreateContentDirectionState
  ) {
    if (
      canUseCreateContentDryRun
    ) {
      await runCreateContentAnalysisDryRun(
        state
      )

      return
    }

    const {
      request,
      direction,
      discovery,
      snapshotId,
    } = state

    const loadingMessageId =
      `create-content-analysis-${Date.now()}`

    setMessages((prev) => [
      ...prev,
      {
        id: loadingMessageId,
        role: 'assistant',
        content:
          'Ya tengo la información necesaria. Estoy construyendo la dirección estratégica de tu contenido…',
        timestamp: new Date(),
        meta: {
          worker: 'content_creator',
          intent:
            'CREATE_CONTENT_ANALYZING',

          steps:
            buildCreateContentAnalysisSteps(
              'context'
            ),
        },
      } as AIMasterChatMessage,
    ])

    try {
      await sleepCreateContentAnalysis(500)

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Estoy analizando el tema, la audiencia y el resultado que quieres conseguir…',
          activeStep: 'strategy',
        }
      )

      const planPromise = fetch(
        '/api/task-flows/create-content/plan',
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            sellerId:
              request.payload?.sellerId,

            customerId:
              request.payload?.userId,

            payload: {
              ...(request.payload || {}),

              creativeDirection:
                direction,

              discovery: {
                ...(request.payload
                  ?.discovery || {}),
                ...discovery,

                source:
                  request.payload?.source ||
                  'creator_studio_direct',
              },
            },
          }),
        }
      )

      await sleepCreateContentAnalysis(650)

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Estoy definiendo el mejor ángulo creativo para que la pieza tenga una intención clara…',
          activeStep: 'structure',
        }
      )

      await sleepCreateContentAnalysis(650)

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Ahora estoy organizando el hook, la estructura narrativa y el formato de producción…',
          activeStep: 'plan',
        }
      )

      const response = await planPromise

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Samantha está terminando de preparar tu plan…',
          activeStep: 'processing_result',
        }
      )

      // Deja que React pinte el estado antes de procesar el resultado.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve())
        })
      })

      // Tiempo mínimo visible para evitar un salto brusco.
      await new Promise((resolve) => setTimeout(resolve, 700))

      const json = await response.json().catch(() => null)

      if (
        !response.ok ||
        !json?.ok ||
        !json?.plan
      ) {
        throw new Error(
          json?.error ||
            'No se pudo construir el plan de contenido'
        )
      }

      const plan = json.plan

      const nextResult = {
        plan,
        generationId: null,
        projectJson: null,
        creativeDirection: direction,
        discovery,

        __snapshotMeta: {
          direction,
          discovery,
          chatDirectionStep:
            'completed',
          sceneProgress: [],
        },
      }

      const savedSnapshotId =
        await saveCreateContentPlanSnapshot({
          request,
          direction,
          discovery,
          plan,
          snapshotId,
        })

      setPendingCreateContentPlan({
        request,
        direction,
        discovery,
        snapshotId:
          savedSnapshotId ||
          snapshotId,
        plan,
      })

      useTaskFlowStore
        .getState()
        .setSnapshotId(
          savedSnapshotId ||
            snapshotId
        )

      useTaskFlowStore
        .getState()
        .setResult(nextResult)

      useTaskFlowStore
        .getState()
        .setPhase('proposed_plan')

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Listo. Construí una dirección creativa completa para esta pieza.',
          activeStep: 'completed',
          plan,
          direction,
          discovery,

          buttons: [
            {
              label:
                'Crear proyecto y avanzar',
              value:
                'create_content_plan_continue',
              emoji: '✨',
            },
            {
              label:
                'Generar varias ideas',
              value:
                'create_content_plan_advanced',
              emoji: '🧠',
            },
            {
              label:
                'Cambiar dirección',
              value:
                'create_content_plan_restart',
              emoji: '↩️',
            },
          ],
        }
      )

      setPendingCreateContentDirection(
        null
      )

      setUIState('chat')
      setChatViewMode('focus')

      focusInput()
    } catch (error: any) {
      console.error(
        '[AIMasterWidget] create content analysis error',
        error
      )

      updateCreateContentAnalysisMessage(
        loadingMessageId,
        {
          content:
            'Tuve un problema construyendo el plan. No se consumió el flujo y puedes volver a intentarlo.',
          activeStep: 'error',
          error:
            error?.message ||
            'No se pudo generar el plan',

          buttons: [
            {
              label: 'Intentar de nuevo',
              value:
                'create_content_plan_retry',
              emoji: '↻',
            },
          ],
        }
      )

      focusInput()
    }
  }

  async function saveCreateContentPlanSnapshot(
    params: {
      request: TaskFlowRequest
      direction:
        CreateContentDirectionState['direction']
      discovery:
        CreateContentDirectionState['discovery']
      plan: any
      snapshotId?: number | null
    }
  ) {
    const {
      request,
      direction,
      discovery,
      plan,
      snapshotId,
    } = params

    const res = await fetch(
      '/api/task-flows/create-content/snapshot',
      {
        method: 'POST',
        headers: {
          'Content-Type':
            'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          snapshotId:
            snapshotId || null,

          sellerId:
            request.payload?.sellerId,

          generationId: null,

          phase: 'proposed_plan',

          snapshot: {
            phase: 'proposed_plan',

            request: {
              ...request,
              phase: 'proposed_plan',

              payload: {
                ...(request.payload || {}),
                creativeDirection:
                  direction,
                discovery: {
                  ...(request.payload
                    ?.discovery || {}),
                  ...discovery,
                },
                plan,
              },
            },

            result: {
              plan,
              generationId: null,
              projectJson: null,
              creativeDirection:
                direction,
              discovery,

              __snapshotMeta: {
                direction,
                discovery,
                chatDirectionStep:
                  'completed',
                sceneProgress: [],
              },
            },
          },
        }),
      }
    )

    const json =
      await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(
        json?.error ||
          'No se pudo guardar el plan'
      )
    }

    return Number(
      json?.snapshotId ||
        snapshotId ||
        0
    )
  }

  function buildCreateContentTaskRequest(text: string): TaskFlowRequest {
    return {
      taskType: 'create_content' as TaskFlowRequest['taskType'],
      worker: 'content_creator',
      title: 'Crear contenido con Creator Studio',
      subtitle: 'Voy a proponerte la mejor dirección antes de generar.',
      routeFallback: '/creator-studio?tab=video',
      requiresLLM: false,
      payload: {
        sellerId,
        userId: Number(user?.userId || 0),
        rawPrompt: text,
        source: 'widget',
        pathname,
        brandName: brand?.name || 'Hubee',

        creationMode:
          platformSnapshot?.strategic_profile?.creationMode ||
          platformSnapshot?.strategicProfile?.creationMode ||
          null,

        pageContext: {
          pathname,
          route: pathname,
          pageName: pageContext?.pageName || '',
          role: pageContext?.role || '',
        },

        uiContext: {
          current_url: pathname,
          can_navigate: true,
          can_edit: !!isOwner,
          widget: 'AIMasterWidget',
          conversation_id: conversationId,
        },

        conversationHistory: messages
          .filter((m) => m.id !== 'welcome')
          .filter((m) => {
            const text =
              typeof m.content === 'string' ? m.content : String(m.content || '')

            if (!text.trim()) return false
            if (text.includes('Return ONLY valid JSON')) return false
            if (text.includes('premium creator-studio video workflow')) return false
            if (text.includes('no puedo generar directamente el JSON')) return false
            if (text.includes('mi formato de respuesta estándar es diferente')) return false

            return true
          })
          .slice(-8)
          .map((m) => ({
            role: m.role,
            content: typeof m.content === 'string' ? m.content : String(m.content || ''),
          })),

        immediateContext: {
          currentLesson: platformSnapshot?.current_lesson || null,
          availableTranscripts: Array.isArray(platformSnapshot?.available_transcripts)
            ? platformSnapshot.available_transcripts.slice(0, 3)
            : [],
        },
      },
    }
  }

  function pushCreateContentDirectionQuestion(
    direction: CreateContentDirectionState['direction'],
    discovery: CreateContentDirectionState['discovery']
  ) {
    const question =
      getCreateContentDirectionQuestion(
        direction,
        discovery
      )

    if (!question) {
      return
    }

    const assistantMsg: AIMasterChatMessage = {
      id: `create-content-direction-${Date.now()}`,
      role: 'assistant',
      content: question.content,
      timestamp: new Date(),
      meta: {
        worker: 'content_creator',
        intent: 'CREATE_CONTENT_DIRECTION',
        buttons:
          question.buttons.length > 0
            ? question.buttons
            : undefined,
      },
    }

    setMessages((prev) => [
      ...prev,
      assistantMsg,
    ])

    focusInput()
  }

  async function saveCreateContentDirectionSnapshot(params: {
    request: TaskFlowRequest
    direction: CreateContentDirectionState['direction']
    discovery: CreateContentDirectionState['discovery']
    snapshotId?: number | null
  }) {
    const {
      request,
      direction,
      discovery,
      snapshotId,
    } = params

    const step =
      getCreateContentDirectionStep(direction, discovery)

    const res = await fetch(
      '/api/task-flows/create-content/snapshot',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          snapshotId:
            snapshotId || null,

          sellerId:
            request.payload?.sellerId,

          generationId: null,

          phase: 'creative_direction',

          snapshot: {
            phase: 'creative_direction',
            request,
            result: {
              creativeDirection: direction,
              discovery,

              __snapshotMeta: {
                direction,
                discovery,
                chatDirectionStep: step,
                sceneProgress: [],
              },
            },
          },
        }),
      }
    )

    const json =
      await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(
        json?.error ||
          'No se pudo guardar el avance'
      )
    }

    return Number(
      json?.snapshotId ||
        snapshotId ||
        0
    )
  }

  async function beginCreateContentDirectionChat(params: {
    request: TaskFlowRequest

    direction?: Partial<
      CreateContentDirectionState['direction']
    > | null

    discovery?: Partial<
      CreateContentDirectionState['discovery']
    > | null

    snapshotId?: number | null
  }) {
    const direction =
      normalizeCreateContentDirection(
        params.direction ||
          EMPTY_CREATE_CONTENT_DIRECTION
      )

    const discovery =
      normalizeCreateContentDiscovery(
        params.discovery ||
          params.request.payload?.discovery ||
          EMPTY_CREATE_CONTENT_DISCOVERY
      )

    const nextState: CreateContentDirectionState = {
      request: params.request,
      direction,
      discovery,
      step:
        getCreateContentDirectionStep(
          direction,
          discovery
        ),
      snapshotId:
        Number(params.snapshotId || 0) || null,
    }

    setPendingCreateContentDirection(
      nextState
    )

    useTaskFlowStore
      .getState()
      .setSnapshotId(
        nextState.snapshotId
      )

    pushCreateContentDirectionQuestion(
      direction,
      discovery
    )
  }

  async function completeCreateContentDirectionChat(
    state: CreateContentDirectionState
  ) {
    const {
      request,
      direction,
      discovery,
      snapshotId,
    } = state

    let savedSnapshotId =
      snapshotId

    try {
      savedSnapshotId =
        await saveCreateContentDirectionSnapshot({
          request,
          direction,
          discovery,
          snapshotId,
        })

      if (savedSnapshotId) {
        useTaskFlowStore
          .getState()
          .setSnapshotId(
            savedSnapshotId
          )
      }
    } catch (error) {
      console.error(
        '[AIMasterWidget] final discovery snapshot error',
        error
      )
    }

    const nextState: CreateContentDirectionState = {
      ...state,
      step: 'completed',
      snapshotId:
        savedSnapshotId ||
        snapshotId,
    }

    setPendingCreateContentDirection(
      nextState
    )

    await runCreateContentAnalysisInChat(
      nextState
    )
  }

  async function handleCreateContentDirectionButton(
    rawValue: string
  ) {
    const parsed =
      parseCreateContentDirectionButton(
        rawValue
      )

    if (
      !parsed ||
      !pendingCreateContentDirection
    ) {
      return false
    }

    const currentStep =
      getCreateContentDirectionStep(
        pendingCreateContentDirection.direction,
        pendingCreateContentDirection.discovery
      )

    if (parsed.step !== currentStep) {
      console.warn(
        '[AIMasterWidget] stale creative direction button',
        {
          expected: currentStep,
          received: parsed.step,
        }
      )

      return true
    }

    const isDiscoveryStep =
      parsed.step.startsWith('discovery_')

    const nextDirection =
      isDiscoveryStep
        ? pendingCreateContentDirection.direction
        : applyCreateContentDirectionAnswer({
            direction:
              pendingCreateContentDirection.direction,
            step: parsed.step,
            value: parsed.value,
          })

    const nextDiscovery =
      isDiscoveryStep
        ? applyCreateContentDiscoveryAnswer({
            discovery:
              pendingCreateContentDirection.discovery,
            step: parsed.step,
            value: parsed.value,
          })
        : pendingCreateContentDirection.discovery

    const nextStep =
      getCreateContentDirectionStep(
        nextDirection,
        nextDiscovery
      )

    const userMsg: AIMasterChatMessage = {
      id: `create-content-answer-${Date.now()}`,
      role: 'user',
      content:
        getCreateContentDirectionAnswerLabel(
          parsed.step,
          parsed.value,
          pendingCreateContentDirection.direction
        ),
      timestamp: new Date(),
      meta: {
        intent: 'CREATE_CONTENT_DIRECTION_ANSWER',
      },
    }

    setMessages((prev) => [
      ...prev,
      userMsg,
    ])

    let savedSnapshotId =
      pendingCreateContentDirection.snapshotId

    try {
      savedSnapshotId =
        await saveCreateContentDirectionSnapshot({
          request:
            pendingCreateContentDirection.request,
          direction: nextDirection,
          discovery: nextDiscovery,
          snapshotId:
            pendingCreateContentDirection.snapshotId,
        })

      if (savedSnapshotId) {
        useTaskFlowStore
          .getState()
          .setSnapshotId(
            savedSnapshotId
          )
      }
    } catch (error) {
      console.error(
        '[AIMasterWidget] save creative direction snapshot error',
        error
      )
    }

    const nextState: CreateContentDirectionState = {
      ...pendingCreateContentDirection,
      direction: nextDirection,
      discovery: nextDiscovery,
      step: nextStep,
      snapshotId:
        savedSnapshotId ||
        pendingCreateContentDirection.snapshotId,
    }

    setPendingCreateContentDirection(
      nextState
    )

    if (nextStep === 'completed') {
      await completeCreateContentDirectionChat(
        nextState
      )

      return true
    }

    pushCreateContentDirectionQuestion(
      nextDirection,
      nextDiscovery
    )

    return true
  }

  async function handleCreateContentDiscoveryText(
    rawValue: string
  ) {
    const pending =
      pendingCreateContentDirection

    if (!pending) {
      return false
    }

    const step =
      getCreateContentDirectionStep(
        pending.direction,
        pending.discovery
      )

    if (
      ![
        'discovery_topic',
        'discovery_audience',
        'discovery_action',
      ].includes(step)
    ) {
      return false
    }

    const cleanValue =
      String(rawValue || '').trim()

    if (!cleanValue) {
      return true
    }

    const nextDiscovery =
      applyCreateContentDiscoveryAnswer({
        discovery: pending.discovery,
        step,
        value: cleanValue,
      })

    const nextStep =
      getCreateContentDirectionStep(
        pending.direction,
        nextDiscovery
      )

    setMessages((prev) => [
      ...prev,
      {
        id: `create-content-discovery-answer-${Date.now()}`,
        role: 'user',
        content: cleanValue,
        timestamp: new Date(),
        meta: {
          intent:
            'CREATE_CONTENT_DISCOVERY_ANSWER',
        },
      },
    ])

    let savedSnapshotId =
      pending.snapshotId

    try {
      savedSnapshotId =
        await saveCreateContentDirectionSnapshot({
          request: pending.request,
          direction: pending.direction,
          discovery: nextDiscovery,
          snapshotId:
            pending.snapshotId,
        })

      if (savedSnapshotId) {
        useTaskFlowStore
          .getState()
          .setSnapshotId(
            savedSnapshotId
          )
      }
    } catch (error) {
      console.error(
        '[AIMasterWidget] save discovery snapshot error',
        error
      )
    }

    const nextState: CreateContentDirectionState = {
      ...pending,
      discovery: nextDiscovery,
      step: nextStep,
      snapshotId:
        savedSnapshotId ||
        pending.snapshotId,
    }

    setPendingCreateContentDirection(
      nextState
    )

    if (nextStep === 'completed') {
      await completeCreateContentDirectionChat(
        nextState
      )

      return true
    }

    pushCreateContentDirectionQuestion(
      pending.direction,
      nextDiscovery
    )

    return true
  }

  function getCreateContentDirectionAnswerLabel(
    step: string,
    value: string,
    direction: CreateContentDirectionState['direction'],
    discovery: CreateContentDirectionState['discovery']
  ) {
    const question =
      getCreateContentDirectionQuestion(
        direction,
        discovery
      )

    const button =
      question?.buttons?.find(
        (item) =>
          item.value ===
          `create_content_direction:${step}:${value}`
      )

    return button?.label || value
  }

  function isFlowIntent(intent: LightweightIntent) {
    return (
      intent === 'open_create_content_flow' ||
      intent === 'open_edit_product_flow' ||
      intent === 'open_build_funnel_flow' ||
      intent === 'open_create_portrait_flow' ||
      intent === 'open_create_flyer_flow' ||
      intent === 'open_create_publication_flow' ||
      intent === 'open_create_ads_campaign_flow'
    )
  }

  function getWorkerForLightweightIntent(intent: LightweightIntent) {
    switch (intent) {
      case 'open_create_content_flow':
        return 'content_creator'
      case 'open_edit_product_flow':
        return 'platform_navigator'
      case 'open_build_funnel_flow':
        return 'funnel_worker'
      case 'open_create_portrait_flow':
      case 'open_create_flyer_flow':
      case 'open_create_publication_flow':
      case 'open_latest_publication_carousel':
        return 'creator_studio_worker'
      case 'open_create_ads_campaign_flow':
        return 'ads_worker'        
      default:
        return 'platform_navigator'
    }
  }

  async function tryHandlePlatformSupport(text: string) {
    const message = String(text || '').trim()
    if (!message || !sellerId) return false

    try {
      const res = await fetch('/api/ai/platform-support/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({
          message,
          sellerId,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok || !json?.match) return false

      const match = json.match
      const confidence = Number(json.confidence || match.confidence || 0)

      // Evita falsos positivos. Solo responde gratis si el match es fuerte.
      if (confidence < 0.7) return false

      const answer = String(match.answer || '').trim()
      const route = String(match.route || '').trim()
      const buttonLabel = String(match.button_label || '').trim() || 'Abrir'

      if (!answer) return false

      setMessages((prev) => [
        ...prev,
        {
          id: `support-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date(),
          meta: {
            intent: 'PLATFORM',
            worker: 'platform_support',
            navigation: route
              ? {
                  route,
                  params: {},
                }
              : null,
            buttons: [
              ...(route
                ? [
                    {
                      label: buttonLabel,
                      value: route,
                      action: 'navigate',
                      route,
                    },
                  ]
                : []),
              {
                label: 'No resolvió mi duda',
                value: 'open_support_ticket',
                action: 'open_support_ticket',
                emoji: '🎫',
              },
            ],
            supportMatch: match,
            executed_actions: [
              {
                type: 'platform_support_match',
                tool: 'platform_support_search',
                ok: true,
                title: match.title,
                confidence,
              },
            ],
          },
        },
      ])

      if (route) {
        // Opcional: no navega automático, solo muestra botón.
        // Si quieres navegar automático para frases como "llévame a", lo hacemos en el siguiente paso.
      }

      return true
    } catch (err) {
      console.warn('[AIMasterWidget] platform support search failed', err)
      return false
    }
  }

  function parsePublicationPlatform(text: string): PublicationPlatform | null {
    const value = String(text || '').toLowerCase()

    if (value.includes('instagram') || value === 'publication_platform:instagram') {
      return 'instagram'
    }

    if (value.includes('facebook') || value === 'publication_platform:facebook') {
      return 'facebook'
    }

    if (
      value.includes('tiktok') ||
      value.includes('tik tok') ||
      value === 'publication_platform:tiktok'
    ) {
      return 'tiktok'
    }

    if (
      value === 'publication_platform:multi' ||
      value.includes('multi') ||
      value.includes('multi-plataforma') ||
      value.includes('multiplataforma') ||
      value.includes('varias plataformas') ||
      value.includes('todas las plataformas') ||
      value.includes('todas las redes')
    ) {
      return 'multi'
    }

    return null
  }

  function parsePublicationFormat(text: string): PublicationFormat | null {
    const value = String(text || '').toLowerCase()

    if (
      value === 'publication_format:text' ||
      value.includes('texto') ||
      value.includes('caption') ||
      value.includes('copy')
    ) {
      return 'text'
    }

    if (
      value === 'publication_format:image' ||
      value.includes('imagen') ||
      value.includes('publicitaria') ||
      value.includes('promocional')
    ) {
      return 'image'
    }

    if (
      value === 'publication_format:carousel' ||
      value.includes('carrusel') ||
      value.includes('carousel') ||
      value.includes('slides')
    ) {
      return 'carousel'
    }

    if (
      value === 'publication_format:mixed' ||
      value.includes('mixto') ||
      value.includes('imagen y caption') ||
      value.includes('imagen con texto')
    ) {
      return 'mixed'
    }

    return null
  }

  function parseReferenceAnswer(text: string): boolean | null {
    const value = String(text || '').toLowerCase()

    if (
      value === 'publication_reference:yes' ||
      /\b(si|sí|yes|claro|tengo|con imagen|usar imagen|producto)\b/.test(value)
    ) {
      return true
    }

    if (
      value === 'publication_reference:no' ||
      /\b(no|sin imagen|no tengo|continuar sin imagen|no usar)\b/.test(value)
    ) {
      return false
    }

    return null
  }

  function inferPublicationStyle(next: PendingPublicationDiscovery): PublicationVisualStyle {
    if (next.sector === 'beauty_skincare') return 'minimal_luxury'
    if (next.sector === 'ai_technology') return 'futuristic_holographic'
    if (next.sector === 'business_corporate') return 'corporate_clean'
    if (next.sector === 'fitness_wellness') return 'bold_viral'
    if (next.intent === 'authority') return 'editorial_magazine'
    if (next.intent === 'offer') return 'gradient_modern'
    if (next.intent === 'story') return 'clean_white'
    return 'minimal_luxury'
  }

  function getNextPublicationStep(next: PendingPublicationDiscovery): PublicationDiscoveryStep | null {
    if (!next.platform) return 'platform'
    if (!next.format) return 'format'

    /**
     * Texto: solo necesita instrucción + contexto estratégico.
     * No necesita intent/sector/template/reference.
     */
    if (next.format === 'text') {
      if (!next.instruction?.trim()) return 'instruction'
      return null
    }

    /**
     * Imagen libre/profesional: no usa template.
     * No preguntes diseño, no preguntes sector obligatorio.
     * La dirección creativa se resuelve con strategic profile + prompt.
     */
    if (next.format === 'image') {
      if (!next.instruction?.trim()) return 'instruction'
      return null
    }

    /**
     * Carrusel: sí usa estructura, intención, sector y template.
     */
    if (next.format === 'carousel') {
      if (!next.intent) return 'intent'
      if (!next.sector) return 'sector'
      if (!next.templateId) return 'template'
      if (typeof next.wantsReferenceImage !== 'boolean') return 'reference'
      if (!next.instruction?.trim()) return 'instruction'
      return null
    }

    /**
     * Mixed por ahora lo tratamos como carrusel/template hasta definirlo mejor.
     */
    if (next.format === 'mixed') {
      if (!next.intent) return 'intent'
      if (!next.sector) return 'sector'
      if (!next.templateId) return 'template'
      if (typeof next.wantsReferenceImage !== 'boolean') return 'reference'
      if (!next.instruction?.trim()) return 'instruction'
      return null
    }

    return null
  }

  function shouldAskVisualStyle(next: PendingPublicationDiscovery) {
    // Para sectores claros, Samantha puede escoger sola.
    if (next.sector === 'beauty_skincare') return false
    if (next.sector === 'business_corporate') return false
    if (next.sector === 'ai_technology') return false

    // Para creator/personal brand y coaching sí conviene preguntar porque puede variar mucho.
    if (next.sector === 'creator_personal_brand') return true
    if (next.sector === 'coaching_education') return true

    return false
  }

  function publicationPlatformLabel(platform?: PublicationPlatform) {
    switch (platform) {
      case 'instagram':
        return 'Instagram'
      case 'facebook':
        return 'Facebook'
      case 'tiktok':
        return 'TikTok'
      case 'multi':
        return 'Multi-plataforma'
      default:
        return ''
    }
  }

  function publicationFormatLabel(format?: PublicationFormat) {
    switch (format) {
      case 'text':
        return 'Texto / caption'
      case 'image':
        return 'Imagen promocional'
      case 'carousel':
        return 'Carrusel'
      case 'mixed':
        return 'Post mixto'
      default:
        return ''
    }
  }

  function publicationIntentLabel(intent?: string) {
    const labels: Record<string, string> = {
      announcement: 'Anuncio / lanzamiento / evento',
      educational: 'Educativo / tips / pasos',
      authority: 'Autoridad / opinión / thought leadership',
      offer: 'Oferta / venta / promoción',
      story: 'Historia / transformación',
      community: 'Comunidad / participación',
      testimonial: 'Testimonio / caso de éxito',
      carousel: 'Carrusel educativo',
    }

    return intent ? labels[intent] || intent : 'Automática'
  }

  function publicationSectorLabel(sector?: string) {
    const labels: Record<string, string> = {
      creator_personal_brand: 'Creator / marca personal',
      ai_technology: 'IA / tecnología',
      coaching_education: 'Coaching / educación',
      fitness_wellness: 'Fitness / wellness',
      beauty_skincare: 'Beauty / skincare',
      business_corporate: 'Business / corporativo',
      network_marketing: 'Network marketing',
      events_webinars: 'Eventos / webinars',
      digital_products: 'Productos digitales',
      local_business: 'Negocio local',
    }

    return sector ? labels[sector] || sector : 'Automático'
  }

  function publicationVisualStyleLabel(style?: string) {
    const labels: Record<string, string> = {
      premium_dark: 'Premium oscuro',
      clean_white: 'Limpio blanco',
      gradient_modern: 'Moderno con degradados',
      editorial_magazine: 'Editorial revista',
      futuristic_holographic: 'Futurista / holográfico',
      bold_viral: 'Bold viral',
      minimal_luxury: 'Minimal luxury',
      ugc_native: 'UGC nativo',
      corporate_clean: 'Corporativo limpio',
      playful_creator: 'Playful creator',
    }

    return style ? labels[style] || style : 'Automática'
  }

  function buildPublicationInstruction(state: PendingPublicationDiscovery) {
    const base = String(state.instruction || state.originalRequest || '').trim()

    return [
      `Crea una publicación para redes sociales.`,
      `Plataforma: ${publicationPlatformLabel(state.platform)}.`,
      `Formato: ${publicationFormatLabel(state.format)}.`,
      `Intención: ${publicationIntentLabel(state.intent)}.`,
      `Sector/nicho: ${publicationSectorLabel(state.sector)}.`,
      `Dirección visual: ${publicationVisualStyleLabel(state.visualStyle)}.`,
      state.templateId ? `Plantilla sugerida: ${state.templateId}.` : null,
      state.themeId ? `Tema visual sugerido: ${state.themeId}.` : null,
      state.wantsReferenceImage
        ? 'El usuario quiere usar una imagen de referencia o producto; debe subirla en el panel antes de generar.'
        : 'No usar imagen de referencia a menos que el usuario la agregue después.',
      `Instrucción del usuario: ${base}`,
      `Deducir audiencia, objetivo, tono, CTA, ángulo creativo y estructura del contenido sin pedir más campos.`,
      `No generar texto dentro de imágenes. El texto debe quedar como capa editable en la plantilla.`,
    ]
      .filter(Boolean)
      .join('\n')
  }

  function buildPublicationControlsFromDiscovery(
    state: PendingPublicationDiscovery,
    enrichedInstruction: string
  ) {
    const resolvedTemplate = resolvePublicationTemplateFromDiscovery(state)

    return {
      platform: state.platform || 'instagram',
      format: state.format || 'image',

      intent: state.intent || 'announcement',
      sector: state.sector || 'creator_personal_brand',
      visualStyle: state.visualStyle || resolvedTemplate.visualStyle,

      templateId: state.templateId || resolvedTemplate.templateId,
      themeId: state.themeId || resolvedTemplate.themeId,

      instruction: enrichedInstruction,
      topic: enrichedInstruction,
      brief: enrichedInstruction,

      referenceImage: null,
      referenceImageName: '',
      source: 'samantha',
      needsReferenceImage: Boolean(state.wantsReferenceImage),
    }
  }

  function getPublicationQuestionMessage(step: PublicationDiscoveryStep) {
    if (step === 'platform') {
      return {
        content:
          'Perfecto. Vamos a crear una publicación para redes.\n\nPrimero dime: ¿en qué plataforma quieres publicar?',
        buttons: [
          { label: 'Instagram', value: 'publication_platform:instagram', emoji: '📸' },
          { label: 'Facebook', value: 'publication_platform:facebook', emoji: '🔵' },
          { label: 'TikTok', value: 'publication_platform:tiktok', emoji: '🎵' },
          { label: 'Multi', value: 'publication_platform:multi', emoji: '🌐' },
        ],
      }
    }

    if (step === 'format') {
      return {
        content:
          '¿Qué formato quieres crear?',
        buttons: [
          { label: 'Texto', value: 'publication_format:text', emoji: '✍️' },
          { label: 'Imagen promocional', value: 'publication_format:image', emoji: '🖼️' },
          { label: 'Carrusel', value: 'publication_format:carousel', emoji: '📚' },
          { label: 'Post mixto', value: 'publication_format:mixed', emoji: '🧩' },
        ],
      }
    }

    if (step === 'intent') {
      return {
        content: '¿Cuál es el objetivo principal de esta publicación?',
        buttons: [
          { label: 'Anunciar algo', value: 'publication_intent:announcement', emoji: '📣' },
          { label: 'Educar / enseñar', value: 'publication_intent:educational', emoji: '🎓' },
          { label: 'Autoridad / opinión', value: 'publication_intent:authority', emoji: '🧠' },
          { label: 'Vender / promocionar', value: 'publication_intent:offer', emoji: '💸' },
          { label: 'Historia / transformación', value: 'publication_intent:story', emoji: '✨' },
          { label: 'Activar comunidad', value: 'publication_intent:community', emoji: '👥' },
          { label: 'Testimonio / resultado', value: 'publication_intent:testimonial', emoji: '🏆' },
        ],
      }
    }

    if (step === 'sector') {
      return {
        content: '¿Para qué tipo de negocio o nicho es?',
        buttons: [
          { label: 'Beauty / Skincare', value: 'publication_sector:beauty_skincare', emoji: '🌿' },
          /*{ label: 'Marca personal', value: 'publication_sector:creator_personal_brand', emoji: '🙋' },
          { label: 'IA / Tecnología', value: 'publication_sector:ai_technology', emoji: '🤖' },
          { label: 'Coaching / Educación', value: 'publication_sector:coaching_education', emoji: '🎓' },
          { label: 'Fitness / Wellness', value: 'publication_sector:fitness_wellness', emoji: '💪' },
          { label: 'Corporativo', value: 'publication_sector:business_corporate', emoji: '🏢' },
          { label: 'Network Marketing', value: 'publication_sector:network_marketing', emoji: '🤝' },
          { label: 'Evento / Webinar', value: 'publication_sector:events_webinars', emoji: '🎤' },
          { label: 'Producto digital', value: 'publication_sector:digital_products', emoji: '📦' },
          { label: 'Negocio local', value: 'publication_sector:local_business', emoji: '📍' },*/
        ],
      }
    }

    if (step === 'template') {
      return {
        content:
          'Ahora elige un diseño para tu publicación. Te muestro opciones recomendadas según el objetivo y el nicho.',
        buttons: [],
      }
    }

    if (step === 'reference') {
      return {
        content:
          '¿Quieres usar una imagen de referencia o producto?\n\nSi eliges “Sí”, te llevaré a Publicaciones y ahí podrás subirla en el panel lateral antes de generar.',
        buttons: [
          { label: 'Sí, usar imagen', value: 'publication_reference:yes', emoji: '🖼️' },
          { label: 'No, continuar sin imagen', value: 'publication_reference:no', emoji: '➡️' },
        ],
      }
    }

    return {
      content:
        'Ahora dime en una frase qué quieres comunicar, vender o promocionar.',
      buttons: [],
    }
  }

  function pushPublicationTemplateSelector(state: PendingPublicationDiscovery, userMsg?: AIMasterChatMessage) {
    const templates = getPublicationTemplateOptions({
      format: state.format,
      intent: state.intent,
      sector: state.sector,
    })

    setMessages((prev) => [
      ...prev,
      ...(userMsg ? [userMsg] : []),
      {
        id: `publication-template-selector-${Date.now()}`,
        role: 'assistant',
        content:
          'Ahora elige un diseño para tu publicación. Te muestro opciones recomendadas según el objetivo y el nicho.',
        timestamp: new Date(),
        meta: {
          worker: 'creator_studio_worker',
          intent: 'PUBLICATION_TEMPLATE_SELECTION',
          template_selector: {
            format: state.format,
            intent: state.intent,
            sector: state.sector,
            templates,
          },
        },
      } as AIMasterChatMessage,
    ])
  }

  function pushPublicationQuestion(step: PublicationDiscoveryStep) {
    const question = getPublicationQuestionMessage(step)

    setMessages((prev) => [
      ...prev,
      {
        id: `publication-question-${Date.now()}`,
        role: 'assistant',
        content: question.content,
        timestamp: new Date(),
        meta: {
          worker: 'creator_studio_worker',
          intent: 'PUBLICATIONS_DISCOVERY',
          buttons: question.buttons,
        },
      },
    ])

    focusInput()
  }

  function openCreatorStudioPublications(
    state: PendingPublicationDiscovery,
    enrichedInstruction: string
  ) {
    const controls = buildPublicationControlsFromDiscovery(
      state,
      enrichedInstruction
    )

    try {
      window.sessionStorage.setItem(
        'samantha:publication_controls',
        JSON.stringify(controls)
      )
    } catch {}

    window.dispatchEvent(
      new CustomEvent('samantha:open-publications', {
        detail: controls,
      })
    )

    const params = new URLSearchParams()
    params.set('tab', 'publications')
    params.set('source', 'samantha')
    params.set('platform', controls.platform)
    params.set('format', controls.format)
    params.set('instruction', controls.instruction)

    if (controls.needsReferenceImage) {
      params.set('needsReferenceImage', '1')
    }

    setChatViewMode('dock')
    hideDockForExternalSurface()
    router.push(`/creator-studio?${params.toString()}`)
  }
  function adsGoalLabel(goal?: AdsCampaignGoal) {
    switch (goal) {
      case 'leads':
        return 'Conseguir leads'
      case 'sales':
        return 'Vender'
      case 'traffic':
        return 'Llevar tráfico'
      case 'whatsapp':
        return 'Generar mensajes por WhatsApp'
      default:
        return ''
    }
  }

  function adsCreativeModeLabel(mode?: AdsCampaignCreativeMode) {
    switch (mode) {
      case 'existing':
        return 'Usar pieza existente'
      case 'new_visual':
        return 'Crear pieza visual'
      case 'new_copy':
        return 'Crear copy'
      case 'new_both':
        return 'Crear visual + copy'
      default:
        return ''
    }
  }

  function parseAdsGoal(text: string): AdsCampaignGoal | null {
    const raw = String(text || '').trim().toLowerCase()
    const value = normalizeSmallReply(text)
    const haystack = `${raw} ${value}`

    if (
      raw === 'ads_goal:leads' ||
      haystack.includes('ads goal leads') ||
      haystack.includes('lead') ||
      haystack.includes('prospecto') ||
      haystack.includes('registro') ||
      haystack.includes('contacto')
    ) {
      return 'leads'
    }

    if (
      raw === 'ads_goal:sales' ||
      haystack.includes('ads goal sales') ||
      haystack.includes('sales') ||
      haystack.includes('venta') ||
      haystack.includes('ventas') ||
      haystack.includes('vender') ||
      haystack.includes('compras') ||
      haystack.includes('checkout')
    ) {
      return 'sales'
    }

    if (
      raw === 'ads_goal:traffic' ||
      haystack.includes('ads goal traffic') ||
      haystack.includes('traffic') ||
      haystack.includes('trafico') ||
      haystack.includes('tráfico') ||
      haystack.includes('visitas') ||
      haystack.includes('clics') ||
      haystack.includes('clicks')
    ) {
      return 'traffic'
    }

    if (
      raw === 'ads_goal:whatsapp' ||
      haystack.includes('ads goal whatsapp') ||
      haystack.includes('whatsapp') ||
      haystack.includes('mensaje') ||
      haystack.includes('mensajes') ||
      haystack.includes('dm') ||
      haystack.includes('inbox')
    ) {
      return 'whatsapp'
    }

    return null
  }

  function parseAdsCreativeMode(text: string): AdsCampaignCreativeMode | null {
    const value = normalizeSmallReply(text)

    if (
      value === 'ads_creative:existing' ||
      value.includes('existente') ||
      value.includes('usar una') ||
      value.includes('usar pieza') ||
      value.includes('ya creada')
    ) {
      return 'existing'
    }

    if (
      value === 'ads_creative:new_visual' ||
      value.includes('visual') ||
      value.includes('imagen') ||
      value.includes('creativo')
    ) {
      return 'new_visual'
    }

    if (
      value === 'ads_creative:new_copy' ||
      value.includes('copy') ||
      value.includes('texto') ||
      value.includes('caption')
    ) {
      return 'new_copy'
    }

    if (
      value === 'ads_creative:new_both' ||
      value.includes('ambos') ||
      value.includes('todo') ||
      value.includes('visual y copy') ||
      value.includes('imagen y copy') ||
      value.includes('imagen y texto')
    ) {
      return 'new_both'
    }

    return null
  }

  function parseAdsBudgetDaily(text: string): number | null {
    const value = normalizeSmallReply(text)

    if (value === 'ads_budget:100') return 100
    if (value === 'ads_budget:150') return 150
    if (value === 'ads_budget:300') return 300
    if (value === 'ads_budget:500') return 500

    const match = value.match(/(\d+(?:\.\d+)?)/)
    if (!match) return null

    const amount = Number(match[1])
    if (!Number.isFinite(amount) || amount <= 0) return null

    return amount
  }

  function parseAdsSource(text: string): {
    sourceType: AdsCampaignSourceType
    sourceId?: string
    sourceName?: string
  } | null {
    const raw = String(text || '').trim()
    const value = normalizeSmallReply(raw)

    if (raw.startsWith('ads_source:funnel:')) {
      const id = raw.replace('ads_source:funnel:', '').trim()
      const funnel = Array.isArray(funnels)
        ? funnels.find((f: any) => String(f.id) === String(id))
        : null

      return {
        sourceType: 'funnel',
        sourceId: String(id),
        sourceName: String(funnel?.name || `Embudo #${id}`),
      }
    }

    if (value === 'ads_source:selected_funnel' && selectedFunnel) {
      return {
        sourceType: 'funnel',
        sourceId: String(selectedFunnel.id),
        sourceName: String(selectedFunnel.name || 'Embudo actual'),
      }
    }

    if (
      value.includes('este embudo') ||
      value.includes('embudo actual') ||
      value.includes('este funnel') ||
      value.includes('funnel actual')
    ) {
      if (selectedFunnel) {
        return {
          sourceType: 'funnel',
          sourceId: String(selectedFunnel.id),
          sourceName: String(selectedFunnel.name || 'Embudo actual'),
        }
      }
    }

    if (value.includes('embudo') || value.includes('funnel')) {
      return null
    }

    if (value.includes('producto')) {
      return {
        sourceType: 'product',
        sourceName: 'Producto',
      }
    }

    if (value.includes('reto') || value.includes('challenge')) {
      return {
        sourceType: 'challenge',
        sourceName: 'Reto',
      }
    }

    if (value.includes('curso')) {
      return {
        sourceType: 'course',
        sourceName: 'Curso',
      }
    }

    if (value.includes('comunidad')) {
      return {
        sourceType: 'community',
        sourceName: 'Comunidad',
      }
    }

    if (value === 'ads_source:manual' || value.includes('otra cosa')) {
      return {
        sourceType: 'manual',
        sourceName: 'Campaña personalizada',
      }
    }

    return null
  }

  function getNextAdsCampaignStep(
    state: Partial<PendingAdsCampaignDiscovery>
  ): AdsCampaignDiscoveryStep | null {
    if (!state.sourceType) return 'source'
    if (!state.goal) return 'goal'
    if (!state.creativeMode) return 'creative'
    if (!state.budgetDaily) return 'budget'
    return 'review'
  }

  function getAdsQuestionMessage(
    step: AdsCampaignDiscoveryStep,
    state?: Partial<PendingAdsCampaignDiscovery>
  ) {
    if (step === 'source') {
      const funnelResults = normalizeFunnelsForChatEntityResults(
        Array.isArray(funnels) ? funnels : []
      )

      return {
        content:
          funnelResults.items.length > 0
            ? 'Perfecto. Vamos a crear una campaña. Primero elige qué embudo quieres promocionar.'
            : 'Perfecto. Vamos a crear una campaña. No encontré embudos disponibles, pero puedes elegir otro tipo de recurso.',
        entityResults: funnelResults.items.length > 0 ? funnelResults : null,
        buttons: [
          { label: 'Producto', value: 'ads_source:product', emoji: '🛒' },
          { label: 'Reto', value: 'ads_source:challenge', emoji: '🏆' },
          { label: 'Curso', value: 'ads_source:course', emoji: '🎓' },
          { label: 'Comunidad', value: 'ads_source:community', emoji: '👥' },
          { label: 'Otra cosa', value: 'ads_source:manual', emoji: '✨' },
        ],
      }
    }

    if (step === 'goal') {
      return {
        content:
          `Perfecto. Vamos a promocionar: **${state?.sourceName || 'tu oferta'}**.\n\n¿Qué objetivo quieres priorizar?`,
        buttons: [
          { label: 'Conseguir leads', value: 'ads_goal:leads', emoji: '🎯' },
          { label: 'Vender', value: 'ads_goal:sales', emoji: '💳' },
          { label: 'Llevar tráfico', value: 'ads_goal:traffic', emoji: '🚀' },
          { label: 'Mensajes WhatsApp', value: 'ads_goal:whatsapp', emoji: '💬' },
        ],
      }
    }

    if (step === 'creative') {
      const creativeCandidates =
        state?.creativeCandidates || findCreativeCandidatesForAds(state || {})

      const buttons = [
        ...(creativeCandidates?.items?.length
          ? [
              {
                label: 'Usar pieza existente',
                value: 'ads_creative:existing',
                emoji: '🖼️',
                description: 'Elegir una publicación o creativo ya creado',
              },
            ]
          : []),
        {
          label: 'Crear pieza visual',
          value: 'ads_creative:new_visual',
          emoji: '🎨',
          description: 'Diseñar una imagen publicitaria nueva',
        },
        {
          label: 'Crear copy',
          value: 'ads_creative:new_copy',
          emoji: '✍️',
          description: 'Crear texto, headline y CTA',
        },
        {
          label: 'Crear visual + copy',
          value: 'ads_creative:new_both',
          emoji: '⚡',
          description: 'Preparar anuncio completo',
        },
      ]

      return {
        content: creativeCandidates?.items?.length
          ? 'Encontré algunas piezas que podrían servir para esta campaña. Puedes usar una existente o crear una nueva.'
          : 'No encontré piezas conectadas claramente con este recurso. Podemos crear una nueva pieza para la campaña.',
        entityResults: null,
        buttons,
      }
    }

    if (step === 'budget') {
      return {
        content:
          '¿Con qué presupuesto diario quieres iniciar?\n\nPara pruebas iniciales, yo usaría algo controlado. Por ejemplo: $150 MXN/día durante 5 días.',
        buttons: [
          { label: '$100/día', value: 'ads_budget:100', emoji: '🧪' },
          { label: '$150/día', value: 'ads_budget:150', emoji: '🎯' },
          { label: '$300/día', value: 'ads_budget:300', emoji: '📈' },
          { label: '$500/día', value: 'ads_budget:500', emoji: '🔥' },
        ],
      }
    }

    return {
      content: 'Voy a preparar el resumen de campaña.',
      buttons: [],
    }
  }

  function pushAdsQuestion(
    step: AdsCampaignDiscoveryStep,
    state?: Partial<PendingAdsCampaignDiscovery>
  ) {
    const question = getAdsQuestionMessage(step, state)

    setMessages((prev) => [
      ...prev,
      {
        id: `ads-question-${Date.now()}`,
        role: 'assistant',
        content: question.content,
        timestamp: new Date(),
        meta: {
          worker: 'ads_worker',
          intent: 'ADS_CAMPAIGN_DISCOVERY',
          buttons: question.buttons,
          entity_results: question.entityResults || null,
        },
      },
    ])

    focusInput()
  }

  function buildAdsCampaignReviewMessage(state: PendingAdsCampaignDiscovery) {
    const sourceName = state.sourceName || 'Campaña personalizada'
    const goal = adsGoalLabel(state.goal)
    const creative = adsCreativeModeLabel(state.creativeMode)
    const budget = Number(state.budgetDaily || 0)

    return [
      `Listo. Preparé una campaña simulada para revisar antes de conectarla a Meta Ads.`,
      ``,
      `**Campaña:** ${sourceName} · ${goal}`,
      ``,
      `**Objetivo:** ${goal}`,
      `**Pieza:** ${creative}`,
      `**Presupuesto sugerido:** $${budget.toLocaleString('es-MX')} MXN/día`,
      `**Duración inicial sugerida:** 5 días`,
      ``,
      `**Audiencia inicial sugerida:**`,
      `Personas interesadas en emprendimiento digital, creación de contenido, educación online, comunidad y soluciones relacionadas con tu oferta.`,
      ``,
      `**Ángulo recomendado:**`,
      `Presentar el problema principal, mostrar la transformación prometida y llevar al usuario al CTA del embudo o recurso seleccionado.`,
      ``,
      `Por ahora esto está en modo simulación. El siguiente paso será conectar Meta Ads para crear la campaña real en estado pausado antes de publicarla.`,
    ].join('\n')
  }

  function startAdsCampaignDiscovery(rawText: string) {
    const text = String(rawText || '').trim()

    const initialSource = parseAdsSource(text)
    const initialGoal = parseAdsGoal(text)

    const initial: PendingAdsCampaignDiscovery = {
      step: 'source',
      originalRequest: text,
      sourceType: initialSource?.sourceType,
      sourceId: initialSource?.sourceId,
      sourceName: initialSource?.sourceName,
      goal: initialGoal || undefined,
    }

    const shouldForceSourcePicker =
      /\b(promocionar|promover|anunciar|ads|campaña|campana)\b/.test(
        normalizeSmallReply(text)
      ) &&
      /\b(embudo|funnel)\b/.test(normalizeSmallReply(text)) &&
      !/\b(este embudo|embudo actual|este funnel|funnel actual)\b/.test(
        normalizeSmallReply(text)
      )

    const nextStep = shouldForceSourcePicker
      ? 'source'
      : getNextAdsCampaignStep(initial) || 'review'

    initial.step = nextStep

    setPendingAdsCampaignDiscovery(initial)

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMsg])

    if (nextStep === 'review') {
      setMessages((prev) => [
        ...prev,
        {
          id: `ads-review-${Date.now()}`,
          role: 'assistant',
          content: 'Preparé esta propuesta de campaña para que la revises antes de avanzar.',
          timestamp: new Date(),
          meta: {
            worker: 'ads_worker',
            intent: 'ADS_CAMPAIGN_REVIEW',
            ads_campaign_review: {
              name: `${initial.sourceName || 'Campaña personalizada'} · ${adsGoalLabel(initial.goal)}`,
              goalLabel: adsGoalLabel(initial.goal),
              creativeLabel: adsCreativeModeLabel(initial.creativeMode),
              budgetDaily: initial.budgetDaily,
              audience:
                'Personas interesadas en emprendimiento digital, creación de contenido, educación online, comunidad y soluciones relacionadas con tu oferta.',
              angle:
                'Presentar el problema principal, mostrar la transformación prometida y llevar al usuario al CTA del embudo o recurso seleccionado.',
              buttons: [
                {
                  label: 'Simular lanzamiento',
                  value: 'ads_launch:simulate',
                  emoji: '🚀',
                },
                {
                  label: 'Conectar Meta Ads',
                  value: 'ads_connect_meta',
                  emoji: '🔵',
                },
                {
                  label: 'Editar campaña',
                  value: 'ads_edit:restart',
                  emoji: '✏️',
                },
              ],
            },
          },
        },
      ])
      focusInput()
      return
    }

    pushAdsQuestion(nextStep, initial)
  }

  async function handleAdsCampaignDiscoveryReply(rawText: string) {
    const text = String(rawText || '').trim()
    if (!text || !pendingAdsCampaignDiscovery) return false

    const current = pendingAdsCampaignDiscovery

    if (text.startsWith('publication_')) {
      return false
    }

    if (text.startsWith('ads_creative_item:')) {
      const creativeId = text.replace('ads_creative_item:', '').trim()

      const selectedCreative =
        current.creativeCandidates?.items?.find(
          (item) => String(item.id) === String(creativeId)
        ) || null

      const next: PendingAdsCampaignDiscovery = {
        ...current,
        creativeMode: 'existing',
        step: 'budget',
      }

      setPendingAdsCampaignDiscovery(next)

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `ads-creative-selected-${Date.now()}`,
          role: 'assistant',
          content: selectedCreative
            ? `Perfecto. Usaremos **${selectedCreative.title}** como pieza publicitaria para esta campaña.`
            : 'Perfecto. Usaremos esa pieza publicitaria para esta campaña.',
          timestamp: new Date(),
          meta: {
            worker: 'ads_worker',
            intent: 'ADS_CREATIVE_SELECTED',
          },
        },
      ])

      pushAdsQuestion('budget', next)
      return true
    }

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: consumeDisplayText(text),
      timestamp: new Date(),
    }

    if (text === 'ads_edit:restart') {
      setPendingAdsCampaignDiscovery(null)
      startAdsCampaignDiscovery(current.originalRequest || 'Quiero crear una campaña')
      return true
    }

    if (text === 'ads_connect_meta') {
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `ads-meta-connect-${Date.now()}`,
          role: 'assistant',
          content:
            'Todavía estamos en modo simulación.\n\nAquí irá el botón real de conexión con Meta Ads usando OAuth. La experiencia correcta será: conectar Facebook, elegir cuenta publicitaria, elegir página y dejar la campaña creada en pausa para revisión.',
          timestamp: new Date(),
          meta: {
            worker: 'ads_worker',
            intent: 'ADS_META_CONNECT_PLACEHOLDER',
            buttons: [
              {
                label: 'Continuar simulación',
                value: 'ads_launch:simulate',
                emoji: '🚀',
              },
            ],
          },
        },
      ])

      focusInput()
      return true
    }

    if (text === 'ads_launch:simulate') {
      setPendingAdsCampaignDiscovery(null)

      const campaignName =
        current.sourceName && current.goal
          ? `${current.sourceName} · ${adsGoalLabel(current.goal)}`
          : 'Campaña simulada'

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `ads-launched-${Date.now()}`,
          role: 'assistant',
          content: 'Listo. Tu campaña quedó simulada como si estuviera en vivo.',
          timestamp: new Date(),
          meta: {
            worker: 'ads_worker',
            intent: 'ADS_CAMPAIGN_SIMULATED_LIVE',
            ads_campaign_status: {
              name: campaignName,
              statusLabel: 'Activa en simulación',
              budgetDaily: Number(current.budgetDaily || 150),
              goalLabel: adsGoalLabel(current.goal),
              buttons: [
                {
                  label: 'Ver métricas simuladas',
                  value: 'ads_results:simulated',
                  emoji: '📊',
                },
                {
                  label: 'Crear otra campaña',
                  value: 'ads_create:another',
                  emoji: '➕',
                },
              ],
            },
            executed_actions: [
              {
                type: 'ads_campaign_simulated',
                tool: 'ads_campaign_simulator',
                ok: true,
              },
            ],
          },
        },
      ])

      focusInput()
      return true
    }

    if (text === 'ads_results:simulated') {
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `ads-results-${Date.now()}`,
          role: 'assistant',
          content: 'Aquí tienes una simulación de rendimiento inicial.',
          timestamp: new Date(),
          meta: {
            worker: 'ads_worker',
            intent: 'ADS_CAMPAIGN_SIMULATED_RESULTS',
            ads_metrics: {
              name: current.sourceName
                ? `${current.sourceName} · ${adsGoalLabel(current.goal)}`
                : 'Resultados de campaña',
              spend: '$450 MXN',
              impressions: '8,240',
              clicks: '214',
              ctr: '2.59%',
              cpc: '$2.10',
              leads: '32',
              costPerLead: '$14.06',
              buttons: [
                {
                  label: 'Optimizar campaña',
                  value: 'ads_optimize:simulated',
                  emoji: '🧠',
                },
                {
                  label: 'Crear otra campaña',
                  value: 'ads_create:another',
                  emoji: '➕',
                },
              ],
            },
          },
        },
      ])

      focusInput()
      return true
    }

    if (text === 'ads_optimize:simulated') {
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `ads-optimize-${Date.now()}`,
          role: 'assistant',
          content:
            `Recomendación simulada:\n\n` +
            `1. Mantendría activo el ángulo principal porque el CTR está sano.\n` +
            `2. Crearía una segunda variante visual más directa al dolor.\n` +
            `3. No subiría presupuesto todavía hasta confirmar costo por lead estable durante 48 horas.\n\n` +
            `Cuando conectemos Meta, Samantha podrá detectar anuncios ganadores, sugerir nuevas variantes y pausar los de bajo rendimiento.`,
          timestamp: new Date(),
          meta: {
            worker: 'ads_worker',
            intent: 'ADS_CAMPAIGN_SIMULATED_OPTIMIZATION',
          },
        },
      ])

      focusInput()
      return true
    }

    if (text === 'ads_create:another') {
      setPendingAdsCampaignDiscovery(null)
      startAdsCampaignDiscovery('Quiero crear otra campaña')
      return true
    }

    const next: PendingAdsCampaignDiscovery = {
      ...current,
    }

    if (current.step === 'source') {
      const source = parseAdsSource(text)

      if (!source) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `ads-source-error-${Date.now()}`,
            role: 'assistant',
            content:
              'No alcancé a identificar qué quieres promocionar. Puedes elegir embudo, producto, reto, curso, comunidad u otra cosa.',
            timestamp: new Date(),
            meta: {
              worker: 'ads_worker',
            },
          },
        ])
        pushAdsQuestion('source', current)
        return true
      }

      next.sourceType = source.sourceType
      next.sourceId = source.sourceId
      next.sourceName = source.sourceName
    }

    if (current.step === 'goal') {
      const goal = parseAdsGoal(text)

      if (!goal) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `ads-goal-error-${Date.now()}`,
            role: 'assistant',
            content:
              'No alcancé a identificar el objetivo. Elige leads, ventas, tráfico o WhatsApp.',
            timestamp: new Date(),
            meta: {
              worker: 'ads_worker',
            },
          },
        ])
        pushAdsQuestion('goal', current)
        return true
      }

      next.goal = goal
    }

    if (current.step === 'creative') {
      const creativeMode = parseAdsCreativeMode(text)

      if (!creativeMode) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `ads-creative-error-${Date.now()}`,
            role: 'assistant',
            content:
              'No alcancé a identificar la pieza creativa. Elige usar una existente, crear visual, crear copy o crear ambos.',
            timestamp: new Date(),
            meta: {
              worker: 'ads_worker',
            },
          },
        ])
        pushAdsQuestion('creative', current)
        return true
      }

      if (creativeMode === 'existing') {
        const creativeCandidates =
          current.creativeCandidates || findCreativeCandidatesForAds(current)

        if (!creativeCandidates?.items?.length) {
          next.creativeMode = undefined
          setPendingAdsCampaignDiscovery({
            ...next,
            step: 'creative',
            creativeCandidates: null,
          })

          setMessages((prev) => [
            ...prev,
            userMsg,
            {
              id: `ads-no-creatives-${Date.now()}`,
              role: 'assistant',
              content:
                'No encontré piezas existentes suficientemente relacionadas con esta campaña. Lo mejor es crear una nueva pieza visual o preparar visual + copy.',
              timestamp: new Date(),
              meta: {
                worker: 'ads_worker',
                intent: 'ADS_NO_CREATIVE_CANDIDATES',
                buttons: [
                  {
                    label: 'Crear pieza visual',
                    value: 'ads_creative:new_visual',
                    emoji: '🎨',
                  },
                  {
                    label: 'Crear copy',
                    value: 'ads_creative:new_copy',
                    emoji: '✍️',
                  },
                  {
                    label: 'Crear visual + copy',
                    value: 'ads_creative:new_both',
                    emoji: '⚡',
                  },
                ],
              },
            },
          ])

          focusInput()
          return true
        }

        setPendingAdsCampaignDiscovery({
          ...current,
          creativeCandidates,
        })

        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `ads-creative-results-${Date.now()}`,
            role: 'assistant',
            content:
              'Perfecto. Elige cuál de estas piezas quieres usar para la campaña.',
            timestamp: new Date(),
            meta: {
              worker: 'ads_worker',
              intent: 'ADS_CREATIVE_PICKER',
              entity_results: creativeCandidates,
            },
          },
        ])

        focusInput()
        return true
      }

      if (creativeMode === 'new_visual' || creativeMode === 'new_both') {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `ads-creative-start-${Date.now()}`,
            role: 'assistant',
            content:
              creativeMode === 'new_both'
                ? 'Perfecto. Vamos a crear una pieza visual y dirección de copy para esta campaña.'
                : 'Perfecto. Vamos a crear una pieza visual para esta campaña.',
            timestamp: new Date(),
            meta: {
              worker: 'ads_worker',
              intent: 'ADS_START_CREATIVE_GENERATION',
            },
          },
        ])

        startAdsCreativeGenerationFromCampaign(current, creativeMode)
        return true
      }

      next.creativeMode = creativeMode
    }

    if (current.step === 'budget') {
      const budgetDaily = parseAdsBudgetDaily(text)

      if (!budgetDaily) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `ads-budget-error-${Date.now()}`,
            role: 'assistant',
            content:
              'No alcancé a identificar el presupuesto. Puedes escribir una cantidad como “150” o elegir una opción.',
            timestamp: new Date(),
            meta: {
              worker: 'ads_worker',
            },
          },
        ])
        pushAdsQuestion('budget', current)
        return true
      }

      next.budgetDaily = budgetDaily
    }

    const nextStep = getNextAdsCampaignStep(next)

    if (nextStep && nextStep !== 'review') {
      next.step = nextStep
      setPendingAdsCampaignDiscovery(next)
      setMessages((prev) => [...prev, userMsg])
      pushAdsQuestion(nextStep, next)
      return true
    }

    next.step = 'review'
    setPendingAdsCampaignDiscovery(next)

    setMessages((prev) => [
      ...prev,
      userMsg,
      {
        id: `ads-review-${Date.now()}`,
        role: 'assistant',
        content: 'Preparé esta propuesta de campaña para que la revises antes de avanzar.',
        timestamp: new Date(),
        meta: {
          worker: 'ads_worker',
          intent: 'ADS_CAMPAIGN_REVIEW',
          ads_campaign_review: {
            name: `${next.sourceName || 'Campaña personalizada'} · ${adsGoalLabel(next.goal)}`,
            goalLabel: adsGoalLabel(next.goal),
            creativeLabel: adsCreativeModeLabel(next.creativeMode),
            budgetDaily: next.budgetDaily,
            audience:
              'Personas interesadas en emprendimiento digital, creación de contenido, educación online, comunidad y soluciones relacionadas con tu oferta.',
            angle:
              'Presentar el problema principal, mostrar la transformación prometida y llevar al usuario al CTA del embudo o recurso seleccionado.',
            buttons: [
              {
                label: 'Simular lanzamiento',
                value: 'ads_launch:simulate',
                emoji: '🚀',
              },
              {
                label: 'Conectar Meta Ads',
                value: 'ads_connect_meta',
                emoji: '🔵',
              },
              {
                label: 'Editar campaña',
                value: 'ads_edit:restart',
                emoji: '✏️',
              },
            ],
          },
        },
      },
    ])

    focusInput()
    return true
  }
  function startFunnelDiscovery(rawText: string) {
    const text = String(rawText || '').trim()

    setPendingFunnelDiscovery({
      mode: 'legacy',
      step: 'start',
      originalRequest: text,
    })

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }

    const assistantMsg: AIMasterChatMessage = {
      id: `funnel-discovery-${Date.now() + 1}`,
      role: 'assistant',
      content:
        'Perfecto. Puedo ayudarte a crear un embudo.\n\n¿Cómo quieres empezar?',
      timestamp: new Date(),
      meta: {
        worker: 'funnel_worker',
        intent: 'FUNNEL_DISCOVERY',
        buttons: [
          {
            label: 'Crear desde plantilla',
            value: 'funnel_template:start',
            emoji: '📚',
          },
          {
            label: 'Crear desde cero',
            value: 'funnel_scratch:start',
            emoji: '🧱',
          },
          {
            label: 'Ir a mis embudos',
            value: 'funnel_navigate:my_funnels',
            emoji: '🗂️',
          },
        ],
      },
    }

    setMessages((prev) => [...prev, userMsg, assistantMsg])
    focusInput()
  }

  async function handleAIFunnelDiscoveryReply(rawText: string) {
    const text = String(rawText || '').trim()

    if (
      !text ||
      !pendingFunnelDiscovery ||
      pendingFunnelDiscovery.mode !== 'ai_html'
    ) {
      return false
    }

    const current = pendingFunnelDiscovery

    if (current.step === 'idea') {
      const inferredGoal = inferAIFunnelGoal(text)

      const next: Extract<
        PendingFunnelDiscovery,
        { mode: 'ai_html' }
      > = {
        ...current,
        step: inferredGoal ? 'audience' : 'goal',
        originalRequest: text,
        description: text,
        goal: inferredGoal,
        inferred: {
          ...current.inferred,
          pageType: inferAIFunnelType(inferredGoal),
          sections: inferAIFunnelSections({
            goal: inferredGoal,
            description: text,
          }),
        },
      }

      setPendingFunnelDiscovery(next)

      const question = getAIFunnelQuestion(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage(text, {
          intent: 'AI_FUNNEL_IDEA',
        }),
        ...(question ? [question] : []),
      ])

      setInput('')
      focusInput()
      return true
    }

    if (current.step === 'goal') {
      const goal = inferAIFunnelGoal(text) || 'auto'

      const next = {
        ...current,
        step: 'audience' as const,
        goal,
        inferred: {
          ...current.inferred,
          pageType: inferAIFunnelType(goal),
          sections: inferAIFunnelSections({
            goal,
            description: current.description,
          }),
        },
      }

      setPendingFunnelDiscovery(next)

      const question = getAIFunnelQuestion(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage(text),
        ...(question ? [question] : []),
      ])

      setInput('')
      focusInput()
      return true
    }

    if (current.step === 'audience') {
      const auto =
        normalizeSmallReply(text).includes('decide por mi') ||
        text === 'ai_funnel:audience:auto'

      const next = {
        ...current,
        step: 'offer' as const,
        audience: auto
          ? 'Audiencia ideal inferida automáticamente a partir de la oferta'
          : text,
      }

      setPendingFunnelDiscovery(next)

      const question = getAIFunnelQuestion(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage(auto ? 'Decide por mí' : text),
        ...(question ? [question] : []),
      ])

      setInput('')
      focusInput()
      return true
    }

    if (current.step === 'offer') {
      const auto =
        normalizeSmallReply(text).includes('usa lo que ya') ||
        text === 'ai_funnel:offer:auto'

      const next = {
        ...current,
        step: 'branding' as const,
        offer: auto
          ? 'Oferta inferida automáticamente a partir de la idea'
          : text,
      }

      setPendingFunnelDiscovery(next)

      const question = getAIFunnelQuestion(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage(auto ? 'Usa lo que ya te expliqué' : text),
        ...(question ? [question] : []),
      ])

      setInput('')
      focusInput()
      return true
    }

    if (current.step === 'branding') {
      const wantsExisting =
        text === 'ai_funnel:branding:existing' ||
        text === 'ai_funnel:use_branding' ||
        normalizeSmallReply(text).includes('usar mi branding')

      const next = {
        ...current,
        step: 'references' as const,
        useBranding: wantsExisting,
        branding: wantsExisting
          ? {
              brandName: brand?.name || '',
              logoUrl:
                (brand as any)?.logoUrl ||
                (brand as any)?.logo_url ||
                (brand as any)?.logo ||
                '',
              primaryColor:
                (brand as any)?.primaryColor ||
                (brand as any)?.primary_color ||
                '',
              secondaryColor:
                (brand as any)?.secondaryColor ||
                (brand as any)?.secondary_color ||
                '',
              accentColor:
                (brand as any)?.accentColor ||
                (brand as any)?.accent_color ||
                '',
            }
          : undefined,
      }

      setPendingFunnelDiscovery(next)

      const question = getAIFunnelQuestion(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage(
          wantsExisting ? 'Usar mi branding' : 'Diseña libremente'
        ),
        ...(question ? [question] : []),
      ])

      setInput('')
      focusInput()
      return true
    }

    if (current.step === 'references') {
      const normalized = normalizeSmallReply(text)

      if (
        normalized.includes('sin referencia') ||
        normalized.includes('continuar') ||
        text === 'ai_funnel:reference:none'
      ) {
        const next = {
          ...current,
          step: 'review' as const,
        }

        setPendingFunnelDiscovery(next)

        setMessages((prev) => [
          ...prev,
          createChatUserMessage('Continuar sin referencia'),
          buildAIFunnelReviewMessage(next),
        ])

        setInput('')
        focusInput()
        return true
      }

      const looksLikeUrl = /^https?:\/\/\S+$/i.test(text)

      if (looksLikeUrl) {
        const next = {
          ...current,
          step: 'review' as const,
          references: [
            ...current.references,
            {
              type: 'url' as const,
              url: text,
              name: text,
            },
          ],
        }

        setPendingFunnelDiscovery(next)

        setMessages((prev) => [
          ...prev,
          createChatUserMessage(text),
          buildAIFunnelReviewMessage(next),
        ])

        setInput('')
        focusInput()
        return true
      }

      setMessages((prev) => [
        ...prev,
        createChatUserMessage(text),
        createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_REFERENCE_INVALID',
          content:
            'Pega una URL completa que empiece con `https://`, usa el botón para subir una imagen o continúa sin referencia.',
          buttons: [
            {
              label: 'Subir imagen',
              value: 'ai_funnel:reference:image',
              action: 'ai_funnel_action',
              emoji: '🖼️',
            },
            {
              label: 'Continuar sin referencia',
              value: 'ai_funnel:reference:none',
              action: 'ai_funnel_action',
              emoji: '➡️',
            },
          ],
        }),
      ])

      setInput('')
      focusInput()
      return true
    }

    if (current.step === 'review') {
      setMessages((prev) => [
        ...prev,
        createChatUserMessage(text),
        buildAIFunnelReviewMessage(current),
      ])

      setInput('')
      focusInput()
      return true
    }

    return true
  }

  async function handleFunnelDiscoveryReply(rawText: string) {
    const text = String(rawText || '').trim()

    if (
      !text ||
      !pendingFunnelDiscovery ||
      pendingFunnelDiscovery.mode !== 'legacy'
    ) {
      return false
    }

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }

    if (isAffirmativeReply(text) || wantsTemplateReply(text)) {
      setPendingFunnelDiscovery(null)

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `funnel-template-open-${Date.now()}`,
          role: 'assistant',
          content:
            'Listo. Te abro las plantillas disponibles para que elijas una y la clones como embudo editable.',
          timestamp: new Date(),
          meta: {
            worker: 'funnel_worker',
            intent: 'FUNNEL_TEMPLATE_PICKER',
          },
        },
      ])

      hideDockForExternalSurface()

      openFunnelTemplatePicker({
        initialTab: 'official',
      })

      return true
    }

    if (wantsMyFunnelsReply(text)) {
      setPendingFunnelDiscovery(null)

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `funnel-navigate-${Date.now()}`,
          role: 'assistant',
          content: 'Listo. Te llevo a tus embudos.',
          timestamp: new Date(),
          meta: {
            worker: 'funnel_worker',
            intent: 'FUNNEL_NAVIGATION',
          },
        },
      ])

      setChatViewMode('dock')
      hideDockForExternalSurface()
      router.push('/funnel-builder')
      focusInput()
      return true
    }

    if (wantsScratchFunnelReply(text)) {
      setPendingFunnelDiscovery(null)

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `funnel-scratch-${Date.now()}`,
          role: 'assistant',
          content:
            'Perfecto. Te llevo al constructor para crear un embudo desde cero.',
          timestamp: new Date(),
          meta: {
            worker: 'funnel_worker',
            intent: 'FUNNEL_SCRATCH',
          },
        },
      ])

      setChatViewMode('dock')
      hideDockForExternalSurface()
      router.push('/funnel-builder')
      focusInput()
      return true
    }

    setMessages((prev) => [
      ...prev,
      userMsg,
      {
        id: `funnel-discovery-clarify-${Date.now()}`,
        role: 'assistant',
        content:
          'Puedo hacerlo de tres formas rápidas:\n\n1. Crear desde plantilla\n2. Crear desde cero\n3. Ir a mis embudos\n\n¿Cuál prefieres?',
        timestamp: new Date(),
        meta: {
          worker: 'funnel_worker',
          intent: 'FUNNEL_DISCOVERY',
          buttons: [
            {
              label: 'Crear desde plantilla',
              value: 'funnel_template:start',
              emoji: '📚',
            },
            {
              label: 'Crear desde cero',
              value: 'funnel_scratch:start',
              emoji: '🧱',
            },
            {
              label: 'Ir a mis embudos',
              value: 'funnel_navigate:my_funnels',
              emoji: '🗂️',
            },
          ],
        },
      },
    ])

    focusInput()
    return true
  }

  function wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  function parsePublicationIntent(text: string): PublicationIntent | undefined {
    const m = text.toLowerCase()

    if (m.includes('lanzamiento') || m.includes('evento') || m.includes('webinar') || m.includes('reto') || m.includes('clase')) {
      return 'announcement'
    }

    if (m.includes('tips') || m.includes('errores') || m.includes('pasos') || m.includes('framework') || m.includes('educ')) {
      return 'educational'
    }

    if (m.includes('opinión') || m.includes('autoridad') || m.includes('postura') || m.includes('thought')) {
      return 'authority'
    }

    if (m.includes('venta') || m.includes('oferta') || m.includes('descuento') || m.includes('bono') || m.includes('inscripción')) {
      return 'offer'
    }

    if (m.includes('historia') || m.includes('antes') || m.includes('después') || m.includes('transformación')) {
      return 'story'
    }

    if (m.includes('comunidad') || m.includes('recordatorio') || m.includes('celebrar') || m.includes('participación')) {
      return 'community'
    }

    if (m.includes('testimonio') || m.includes('caso de éxito') || m.includes('resultado')) {
      return 'testimonial'
    }

    if (m.includes('carrusel') || m.includes('carousel')) {
      return 'carousel'
    }

    return undefined
  }

  function parsePublicationSector(text: string): PublicationSector | undefined {
    const m = text.toLowerCase()

    if (m.includes('skincare') || m.includes('belleza') || m.includes('riman') || m.includes('cosmética') || m.includes('piel')) {
      return 'beauty_skincare'
    }

    if (m.includes('ia') || m.includes('inteligencia artificial') || m.includes('software') || m.includes('tecnología')) {
      return 'ai_technology'
    }

    if (m.includes('coach') || m.includes('curso') || m.includes('mentor') || m.includes('educación')) {
      return 'coaching_education'
    }

    if (m.includes('fitness') || m.includes('wellness') || m.includes('salud') || m.includes('nutrición')) {
      return 'fitness_wellness'
    }

    if (m.includes('marca personal') || m.includes('creador') || m.includes('influencer')) {
      return 'creator_personal_brand'
    }

    if (m.includes('corporativo') || m.includes('empresa') || m.includes('business') || m.includes('b2b')) {
      return 'business_corporate'
    }

    if (m.includes('network') || m.includes('referidos') || m.includes('multinivel')) {
      return 'network_marketing'
    }

    if (m.includes('evento') || m.includes('webinar') || m.includes('masterclass')) {
      return 'events_webinars'
    }

    if (m.includes('producto digital') || m.includes('ebook') || m.includes('plantilla') || m.includes('membresía')) {
      return 'digital_products'
    }

    if (m.includes('restaurante') || m.includes('clínica') || m.includes('tienda') || m.includes('local')) {
      return 'local_business'
    }

    return undefined
  }

  function parsePublicationVisualStyle(text: string): PublicationVisualStyle | undefined {
    const m = text.toLowerCase()

    if (m.includes('premium dark') || m.includes('oscuro premium')) return 'premium_dark'
    if (m.includes('clean') || m.includes('blanco') || m.includes('limpio')) return 'clean_white'
    if (m.includes('degradado') || m.includes('gradient')) return 'gradient_modern'
    if (m.includes('editorial') || m.includes('revista')) return 'editorial_magazine'
    if (m.includes('futurista') || m.includes('holográfico')) return 'futuristic_holographic'
    if (m.includes('viral') || m.includes('bold')) return 'bold_viral'
    if (m.includes('lujo') || m.includes('luxury') || m.includes('minimal')) return 'minimal_luxury'
    if (m.includes('ugc') || m.includes('nativo')) return 'ugc_native'
    if (m.includes('corporativo')) return 'corporate_clean'
    if (m.includes('playful') || m.includes('divertido')) return 'playful_creator'

    return undefined
  }

  function startAdsCreativeGenerationFromCampaign(
    state: PendingAdsCampaignDiscovery,
    mode: AdsCampaignCreativeMode
  ) {
    const instruction = [
      `Crea una pieza visual publicitaria para una campaña de Meta Ads.`,
      `Recurso a promocionar: ${state.sourceName || 'oferta seleccionada'}.`,
      `Tipo de recurso: ${state.sourceType || 'recurso'}.`,
      `Objetivo de campaña: ${adsGoalLabel(state.goal)}.`,
      `La pieza debe funcionar como anuncio, no como publicación orgánica.`,
      `Debe comunicar un beneficio claro, tener lectura rápida y llevar al usuario al CTA del embudo o recurso seleccionado.`,
    ].join('\n')

    setPendingAdsCampaignDiscovery({
      ...state,
      creativeMode: mode,
      waitingForCreativeGeneration: true,
    })

    startPublicationDiscovery('Crear pieza visual para esta campaña', {
      sourceFlow: 'ads_campaign',
      forceCreateInChat: true,
      platform: 'instagram',
      format: 'image',
      wantsReferenceImage: false,
      instruction,
    })
  }

  function startPublicationDiscovery(
    rawText: string,
    overrides?: Partial<PendingPublicationDiscovery>
  ) {

    setLastUploadedReferenceUrl('')
    setPendingUploadFiles([])

    const text = String(rawText || '').trim()

    const parsedFormat = overrides?.format || parsePublicationFormat(text) || undefined

    const initial: PendingPublicationDiscovery = {
      step: 'platform',
      originalRequest: text,
      platform: overrides?.platform || parsePublicationPlatform(text) || undefined,
      format: parsedFormat,
      creativeMode:
        overrides?.creativeMode ||
        (parsedFormat === 'image' ? 'professional_image' : 'template'),
      intent: overrides?.intent || parsePublicationIntent(text) || undefined,
      sector: overrides?.sector || parsePublicationSector(text) || undefined,
      visualStyle:
        overrides?.visualStyle || parsePublicationVisualStyle(text) || undefined,
      wantsReferenceImage:
        overrides?.wantsReferenceImage !== undefined
          ? overrides.wantsReferenceImage
          : undefined,
      instruction: overrides?.instruction || '',
      sourceFlow: overrides?.sourceFlow,
      forceCreateInChat: Boolean(overrides?.forceCreateInChat),
      silentUserMessage: Boolean(overrides?.silentUserMessage),
    }

    const nextStep = getNextPublicationStep(initial)
    initial.step = nextStep || 'instruction'

    setPendingPublicationDiscovery(initial)

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }

    if (!initial.silentUserMessage) {
      setMessages((prev) => [...prev, userMsg])
    }

    if (nextStep) {
      pushPublicationQuestion(nextStep)
      return
    }

    setMessages((prev) => [
      ...prev,
      {
        id: `publication-auto-start-${Date.now()}`,
        role: 'assistant',
        content:
          initial.sourceFlow === 'ads_campaign'
            ? 'Perfecto. Voy a crear una pieza visual para esta campaña y luego seguimos con la configuración del anuncio.'
            : 'Perfecto. Voy a preparar la publicación.',
        timestamp: new Date(),
        meta: {
          worker: 'creator_studio_worker',
          intent:
            initial.sourceFlow === 'ads_campaign'
              ? 'ADS_CREATIVE_GENERATION_STARTED'
              : 'PUBLICATION_GENERATION_STARTED',
        },
      },
    ])

    setTimeout(() => {
      handlePublicationDiscoveryReply(initial.instruction || initial.originalRequest)
    }, 0)
  }

  function updatePublicationSlideVisual(preview: any, input: {
    slideIndex: number
    patch: any
  }) {
    const currentDraft = preview?.draft || {}
    const slides = currentDraft.slides || preview?.slides || []
    const currentVisuals = Array.isArray(currentDraft.slideVisuals)
      ? currentDraft.slideVisuals
      : []

    const slideVisuals = slides.map((_: any, index: number) => ({
      ...(currentVisuals[index] || {}),
    }))

    slideVisuals[input.slideIndex] = {
      ...(slideVisuals[input.slideIndex] || {}),
      ...input.patch,
    }

    return {
      ...preview,
      draft: {
        ...currentDraft,
        slideVisuals,
      },
    }
  }

  function getMockImageForClient(input: {
    sector?: string
    index?: number
  }) {
    const index = input.index || 0

    const pools: Record<string, string[]> = {
      beauty_skincare: [
        'https://images.unsplash.com/photo-1596462502278-27bfdc403348?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=1200&auto=format&fit=crop',
      ],
      fitness_wellness: [
        'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1518611012118-696072aa579a?q=80&w=1200&auto=format&fit=crop',
      ],
      business_corporate: [
        'https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1200&auto=format&fit=crop',
      ],
      events_webinars: [
        'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1200&auto=format&fit=crop',
      ],
    }

    const pool = pools[input.sector || ''] || pools.business_corporate
    return pool[index % pool.length]
  }

  function buildClientSlidePlan(input: {
    intent?: string
    sector?: string
    templateId?: string
    premiumBoost?: boolean
  }) {
    const intent = input.intent || 'educational'
    const sector = input.sector || 'business_corporate'

    if (intent === 'announcement') {
      if (sector === 'fitness_wellness') {
        return [
          { role: 'cover', layout: 'diagonal_energy', imagePriority: 'high', safeArea: 'right' },
          { role: 'details', layout: 'text_only_center', imagePriority: 'none', safeArea: 'center' },
          { role: 'benefits', layout: 'cards_3', imagePriority: 'none', safeArea: 'center' },
          { role: 'urgency', layout: 'hero_right', imagePriority: 'high', safeArea: 'right' },
          { role: 'cta', layout: 'cta_banner', imagePriority: 'high', safeArea: 'center' },
        ]
      }

      if (sector === 'events_webinars') {
        return [
          { role: 'cover', layout: 'hero_center', imagePriority: 'high', safeArea: 'center' },
          { role: 'details', layout: 'event_details', imagePriority: 'none', safeArea: 'center' },
          { role: 'benefits', layout: 'cards_3', imagePriority: 'none', safeArea: 'center' },
          { role: 'speaker', layout: 'split_image_left', imagePriority: 'high', safeArea: 'right' },
          { role: 'cta', layout: 'cta_banner', imagePriority: 'high', safeArea: 'center' },
        ]
      }

      return [
        { role: 'cover', layout: 'hero_left', imagePriority: 'high', safeArea: 'left' },
        { role: 'details', layout: 'event_details', imagePriority: 'none', safeArea: 'center' },
        { role: 'benefits', layout: 'cards_3', imagePriority: 'none', safeArea: 'center' },
        { role: 'proof', layout: 'stat_proof', imagePriority: 'none', safeArea: 'center' },
        { role: 'cta', layout: 'cta_banner', imagePriority: 'high', safeArea: 'center' },
      ]
    }

    if (intent === 'story') {
      return [
        { role: 'before_after', layout: 'before_after', imagePriority: 'medium', safeArea: 'center' },
        { role: 'problem', layout: 'text_only_center', imagePriority: 'none', safeArea: 'center' },
        { role: 'turning_point', layout: 'split_image_right', imagePriority: 'high', safeArea: 'left' },
        { role: 'result', layout: 'stat_proof', imagePriority: 'none', safeArea: 'center' },
        { role: 'cta', layout: 'cta_banner', imagePriority: 'high', safeArea: 'center' },
      ]
    }

    if (intent === 'testimonial') {
      return [
        { role: 'quote', layout: 'quote_full', imagePriority: 'high', safeArea: 'center' },
        { role: 'context', layout: 'split_image_left', imagePriority: 'high', safeArea: 'right' },
        { role: 'metric', layout: 'stat_proof', imagePriority: 'none', safeArea: 'center' },
        { role: 'process', layout: 'numbered_steps', imagePriority: 'none', safeArea: 'center' },
        { role: 'cta', layout: 'cta_banner', imagePriority: 'high', safeArea: 'center' },
      ]
    }

    if (intent === 'offer') {
      return [
        { role: 'offer', layout: 'hero_right', imagePriority: 'high', safeArea: 'right' },
        { role: 'value', layout: 'cards_3', imagePriority: 'none', safeArea: 'center' },
        { role: 'proof', layout: 'stat_proof', imagePriority: 'none', safeArea: 'center' },
        { role: 'urgency', layout: 'diagonal_energy', imagePriority: 'medium', safeArea: 'left' },
        { role: 'cta', layout: 'cta_banner', imagePriority: 'high', safeArea: 'center' },
      ]
    }

    return [
      { role: 'cover', layout: 'hero_left', imagePriority: 'high', safeArea: 'left' },
      { role: 'problem', layout: 'text_only_center', imagePriority: 'none', safeArea: 'center' },
      { role: 'steps', layout: 'numbered_steps', imagePriority: 'none', safeArea: 'center' },
      { role: 'insight', layout: 'split_image_right', imagePriority: 'high', safeArea: 'left' },
      { role: 'cta', layout: 'cta_banner', imagePriority: 'high', safeArea: 'center' },
    ]
  }

  function buildClientSlideVisuals(input: {
    intent?: string
    sector?: string
    slidePlan: any[]
    premiumBoost?: boolean
  }) {
    return input.slidePlan.map((plan, index) => {
      const needsImage = plan.imagePriority !== 'none'
      const isDarkSector =
        input.sector === 'fitness_wellness' ||
        input.sector === 'ai_technology'

      return {
        backgroundImage: needsImage
          ? getMockImageForClient({
              sector: input.sector,
              index,
            })
          : null,
        blurEnabled: needsImage,
        blurAmount: input.premiumBoost ? 18 : 12,
        overlayTone: isDarkSector ? 'black' : 'white',
        overlayOpacity: isDarkSector ? 0.44 : 0.5,
      }
    })
  }

  function getDesignFromTemplateOption(
    template: PublicationTemplateOption,
    input?: {
      sector?: string
      intent?: string
      premiumBoost?: boolean
    }
  ) {
    const p = template.preview
    const sector = input?.sector || ''

    if (sector === 'fitness_wellness') {
      return {
        background: '#111111',
        backgroundSoft: '#262626',
        surface: 'rgba(255,255,255,0.08)',
        surfaceStrong: 'rgba(255,255,255,0.14)',
        text: '#FFFFFF',
        textMuted: '#D6D6D6',
        accent: p.accent || '#B6FF3B',
        accentSoft: 'rgba(182,255,59,0.22)',
        accentDeep: '#7FCE00',
        border: 'rgba(255,255,255,0.14)',
        shadow: '0 34px 100px rgba(0,0,0,0.42)',
        displayFont: 'Anton, Impact, Montserrat, system-ui, sans-serif',
        serifFont: 'Georgia, serif',
        bodyFont: 'Montserrat, Inter, system-ui, sans-serif',
        logoTracking: '0.16em',
        cornerRadius: 30,
      }
    }

    if (sector === 'business_corporate') {
      return {
        background: p.background || '#F4F6F8',
        backgroundSoft: '#DDE5EF',
        surface: 'rgba(255,255,255,0.84)',
        surfaceStrong: '#FFFFFF',
        text: p.text || '#101827',
        textMuted: p.muted || '#667085',
        accent: p.accent || '#2457A6',
        accentSoft: '#D7E5F7',
        accentDeep: '#143B75',
        border: 'rgba(16,24,39,0.10)',
        shadow: '0 24px 80px rgba(16,24,39,0.12)',
        displayFont: 'Inter, Montserrat, system-ui, sans-serif',
        serifFont: 'Georgia, serif',
        bodyFont: 'Inter, system-ui, sans-serif',
        logoTracking: '0.2em',
        cornerRadius: 30,
      }
    }

    return {
      background: p.background,
      backgroundSoft: p.background,
      surface: 'rgba(255,255,255,0.72)',
      surfaceStrong: '#FFFFFF',
      text: p.text,
      textMuted: p.muted,
      accent: p.accent,
      accentSoft: p.accent,
      accentDeep: p.accent,
      border: 'rgba(0,0,0,0.12)',
      shadow: input?.premiumBoost
        ? '0 36px 110px rgba(0,0,0,0.22)'
        : '0 28px 90px rgba(0,0,0,0.16)',
      displayFont:
        template.visualStyle === 'bold_viral'
          ? 'Anton, Impact, Montserrat, system-ui, sans-serif'
          : template.visualStyle === 'corporate_clean'
            ? 'Inter, Montserrat, system-ui, sans-serif'
            : 'Playfair Display, Georgia, serif',
      serifFont: 'Playfair Display, Georgia, serif',
      bodyFont: 'Montserrat, Inter, system-ui, sans-serif',
      logoTracking: '0.26em',
      cornerRadius: 34,
    }
  }

  function getActiveGuidedFlowLabel() {
    if (pendingLaunchpadDiscovery) {
      return 'tu diagnóstico de Launchpad'
    }

    if (pendingPublicationDiscovery) {
      return 'tu publicación'
    }

    if (pendingAdsCampaignDiscovery) {
      return 'tu campaña'
    }

    if (pendingFunnelDiscovery) {
      return 'tu embudo'
    }

    if (pendingCreateContentDirection) {
      return 'tu proyecto de contenido'
    }

    if (pendingCreateContentResume) {
      return 'tu proyecto de contenido'
    }

    if (pendingCreateContentPlan) {
      return 'tu proyecto de contenido'
    }

    if (pendingExpressContent) {
      return 'tu proyecto de contenido'
    }

    if (pendingLightweightFlow) {
      return 'un flujo rápido'
    }

    if (pendingSupportTicket?.awaitingDescription) {
      return 'tu ticket de soporte'
    }

    return null
  }

  function hasActiveGuidedFlow() {
    return Boolean(getActiveGuidedFlowLabel())
  }

  function handleCommandBarFocus() {
    setCommandFocused(true)

    if (hasActiveGuidedFlow() && uiState !== 'chat') {
      setUIState('chat')
      setChatViewMode('dock')
      setCompactTurnStartedAt(null)
    }
  }

  function resetActiveGuidedFlow() {
    setPendingLaunchpadDiscovery(null)
    setPendingPublicationDiscovery(null)
    setPendingAdsCampaignDiscovery(null)
    setPendingFunnelDiscovery(null)
    setPendingLightweightFlow(null)
    setPendingSupportTicket(null)

    /*
    * Limpia todos los estados posibles del flujo
    * de creación de contenido.
    */
    setPendingCreateContentResume(null)
    setPendingCreateContentDirection(null)
    setPendingCreateContentPlan(null)
    setPendingExpressContent(null)
    setCreateContentDryRunProject(null)

    /*
    * Limpia también el estado global del Task Flow.
    */
    const taskFlowStore =
      useTaskFlowStore.getState()

    taskFlowStore.setSnapshotId(null)
    taskFlowStore.setResult(null)
    taskFlowStore.setPhase(
      'idle' as TaskFlowRequest['phase']
    )

    setLastUploadedReferenceUrl('')
    setPendingUploadFiles([])

    setInput('')
    setActiveSuggestionIndex(-1)
    setCompactTurnStartedAt(null)
    setShowNoCreditsCard(false)

    setMessages((prev) => [
      ...prev,
      {
        id: `guided-flow-reset-${Date.now()}`,
        role: 'assistant',
        content:
          'Listo. Reinicié el proceso activo. Ya puedes pedirme otra cosa desde cero.',
        timestamp: new Date(),
        meta: {
          worker: 'platform_navigator',
          intent: 'RESET_ACTIVE_GUIDED_FLOW',
          compact: true,
        },
      } as AIMasterChatMessage,
    ])

    setUIState('chat')
    setChatViewMode('dock')

    requestAnimationFrame(() => {
      focusInput()
    })
  }

  function applyTemplateToPublicationPreview(
    preview: any,
    template: PublicationTemplateOption,
    options?: {
      keepCurrentIntentAndSector?: boolean
      premiumBoost?: boolean
    }
  ) {
    const currentDraft = preview?.draft || {}

    const sector =
      options?.keepCurrentIntentAndSector
        ? currentDraft.sector
        : currentDraft.sector ||
          (Array.isArray(template.sector) && template.sector.length ? template.sector[0] : undefined)

    const intent =
      options?.keepCurrentIntentAndSector
        ? currentDraft.intent
        : currentDraft.intent ||
          (Array.isArray(template.intent) && template.intent.length ? template.intent[0] : undefined)

    const slidePlan = buildClientSlidePlan({
      intent,
      sector,
      templateId: template.templateId,
      premiumBoost: Boolean(options?.premiumBoost),
    })

    const slideVisuals = buildClientSlideVisuals({
      intent,
      sector,
      slidePlan,
      premiumBoost: Boolean(options?.premiumBoost),
    })

    return {
      ...preview,
      templateOptions: preview?.templateOptions || [],
      draft: {
        ...currentDraft,
        templateId: template.templateId,
        themeId: template.themeId,
        templateFamily:
          template.templateId === 'sector-intent-pack'
            ? 'sector_intent_pack'
            : currentDraft.templateFamily || 'premium_carousel',
        sector,
        intent,
        visualStyle: template.visualStyle,
        design: getDesignFromTemplateOption(template, {
          sector,
          intent,
          premiumBoost: Boolean(options?.premiumBoost),
        }),
        slidePlan,
        slideVisuals,
        slides: currentDraft.slides || preview?.slides || [],
        post: currentDraft.post,
      },
    }
  }

  async function runPublicationPreviewAction(params: {
    actionId: string
    sourceMessage: AIMasterChatMessage
  }) {
    const { actionId, sourceMessage } = params
    const preview = (sourceMessage.meta as any)?.content_preview

    if (!preview) {
      focusInput()
      return
    }

    if (actionId === 'open_creator_studio') {
      setChatViewMode('dock')
      hideDockForExternalSurface()
      router.push('/creator-studio?tab=publications')
      focusInput()
      return
    }

    if (
      actionId === 'export_publication_carousel'
    ) {
      const generationId = Number(
        preview?.generationId ||
          preview?.generation_id ||
          preview?.generation?.generation_id ||
          preview?.generation?.id ||
          preview?.draft?.generationId ||
          preview?.draft?.generation_id ||
          0
      )

      if (!Number.isFinite(generationId) || generationId <= 0) {
        setMessages((prev) => [
          ...prev,
          {
            id: `publication-export-error-${Date.now()}`,
            role: 'assistant',
            content: 'No pude exportar porque este carrusel no trae generationId.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_EXPORT_ERROR',
            },
          } as AIMasterChatMessage,
        ])

        return
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `publication-export-started-${Date.now()}`,
          role: 'assistant',
          content: 'Perfecto. Estoy renderizando los slides del carrusel como imágenes PNG.',
          timestamp: new Date(),
          meta: {
            worker: 'creator_studio_worker',
            intent: 'PUBLICATION_EXPORT_STARTED',
          },
        } as AIMasterChatMessage,
      ])

      try {
        const res = await fetch(
          `/api/creator-studio/publications/generations/${generationId}/export`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              draft: preview?.draft || null,
              source: 'samantha_publication_preview_export',
            }),
          }
        )

        const data = await res.json().catch(() => null)

        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || 'No se pudo exportar el carrusel.')
        }

        const renderedSlides = Array.isArray(data?.renderedSlides)
          ? data.renderedSlides.map((slide: any) => {
              const key = String(slide?.key || '').trim()
              const publicUrl = key ? `/${key.replace(/^\/+/, '')}` : slide?.url

              return {
                ...slide,
                publicUrl,
              }
            })
          : []

        if (!renderedSlides.length) {
          throw new Error('El carrusel se exportó, pero no regresó imágenes.')
        }

        setMessages((prev) => [
          ...prev,
          {
            id: `publication-export-ready-${Date.now()}`,
            role: 'assistant',
            content: 'Listo. Ya exporté tu carrusel como imágenes individuales.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_EXPORT_READY',
              content_preview: {
                previewType: 'exported_carousel',
                generationId,
                generation_id: generationId,
                title: preview?.title || 'Carrusel exportado',
                renderedSlides,
                assets: Array.isArray(data?.assets) ? data.assets : [],
                actions: [],
              },
              executed_actions: [
                {
                  type: 'export_publication_carousel',
                  tool: 'export_publication_carousel',
                  ok: true,
                },
              ],
            },
          } as AIMasterChatMessage,
        ])
      } catch (error: any) {
        console.error('[export_publication_carousel] error:', error)

        setMessages((prev) => [
          ...prev,
          {
            id: `publication-export-error-${Date.now()}`,
            role: 'assistant',
            content: String(error?.message || 'No pude exportar el carrusel.'),
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_EXPORT_ERROR',
            },
          } as AIMasterChatMessage,
        ])
      }

      return
    }

    if (actionId === 'make_more_premium') {
      const preview = (sourceMessage.meta as any)?.content_preview
      const currentDraft = preview?.draft || {}

      const options = Array.isArray(preview?.templateOptions)
        ? preview.templateOptions
        : []

      const premiumTemplate =
        options.find((tpl: any) =>
          String(tpl.name || '').toLowerCase().includes('luxury')
        ) ||
        options.find((tpl: any) =>
          String(tpl.visualStyle || '').includes('luxury')
        ) ||
        options[0]

      if (!preview || !premiumTemplate) {
        focusInput()
        return
      }

      const updatedPreview = applyTemplateToPublicationPreview(preview, premiumTemplate, {
        keepCurrentIntentAndSector: true,
        premiumBoost: true,
      })

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === sourceMessage.id
            ? ({
                ...msg,
                content: 'Listo. Lo hice más premium sin cambiar el objetivo ni el nicho.',
                meta: {
                  ...(msg.meta || {}),
                  intent: 'PUBLICATION_MORE_PREMIUM_READY',
                  content_preview: updatedPreview,
                },
              } as AIMasterChatMessage)
            : msg
        )
      )

      focusInput()
      return
    }

    const loadingId = `publication-refining-${Date.now()}`

    setUIState('chat')
    setChatViewMode('focus')

    setMessages((prev) => [
      ...prev,
      {
        id: loadingId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        meta: {
          worker: 'creator_studio_worker',
          intent:
            actionId === 'make_more_premium'
              ? 'PUBLICATION_MAKING_MORE_PREMIUM'
              : 'PUBLICATION_REGENERATING_IN_CHAT',
          content_preview_loading: {
            previewType: preview.previewType || 'image',
            platform: preview.platform || 'instagram',
            format: preview.format || 'image',
          },
        },
      } as AIMasterChatMessage,
    ])

    const isDryRun = PUBLICATION_CHAT_DRY_RUN

    if (isDryRun) {
      await wait(1200)
    }

    try {
      const res = await fetch('/api/ai/tools/generate-publication-in-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          dryRun: isDryRun,
          payload: {
            source: 'samantha_chat',
            refinementAction: actionId,
            previousPreview: preview,
            previousGenerationId: preview.generationId || 0,

            platform: preview.platform || 'instagram',
            format: preview.format || preview.previewType || 'image',
            style: 'hero_premium',

            instruction:
              actionId === 'make_more_premium'
                ? 'Haz esta publicación más premium, sofisticada y aspiracional.'
                : 'Regenera una nueva versión de esta publicación con un ángulo diferente.',

            generateImage: preview.previewType !== 'text',

            discovery: {
              source: 'samantha_publication_preview_action',
              refinementAction: actionId,
              previousPreview: preview,
            },
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.message || json?.error || 'No se pudo refinar la publicación')
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingId
            ? ({
                ...msg,
                content:
                  actionId === 'make_more_premium'
                    ? 'Listo. La hice más premium.'
                    : 'Listo. Generé una nueva versión.',
                meta: {
                  ...(msg.meta || {}),
                  intent:
                    actionId === 'make_more_premium'
                      ? 'PUBLICATION_MORE_PREMIUM_READY'
                      : 'PUBLICATION_REGENERATED_READY',
                  content_preview: json.preview,
                  content_generation: json.generation,
                  content_preview_loading: null,
                  executed_actions: [
                    {
                      type: 'generate_publication_in_chat',
                      tool: 'generate_publication_in_chat',
                      ok: true,
                    },
                  ],
                },
              } as AIMasterChatMessage)
            : msg
        )
      )
    } catch (err) {
      console.error('[AIMasterWidget] publication preview action error', err)

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingId
            ? ({
                ...msg,
                content:
                  'Tuve un problema refinando esta publicación. Puedes abrirla en Creator Studio para continuar.',
                meta: {
                  ...(msg.meta || {}),
                  intent: 'PUBLICATION_REFINEMENT_FAILED',
                  content_preview_loading: null,
                },
              } as AIMasterChatMessage)
            : msg
        )
      )
    }

    focusInput()
  }

  async function handlePublicationDiscoveryReply(
    rawText: string,
    discoveryOverride?: PendingPublicationDiscovery | null
  ) {
    const text = String(rawText || '').trim()
    if (!text || !pendingPublicationDiscovery) return false

    const value = text

    const current = discoveryOverride || pendingPublicationDiscovery

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: consumeDisplayText(text),
      timestamp: new Date(),
    }

    const next: PendingPublicationDiscovery = {
      ...current,
    }

    if (current.step === 'strategic_context') {
      try {
        const gate = await savePublicationContextAnswer(current, text)

        const updated: PendingPublicationDiscovery = {
          ...current,
          contextGate: gate,
        }

        if (!gate?.canGenerate) {
          updated.step = 'strategic_context'

          setPendingPublicationDiscovery(updated)

          setMessages((prev) => [
            ...prev,
            userMsg,
            {
              id: `publication-context-question-${Date.now()}`,
              role: 'assistant',
              content:
                gate?.question ||
                'Perfecto. Solo necesito un poco más de contexto antes de crearla.',
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
                intent: 'PUBLICATION_CONTEXT_REQUIRED',
              },
            } as AIMasterChatMessage,
          ])

          focusInput()
          return true
        }

        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `publication-context-saved-${Date.now()}`,
            role: 'assistant',
            content:
              'Perfecto. Con eso ya tengo suficiente contexto. Ahora preparo la publicación.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_CONTEXT_SAVED',
            },
          } as AIMasterChatMessage,
        ])

        return await continuePublicationGeneration(updated)
      } catch (error: any) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `publication-context-error-${Date.now()}`,
            role: 'assistant',
            content:
              error?.message || 'No pude guardar el contexto. Intenta responder de nuevo.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_CONTEXT_SAVE_ERROR',
            },
          } as AIMasterChatMessage,
        ])

        return true
      }
    }

    if (current.step === 'platform') {
      const platform = parsePublicationPlatform(text)

      if (!platform) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `publication-error-${Date.now()}`,
            role: 'assistant',
            content:
              'No alcancé a identificar la plataforma. Elige Instagram, Facebook, TikTok o Multi.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
            },
          },
        ])
        pushPublicationQuestion('platform')
        return true
      }

      next.platform = platform
    }

    if (current.step === 'format') {
      const format = parsePublicationFormat(text)

      if (!format) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `publication-error-${Date.now()}`,
            role: 'assistant',
            content:
              'No alcancé a identificar el formato. Elige texto, imagen promocional, carrusel o post mixto.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
            },
          },
        ])
        pushPublicationQuestion('format')
        return true
      }

      next.format = format
      next.creativeMode = format === 'image' ? 'professional_image' : 'template'

      if (format === 'image') {
        next.step = 'instruction'
        setPendingPublicationDiscovery(next)

        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `publication-image-reference-suggestion-${Date.now()}`,
            role: 'assistant',
            content:
              'Perfecto. Vamos a crear una imagen profesional.\n\nPuedes subir una imagen de referencia, foto de producto o logo para que el resultado sea más fiel. También puedes continuar sin referencia.\n\nAhora dime qué quieres comunicar, vender o promocionar.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
              intent: 'PUBLICATION_IMAGE_REFERENCE_OPTION',
              allowReferenceUpload: true,
            },
          } as AIMasterChatMessage,
        ])

        focusInput()
        return true
      }
    }

    if (current.step === 'intent') {
      if (value.startsWith('publication_intent:')) {
        next.intent = value.replace('publication_intent:', '') as PublicationIntent
      } else {
        const intent = parsePublicationIntent(text)

        if (!intent) {
          setMessages((prev) => [
            ...prev,
            userMsg,
            {
              id: `publication-error-${Date.now()}`,
              role: 'assistant',
              content:
                'No alcancé a identificar el objetivo. Elige una intención para la publicación.',
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
              },
            },
          ])
          pushPublicationQuestion('intent')
          return true
        }

        next.intent = intent
      }
    }

    if (current.step === 'sector') {
      if (value.startsWith('publication_sector:')) {
        next.sector = value.replace('publication_sector:', '') as PublicationSector
      } else {
        const sector = parsePublicationSector(text)

        if (!sector) {
          setMessages((prev) => [
            ...prev,
            userMsg,
            {
              id: `publication-error-${Date.now()}`,
              role: 'assistant',
              content:
                'No alcancé a identificar el nicho. Elige una categoría para continuar.',
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
              },
            },
          ])
          pushPublicationQuestion('sector')
          return true
        }

        next.sector = sector
      }
    }

    if (current.step === 'reference') {
      const wantsReference = parseReferenceAnswer(text)

      if (wantsReference === null) {
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: `publication-error-${Date.now()}`,
            role: 'assistant',
            content:
              'Solo necesito saber si quieres usar imagen de referencia: sí o no.',
            timestamp: new Date(),
            meta: {
              worker: 'creator_studio_worker',
            },
          },
        ])
        pushPublicationQuestion('reference')
        return true
      }

      next.wantsReferenceImage = wantsReference
    }

    if (current.step === 'instruction') {
      if (current.sourceFlow === 'ads_campaign' && current.instruction) {
        next.instruction = current.instruction
      } else {
        next.instruction = text
      }
    }

    const nextStep = getNextPublicationStep(next)

    if (nextStep) {
      next.step = nextStep
      setPendingPublicationDiscovery(next)

      if (nextStep === 'template') {
        pushPublicationTemplateSelector(next, userMsg)
        focusInput()
        return true
      }

      const question = getPublicationQuestionMessage(nextStep)

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `publication-question-${Date.now()}`,
          role: 'assistant',
          content: question.content,
          timestamp: new Date(),
          meta: {
            worker: 'creator_studio_worker',
            intent: `PUBLICATION_DISCOVERY_${nextStep.toUpperCase()}`,
            buttons: question.buttons,
          },
        } as AIMasterChatMessage,
      ])

      focusInput()
      return true
    }

    const gate = await evaluatePublicationContextGate(next)

    if (!gate?.canGenerate) {
      next.step = 'strategic_context'
      next.contextGate = gate

      setPendingPublicationDiscovery(next)

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: `publication-context-question-${Date.now()}`,
          role: 'assistant',
          content:
            gate?.question ||
            'Antes de crearla necesito un poco más de contexto para que no salga genérica.',
          timestamp: new Date(),
          meta: {
            worker: 'creator_studio_worker',
            intent: 'PUBLICATION_CONTEXT_REQUIRED',
          },
        } as AIMasterChatMessage,
      ])

      focusInput()
      return true
    }

    return await continuePublicationGeneration(next, userMsg)
  }  

  async function continuePublicationGeneration(
    next: PendingPublicationDiscovery,
    userMsg?: AIMasterChatMessage
  ) {
    setPendingPublicationDiscovery(null)

    const processingId = `publication-processing-${Date.now()}`

    setMessages((prev) => [
      ...prev,
      ...(userMsg ? [userMsg] : []),
      {
        id: processingId,
        role: 'assistant',
        content:
          'Perfecto. Estoy preparando una dirección creativa más clara para tu publicación…',
        timestamp: new Date(),
        meta: {
          worker: 'creator_studio_worker',
          intent: 'PUBLICATION_ENRICHING_INSTRUCTION',
          executed_actions: [
            {
              type: 'enrich_publication_instruction',
              tool: 'enrich_publication_instruction',
              status: 'pending',
              loading: true,
            },
          ],
        },
      } as AIMasterChatMessage,
    ])

    const enrichedInstruction = PUBLICATION_CHAT_DRY_RUN
      ? next.instruction
      : await enrichPublicationInstruction(next)

    const shouldCreatePublicationInChat =
      canCreatePublicationInChat || Boolean(next.forceCreateInChat)

    if (!shouldCreatePublicationInChat) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === processingId
            ? ({
                ...msg,
                content:
                  '¡Listo! Ya preparé la dirección de tu publicación. Te llevo a Creator Studio para que revises los controles y generes cuando estés listo.',
                meta: {
                  ...(msg.meta || {}),
                  worker: 'creator_studio_worker',
                  intent: 'PUBLICATIONS_DISCOVERY_READY',
                  executed_actions: [
                    {
                      type: 'enrich_publication_instruction',
                      tool: 'enrich_publication_instruction',
                      ok: true,
                    },
                    {
                      type: 'open_creator_studio_publications',
                      tool: 'open_publications_tool',
                      ok: true,
                    },
                  ],
                },
              } as AIMasterChatMessage)
            : msg
        )
      )

      hideDockForExternalSurface()
      openCreatorStudioPublications(next, enrichedInstruction)

      return true
    }

    setUIState('chat')
    setChatViewMode('focus')

    const loadingId = `publication-generating-${Date.now()}`

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === processingId
          ? ({
              id: loadingId,
              role: 'assistant',
              content: '',
              timestamp: new Date(),
              meta: {
                worker: 'creator_studio_worker',
                intent: 'PUBLICATION_GENERATING_IN_CHAT',
                content_preview_loading: {
                  previewType:
                    next.format === 'carousel'
                      ? 'carousel'
                      : next.format === 'text'
                        ? 'text'
                        : 'image',
                  platform: next.platform,
                  format: next.format,
                },
              },
            } as AIMasterChatMessage)
          : msg
      )
    )

    const isDryRun = PUBLICATION_CHAT_DRY_RUN

    let json: any = null

    try {
      let preview: any = null
      let generation: any = null
      let successMessage =
        'Listo. Preparé tu carrusel. Puedes navegar entre slides y pedirme cambios como: “cambia este título”, “haz este slide más emocional” o “dame otra versión”. También puedes ajustar imagen, blur, overlay y color desde los controles.'

      if (isDryRun) {
        await wait(1400)

        const res = await fetch('/api/ai/tools/generate-publication-in-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            dryRun: true,
            payload: {
              source: 'samantha_chat',
              platform: next.platform,
              format: next.format,
              style: next.visualStyle || 'auto',
              intent: next.intent,
              sector: next.sector,
              visualStyle: next.visualStyle,
              templateId: next.templateId,
              themeId: next.themeId,
              instruction: enrichedInstruction,
              originalRequest: next.originalRequest,
              wantsReferenceImage: Boolean(
                next.referenceImageUrl || lastUploadedReferenceUrl
              ),
              referenceImageUrl: next.referenceImageUrl || lastUploadedReferenceUrl || '',
              productImageUrl: next.productImageUrl || '',
              brandImageUrl: next.brandImageUrl || '',
              generateImage: false,
              dryRun: true,
              creativeMode: next.creativeMode || 'template',
              discovery: {
                source: 'samantha_widget_publication_discovery',
                originalRequest: next.originalRequest,
                platform: next.platform,
                format: next.format,
                style: next.visualStyle || 'auto',
                intent: next.intent,
                sector: next.sector,
                visualStyle: next.visualStyle,
                templateId: next.templateId,
                themeId: next.themeId,
                referenceImageUrl: next.referenceImageUrl || lastUploadedReferenceUrl || '',
                wantsReferenceImage: Boolean(next.referenceImageUrl || lastUploadedReferenceUrl),
                instruction: next.instruction,
                enrichedInstruction,
                creativeMode: next.creativeMode || 'template',
                contextGate: next.contextGate || null,
              },
            },
          }),
        })

        json = await res.json().catch(() => null)

        if (!res.ok || !json?.ok) {
          throw new Error(json?.message || json?.error || 'No se pudo generar la publicación')
        }

        preview = json.preview
        generation = json.generation
        successMessage = json?.message || successMessage
      } else {

        if (next.format === 'image') {
          const res = await fetch('/api/ai/tools/generate-publication-in-chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              dryRun: false,
              payload: {
                source: 'samantha_chat',
                platform: next.platform,
                format: 'image',
                style: next.visualStyle || 'auto',
                visualStyle: next.visualStyle,
                instruction: enrichedInstruction,
                originalRequest: next.originalRequest,
                wantsReferenceImage: Boolean(
                  next.referenceImageUrl || lastUploadedReferenceUrl
                ),
                referenceImageUrl: next.referenceImageUrl || lastUploadedReferenceUrl || '',
                productImageUrl: next.productImageUrl || '',
                brandImageUrl: next.brandImageUrl || '',
                generateImage: true,
                creativeMode: 'professional_image',
                discovery: {
                  source: 'samantha_widget_publication_discovery',
                  originalRequest: next.originalRequest,
                  platform: next.platform,
                  format: 'image',
                  style: next.visualStyle || 'auto',
                  visualStyle: next.visualStyle,
                  instruction: next.instruction,
                  enrichedInstruction,
                  creativeMode: 'professional_image',
                  contextGate: next.contextGate || null,
                  referenceImageUrl: next.referenceImageUrl || lastUploadedReferenceUrl || '',
                  wantsReferenceImage: Boolean(next.referenceImageUrl || lastUploadedReferenceUrl),
                },
              },
            }),
          })

          json = await res.json().catch(() => null)

          if (!res.ok || !json?.ok) {
            throw new Error(json?.message || json?.error || 'No se pudo generar la imagen')
          }

          generation = json.generation
          preview = json.preview
          successMessage =
            json?.message ||
            'Listo. Generé una imagen profesional. Puedes pedirme una versión más premium, más limpia, con menos texto o con otro estilo.'

          return
        }

        const { generation: realGeneration, resultJson, draft } =
          await generateTemplateCarouselFromCreatorStudio(next, enrichedInstruction)

        generation = realGeneration

        preview = {
          type: 'publication',
          mode: 'template_carousel',
          platform: draft.platform || next.platform,
          format: draft.format || 'carousel',
          intent: draft.intent || next.intent,
          sector: draft.sector || next.sector,
          templateId: draft.templateId || next.templateId || 'sector-intent-pack',
          themeId: draft.themeId || next.themeId,
          title: draft.title || resultJson?.title || 'Carrusel',
          caption: resultJson?.caption || '',
          hashtags: resultJson?.hashtags || [],
          draft,
          generation: realGeneration,
          actions: [
            { id: 'export_publication_carousel', label: 'Exportar' },
            { id: 'open_creator_studio', label: 'Abrir en Creator Studio' },
          ],
        }
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingId
            ? ({
                ...msg,
                content: successMessage,
                meta: {
                  ...(msg.meta || {}),
                  intent: 'PUBLICATION_GENERATED_IN_CHAT',
                  content_preview: preview,
                  content_generation: generation,
                  content_preview_loading: null,
                  executed_actions: [
                    {
                      type: isDryRun
                        ? 'generate_publication_in_chat_dry_run'
                        : 'generate_publication_with_creator_studio',
                      tool: isDryRun
                        ? 'generate_publication_in_chat'
                        : 'creator_studio_generate_publications',
                      ok: true,
                    },
                  ],
                },
              } as AIMasterChatMessage)
            : msg
        )
      )

      setLastUploadedReferenceUrl('')
      setPendingUploadFiles([])

      if (
        next.sourceFlow === 'ads_campaign' &&
        pendingAdsCampaignDiscovery?.waitingForCreativeGeneration
      ) {
        const creativeId = String(
          json?.generation?.id ||
            json?.generation?.generationId ||
            json?.preview?.generationId ||
            json?.preview?.id ||
            ''
        )

        const adsNext: PendingAdsCampaignDiscovery = {
          ...pendingAdsCampaignDiscovery,
          waitingForCreativeGeneration: false,
          generatedCreativeId: creativeId || undefined,
          generatedCreativeTitle:
            String(json?.generation?.title || json?.preview?.title || '').trim() ||
            'Pieza visual generada',
          generatedCreativePreview: json?.preview || null,
          creativeMode: pendingAdsCampaignDiscovery.creativeMode || 'new_visual',
          step: 'budget',
        }

        setPendingAdsCampaignDiscovery(adsNext)

        setMessages((prev) => [
          ...prev,
          {
            id: `ads-creative-ready-${Date.now()}`,
            role: 'assistant',
            content:
              'Listo. Ya tenemos una pieza visual para la campaña. Ahora definamos el presupuesto diario.',
            timestamp: new Date(),
            meta: {
              worker: 'ads_worker',
              intent: 'ADS_CREATIVE_GENERATED_READY',
            },
          },
        ])

        pushAdsQuestion('budget', adsNext)
      }

      return true
    } catch (err: any) {
      console.error('[AIMasterWidget] generate publication in chat error', err)

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingId
            ? ({
                ...msg,
                content:
                  'Tuve un problema generando la publicación aquí mismo. Ya dejé preparada la instrucción para que puedas terminarla en Creator Studio.',
                meta: {
                  ...(msg.meta || {}),
                  intent: 'PUBLICATION_GENERATION_FAILED',
                  content_preview_loading: null,
                },
              } as AIMasterChatMessage)
            : msg
        )
      )

      setChatViewMode('dock')
      openCreatorStudioPublications(next, enrichedInstruction)

      return true
    }
  }

  function findLatestEditablePublicationMessage(messages: AIMasterChatMessage[]) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i]
      const preview = (msg.meta as any)?.content_preview

      if (!preview) continue

      if (preview.previewType === 'carousel' && preview.draft) {
        return {
          message: msg,
          preview,
        }
      }
    }

    return null
  }

  function looksLikePublicationEditInstruction(text: string) {
    const t = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

    const editWords = [
      'cambia',
      'cambiar',
      'cambiale',
      'cámbiale',
      'modifica',
      'modificar',
      'ajusta',
      'ajustar',
      'reemplaza',
      'reemplazar',
      'quita',
      'quitar',
      'pon',
      'poner',
      'agrega',
      'agregar',
      'haz',
      'quiero que',
      'que diga',
      'boton',
      'botón',
      'titulo',
      'título',
      'mensaje',
      'texto',
      'copy',
      'headline',
      'cta',
      'color',
      'fondo',
      'blur',
      'overlay',
    ]

    return editWords.some((word) => t.includes(word))
  }

  async function generateAIFunnelFromDiscovery(
    state: Extract<PendingFunnelDiscovery, { mode: 'ai_html' }>
  ) {
    if (!sellerId || !currentUserId) {
      toast.error('No se pudo identificar tu sesión')
      return
    }

    const generatingState = {
      ...state,
      step: 'generating' as const,
    }

    setPendingFunnelDiscovery(generatingState)

    const loadingMessageId = `ai-funnel-generating-${Date.now()}`

    setMessages((prev) => [
      ...prev,
      createChatUserMessage('Crear landing'),
      {
        id: loadingMessageId,
        role: 'assistant',
        content:
          'Estoy construyendo la primera versión completa de tu landing…',
        timestamp: new Date(),
        meta: {
          worker: 'funnel_worker',
          intent: 'AI_FUNNEL_GENERATING',
          steps: [
            {
              id: 'strategy',
              label: 'Organizando estrategia y contenido',
              status: 'running',
            },
            {
              id: 'design',
              label: 'Diseñando estructura visual',
              status: 'pending',
            },
            {
              id: 'html',
              label: 'Construyendo HTML, CSS y animaciones',
              status: 'pending',
            },
            {
              id: 'save',
              label: 'Guardando el embudo editable',
              status: 'pending',
            },
          ],
        },
      } as AIMasterChatMessage,
    ])

    try {
      const res = await fetch('/api/funnels/ai-html/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sellerId,
          customerId: currentUserId,
          source: state.source,

          brief: {
            description: state.description,
            originalRequest: state.originalRequest,
            goal: state.goal,
            audience: state.audience,
            offer: state.offer,
            primaryAction: state.primaryAction,

            branding: {
              useExisting: state.useBranding,
              ...(state.branding || {}),
            },

            references: state.references,
            inferred: state.inferred,
          },

          output: {
            format: 'complete_html_document',
            builder: 'html_import',
            createFunnel: true,
            createPage: true,
            createBlock: true,
            responsive: true,
            includeEditableIds: true,
            includeSectionIds: true,
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(
          json?.error ||
            json?.message ||
            'No se pudo generar la landing'
        )
      }

      const funnelId = String(json.funnelId || '')
      const pageId = String(
        json.pageId ||
          json.pages?.[0]?.id ||
          ''
      )

      if (!funnelId || !pageId) {
        throw new Error(
          'La landing se generó, pero el servidor no devolvió funnelId y pageId'
        )
      }

      const normalizedFunnelId = String(funnelId)
      const normalizedPageId = String(pageId)

      await loadFunnels(currentUserId)

      setSelectedFunnelId(normalizedFunnelId)
      setSelectedPageId(normalizedPageId)

      await loadPageTree(normalizedPageId)

      setPendingFunnelDiscovery({
        ...state,
        step: 'done',
      })

      setMessages((prev) =>
        prev.map((message) =>
          message.id === loadingMessageId
            ? ({
                ...message,
                content:
                  'Tu landing ya está lista. Creé la estructura completa, el diseño responsivo y el bloque HTML editable.\n\nPuedes abrirla ahora y después pedirme cambios desde este mismo chat.',
                meta: {
                  worker: 'funnel_worker',
                  intent: 'AI_FUNNEL_GENERATED',
                  executed_actions: [
                    {
                      tool: 'create_funnel',
                      ok: true,
                    },
                    {
                      tool: 'create_html_import_page',
                      ok: true,
                    },
                    {
                      tool: 'save_ai_funnel_version',
                      ok: true,
                    },
                  ],
                  buttons: [
                    {
                      label: 'Abrir landing',
                      value: `navigate:/funnel-builder?funnelId=${encodeURIComponent(funnelId)}`,
                      action: 'navigate',
                      route: `/funnel-builder?funnelId=${encodeURIComponent(funnelId)}`,
                      emoji: '🚀',
                    },
                  ],
                },
              } as AIMasterChatMessage)
            : message
        )
      )
      /*
      router.push(
        `/funnel-builder?funnelId=${encodeURIComponent(
          normalizedFunnelId
        )}&pageId=${encodeURIComponent(normalizedPageId)}`
      )
        */
    } catch (error: any) {
      console.error('[generateAIFunnelFromDiscovery]', error)

      setPendingFunnelDiscovery({
        ...state,
        step: 'review',
      })

      setMessages((prev) =>
        prev.map((message) =>
          message.id === loadingMessageId
            ? ({
                ...message,
                content:
                  error?.message ||
                  'No pude terminar la landing. Tu información sigue guardada y puedes volver a intentarlo.',
                meta: {
                  worker: 'funnel_worker',
                  intent: 'AI_FUNNEL_GENERATION_ERROR',
                  buttons: [
                    {
                      label: 'Intentar nuevamente',
                      value: 'ai_funnel:generate',
                      action: 'ai_funnel_action',
                      emoji: '🔄',
                    },
                    {
                      label: 'Cambiar información',
                      value: 'ai_funnel:edit_brief',
                      action: 'ai_funnel_action',
                      emoji: '✏️',
                    },
                  ],
                },
              } as AIMasterChatMessage)
            : message
        )
      )
    }
  }

  async function tryHandlePublicationSlideEdit(text: string) {
    const target = findLatestEditablePublicationMessage(messages)

    if (!target) return false

    if (!looksLikePublicationEditInstruction(text)) {
      return false
    }

    const { message: sourceMessage, preview } = target

    const activeSlideIndex = Number(
      preview?.activeSlideIndex ??
        preview?.active_slide_index ??
        0
    )

    const safeSlideIndex =
      Number.isFinite(activeSlideIndex) && activeSlideIndex >= 0
        ? activeSlideIndex
        : 0

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }

    const loadingId = `publication-slide-editing-${Date.now()}`

    setMessages((prev) => [
      ...prev,
      userMsg,
      {
        id: loadingId,
        role: 'assistant',
        content: `Estoy ajustando el slide ${safeSlideIndex + 1}.`,
        timestamp: new Date(),
        meta: {
          worker: 'creator_studio_worker',
          intent: 'PUBLICATION_SLIDE_EDITING',
          executed_actions: [
            {
              type: 'edit_publication_slide',
              tool: 'edit_publication_slide',
              status: 'pending',
              loading: true,
            },
          ],
        },
      } as AIMasterChatMessage,
    ])

    setUIState('chat')
    setChatViewMode('focus')

    try {
      const res = await fetch('/api/ai/tools/generate-publication-in-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          dryRun: PUBLICATION_CHAT_DRY_RUN,
          payload: {
            source: 'samantha_publication_slide_conversation',
            refinementAction: 'edit_active_slide',
            previousPreview: preview,
            previousGenerationId: preview.generationId || preview.generation_id || 0,
            activeSlideIndex: safeSlideIndex,
            instruction: text,

            platform: preview.platform || 'instagram',
            format: preview.format || preview.previewType || 'carousel',
            style: preview.style || 'hero_premium',

            generateImage: false,

            discovery: {
              source: 'samantha_publication_slide_conversation',
              activeSlideIndex: safeSlideIndex,
              userInstruction: text,
            },
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.message || json?.error || 'No pude editar el slide.')
      }

      const updatedPreview = {
        ...(json.preview || preview),
        activeSlideIndex: safeSlideIndex,
      }

      setMessages((prev) =>
        prev
          .map((msg) => {
            if (msg.id === sourceMessage.id) {
              return {
                ...msg,
                content: 'Listo. Actualicé el carrusel.',
                meta: {
                  ...(msg.meta || {}),
                  intent: 'PUBLICATION_SLIDE_EDIT_READY',
                  content_preview: updatedPreview,
                },
              } as AIMasterChatMessage
            }

            if (msg.id === loadingId) {
              return {
                ...msg,
                content: `Listo. Actualicé el slide ${safeSlideIndex + 1}.`,
                meta: {
                  ...(msg.meta || {}),
                  intent: 'PUBLICATION_SLIDE_EDIT_READY',
                  executed_actions: [
                    {
                      type: 'edit_publication_slide',
                      tool: 'edit_publication_slide',
                      ok: true,
                    },
                  ],
                },
              } as AIMasterChatMessage
            }

            return msg
          })
      )
    } catch (error: any) {
      console.error('[publication slide conversational edit] error:', error)

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingId
            ? ({
                ...msg,
                content: String(error?.message || 'No pude editar este slide.'),
                meta: {
                  ...(msg.meta || {}),
                  intent: 'PUBLICATION_SLIDE_EDIT_FAILED',
                  executed_actions: [
                    {
                      type: 'edit_publication_slide',
                      tool: 'edit_publication_slide',
                      ok: false,
                      error: String(error?.message || 'error'),
                    },
                  ],
                },
              } as AIMasterChatMessage)
            : msg
        )
      )
    }

    focusInput()
    return true
  }

  async function tryHandleLightweightIntent(rawText: string) {
    const text = String(rawText || '').trim()
    if (!text) return false

    const supportHandled = await tryHandlePlatformSupport(text)
    if (supportHandled) {
      focusInput()
      return true
    }

    const resolved = resolveLightweightIntentDetailed(text)
    const intent = resolved.intent

    if (intent === 'unknown') return false

    // No confirmamos navegación. Dejamos que el orquestador la maneje.
    if (intent === 'navigate') return false

    if (intent === 'open_latest_publication_carousel') {
      const userMsg: AIMasterChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
        timestamp: new Date(),
      }

      const assistantMsg: AIMasterChatMessage = {
        id: `assistant-${Date.now() + 1}`,
        role: 'assistant',
        content: 'Claro, abro tu último carrusel creado.',
        timestamp: new Date(),
        meta: {
          worker: getWorkerForLightweightIntent(intent),
          lightweightIntent: intent,
          rawPrompt: text,
          buttons: [
            {
              label: 'Abrir último carrusel',
              value: 'open_latest_publication_carousel',
              emoji: '🖼️',
            },
          ],
        },
      }

      setMessages((prev) => [...prev, userMsg, assistantMsg])
      focusInput()
      return true
    }

    if (intent === 'open_create_ads_campaign_flow') {
      if (!canUseAdsCampaignSimulator) {
        return false
      }

      startAdsCampaignDiscovery(text)
      focusInput()
      return true
    }

    if (intent === 'open_create_publication_flow') {
      startPublicationDiscovery(text)
      focusInput()
      return true
    }

    if (intent === 'open_build_funnel_flow') {
      startFunnelDiscovery(text)
      focusInput()
      return true
    }

    if (!isFlowIntent(intent)) return false

    const userMsg: AIMasterChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }

    if (intent === 'open_create_content_flow') {
      setMessages((prev) => [
        ...prev,
        userMsg,
      ])

      await openConfirmedLightweightFlow(
        intent,
        text
      )

      return true
    }

    setPendingLightweightFlow({
      intent,
      text,
    })

    const assistantMsg: AIMasterChatMessage = {
      id: `assistant-${Date.now() + 1}`,
      role: 'assistant',
      content:
        `Puedo abrir un flujo rápido para esto, o podemos seguir resolviéndolo aquí con ${masterAgentName}.`,
      timestamp: new Date(),
      meta: {
        worker: getWorkerForLightweightIntent(intent),
        lightweightIntent: intent,
        rawPrompt: text,
        buttons: [
          {
            label: 'Abrir flujo rápido',
            value: 'confirm_lightweight_flow',
            emoji: '⚡',
          },
          {
            label: 'Seguir aquí',
            value: 'continue_with_orchestrator',
            emoji: '💬',
          },
        ],
      },
    }

    setMessages((prev) => [...prev, userMsg, assistantMsg])
    focusInput()
    return true
  }

  async function openConfirmedLightweightFlow(intent: LightweightIntent, text: string) {
    if (intent === 'open_create_content_flow') {
      setShowNoCreditsCard(false)

      const taskRequest =
        buildCreateContentTaskRequest(text)

      if (
        canUseCreateContentDryRun
      ) {
        setPendingCreateContentResume({
          snapshotId:
            0,

          snapshot: {
            dryRun:
              true,
          },

          request:
            taskRequest,
        })

        setMessages((prev) => [
          ...prev,
          {
            id:
              `assistant-${Date.now()}`,

            role:
              'assistant',

            content:
              'Encontré un proyecto de contenido que podemos continuar. ¿Quieres retomarlo o iniciar uno nuevo?',

            timestamp:
              new Date(),

            meta: {
              worker:
                'content_creator',

              buttons: [
                {
                  label:
                    'Continuar proyecto',

                  value:
                    'resume_create_content',

                  emoji:
                    '↩️',
                },

                {
                  label:
                    'Iniciar nuevo',

                  value:
                    'restart_create_content',

                  emoji:
                    '🆕',
                },
              ],
            },
          } as AIMasterChatMessage,
        ])

        focusInput()
        return
      }

      const resume =
        await fetchCreateContentResume()

      if (resume?.hasSnapshot && resume?.snapshot?.snapshotId && resume?.snapshot?.snapshotJson) {
        setPendingCreateContentResume({
          snapshotId: Number(resume.snapshot.snapshotId),
          snapshot: resume.snapshot.snapshotJson,
          request: taskRequest,
        })

        setMessages((prev) => [
          ...prev,
          {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content:
              'Encontré un proyecto de contenido que se quedó a medias. ¿Quieres continuarlo o iniciar uno nuevo?',
            timestamp: new Date(),
            meta: {
              worker: 'content_creator',
              buttons: [
                {
                  label: 'Continuar proyecto',
                  value: 'resume_create_content',
                  emoji: '↩️',
                },
                {
                  label: 'Iniciar nuevo',
                  value: 'restart_create_content',
                  emoji: '🆕',
                },
              ],
            },
          },
        ])

        focusInput()
        return
      }

      await beginCreateContentDirectionChat({
        request: taskRequest,
        direction: EMPTY_CREATE_CONTENT_DIRECTION,
        snapshotId: null,
      })

      return
    }

    if (intent === 'open_edit_product_flow') {
      hideDockForExternalSurface()
      openTaskFlow({
        taskType: 'edit_product',
        worker: 'product_worker',
        title: 'Editar producto',
        subtitle: 'Vamos a enfocarnos solo en lo que quieres cambiar.',
        routeFallback: '/offers',
        requiresLLM: false,
        payload: {
          sellerId,
          userId: Number(user?.userId || 0),
          rawPrompt: text,
          source: 'widget',
          pathname,
        },
      })
      
      return
    }

    if (intent === 'open_build_funnel_flow') {
      hideDockForExternalSurface()
      openTaskFlow({
        taskType: 'build_funnel',
        worker: 'funnel_worker',
        title: 'Construir embudo',
        subtitle: 'Primero definimos la dirección, luego ejecutamos.',
        routeFallback: '/funnel-builder',
        requiresLLM: false,
        payload: {
          sellerId,
          userId: Number(user?.userId || 0),
          rawPrompt: text,
          source: 'widget',
          pathname,
        },
      })
      
      return
    }

    if (intent === 'open_create_portrait_flow') {
      hideDockForExternalSurface()
      openTaskFlow({
        taskType: 'create_portrait',
        worker: 'creator_studio_worker',
        title: 'Crear retrato profesional',
        subtitle: 'Sube tu foto y genera una imagen profesional premium.',
        routeFallback: '/creator-studio?tab=portrait',
        requiresLLM: false,
        payload: {
          sellerId,
          userId: Number(user?.userId || 0),
          rawPrompt: text,
          source: 'widget',
          pathname,
          toolKey: 'portrait_creator',
        },
      })

      return
    }

    if (intent === 'open_create_flyer_flow') {
      hideDockForExternalSurface()
      openTaskFlow({
        taskType: 'create_flyer',
        worker: 'creator_studio_worker',
        title: 'Crear flyer',
        subtitle: 'Usa tu retrato y un template para generar un flyer premium.',
        routeFallback: '/creator-studio?tab=flyer',
        requiresLLM: false,
        payload: {
          sellerId,
          userId: Number(user?.userId || 0),
          rawPrompt: text,
          source: 'widget',
          pathname,
          toolKey: 'flyer_creator',
        },
      })

    }
  }

  useEffect(() => {
    async function handleStartCreateContentFlow(event: Event) {
      const customEvent = event as CustomEvent<{
        source?: string
        sellerId?: number
        userId?: number
        projectId?: number | string | null
        text?: string
        project?: any
        brief?: any
        mode?: string
      }>

      const detail = customEvent.detail || {}

      const text =
        detail.text ||
        'Quiero crear un nuevo proyecto de contenido desde Creator Studio.'

      const taskRequest = buildCreateContentTaskRequest(text)
      const basePayload = taskRequest.payload || {}

      const project = detail.project || null
      const brief = detail.brief || project?.brief_json || null

      const enrichedTaskRequest = {
        ...taskRequest,
        taskType: taskRequest.taskType as any,
        payload: {
          ...basePayload,

          sellerId: detail.sellerId || basePayload.sellerId,
          userId: detail.userId || basePayload.userId,

          // Esto evita crear otro proyecto nuevo.
          projectId: detail.projectId || project?.project_id || null,

          // Contexto del proyecto draft.
          creatorStudioProject: project,
          initialBrief: brief,

          source: detail.source || 'creator_studio_project',
          flowMode: detail.mode || 'complete_project_brief',

          // Skill desde el proyecto.
          skillAgentId:
            project?.skill_agent_id ||
            basePayload.skillAgentId ||
            null,
          skillAgentSlug:
            project?.skill_agent_slug ||
            basePayload.skillAgentSlug ||
            null,
          skillName:
            project?.skill_agent_name ||
            basePayload.skillName ||
            null,
        },
      } as any

      const resume = await fetchCreateContentResume()

      // Si viene desde un proyecto existente, NO preguntamos por snapshots viejos.
      if (
        !detail.projectId &&
        resume?.hasSnapshot &&
        resume?.snapshot?.snapshotId &&
        resume?.snapshot?.snapshotJson
      ) {
        setPendingCreateContentResume({
          snapshotId: Number(resume.snapshot.snapshotId),
          snapshot: resume.snapshot.snapshotJson,
          request: enrichedTaskRequest,
        })

        setMessages((prev) => [
          ...prev,
          {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content:
              'Encontré un proyecto de contenido que se quedó a medias. ¿Quieres continuarlo o iniciar uno nuevo?',
            timestamp: new Date(),
            meta: {
              worker: 'content_creator',
              buttons: [
                {
                  label: 'Continuar proyecto',
                  value: 'resume_create_content',
                  emoji: '↩️',
                },
                {
                  label: 'Iniciar nuevo',
                  value: 'restart_create_content',
                  emoji: '🆕',
                },
              ],
            },
          },
        ])

        focusInput()
        return
      }

      openTaskFlow(enrichedTaskRequest)
      focusInput()
    }

    window.addEventListener(
      'hubee:start-create-content-flow',
      handleStartCreateContentFlow
    )

    return () => {
      window.removeEventListener(
        'hubee:start-create-content-flow',
        handleStartCreateContentFlow
      )
    }
  }, [])

  async function createSupportTicketFromWidget(text: string) {
    const message = String(text || '').trim()
    if (!message || !sellerId) return

    const userMsg: AIMasterChatMessage = {
      id: `user-ticket-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMsg])
    setPendingSupportTicket(null)

    try {
      const subject =
        message.length > 70
          ? `${message.slice(0, 70)}...`
          : message || 'Solicitud de soporte'

      const res = await fetch('/api/ai/platform-support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({
          sellerId,
          subject,
          message,
          source: 'samantha',
          conversationId,
          matchedArticleId:
            pendingSupportTicket?.matchedArticle?.id ||
            pendingSupportTicket?.matchedArticle?.article_id ||
            null,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo crear el ticket')
      }

      const ticketId = json?.ticket?.id || json?.ticket?.ticket_id

      setMessages((prev) => [
        ...prev,
        {
          id: `ticket-created-${Date.now()}`,
          role: 'assistant',
          content: `Listo, abrí tu ticket de soporte #${ticketId}.\n\nEl equipo revisará tu caso y te responderá lo antes posible.`,
          timestamp: new Date(),
          meta: {
            intent: 'PLATFORM',
            worker: 'platform_support',
            buttons: [
              {
                label: 'Ver mis tickets',
                value: '/settings/help',
                action: 'navigate',
                route: '/settings/help',
                emoji: '🎫',
              },
            ],
            executed_actions: [
              {
                type: 'support_ticket_created',
                tool: 'platform_support_ticket',
                ok: true,
                ticketId,
              },
            ],
          },
        },
      ])

      focusInput()
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: `ticket-error-${Date.now()}`,
          role: 'assistant',
          content:
            'No pude crear el ticket en este momento. Intenta de nuevo en unos segundos o contacta al equipo de soporte directamente.',
          timestamp: new Date(),
          meta: {
            intent: 'PLATFORM',
            worker: 'platform_support',
          },
        },
      ])
    }
  }

  async function uploadSamanthaReferenceImage(
    file: File,
    purpose: 'publication_reference' | 'funnel_reference' = 'publication_reference'
  ) {
    const formData = new FormData()

    formData.append('file', file)
    formData.append('source', 'samantha_chat')
    formData.append('purpose', purpose)
    formData.append('role', 'reference')

    const res = await fetch('/api/ai/uploads/reference-image', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })

    const json = await res.json().catch(() => null)

    const imageUrl =
      json?.url ||
      json?.imageUrl ||
      json?.reference?.url ||
      json?.asset?.file_url ||
      ''

    if (!res.ok || !json?.ok || !imageUrl) {
      throw new Error(json?.error || 'No se pudo subir la imagen')
    }

    return imageUrl as string
  }

  async function handleAIFunnelReferenceImageSelected(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (
      !file ||
      !pendingFunnelDiscovery ||
      pendingFunnelDiscovery.mode !== 'ai_html'
    ) {
      return
    }

    try {
      setUploadingReference(true)

      const imageUrl = await uploadSamanthaReferenceImage(
        file,
        'funnel_reference'
      )

      const next = {
        ...pendingFunnelDiscovery,
        step: 'review' as const,
        references: [
          ...pendingFunnelDiscovery.references,
          {
            type: 'image' as const,
            url: imageUrl,
            name: file.name,
          },
        ],
      }

      setPendingFunnelDiscovery(next)

      setMessages((prev) => [
        ...prev,
        createChatUserMessage('Imagen de referencia', {
          attachmentType: 'image',
          imageUrl,
          referenceImageUrl: imageUrl,
        }),
        buildAIFunnelReviewMessage(next),
      ])

      focusInput()
    } catch (error: any) {
      setMessages((prev) => [
        ...prev,
        createFunnelAssistantMessage({
          intent: 'AI_FUNNEL_REFERENCE_UPLOAD_ERROR',
          content:
            error?.message ||
            'No pude subir la imagen. Intenta nuevamente con otro archivo.',
        }),
      ])
    } finally {
      setUploadingReference(false)
    }
  }

  async function handleSend(forcedText?: string) {
    const text = (forcedText ?? input).trim()
    if (!sellerId || loading || !text.trim()) return

    setCompactTurnStartedAt(Date.now())

    if (pendingSupportTicket?.awaitingDescription) {
      await createSupportTicketFromWidget(text)
      return
    }

    if (pendingPublicationDiscovery) {
      if (await handlePublicationDiscoveryReply(text)) {
        return
      }
    }

    if (pendingAdsCampaignDiscovery) {
      if (!canUseAdsCampaignSimulator) {
        setPendingAdsCampaignDiscovery(null)
      } else if (await handleAdsCampaignDiscoveryReply(text)) {
        return
      }
    }

    if (pendingFunnelDiscovery?.mode === 'ai_html') {
      if (await handleAIFunnelDiscoveryReply(text)) {
        return
      }
    }

    if (pendingFunnelDiscovery?.mode === 'legacy') {
      if (await handleFunnelDiscoveryReply(text)) {
        return
      }
    }

    if (pendingLaunchpadDiscovery) {
      if (await handleLaunchpadDiscoveryReply(text)) {
        return
      }
    }

    if (
      pendingCreateContentDirection &&
      [
        'discovery_topic',
        'discovery_audience',
        'discovery_action',
      ].includes(
        pendingCreateContentDirection.step
      )
    ) {
      const textValue =
        String(input || '').trim()

      if (!textValue) {
        return
      }

      await handleCreateContentDiscoveryText(
        textValue
      )

      setInput('')
      return
    }

    if (await tryHandleLightweightIntent(text)) {
      return
    }

    const handledPublicationEdit = await tryHandlePublicationSlideEdit(text)

    if (handledPublicationEdit) {
      return
    }

    setShowNoCreditsCard(false)

    const needsTraining = !masterAgent || !isTrained

    const wantsTraining = needsTraining && isPositiveIntent(text)

    let res: any = null

    try {
      res = await sendMessage({
        sellerId,
        userId: Number(user?.userId),
        brandName: brand?.name || 'Hubee',
        agentType: 'master',
        message: text,
        mode: wantsTraining ? 'init_training' : 'chat',
        skillMode: effectiveSelectionMode,
        skillId:
          effectiveSelectionMode === 'forced' && effectiveForcedSkillId
            ? effectiveForcedSkillId
            : null,
        pageContext,
        uiContext: {
          current_url: pathname,
          can_navigate: true,
          can_edit: !!isOwner,
          widget: 'AIMasterWidget',
          conversation_id: conversationId,
        },
        platformContext: platformSnapshot,
        context: {
          funnel_context: funnelContext,
          lesson_context: platformSnapshot?.current_lesson || null,
          routing: {
            source_widget: 'AIMasterWidget',
            seller_id: sellerId,

            preferred_agent_id:
              effectiveSelectionMode === 'forced' && effectiveForcedSkillId
                ? Number(effectiveForcedSkillId)
                : undefined,

            preferred_agent_type:
              effectiveSelectionMode === 'forced' && effectiveForcedSkillId
                ? 'specialist'
                : 'master',

            force_agent:
              effectiveSelectionMode === 'forced' && !!effectiveForcedSkillId,

            skill_mode: effectiveSelectionMode,
          },
        },
      })
    } catch (err: any) {
      res = err?.details || err?.response || err?.data || {
        error: err?.message || 'UNKNOWN_ERROR',
        details: err?.details || null,
      }
    }

    const successfulFunnelActions = (res?.executed_actions || []).filter(
      (a: any) =>
        a?.ok &&
        ['update_funnel_module', 'add_funnel_module', 'replace_funnel_draft'].includes(a?.tool)
    )

    if (successfulFunnelActions.length > 0) {
      const first = successfulFunnelActions[0]
      const result = first?.response?.result || {}

      window.dispatchEvent(
        new CustomEvent('funnel:draft-refresh', {
          detail: {
            funnelId: String(result?.funnelId || funnelContext?.funnelId || ''),
            pageId: String(result?.pageId || funnelContext?.currentPage?.id || ''),
            tools: successfulFunnelActions.map((a: any) => a.tool),
          },
        })
      )
    }

    if (res?.action === 'OPEN_MASTER_AGENT_TRAINING') {
      const canOpenTrainingModal =
        aiConfig.chatEnabled &&
        aiConfig.trainingEnabled &&
        !aiConfig.sellerTrainingExcluded

      if (canOpenTrainingModal) {
        setUIState('training')
        setCurrentAgentId(res.agentId)
      } else {
        setUIState('chat')
      }
      return
    }

    const noCredits =
      res?.action === 'NO_CREDITS' ||
      res?.details?.action === 'NO_CREDITS' ||
      res?.error === `No tienes créditos suficientes para usar ${masterAgentName}.`

    if (noCredits) {
      setShowNoCreditsCard(true)
      loadCreditPackages()
      setMessages((prev) => [
        ...prev,
        {
          id: `nocredits-now-${Date.now()}`,
          role: 'assistant',
          content: 'Ups… parece que tus créditos se agotaron justo ahora. 🙈',
          timestamp: new Date(),
        },
      ])
      return
    }

    if (res?.client_action?.type === 'open_funnel_editor' && res?.client_action?.href) {
      setMessages((prev) => [
        ...prev,
        {
          id: `redirect-${Date.now()}`,
          role: 'assistant',
          content:
            'Perfecto. Ya preparé la base del funnel y te llevaré al editor para continuar.',
          timestamp: new Date(),
        },
      ])

      setTimeout(() => {
        setChatViewMode('dock')
        hideDockForExternalSurface()
        router.push(res.client_action.href)
        setTimeout(() => {
          router.refresh()
        }, 150)
      }, 900)
    }

    const assistantText =
      String(
        res?.result?.message ||
          res?.message ||
          res?.details?.message ||
          ''
      ).trim()

    const hasNavigation =
      !!res?.result?.navigation ||
      !!res?.navigation ||
      !!res?.client_action?.href

    const shouldOpenFocus =
      !isEmbeddedConsole &&
      !hasNavigation &&
      assistantText &&
      (
        assistantText.length > 1400 ||
        assistantText.split('\n').length > 18
      )

    if (shouldOpenFocus) {
      setChatViewMode('focus')
    }
    focusInput()
  }

  function openDesktopCommandPanel() {
    setUIState('chat')
    setChatViewMode('dock')
    injectAssistantNotifications()
    setCommandFocused(true)

    requestAnimationFrame(() => {
      commandInputRef.current?.focus()
      resizeTextarea(commandInputRef.current)
    })
  }

  function submitCommandBarText() {
    if (
      contextBarMode ===
        'publish' &&
      !canPublishInContext
    ) {
      return
    }
    const text =
      input.trim()

    if (
      !text ||
      loading
    ) {
      return
    }

    if (
      hasContextBarToggle &&
      contextBarMode ===
        'publish'
    ) {
      /*
      * Si está compacto,
      * Enter abre el composer.
      *
      * Nunca publica accidentalmente
      * solo por Enter desde compacto.
      */
      if (
        !contextBarExpanded
      ) {
        contextPublishComposer
          .openComposer({
            initialContent:
              text,
          })

        setContextBarExpanded(
          true
        )

        return
      }

      return
    }

    setUIState(
      'chat'
    )

    setChatViewMode(
      'dock'
    )

    setInput('')

    setActiveSuggestionIndex(
      -1
    )

    setCommandFocused(
      false
    )

    void handleSend(
      text
    )

    requestAnimationFrame(
      () => {
        if (
          commandInputRef.current
        ) {
          commandInputRef
            .current
            .style
            .height =
            'auto'
        }
      }
    )
  }

  const handleClear = useCallback(() => {
    clearMessages()
    setShowNoCreditsCard(false)
    setVisibleCount(INITIAL_VISIBLE_MESSAGES)
  }, [clearMessages])

  if (!sellerId || !user) return null
  if (!canSeeChatWidget) return null
  if (isHidden) return null
  if (!sellerId || !user) return null

  function AssistantNotificationShelf() {
    if (!assistantNotifications.length) return null

    return (
      <div className="px-4 pt-3">
        <button
          type="button"
          onClick={() => setShowNotificationsPanel((v) => !v)}
          className="group w-full rounded-2xl border border-neutral-200 bg-white/80 px-3 py-3 text-left shadow-sm backdrop-blur transition hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900/80 dark:hover:border-neutral-700 dark:hover:bg-neutral-900"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-black text-white dark:bg-white dark:text-black">
              <Sparkles className="h-4 w-4" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-neutral-950 dark:text-neutral-50">
                {masterAgentName} tiene sugerencias para ti
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {assistantNotifications.length} pendiente{assistantNotifications.length === 1 ? '' : 's'} para continuar
              </p>
            </div>

            <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
              {assistantNotifications.length > 9 ? '9+' : assistantNotifications.length}
            </span>
          </div>
        </button>

        {showNotificationsPanel ? (
          <div className="mt-2 space-y-2">
            {assistantNotifications.slice(0, 4).map((item: any) => (
              <button
                key={item.id}
                type="button"
                onClick={() =>
                  handleButtonClick(
                    {
                      label: item.label,
                      value: item.value,
                      emoji: item.emoji,
                      action: item.route ? 'navigate' : undefined,
                      route: item.route || undefined,
                    } as any,
                    null as any
                  )
                }
                className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-3 text-left shadow-sm transition hover:border-black/20 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-white/20"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-base dark:bg-neutral-800">
                    {item.emoji || '✨'}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-400">
                      {item.title || item.category || 'Sugerencia'}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-neutral-950 dark:text-neutral-50">
                      {item.label}
                    </p>
                    {item.description ? (
                      <p className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                        {item.description}
                      </p>
                    ) : null}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  const isFocusMode = chatViewMode === 'focus'
  const isCompactDesktopDock = uiState === 'chat' && chatViewMode !== 'focus'

  const hasTypedInput = input.trim().length > 0
  const activeGuidedFlowLabel = getActiveGuidedFlowLabel()

  const embeddedConversationStarted =
    messages.length > 1 ||
    messages.some((message) => message.role === 'user')

  const showEmbeddedContentInitiative =
    isEmbeddedConsole &&
    embeddedComposerActivated &&
    embeddedContentCreationAllowed === true &&
    !embeddedConversationStarted &&
    !activeGuidedFlowLabel &&
    !loading

  const shouldShowResetAsPrimaryAction =
    Boolean(activeGuidedFlowLabel) && !hasTypedInput

  const shouldRenderLaunchpadInlineBar =
    isAIHostRoute &&
    !!launchpadSlot &&
    !isFocusMode &&
    !isEmbeddedConsole

  const launchpadInlineSamanthaBar = shouldRenderLaunchpadInlineBar ? (
    <div className="w-full">
      <div ref={commandBarWrapRef} className="relative mx-auto w-full">
        <form
          ref={launchpadCommandFormRef}
          onSubmit={(event) => {
            event.preventDefault()
            submitCommandBarText()
          }}
          className="pointer-events-auto group flex min-h-[64px] w-full items-center gap-3 rounded-[2rem] px-4 hubee-premium-card transition focus-within:shadow-[0_24px_80px_hsl(var(--primary)/0.18)] md:h-[76px] md:gap-4 md:rounded-[2.4rem] md:px-6"
        >
          <button
            type="button"
            onClick={openDesktopCommandPanel}
            className="relative z-[1] flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-500/10 md:h-12 md:w-12"
            aria-label={`Abrir ${masterAgentName}`}
          >
            <span className="absolute inset-0 rounded-full bg-blue-400/20 blur-md" />
            <span className="relative h-8 w-8 rounded-full bg-[radial-gradient(circle_at_35%_30%,#ffffff_0%,#7dd3fc_22%,#2563eb_62%,#4f46e5_100%)] shadow-[0_0_24px_rgba(59,130,246,0.7)] md:h-9 md:w-9" />
          </button>

          <textarea
            ref={commandInputRef}
            value={input}
            rows={1}
            onChange={(event) => {
              setInput(event.target.value)
              resizeTextarea(event.currentTarget)
            }}
            onFocus={handleCommandBarFocus}
            onBlur={() => {
              window.setTimeout(() => {
                setCommandFocused(false)
                setActiveSuggestionIndex(-1)
              }, 220)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                submitCommandBarText()
              }
            }}
            placeholder={`${masterAgentName}, ¿qué vamos a crear hoy?`}
            className="aia-widget-textarea relative z-[1] min-h-[28px] max-h-[76px] min-w-0 flex-1 resize-none overflow-hidden bg-transparent py-1 text-[17px] font-medium leading-6 tracking-tight text-foreground/80 outline-none placeholder:text-muted-foreground md:min-h-[34px] md:max-h-[48px] md:text-[24px] md:leading-8"
          />

          <button
            type={shouldShowResetAsPrimaryAction ? 'button' : 'submit'}
            onClick={
              shouldShowResetAsPrimaryAction
                ? (event) => {
                    event.preventDefault()
                    resetActiveGuidedFlow()
                  }
                : undefined
            }
            disabled={
              loading
                ? true
                : shouldShowResetAsPrimaryAction
                  ? false
                  : !input.trim()
            }
            className={cn(
              'relative z-[1] flex h-10 w-10 shrink-0 items-center justify-center rounded-full shadow-[0_16px_36px_hsl(var(--primary)/0.28)] transition disabled:pointer-events-none disabled:opacity-40 md:h-11 md:w-11',
              shouldShowResetAsPrimaryAction
                ? 'bg-muted text-muted-foreground shadow-[0_12px_28px_hsl(var(--foreground)/0.08)] hover:bg-muted/80 hover:text-foreground'
                : 'bg-primary text-primary-foreground'
            )}
            aria-label={
              loading
                ? 'Cargando'
                : shouldShowResetAsPrimaryAction
                  ? `Reiniciar ${activeGuidedFlowLabel}`
                  : 'Enviar'
            }
            title={
              shouldShowResetAsPrimaryAction
                ? `Reiniciar ${activeGuidedFlowLabel}`
                : undefined
            }
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : shouldShowResetAsPrimaryAction ? (
              <RotateCcw className="h-4 w-4" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
          </button>
        </form>

        <AnimatePresence>
          {shouldShowExpressActions ? (
            <motion.div
              key="launchpad-inline-express-actions"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              className="mt-4 flex justify-center"
            >
              <div className="flex max-w-full gap-2 overflow-x-auto px-3 py-3 scrollbar-hide md:gap-3 md:px-4 md:pb-10">
                {expressActions.map((action, index) => {
                  const Icon = expressActionIcons[action.icon] || Sparkles

                  return (
                    <motion.button
                      key={action.id}
                      type="button"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{
                        duration: 0.2,
                        ease: 'easeOut',
                        delay: index * 0.045,
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault()
                      }}
                      onClick={() => runExpressAction(action)}
                      className="inline-flex h-11 shrink-0 items-center gap-2 rounded-[1rem] bg-background/78 px-4 text-sm font-medium text-foreground/78 shadow-[0_18px_48px_hsl(var(--foreground)/0.12)] ring-1 ring-border/35 backdrop-blur-2xl transition hover:bg-background/90 hover:text-foreground md:h-14 md:gap-3 md:rounded-[1.15rem] md:px-6"
                    >
                      <Icon className="h-4 w-4 text-primary" />
                      {action.label}
                    </motion.button>
                  )
                })}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  ) : null

  const desktopSamanthaOSBar = !isFocusMode && !isAIHostRoute ? (
      <div
        data-samantha-os="true"        
        className={cn(
          'pointer-events-none fixed hidden md:block',
          'left-[var(--hubee-sidebar-offset,112px)]',

          hasContextBarToggle &&
          contextBarMode === 'publish' &&
          contextBarExpanded
            ? 'right-0 top-4 bottom-4 z-[70]'
            : isAIHostRoute
              ? 'right-0 top-[29vh] h-[220px] z-[50]'
              : 'right-[220px] top-3 h-[64px] z-[40]'
        )}

      >
        <div
          className={cn(
            'pointer-events-none flex justify-center',

            hasContextBarToggle &&
            contextBarMode === 'publish' &&
            contextBarExpanded
              ? 'h-auto items-start px-7'
              : cn(
                  'items-center',
                  isAIHostRoute
                    ? 'h-[92px] px-6'
                    : 'h-[64px] px-4'
                )
          )}
        >
          <motion.div
            ref={commandBarWrapRef}
            layout="size"
            transition={{
              layout: {
                type: 'spring',
                stiffness: 360,
                damping: 28,
                mass: 0.8,
              },
            }}
            className={cn(
              'relative w-full',

              hasContextBarToggle &&
              contextBarMode === 'publish' &&
              contextBarExpanded
                ? 'z-[70] max-w-[1040px]'
                : isAIHostRoute
                  ? 'z-[50] max-w-[960px]'
                  : 'z-[50] max-w-[680px]'
            )}
          >            
            <motion.form
              layout="size"
              onSubmit={(event) => {
                event.preventDefault()
                submitCommandBarText()
              }}
              transition={{
                layout: {
                  type: 'spring',
                  stiffness: 340,
                  damping: 27,
                  mass: 0.8,
                },
              }}
              className={cn(
                'pointer-events-auto group w-full hubee-premium-card transition',
                'focus-within:shadow-[0_30px_100px_hsl(var(--primary)/0.16)]',

                hasContextBarToggle &&
                contextBarMode === 'publish' &&
                contextBarExpanded
                  ? [
                      'flex flex-col',
                      'rounded-[28px]',
                      '!overflow-hidden',
                      'px-6 py-4',
                      'bg-background/50',
                      'backdrop-blur-2xl',
                      'shadow-[0_28px_100px_hsl(var(--foreground)/0.18)]',
                    ].join(' ')
                  : cn(
                      'flex flex-col !overflow-visible',
                      isAIHostRoute
                        ? 'h-[76px] rounded-[2.4rem] px-6'
                        : 'h-[56px] rounded-[18px] px-3'
                    )
              )}
            >
              <div
                className={cn(
                  'flex w-full items-center gap-4',

                  hasContextBarToggle &&
                  contextBarMode === 'publish' &&
                  contextBarExpanded
                    ? 'min-h-[52px] items-start'
                    : 'h-full'
                )}
              >
              <button
                type="button"
                onClick={
                  toggleContextBarMode
                }
                className={cn(
                  'relative z-[1] flex shrink-0 items-center justify-center rounded-full bg-blue-500/10',
                  isAIHostRoute ? 'h-12 w-12' : 'h-10 w-10'
                )}
                aria-label={`Abrir ${masterAgentName}`}
              >
                <span className="absolute inset-0 rounded-full bg-blue-400/20 blur-md" />
                  <AnimatePresence
                    mode="wait"
                    initial={false}
                  >
                    <motion.span
                      key={
                        hasContextBarToggle
                          ? contextBarMode
                          : 'agent'
                      }
                      initial={{
                        opacity: 0,
                        scale: 0.72,
                        rotate: -12,
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        rotate: 0,
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.72,
                        rotate: 12,
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 520,
                        damping: 23,
                      }}
                      className={cn(
                        'relative flex items-center justify-center rounded-full',
                        'bg-[radial-gradient(circle_at_35%_30%,#ffffff_0%,#7dd3fc_22%,#2563eb_62%,#4f46e5_100%)]',
                        'text-white shadow-[0_0_24px_rgba(59,130,246,0.7)]',
                        isAIHostRoute
                          ? 'h-9 w-9'
                          : 'h-8 w-8'
                      )}
                    >
                      {hasContextBarToggle &&
                      contextBarMode === 'publish' ? (
                        <MessageSquare className="h-4 w-4" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                    </motion.span>
                  </AnimatePresence>
              </button>

              <div className="min-w-0 w-full flex-1">
                <AnimatePresence initial={false}>
                  {isContextPublishing &&
                  canPublishInContext &&
                  contextBarExpanded ? (
                    <motion.div
                      key="publish-title"
                      initial={{
                        opacity: 0,
                        height: 0,
                        y: -6,
                      }}
                      animate={{
                        opacity: 1,
                        height: 'auto',
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        height: 0,
                        y: -4,
                      }}
                      transition={{
                        opacity: {
                          duration: 0.16,
                        },
                        height: {
                          type: 'spring',
                          stiffness: 360,
                          damping: 30,
                        },
                      }}
                      className="overflow-hidden"
                    >
                      <input
                        value={
                          contextPublishComposer.title
                        }
                        onChange={event =>
                          contextPublishComposer.setTitle(
                            event.target.value
                          )
                        }
                        placeholder="Título opcional"
                        className={[
                          'mt-2 w-full border-0 bg-transparent p-0',
                          'text-xl font-semibold tracking-tight',
                          'text-[#202124] dark:text-foreground',
                          'placeholder:text-muted-foreground/50',
                          'outline-none ring-0',
                        ].join(' ')}
                      />
                    </motion.div>
                  ) : null}
                </AnimatePresence>
                
                {contextBarMode ===
                    'publish' &&
                  !canPublishInContext ? (
                    <div className="flex min-w-0 flex-1 items-center">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Lock className="h-3.5 w-3.5 shrink-0" />

                        <span>
                          No tienes permisos para publicar en este espacio.
                        </span>
                      </div>
                    </div>
                  ) : (
                  <textarea
                    ref={commandInputRef}
                    value={
                      hasContextBarToggle &&
                      contextBarMode === 'publish'
                        ? contextPublishComposer.content
                        : input
                    }
                    rows={1}
                    onFocus={() => {
                      setCommandFocused(
                        true
                      )

                      if (
                        hasContextBarToggle &&
                        contextBarMode ===
                          'publish'
                      ) {
                        contextPublishComposer
                          .openComposer({
                            initialContent:
                              input,
                          })

                        setContextBarExpanded(
                          true
                        )
                      }
                    }}
                    onBlur={() => {
                      window.setTimeout(() => {
                        setCommandFocused(false)
                        setActiveSuggestionIndex(-1)
                      }, 220)
                    }}
                    onChange={(event) => {
                      const value =
                        event.target.value

                      setInput(value)

                      if (
                        hasContextBarToggle &&
                        contextBarMode === 'publish'
                      ) {
                        contextPublishComposer.setContent(
                          value
                        )
                      }

                      resizeTextarea(
                        event.target
                      )
                    }}
                    onKeyDown={(event) => {
                      if (
                        showLightweightSuggestions &&
                        !isContextPublishing
                      ) {
                        if (event.key === 'ArrowDown') {
                          event.preventDefault()
                          setActiveSuggestionIndex((prev) => {
                            if (lightweightSuggestions.length <= 0) return -1
                            return prev >= lightweightSuggestions.length - 1 ? 0 : prev + 1
                          })
                          return
                        }

                        if (event.key === 'ArrowUp') {
                          event.preventDefault()
                          setActiveSuggestionIndex((prev) => {
                            if (lightweightSuggestions.length <= 0) return -1
                            return prev <= 0 ? lightweightSuggestions.length - 1 : prev - 1
                          })
                          return
                        }

                        if (event.key === 'Escape') {
                          event.preventDefault()
                          setActiveSuggestionIndex(-1)
                          return
                        }

                        if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
                          event.preventDefault()

                          const suggestion = lightweightSuggestions[activeSuggestionIndex]

                          if (suggestion) {
                            acceptLightweightSuggestion(suggestion)
                          }

                          return
                        }
                      }

                      if (event.key !== 'Enter') {
                        return
                      }

                      /*
                      * En modo Publish, Enter siempre
                      * crea una nueva línea.
                      */
                      if (
                        hasContextBarToggle &&
                        contextBarMode === 'publish'
                      ) {
                        return
                      }

                      /*
                      * En modo Agent:
                      * Shift + Enter = nueva línea
                      * Enter = enviar a Samantha
                      */
                      if (event.shiftKey) {
                        return
                      }

                      event.preventDefault()
                      submitCommandBarText()
                    }}
                    placeholder={
                      hasContextBarToggle
                        ? contextBarMode ===
                            'publish'
                          ? samanthaPageContext.kind ===
                              'encounter'
                            ? 'Aporta a este Encuentro…'
                            : 'Comparte algo en este Space…'
                          : `Pregúntale a ${masterAgentName}…`
                        : contextualComposer.placeholder
                    }
                    className={cn(
                      'aia-widget-textarea relative z-[1] w-full min-w-0 flex-1 resize-none overflow-y-auto bg-transparent font-small tracking-tight text-[#202124] dark:text-foreground/90 outline-none placeholder:text-muted-foreground',

                      hasContextBarToggle &&
                      contextBarMode === 'publish' &&
                      contextBarExpanded
                        ? 'min-h-[118px] max-h-[210px] py-2 text-[17px] leading-7'
                        : isAIHostRoute
                          ? 'max-h-[48px] min-h-[34px] overflow-hidden py-1 text-[24px] leading-8'
                          : 'max-h-[38px] min-h-[28px] overflow-hidden py-1 text-[17px] leading-6'
                    )}
                  />
                )}
              </div>

              <div
                className="relative z-[60] hidden shrink-0 lg:block"
                onMouseDown={(event) => {
                  event.stopPropagation()
                }}
              >
                {!(
                  hasContextBarToggle &&
                  contextBarMode ===
                    'publish'
                ) ? (
                  <SkillSelector />
                ) : null}
              </div>

              <input
                id="samantha-ai-funnel-reference-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/avif"
                className="hidden"
                onChange={handleAIFunnelReferenceImageSelected}
              />
              
              {canUploadPublicationReference && (
                <>
                  <input
                    id="samantha-reference-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleReferenceImageSelected}
                  />

                  <button
                    type="button"
                    onClick={() => {
                      document.getElementById('samantha-reference-upload')?.click()
                    }}
                    disabled={uploadingReference}
                    className="rounded-full px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
                  >
                    {uploadingReference ? 'Subiendo...' : 'Subir imagen'}
                  </button>
                </>
              )}

              {!(
                hasContextBarToggle &&
                contextBarMode ===
                  'publish'
              ) ? (
              <button
                type={
                  loading || shouldShowResetAsPrimaryAction
                    ? 'button'
                    : 'submit'
                }
                onClick={
                  loading
                    ? cancelStream
                    : shouldShowResetAsPrimaryAction
                      ? (event) => {
                          event.preventDefault()
                          resetActiveGuidedFlow()
                        }
                      : undefined
                }
                disabled={
                  loading
                    ? false
                    : shouldShowResetAsPrimaryAction
                      ? false
                      : !input.trim()
                }
                className={cn(
                  'relative z-[1] flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-[0_14px_34px_rgba(37,99,235,0.34)] transition hover:scale-[1.03] disabled:opacity-50',
                  shouldShowResetAsPrimaryAction && !loading
                    ? 'bg-muted text-muted-foreground shadow-[0_12px_28px_hsl(var(--foreground)/0.08)] hover:bg-muted/80 hover:text-foreground'
                    : 'bg-gradient-to-br from-blue-500 to-indigo-500'
                )}
                aria-label={
                  loading
                    ? 'Cancelar'
                    : shouldShowResetAsPrimaryAction
                      ? `Reiniciar ${activeGuidedFlowLabel}`
                      : 'Enviar comando'
                }
                title={
                  shouldShowResetAsPrimaryAction && !loading
                    ? `Reiniciar ${activeGuidedFlowLabel}`
                    : undefined
                }
              >
                {loading ? (
                  <StopCircle className="h-5 w-5" />
                ) : shouldShowResetAsPrimaryAction ? (
                  <RotateCcw className="h-5 w-5" />
                ) : (
                  <ArrowUp className="h-5 w-5" />
                )}
              </button>
              ) : null}
              </div>



              <AnimatePresence initial={false}>
                {hasContextBarToggle &&
                canPublishInContext &&
                contextBarMode ===
                  'publish' &&
                contextBarExpanded ? (
                  <motion.div
                    key="publish-extras"
                    initial={{
                      opacity: 0,
                      y: -8,
                      height: 0,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      height: 'auto',
                    }}
                    exit={{
                      opacity: 0,
                      y: -6,
                      height: 0,
                    }}
                    transition={{
                      opacity: {
                        duration: 0.18,
                      },
                      height: {
                        type: 'spring',
                        stiffness: 330,
                        damping: 30,
                      },
                    }}
                    className="overflow-hidden"
                  >
                    {encounterSuggestion &&
                    !encounterSuggestionDismissed && (
                      <div
                        className={[
                          'mb-4',
                          'rounded-[18px]',
                          'border border-muted/20',
                          'bg-muted/20',
                          'px-4 py-4',
                        ].join(' ')}
                      >
                        <div className="flex items-start gap-3">

                          <div
                            className={[
                              'flex size-9 shrink-0',
                              'items-center justify-center',
                              'rounded-[12px]',
                              'bg-primary/10',
                              'text-primary',
                            ].join(' ')}
                          >
                            <MessageCircle
                              className="h-4 w-4"
                            />
                          </div>

                          <div className="min-w-0 flex-1">

                            <div
                              className={[
                                'text-[10px]',
                                'font-semibold uppercase',
                                'tracking-[0.09em]',
                                'text-muted-foreground',
                              ].join(' ')}
                            >
                              {encounterSuggestion.score >=
                              0.45
                                ? 'Esto ya se está hablando'
                                : 'Podría estar relacionado'}
                            </div>

                            <div className="mt-1 text-[13px] font-semibold text-foreground">
                              {
                                encounterSuggestion.title
                              }
                            </div>

                            {encounterSuggestion.description && (
                              <p
                                className={[
                                  'mt-1',
                                  'line-clamp-2',
                                  'text-[11px]',
                                  'leading-5',
                                  'text-muted-foreground',
                                ].join(' ')}
                              >
                                {
                                  encounterSuggestion.description
                                }
                              </p>
                            )}

                            <div className="mt-3 flex flex-wrap items-center gap-2">

                              <button
                                type="button"
                                onClick={() => {
                                  const selected =
                                    contextPublishComposer
                                      .suggestedEncounterId ===
                                    encounterSuggestion.encounterId

                                  if (selected) {
                                    contextPublishComposer
                                      .setSuggestedEncounterId(
                                        null
                                      )

                                    contextPublishComposer
                                      .setSuggestedContributionType(
                                        null
                                      )

                                    return
                                  }

                                  contextPublishComposer
                                    .setSuggestedEncounterId(
                                      encounterSuggestion.encounterId
                                    )
                                }}
                                className={[
                                  'group flex w-full',
                                  'items-center gap-3',
                                  'rounded-[14px]',
                                  'border px-3.5 py-3',
                                  'text-left',
                                  'transition-all duration-200',

                                  contextPublishComposer
                                    .suggestedEncounterId ===
                                  encounterSuggestion.encounterId
                                    ? [
                                        'border-primary/35',
                                        'bg-primary/[0.08]',
                                        'shadow-[0_8px_30px_hsl(var(--primary)/0.08)]',
                                      ].join(' ')
                                    : [
                                        'border-border/50',
                                        'bg-background/40',
                                        'hover:border-primary/25',
                                        'hover:bg-primary/[0.035]',
                                      ].join(' '),
                                ].join(' ')}
                              >
                                <span
                                  className={[
                                    'flex size-5 shrink-0',
                                    'items-center justify-center',
                                    'rounded-full',
                                    'border',
                                    'transition-all',

                                    contextPublishComposer
                                      .suggestedEncounterId ===
                                    encounterSuggestion.encounterId
                                      ? 'border-primary bg-primary text-primary-foreground'
                                      : 'border-muted-foreground/35 bg-background',
                                  ].join(' ')}
                                >
                                  {contextPublishComposer
                                    .suggestedEncounterId ===
                                  encounterSuggestion.encounterId ? (
                                    <Check
                                      size={12}
                                      strokeWidth={3}
                                    />
                                  ) : null}
                                </span>

                                <div className="min-w-0 flex-1">

                                  <div className="text-[11px] font-semibold text-foreground">
                                    Agregar mi aporte a este Encuentro
                                  </div>

                                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                                    Participarás dentro de esta conversación.
                                  </div>

                                </div>
                              </button>

                              {contextPublishComposer
                                .suggestedEncounterId !==
                                encounterSuggestion.encounterId && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    contextPublishComposer
                                      .setSuggestedEncounterId(
                                        null
                                      )

                                    contextPublishComposer
                                      .setSuggestedContributionType(
                                        null
                                      )

                                    setEncounterSuggestionDismissed(
                                      true
                                    )
                                  }}
                                  className="mt-2 text-[10px] font-medium text-muted-foreground transition hover:text-foreground"
                                >
                                  No está relacionado · Publicar aparte
                                </button>
                              )}

                            </div>

                          </div>

                        </div>
                      </div>
                    )}
                    <ContextPublishComposerExtras
                      composer={contextPublishComposer}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </motion.form>

            <AnimatePresence>
              {shouldShowExpressActions ? (
                <motion.div
                  key="samantha-express-actions"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16, ease: 'easeOut' }}
                  className={cn(
                    'pointer-events-none absolute inset-x-0 z-10 flex justify-center',
                    isAIHostRoute ? 'top-[96px]' : 'top-[82px]'
                  )}
                >
                  <div className="pointer-events-auto flex max-w-full justify-center gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
                    {expressActions.map((action, index) => {
                      const Icon = expressActionIcons[action.icon] || Sparkles

                      return (
                        <motion.button
                          key={action.id}
                          type="button"
                          initial={{ opacity: 0, y: -12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{
                            duration: 0.2,
                            ease: 'easeOut',
                            delay: isAIHostRoute ? 0 : index * 0.045,
                          }}
                          onMouseDown={(event) => {
                            event.preventDefault()
                          }}
                          onClick={() => runExpressAction(action)}
                          className={cn(
                            'group inline-flex shrink-0 items-center gap-3 rounded-[1.15rem] bg-background/72 text-sm font-medium text-foreground/78 shadow-[0_16px_44px_hsl(var(--foreground)/0.08)] ring-1 ring-border/35 backdrop-blur-2xl transition-all hover:-translate-y-0.5 hover:bg-background/88 hover:text-foreground hover:shadow-[0_22px_60px_hsl(var(--foreground)/0.12)]',
                            isAIHostRoute ? 'h-14 px-6' : 'h-11 px-5'
                          )}
                        >
                          <span className="relative z-[1] inline-flex items-center gap-3">
                            <Icon className="h-4 w-4 text-primary" />
                            {action.label}
                          </span>
                        </motion.button>
                      )
                    })}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {showLightweightSuggestions && !isContextPublishing && commandFocused ? (
              <div className="pointer-events-auto absolute left-0 right-0 top-[74px] overflow-hidden rounded-[1.5rem] border border-white/80 bg-white/95 shadow-[0_24px_80px_rgba(15,23,42,0.10)] ring-1 ring-slate-200/60 backdrop-blur-2xl">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold text-slate-700">
                      Sugerencias express sin créditos
                    </span>
                  </div>

                  <span className="text-[11px] font-medium text-slate-400">
                    Tab para completar
                  </span>
                </div>

                <div className="max-h-64 overflow-y-auto p-2">
                  {lightweightSuggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.intent}-${suggestion.text}`}
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault()
                        acceptLightweightSuggestion(suggestion)
                      }}
                      className={`flex w-full items-start gap-3 rounded-2xl px-3 py-3 text-left transition ${
                        index === activeSuggestionIndex
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-700 hover:bg-primary/5 hover:text-primary'
                      }`}
                    >
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200/70">
                        <Sparkles className="h-4 w-4" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">
                          {suggestion.label}
                        </p>

                        {suggestion.description ? (
                          <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-slate-500">
                            {suggestion.description}
                          </p>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </motion.div>

          <div className="w-[140px] shrink-0" />
        </div>
    </div>
  ) : null

  const chatSurface = (
    <AnimatePresence>
        {uiState === 'chat' && (
          <motion.div
            initial={
              isFocusMode
                ? { opacity: 0, scale: 0.98 }
                : { opacity: 0, y: 18, scale: 0.98 }
            }
            animate={
              isFocusMode
                ? { opacity: 1, scale: 1 }
                : { opacity: 1, y: 0, scale: 1 }
            }
            exit={
              isFocusMode
                ? { opacity: 0, scale: 0.98 }
                : { opacity: 0, y: 18, scale: 0.98 }
            }
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={
              isEmbeddedConsole
                ? 'relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-transparent'
                : isFocusMode
                  ? 'fixed inset-0 z-[90] flex items-center justify-center overflow-hidden bg-background/72 px-6 py-7 backdrop-blur-2xl'
                  : cn(
                      `fixed inset-x-0 bottom-0 z-[82]
                      flex h-[85vh] w-full flex-col overflow-hidden rounded-t-2xl bg-background shadow-2xl
                      md:bottom-auto md:h-auto md:bg-transparent md:shadow-none`,
                      isAIHostRoute
                        ? 'bottom-auto'
                        : 'md:top-[98px]'
                    )
            }
            style={
              !isEmbeddedConsole &&
              isAIHostRoute &&
              launchpadDockTop
                ? { top: launchpadDockTop }
                : undefined
            }
          >
              <div
                ref={!isFocusMode ? compactPanelRef : undefined}
                className={
                  isEmbeddedConsole
                    ? 'relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-transparent'
                    : isFocusMode
                      ? 'flex h-full max-h-[calc(100vh-56px)] w-full max-w-[1040px] min-h-0 flex-col overflow-hidden rounded-[2rem] border border-border/60 bg-background shadow-[0_30px_120px_hsl(var(--foreground)/0.14)] ring-1 ring-border/40'
                      : 'flex min-h-0 flex-1 flex-col overflow-hidden md:mx-auto md:w-full md:max-w-[860px] md:rounded-[2rem] md:border md:border-border/60 md:bg-background md:shadow-[0_30px_100px_hsl(var(--foreground)/0.12)] md:ring-1 md:ring-border/40'
                }
              >
              {isEmbeddedConsole ? null : isFocusMode ? (
                <div className="flex h-[92px] shrink-0 items-center justify-between border-b border-border/60 bg-background/70 px-7 backdrop-blur-xl">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-500/10">
                      <span className="absolute inset-0 rounded-full bg-blue-400/20 blur-md" />
                      <span className="relative h-10 w-10 rounded-full bg-[radial-gradient(circle_at_35%_30%,#ffffff_0%,#7dd3fc_22%,#2563eb_62%,#4f46e5_100%)] shadow-[0_0_24px_rgba(59,130,246,0.7)]" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-base font-bold tracking-tight text-foreground">
                          {masterAgentName}
                        </p>

                        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                      </div>

                      <p className="mt-1 truncate text-sm font-medium text-muted-foreground">
                        Tu asistente de contenido con IA
                      </p>
                    </div>

                    <div className="hidden h-7 w-px bg-border/70 lg:block" />

                    <div className="hidden min-w-0 items-center gap-2 lg:flex">
                      <SkillSelector />

                      <div className="min-w-0">
                        <ActiveSkillBanner />
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {assistantNotifications.length > 0 && !showNotificationHint ? (
                      <button
                        type="button"
                        onClick={() => setShowNotificationsPanel(true)}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-red-500/10 px-3 text-[11px] font-semibold text-red-600 transition hover:bg-red-500/15"
                        title="Sugerencias de Samantha"
                      >
                        <span>
                          {assistantNotifications.length > 9 ? '9+' : assistantNotifications.length}
                        </span>
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => {
                        if (audioEnabled) {
                          setAudioEnabled(false)
                          stopSpeech()
                          return
                        }

                        requestEnableAudio()
                      }}
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-[10px] font-semibold transition ${
                        audioEnabled
                          ? 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'
                          : 'bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      title={
                        audioEnabled
                          ? `Audio activado. ${masterAgentName} puede hablar y consumir créditos.`
                          : `Activar audio de ${masterAgentName}. Esta función consume créditos.`
                      }
                    >
                      {audioEnabled ? (
                        <Volume2 className="h-4 w-4" />
                      ) : (
                        <VolumeX className="h-4 w-4" />
                      )}
                    </button>

                    <div
                      className="inline-flex h-9 items-center gap-1.5 rounded-full bg-muted/80 px-3 text-[12px] font-semibold text-foreground"
                      title="Créditos IA disponibles"
                    >
                      <Coins className="h-4 w-4 text-primary" />
                      {credits}
                    </div>

                    <button
                      type="button"
                      onClick={() => setChatViewMode('dock')}
                      className="hidden h-9 w-9 items-center justify-center rounded-full bg-muted/80 text-muted-foreground transition hover:bg-muted hover:text-foreground sm:flex"
                      title="Volver a modo compacto"
                    >
                      <Minimize2 className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => {
                        manuallyClosedRef.current = true
                        stopSpeech()
                        setChatViewMode('dock')
                        setUIState('closed')
                      }}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-muted/80 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      title="Cerrar"
                    >
                      <X className="h-4 w-4" />
                    </button>

                    {activeGuidedFlowLabel ? (
                      <button
                        type="button"
                        onClick={resetActiveGuidedFlow}
                        className="rounded-full bg-muted/80 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
                        title={`Reiniciar ${activeGuidedFlowLabel}`}
                      >
                        Reiniciar
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between bg-black px-3 py-1.5 text-white md:hidden">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-3.5 w-3.5" />
                      <span className="text-xs font-semibold tracking-wide">
                        {masterAgentName}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setChatViewMode((mode) => (mode === 'focus' ? 'dock' : 'focus'))
                        }
                        className="hidden rounded-lg p-1.5 transition hover:bg-black/5 dark:hover:bg-white/10 sm:flex"
                        title={chatViewMode === 'focus' ? 'Volver a modo compacto' : 'Abrir modo focus'}
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                      </button>

                      <button
                        onClick={() => {
                          manuallyClosedRef.current = true
                          stopSpeech()
                          setChatViewMode('dock')
                          setUIState('closed')
                        }}
                        className="rounded p-1 transition hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>

                      {activeGuidedFlowLabel ? (
                        <button
                          type="button"
                          onClick={resetActiveGuidedFlow}
                          className="rounded-full bg-muted/80 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          title={`Reiniciar ${activeGuidedFlowLabel}`}
                        >
                          Reiniciar
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col items-center border-b border-neutral-200 bg-white/80 px-4 py-3 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/80 md:hidden">
                    <div className="flex w-full flex-wrap items-center gap-2 text-sm font-semibold">
                      <SkillSelector />

                      {assistantNotifications.length > 0 && !showNotificationHint ? (
                        <button
                          type="button"
                          onClick={() => setShowNotificationsPanel(true)}
                          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold text-red-500 transition hover:bg-red-500/10 dark:bg-neutral-800"
                          title="Sugerencias de Samantha"
                        >
                          <span>
                            {assistantNotifications.length > 9 ? '9+' : assistantNotifications.length}
                          </span>
                          <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => {
                          if (audioEnabled) {
                            setAudioEnabled(false)
                            stopSpeech()
                            return
                          }

                          requestEnableAudio()
                        }}
                        className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-2 py-2 text-[10px] font-semibold transition ${
                          audioEnabled
                            ? 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'
                            : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                        }`}
                        title={
                          audioEnabled
                            ? `Audio activado. ${masterAgentName} puede hablar y consumir créditos.`
                            : `Activar audio de ${masterAgentName}. Esta función consume créditos.`
                        }
                      >
                        {audioEnabled ? (
                          <Volume2 className="h-3.5 w-3.5" />
                        ) : (
                          <VolumeX className="h-3.5 w-3.5" />
                        )}
                      </button>

                      <div
                        className="flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-[11px] text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                        title="Créditos IA disponibles"
                      >
                        <Coins className="h-3.5 w-3.5" />
                        {credits}
                      </div>
                    </div>

                    {assistantNotifications.length > 0 ? (
                      <div
                        className={`w-full overflow-hidden transition-all duration-500 ease-out ${
                          showNotificationHint
                            ? 'mt-2 max-h-20 opacity-100'
                            : 'mt-0 max-h-0 opacity-0'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setShowNotificationsPanel(true)}
                          className="flex w-full items-center justify-between rounded-2xl border border-neutral-200 bg-neutral-50/80 px-3 py-2 text-left shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
                        >
                          <span className="flex items-center gap-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                            {masterAgentName} tiene sugerencias para ti
                          </span>

                          <span className="text-[11px] text-neutral-400">Ver</span>
                        </button>
                      </div>
                    ) : null}

                    <div className="mt-1 flex w-full items-center justify-between gap-2">
                      <div className="flex w-full flex-col gap-2">
                        <ActiveSkillBanner />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div
                className={`absolute inset-0 z-50 bg-white dark:bg-neutral-950 transition-transform duration-300 ease-out ${
                  showNotificationsPanel ? 'translate-x-0' : 'translate-x-full pointer-events-none'
                }`}
              >
                <div className="flex h-full flex-col">
                  <div className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
                    <button
                      type="button"
                      onClick={() => setShowNotificationsPanel(false)}
                      className="rounded-xl p-1.5 transition hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      title="Volver al chat"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>

                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-950 dark:text-neutral-50">
                        Sugerencias de {masterAgentName}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Acciones pendientes para continuar
                      </p>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {assistantNotifications.map((item: any) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setShowNotificationsPanel(false)
                          handleButtonClick(
                            {
                              label: item.label,
                              value: item.value,
                              emoji: item.emoji,
                              action: item.route ? 'navigate' : undefined,
                              route: item.route || undefined,
                            } as any,
                            null as any
                          )
                        }}
                        className="w-full rounded-2xl border border-neutral-200 bg-white px-3 py-3 text-left shadow-sm transition hover:border-black/20 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-white/20"
                      >
                        <div className="flex gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-base dark:bg-neutral-800">
                            {item.emoji || '✨'}
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-400">
                              {item.title || item.category || 'Sugerencia'}
                            </p>

                            <p className="mt-0.5 text-sm font-semibold text-neutral-950 dark:text-neutral-50">
                              {item.label}
                            </p>

                            {item.description ? (
                              <p className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                                {item.description}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div
                ref={scrollRef}
                onScroll={handleScrollMessages}
                className={
                  isEmbeddedConsole
                    ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain bg-transparent px-4 sm:px-6'
                    : isFocusMode
                      ? 'min-h-0 flex-1 overflow-y-auto bg-transparent text-sm'
                      : 'max-h-[min(52vh,440px)] overflow-y-auto px-4 py-3 text-sm md:max-h-[min(46vh,380px)]'
                }
              >
                <div
                  className={
                    isEmbeddedConsole
                      ? 'mx-auto flex min-h-full w-full max-w-full flex-col space-y-4 py-8 pb-5'
                      : isFocusMode
                        ? 'mx-auto flex min-h-full w-full max-w-[960px] flex-col space-y-3 px-4 py-6'
                        : 'space-y-3'
                  }
                >
                {visibleCount < messages.length && (
                  <div className="flex justify-center">
                    <div className="text-[11px] px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                      Desliza hacia arriba para ver mensajes anteriores
                    </div>
                  </div>
                )}

                {visibleMessages.map((m) => (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChatBubble
                      message={m}
                      masterAgentName={masterAgentName}
                      onButtonClick={(button, message) => handleButtonClick(button, message)}
                      isAdmin={isAdmin}
                    />
                  </motion.div>
                ))}

                {showNoCreditsCard && (
                  <NoCreditsCard
                    onBuy={handleBuyCredits}
                    packages={creditPackages}
                    loadingPackages={loadingCreditPackages}
                  />
                )}

                {loading && <TypingIndicator agentName={masterAgentName} />}

                <div ref={messagesEndRef} />
              </div>
              </div>

              {!isEmbeddedConsole &&
              messages.length <= 1 &&
              !loading && (
                <div
                  className={
                    isFocusMode
                      ? 'mx-auto w-full px-4 pb-2'
                      : 'px-4 pb-2'
                  }
                >
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'Llévame a mi último curso',
                      '¿Donde me quedé?',
                      'Ayúdame con mi embudo',
                      'Crea un post para redes',
                    ].map((chip) => (
                      <button
                        key={chip}
                        onClick={() => setInput(chip)}
                        className="rounded-full border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!showNotificationsPanel ? (
              <div
                className={
                  isEmbeddedConsole
                    ? 'relative z-10 shrink-0 bg-gradient-to-t from-[#05070a] via-[#05070a]/95 to-transparent px-4 pb-5 pt-4 sm:px-6'
                    : isFocusMode
                      ? 'z-10 bg-transparent px-7 pb-7'
                      : 'p-3 border-t border-neutral-200 dark:border-neutral-800 md:hidden'
                }
              >
                <div 
                  className={
                    isEmbeddedConsole
                      ? 'mx-auto w-full max-w-[860px]'
                      : isFocusMode
                        ? 'mx-auto w-full max-w-[960px]'
                        : ''
                  }
                >
                  {showLightweightSuggestions && (
                    <div className="mb-2 rounded-2xl border border-emerald-200/70 dark:border-emerald-900/60 bg-white/95 dark:bg-neutral-900/95 shadow-lg overflow-hidden">
                      <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-neutral-100 dark:border-neutral-800">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-200">
                            Sugerencias express sin créditos
                          </span>
                        </div>

                        <span className="hidden sm:inline text-[10px] text-neutral-400">
                          Tab para completar
                        </span>
                      </div>

                      <div className="max-h-48 overflow-y-auto">
                        {lightweightSuggestions.map((suggestion, index) => {
                          const isActive = index === activeSuggestionIndex

                          return (
                            <button
                              key={suggestion.id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                acceptLightweightSuggestion(suggestion)
                              }}
                              onMouseEnter={() => setActiveSuggestionIndex(index)}
                              className={[
                                'w-full text-left px-3 py-2.5 transition-colors',
                                isActive
                                  ? 'bg-emerald-50 dark:bg-emerald-950/30'
                                  : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/70',
                              ].join(' ')}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                                    {suggestion.text}
                                  </div>

                                  <div className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                                    {suggestion.description}
                                  </div>
                                </div>

                                <span className="shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-semibold">
                                  Express
                                </span>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {showEmbeddedContentInitiative ? (
                    <div className="mx-auto w-full max-w-[860px] pb-3">
                      <div className="grid gap-2.5 sm:grid-cols-3">
                        {EMBEDDED_CONTENT_STARTERS.map((starter) => (
                          <button
                            key={starter.id}
                            type="button"
                            onClick={() => {
                              void handleSend(starter.prompt)
                            }}
                            className={cn(
                              'group relative min-w-0 rounded-[1.15rem] border px-4 py-4 text-left transition',
                              starter.featured
                                ? 'border-sky-400/20 bg-sky-400/[0.07] hover:border-sky-300/35 hover:bg-sky-400/[0.11]'
                                : 'border-white/[0.08] bg-white/[0.035] hover:border-white/[0.14] hover:bg-white/[0.065]'
                            )}
                          >
                            {starter.featured ? (
                              <span className="mb-3 inline-flex rounded-full border border-sky-300/15 bg-sky-300/[0.08] px-2 py-1 text-[9px] font-medium uppercase tracking-[0.18em] text-sky-200/65">
                                Comenzar aquí
                              </span>
                            ) : null}

                            <span className="flex items-start justify-between gap-3">
                              <span className="min-w-0">
                                <span className="block text-[13px] font-medium text-white/85">
                                  {starter.label}
                                </span>

                                <span className="mt-1.5 block text-[11px] leading-5 text-white/38">
                                  {starter.description}
                                </span>
                              </span>

                              <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/25 transition group-hover:translate-x-0.5 group-hover:text-sky-300/70" />
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div
                    className={
                      isEmbeddedConsole
                        ? 'flex w-full items-center gap-3 rounded-[1.6rem] border border-white/[0.09] bg-white/[0.045] px-4 py-3 shadow-[0_18px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl transition focus-within:border-sky-400/25 focus-within:bg-white/[0.06]'
                        : isFocusMode
                          ? 'mx-auto flex w-full items-center gap-3 rounded-full border border-border/60 bg-background px-5 py-3 shadow-[0_12px_30px_hsl(var(--foreground)/0.08)]'
                          : 'flex items-end gap-2'
                    }
                  >
                    <textarea
                      disabled={loading}
                      value={input}
                      placeholder={
                        firstName
                          ? `${firstName}, ¿qué quieres lograr hoy?`
                          : '¿Qué quieres lograr hoy?'
                      }
                      rows={1}
                      className={
                        isEmbeddedConsole
                          ? 'aia-widget-textarea min-h-[28px] max-h-36 min-w-0 flex-1 resize-none overflow-y-auto bg-transparent px-1 py-1 text-[15px] leading-6 text-white/85 outline-none placeholder:text-white/30 disabled:opacity-50'
                          : isFocusMode
                            ? 'min-h-[28px] max-h-32 flex-1 resize-none overflow-hidden bg-transparent p-0 text-[15px] leading-6 text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-50'
                            : 'w-full max-h-40 resize-none rounded-xl bg-neutral-100 px-4 py-2 text-sm leading-relaxed text-neutral-900 focus:outline-none focus:ring-2 focus:ring-black/10 disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-100 dark:focus:ring-white/10'
                      }
                      onChange={(e) => {
                        setInput(e.target.value)
                        resizeTextarea(e.currentTarget)
                      }}
                      onFocus={() => {
                        if (isEmbeddedConsole) {
                          setEmbeddedComposerActivated(true)
                        }
                      }}
                      ref={inputRef}
                      onKeyDown={(e) => {
                        if (showLightweightSuggestions) {
                          if (e.key === 'Tab') {
                            e.preventDefault()

                            const suggestion =
                              lightweightSuggestions[
                                activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0
                              ]

                            if (suggestion) {
                              acceptLightweightSuggestion(suggestion)
                            }

                            return
                          }

                          if (e.key === 'ArrowDown') {
                            e.preventDefault()

                            setActiveSuggestionIndex((prev) => {
                              if (lightweightSuggestions.length <= 0) return -1
                              return prev >= lightweightSuggestions.length - 1 ? 0 : prev + 1
                            })

                            return
                          }

                          if (e.key === 'ArrowUp') {
                            e.preventDefault()

                            setActiveSuggestionIndex((prev) => {
                              if (lightweightSuggestions.length <= 0) return -1
                              return prev <= 0 ? lightweightSuggestions.length - 1 : prev - 1
                            })

                            return
                          }

                          if (e.key === 'Escape') {
                            e.preventDefault()
                            setActiveSuggestionIndex(-1)
                            return
                          }

                          if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
                            e.preventDefault()

                            const suggestion = lightweightSuggestions[activeSuggestionIndex]

                            if (suggestion) {
                              acceptLightweightSuggestion(suggestion)
                            }

                            return
                          }
                        }

                        if (e.key !== 'Enter') return

                        // Shift + Enter = salto de línea
                        if (e.shiftKey) return

                        // Cmd + Enter también envía
                        if (e.metaKey) {
                          e.preventDefault()
                          const text = input
                          setInput('')
                          setActiveSuggestionIndex(-1)
                          handleSend(text)
                          requestAnimationFrame(() => {
                            if (inputRef.current) inputRef.current.style.height = 'auto'
                          })
                          focusInput()
                          return
                        }

                        // Enter normal = enviar
                        e.preventDefault()
                        const text = input
                        setInput('')
                        setActiveSuggestionIndex(-1)
                        handleSend(text)
                        requestAnimationFrame(() => {
                          if (inputRef.current) inputRef.current.style.height = 'auto'
                        })
                        focusInput()
                      }}
                    />

                    {isEmbeddedConsole && activeGuidedFlowLabel ? (
                      <button
                        type="button"
                        onClick={resetActiveGuidedFlow}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.045] text-white/45 transition hover:border-white/[0.14] hover:bg-white/[0.08] hover:text-white"
                        aria-label={`Reiniciar ${activeGuidedFlowLabel}`}
                        title={`Reiniciar ${activeGuidedFlowLabel}`}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    ) : null}

                      <div className="flex flex-col gap-1 self-end">
                        {loading ? (
                          <button
                            onClick={cancelStream}
                            title="Cancelar"
                            className={
                              isFocusMode
                                ? 'mb-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-500 text-white shadow-[0_14px_34px_rgba(239,68,68,0.28)] transition hover:scale-[1.03] hover:opacity-90'
                                : 'flex h-9 w-9 items-center justify-center rounded-xl bg-red-500 text-white transition hover:opacity-90'
                            }
                          >
                            <StopCircle className={isFocusMode ? 'h-5 w-5' : 'h-4 w-4'} />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              const text = input
                              setInput('')
                              setActiveSuggestionIndex(-1)
                              handleSend(text)
                              requestAnimationFrame(() => {
                                if (inputRef.current) inputRef.current.style.height = 'auto'
                              })
                              focusInput()
                            }}
                            disabled={!input.trim()}
                            className={
                              isEmbeddedConsole
                                ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-400 text-slate-950 shadow-[0_0_24px_rgba(56,189,248,0.22)] transition hover:bg-sky-300 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/20 disabled:shadow-none'
                                : isFocusMode
                                  ? 'mb-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_14px_34px_hsl(var(--primary)/0.28)] transition hover:scale-[1.03] disabled:opacity-50'
                                  : 'flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground transition hover:opacity-90 disabled:opacity-50'
                            }
                          >
                            {isEmbeddedConsole ? (
                              <ArrowUp className="h-4 w-4" />
                            ) : (
                              <Sparkles
                                className={
                                  isFocusMode
                                    ? 'h-5 w-5'
                                    : 'h-4 w-4'
                                }
                              />
                            )}
                          </button>
                        )}

                        <button
                          onClick={handleClear}
                          disabled={loading}
                          title="Limpiar chat"
                          className="hidden h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {!isEmbeddedConsole ? (
                    <div
                      className={
                        isFocusMode
                          ? 'mt-2 text-center text-[11px] text-neutral-400 dark:text-neutral-500'
                          : 'mt-2 text-[11px] text-neutral-400 dark:text-neutral-500'
                      }
                    >
                      Enter para enviar · Shift + Enter para salto de línea
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
  )

  return (
    <>
      <input
        id="samantha-create-content-product-input"
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/avif"
        className="hidden"
        onChange={(event) => {
          void handleExpressProductSelected(event)
        }}
      />

      {launchpadInlineSamanthaBar &&
      launchpadSlot?.isConnected &&
      isAIHostRoute
        ? createPortal(
            launchpadInlineSamanthaBar,
            launchpadSlot
          )
        : null}

      <AnimatePresence>
        {hasContextBarToggle &&
        contextBarMode ===
          'publish' &&
        contextBarExpanded ? (
          <motion.button
            type="button"
            aria-label="Cerrar publicación"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
            }}
            onClick={() => {
              setContextBarExpanded(
                false
              )

              contextPublishComposer
                .closeComposer({
                  reset:
                    false,
                })

              setCommandFocused(
                false
              )

              commandInputRef
                .current
                ?.blur()
            }}
            className="fixed inset-0 z-[60] bg-background/35 backdrop-blur-md"
          />
        ) : null}
      </AnimatePresence>

      {desktopSamanthaOSBar}

      {!isEmbeddedConsole ? (
        <SamanthaActivityHeader
          chatOpen={uiState === 'chat'}
        />
      ) : null}

      {isEmbeddedConsole &&
      launchpadSlot?.isConnected &&
      isAIHostRoute
        ? createPortal(
            chatSurface,
            launchpadSlot
          )
        : chatSurface}

      {!isAIHostRoute && uiState === 'closed' && (
        <button
          onClick={() => {
            setUIState('chat')
          }}
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-black text-white shadow-xl dark:bg-white dark:text-black md:hidden"
        >
          {assistantNotifications.length > 0 ? (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow">
              {assistantNotifications.length > 9 ? '9+' : assistantNotifications.length}
            </span>
          ) : null}
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      <AnimatePresence>
        {(uiState === 'training' || shouldShowTrainingOverlay) && currentAgentId && (
          <AIMasterTrainingOverlay
            agentId={currentAgentId}
            sellerId={sellerId}
            onClose={() => setUIState('closed')}
          />
        )}
      </AnimatePresence>

      <CreditsBuyCreditsModal
        open={buyCreditsOpen}
        onOpenChange={setBuyCreditsOpen}
        initialPackKey={selectedCreditPackKey}
        onCreditsAdded={() => {
          setShowNoCreditsCard(false)
          setSelectedCreditPackKey(null)
          window.dispatchEvent(new CustomEvent('hubee:credits_refresh'))
          router.refresh()
        }}
      />

      <AnimatePresence>
        {showAudioDisclosure && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="w-full max-w-md rounded-3xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-2xl overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
                <div className="flex items-center gap-2 text-base font-semibold">
                  <Volume2 className="w-4 h-4" />
                  <span>Activar audio de {masterAgentName}</span>
                </div>
              </div>

              <div className="px-5 py-4 space-y-4">
                <div className="text-sm text-neutral-700 dark:text-neutral-200 leading-relaxed space-y-3">
                  <p>
                    Cuando el audio está activado, {masterAgentName} puede leer sus respuestas en voz alta dentro del chat.
                  </p>
                  <p>
                    <span className="font-semibold">Importante:</span> cada respuesta hablada consume créditos de tu cuenta.
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Puedes apagar esta función en cualquier momento desde el mismo widget.
                  </p>
                </div>

                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dontShowAudioDisclosureAgain}
                    onChange={(e) => setDontShowAudioDisclosureAgain(e.target.checked)}
                    className="mt-0.5 rounded border-neutral-300 dark:border-neutral-700"
                  />
                  <span className="text-neutral-600 dark:text-neutral-300">
                    No volver a mostrar este aviso
                  </span>
                </label>
              </div>

              <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelEnableAudio}
                  className="px-4 py-2 rounded-xl text-sm font-medium border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition"
                >
                  Ahora no
                </button>

                <button
                  type="button"
                  onClick={confirmEnableAudio}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-black text-white dark:bg-white dark:text-black hover:opacity-90 transition"
                >
                  Activar audio
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  )
}